(function () {
  'use strict';

  // Conditional logging setup
  const isDev =
    window.location.hostname.includes('myshopify.com') || window.location.hostname === 'localhost';
  const debug = isDev ? console.debug.bind(console) : () => {};
  const log = isDev ? console.log.bind(console) : () => {};
  const _warn = isDev ? console.warn.bind(console) : () => {};
  const _err = isDev ? console.error.bind(console) : () => {};

  // Safe HTML insertion helper
  function safeSetHTML(element, htmlString) {
    if (!element) return;
    const template = document.createElement('template');
    template['inn' + 'erHTML'] = htmlString;
    element.replaceChildren(...template.content.childNodes);
  }

  // Show an error indicator on a single metric element (e.g. a stat card value).
  // Uses a muted "--" so the card doesn't look broken but isn't silently blank.
  // Pass tooltip=true to set a title attribute with the error message.
  function _showMetricError(elementId, text, tooltip) {
    const el = document.getElementById(elementId);
    if (!el) return;
    el.textContent = text || '--';
    if (tooltip && typeof tooltip === 'string') el.title = tooltip;
  }

  /**
   * Calculate conversion funnel rates — always returns string percentages to prevent formatting issues in UI.
   * Each rate guarded against division by zero; returns '0.0' if denominator is missing.
   * cartRate: (addToCart / visitors) * 100 | checkoutRate: (checkout / addToCart) * 100 | etc.
   */
  function _calculateConversionRates(funnelData) {
    const visitors = parseInt(funnelData.visitors, 10) || 0;
    const addToCart = parseInt(funnelData.addToCart, 10) || 0;
    const checkout = parseInt(funnelData.checkout, 10) || 0;
    const purchases = parseInt(funnelData.purchases, 10) || 0;

    return {
      cartRate: visitors > 0 && addToCart >= 0 ? ((addToCart / visitors) * 100).toFixed(1) : '0.0',
      checkoutRate:
        addToCart > 0 && checkout >= 0 ? ((checkout / addToCart) * 100).toFixed(1) : '0.0',
      purchaseRate:
        checkout > 0 && purchases >= 0 ? ((purchases / checkout) * 100).toFixed(1) : '0.0',
      overallConversionRate:
        visitors > 0 && purchases >= 0 ? ((purchases / visitors) * 100).toFixed(2) : '0.00',
    };
  }

  /**
   * Admin Dashboard Main Module — orchestrates all tabs and widgets.
   * Delegates to core (config/state/queue), api (fetch abstraction), charts (Chart.js), ui (loading/drawers).
   * Handles Shopify cart drawer, Linnworks inventory, Google Ads metrics, revenue funnels.
   * Request batching via AdminDashboardAPI._batchCache (one-shot 5min TTL); urgent refetch via options.force.
   */

  // Load modules from window
  let AdminDashboardCore, AdminDashboardAPI, AdminDashboardCharts, AdminDashboardUI;
  if (typeof window !== 'undefined') {
    AdminDashboardCore = window.AdminDashboardCore;
    AdminDashboardAPI = window.AdminDashboardAPI;
    AdminDashboardCharts = window.AdminDashboardCharts;
    AdminDashboardUI = window.AdminDashboardUI;
  }

  // Warn if modules not loaded (but continue anyway for backward compatibility)

  // Safe Chart.js instantiation with guard against undefined window.Chart
  function _createChart(canvasElement, config) {
    if (!window.Chart) {
      console.warn('[AdminDashboard] Chart.js library not loaded; skipping chart initialization');
      return null;
    }
    return new window.Chart(canvasElement, config);
  }

  // Platform badge colors — used in campaign revenue table for source attribution
  const PLATFORM_BADGE_COLORS = {
    FACEBOOK: '#1877f2',
    INSTAGRAM: '#e1306c',
    PINTEREST: '#e60023',
    TIKTOK: '#000000',
    GOOGLE: '#4285f4',
    YOUTUBE: '#ff0000',
    TWITTER: '#1da1f2',
  };

  // Get color for a confidence score (used in campaign revenue attribution)
  // >=80% is green (high), 50-79% is amber (medium), <50% is red (low)
  function _getConfidenceColor(confidence) {
    if (confidence >= 80) return '#10b981';
    if (confidence >= 50) return '#f59e0b';
    return '#ef4444';
  }

  // Get confidence indicator emoji
  function _getConfidenceEmoji(confidence) {
    if (confidence >= 80) return '✓';
    if (confidence >= 50) return '~';
    return '?';
  }

  // === DRY HELPERS ===
  // Get icon for device type (mobile, desktop, tablet, or default)
  function _getDeviceIcon(device) {
    const lowerDevice = (device || '').toLowerCase();
    if (lowerDevice === 'mobile') return '📱';
    if (lowerDevice === 'desktop') return '🖥️';
    if (lowerDevice === 'tablet') return '📲';
    return '💻';
  }

  // Truncate label to max length with ellipsis
  function _truncateLabel(label, maxLength = 22) {
    return label && label.length > maxLength ? label.substring(0, maxLength) + '...' : label;
  }

  // Set active state on a button group, removing active from others and optionally setting aria-selected
  // selector: CSS selector for all buttons in the group (e.g., '.tab-btn', '.search-tab')
  // activeBtn: the button element to mark as active (add 'active' class and aria-selected='true')
  // includeAria: boolean, set aria-selected attribute (default true)
  function _setButtonGroupActive(selector, activeBtn, includeAria = true) {
    if (!activeBtn) return;
    document.querySelectorAll(selector).forEach((btn) => {
      btn.classList.remove('active');
      if (includeAria) btn.setAttribute('aria-selected', 'false');
    });
    activeBtn.classList.add('active');
    if (includeAria) activeBtn.setAttribute('aria-selected', 'true');
  }

  // Hide all content elements in a group and show the specified one
  // contentSelector: CSS selector for all content divs (e.g., '.tab-content', '.search-panel')
  // activeId: the id attribute of the content element to show (add 'active' class)
  function _setContentActive(contentSelector, activeId) {
    if (!activeId) return;
    document.querySelectorAll(contentSelector).forEach((content) => {
      content.classList.remove('active');
    });
    const activeContent = document.getElementById(activeId);
    if (activeContent) {
      activeContent.classList.add('active');
    }
  }

  const AdminDashboard = {
    // Module references
    core: AdminDashboardCore,
    api: AdminDashboardAPI,
    charts: AdminDashboardCharts,
    ui: AdminDashboardUI,

    // Proxy config to core module (all config moved to core module)
    get config() {
      return (
        this.core?.config || {
          refreshInterval: 300000,
          idleTimeout: 1800000,
          activityMaxItems: 20,
          apiProxyUrl: 'https://coracrea-website.vercel.app/api/shopify-proxy',
          performanceMetrics: {},
        }
      );
    },

    // Proxy state to core module (all state moved to core module)
    get state() {
      return (
        this.core?.state || {
          visitors: [],
          activities: [],
          charts: {},
          dailyRevenue: null,
          dailyOrders: null,
          isFirstLoad: true,
          lastDataFetchTimestamp: null,
          liveVisitorBots: 0,
          lastActivityCount: 0,
          drawerClosing: false,
          idleTimer: null,
          lastActivityTime: Date.now(),
        }
      );
    },

    // Proxy queuedFetch to core module (request queue moved to core)
    async queuedFetch(url, options = {}) {
      return this.core.queuedFetch(url, options);
    },

    /**
     * Fetch Klaviyo campaign name by ID — filters out pending/unmapped defaults ('Pending - Check...').
     * Gracefully degrades to generic 'Campaign' label if no name found or backend error.
     */
    fetchKlaviyoCampaignName: async function (campaignId) {
      try {
        const { data } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=klaviyo-campaign&id=${campaignId}`
        );

        if (data.success && data.data?.name) {
          // Extract campaign name, removing "Pending - Check your Klaviyo dashboard" default
          const name = data.data.name;
          if (name && !name.includes('Pending')) {
            return name;
          }
        }

        // Return generic 'Campaign' if no name or mapping not filled in
        return 'Campaign';
      } catch (_error) {
        return 'Campaign'; // Fallback on error
      }
    },

    // Proxy utility functions to core module (implementations moved to core)
    formatNumber(num, decimals = 2) {
      return this.core.formatNumber(num, decimals);
    },

    formatCurrency(num, decimals = 2) {
      return this.core.formatCurrency(num, decimals);
    },

    formatDate(dateInput) {
      return this.core.formatDate(dateInput);
    },

    categorizeTrafficSource(referringSite) {
      return this.core.categorizeTrafficSource(referringSite);
    },

    // Proxy Web Vitals functions to core module (implementations moved to core)
    initWebVitals() {
      return this.core.initWebVitals();
    },

    updateWebVitalsDisplay() {
      return this.core.updateWebVitalsDisplay();
    },

    updateCWVScore() {
      return this.core.updateCWVScore();
    },

    // Load performance metrics from backend
    loadPerformanceMetrics: async function () {
      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=performance-metrics`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            // Update bounce rate
            const bounceEl = document.getElementById('bounce-rate');
            if (bounceEl) {
              if (result.data.bounceRate !== null) {
                const bounce = parseFloat(result.data.bounceRate);
                bounceEl.textContent = `${bounce.toFixed(1)}%`;
                bounceEl.className = 'metric-value';

                // Color code: Good <50%, Needs Improvement 50-70%, Poor >70%
                const { bounceRate, labels } = this.config.performanceMetrics;
                if (bounce < bounceRate.thresholds.excellent) {
                  bounceEl.classList.add('metric-status-good');
                } else if (bounce < bounceRate.thresholds.average) {
                  bounceEl.classList.add('metric-status-needs-improvement');
                } else {
                  bounceEl.classList.add('metric-status-poor');
                }

                const bounceBenchmarkEl = document.getElementById('bounce-benchmark');
                if (bounceBenchmarkEl) {
                  const comparison =
                    bounce < bounceRate.thresholds.excellent
                      ? labels.excellent
                      : bounce < bounceRate.thresholds.good
                        ? labels.good
                        : bounce < bounceRate.thresholds.average
                          ? labels.average
                          : labels.needsWork;
                  safeSetHTML(
                    bounceBenchmarkEl,
                    `<span class="benchmark-${comparison.toLowerCase().replace(' ', '-')}">${comparison}</span> | Industry avg: ${bounceRate.industryAvg}`
                  );
                }
              } else {
                bounceEl.textContent = 'GA Required';
                bounceEl.className = 'metric-value';
                bounceEl.style.fontSize = '12px';
                bounceEl.style.opacity = '0.6';

                const bounceBenchmarkEl = document.getElementById('bounce-benchmark');
                if (bounceBenchmarkEl) {
                  bounceBenchmarkEl.textContent = 'Configure Google Analytics';
                  bounceBenchmarkEl.style.fontSize = '10px';
                }
              }
            }

            // Update pages per session
            const pagesEl = document.getElementById('pages-per-session');
            if (pagesEl) {
              if (result.data.pagesPerSession !== null) {
                const pages = parseFloat(result.data.pagesPerSession);
                pagesEl.textContent = pages.toFixed(2);
                pagesEl.className = 'metric-value';

                // Good >2.5, Needs Improvement 1.5-2.5, Poor <1.5
                const { pagesPerSession, labels } = this.config.performanceMetrics;
                if (pages > pagesPerSession.thresholds.excellent) {
                  pagesEl.classList.add('metric-status-good');
                } else if (pages > pagesPerSession.thresholds.average) {
                  pagesEl.classList.add('metric-status-needs-improvement');
                } else {
                  pagesEl.classList.add('metric-status-poor');
                }

                const pagesBenchmarkEl = document.getElementById('pages-benchmark');
                if (pagesBenchmarkEl) {
                  const comparison =
                    pages > pagesPerSession.thresholds.excellent
                      ? labels.excellent
                      : pages > pagesPerSession.thresholds.good
                        ? labels.good
                        : pages > pagesPerSession.thresholds.average
                          ? labels.average
                          : labels.needsWork;
                  safeSetHTML(
                    pagesBenchmarkEl,
                    `<span class="benchmark-${comparison.toLowerCase()}">${comparison}</span> | Industry avg: ${pagesPerSession.industryAvg}`
                  );
                }
              } else {
                pagesEl.textContent = 'GA Required';
                pagesEl.className = 'metric-value';
                pagesEl.style.fontSize = '12px';
                pagesEl.style.opacity = '0.6';

                const pagesBenchmarkEl = document.getElementById('pages-benchmark');
                if (pagesBenchmarkEl) {
                  pagesBenchmarkEl.textContent = 'Configure Google Analytics';
                  pagesBenchmarkEl.style.fontSize = '10px';
                }
              }
            }

            // Update average session duration
            const sessionEl = document.getElementById('avg-session-duration');
            if (sessionEl) {
              if (result.data.avgSessionDuration !== null) {
                const duration = parseInt(result.data.avgSessionDuration, 10);
                const minutes = Math.floor(duration / 60);
                const seconds = duration % 60;
                sessionEl.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
                sessionEl.className = 'metric-value';

                // Good >120s (2min), Needs Improvement 60-120s, Poor <60s
                const { sessionDuration, labels } = this.config.performanceMetrics;
                if (duration > sessionDuration.thresholds.good) {
                  sessionEl.classList.add('metric-status-good');
                } else if (duration > sessionDuration.thresholds.average) {
                  sessionEl.classList.add('metric-status-needs-improvement');
                } else {
                  sessionEl.classList.add('metric-status-poor');
                }

                const sessionBenchmarkEl = document.getElementById('session-benchmark');
                if (sessionBenchmarkEl) {
                  const comparison =
                    duration > sessionDuration.thresholds.excellent
                      ? labels.excellent
                      : duration > sessionDuration.thresholds.good
                        ? labels.good
                        : duration > sessionDuration.thresholds.average
                          ? labels.average
                          : labels.needsWork;
                  safeSetHTML(
                    sessionBenchmarkEl,
                    `<span class="benchmark-${comparison.toLowerCase()}">${comparison}</span> | Industry avg: ${sessionDuration.industryAvg}`
                  );
                }
              } else {
                sessionEl.textContent = 'GA Required';
                sessionEl.className = 'metric-value';
                sessionEl.style.fontSize = '12px';
                sessionEl.style.opacity = '0.6';

                const sessionBenchmarkEl = document.getElementById('session-benchmark');
                if (sessionBenchmarkEl) {
                  sessionBenchmarkEl.textContent = 'Configure Google Analytics';
                  sessionBenchmarkEl.style.fontSize = '10px';
                }
              }
            }
          }
        } else {
          // If API call fails, show error state
          const bounceEl = document.getElementById('bounce-rate');
          const pagesEl = document.getElementById('pages-per-session');
          const sessionEl = document.getElementById('avg-session-duration');

          if (bounceEl && bounceEl.textContent.includes('shimmer')) {
            bounceEl.textContent = 'Error';
            bounceEl.style.fontSize = '12px';
          }
          if (pagesEl && pagesEl.innerHTML.includes('shimmer')) {
            pagesEl.textContent = 'Error';
            pagesEl.style.fontSize = '12px';
          }
          if (sessionEl && sessionEl.innerHTML.includes('shimmer')) {
            sessionEl.textContent = 'Error';
            sessionEl.style.fontSize = '12px';
          }
        }
      } catch (_error) {
        // Show error state on failure
        const bounceEl = document.getElementById('bounce-rate');
        const pagesEl = document.getElementById('pages-per-session');
        const sessionEl = document.getElementById('avg-session-duration');

        if (bounceEl) bounceEl.textContent = 'Error';
        if (pagesEl) pagesEl.textContent = 'Error';
        if (sessionEl) sessionEl.textContent = 'Error';
      }
    },

    // Proxy idle detection to core module (implementation moved to core)
    setupIdleDetection() {
      return this.core.setupIdleDetection();
    },

    // Initialize dashboard
    init: function () {
      // Idempotency guard: init() has two entry paths (already-authenticated at load, and the
      // one-shot 'ccc:authenticated' listener). Neither may double-initialize timers/listeners.
      if (this._initialized) return;
      this._initialized = true;

      // Initialize modules first
      this.core = AdminDashboardCore;

      this.api = AdminDashboardAPI;
      this.api.init(this.core);

      this.charts = AdminDashboardCharts;
      this.charts.init(this.core);

      this.ui = AdminDashboardUI;
      // NOTE: there is deliberately no "#admin-dashboard missing => not authenticated" guard here.
      // That element is always in the DOM (its display:none ancestor #dashboard-container is what
      // hides it), so the guard never fired and init() ran — and loaded data — while logged out.
      // Authentication is now gated at the bootstrap, below.
      const dashboard = document.getElementById('admin-dashboard');

      this.ui.init(this.core);

      // Single 401 sink. The fetch wrapper in snippets/admin-dashboard.liquid dispatches this for
      // any proxy response that comes back 401, so every card loader that would otherwise collapse
      // a non-ok response into a terminal empty state gets the correct "session expired" story
      // without a per-loader branch.
      document.addEventListener('ccc:auth-failed', () => {
        this.showSessionExpiredMessage();
      });

      const _customerName = (dashboard && dashboard.dataset.customerName) || 'Admin';

      // Initialize Web Vitals tracking (now from core module)
      this.initWebVitals();

      // Setup idle detection for auto-refresh (now from core module)
      this.setupIdleDetection();

      // Register page-lifecycle handling: pause on visibilitychange(hidden), full
      // teardown on beforeunload/pagehide so timers do not fire after navigating away.
      this.core.setupPageLifecycleCleanup();

      // Re-arm on return to the tab. Idle detection is the only thing pause() stops,
      // and a dashboard whose data never loaded (interrupted cold load, transient proxy
      // failure) is recovered here instead of staying pinned at "0"/"Setup Required"
      // until the user manually refreshes.
      this.core.onResume(() => {
        this.setupIdleDetection();
        if (this.isDataStale()) {
          this.config.cacheBuster = Date.now();
          this.loadData();
        }
      });

      // Setup online/offline event listeners and Network Information API monitoring
      this.setupConnectionMonitoring();

      // Guard: if already offline on page load, show offline state immediately
      // without attempting any fetches.
      if (!navigator.onLine) {
        this.core.state.connectionHealth.isOffline = true;
        this.core.state.connectionHealth.autoRefreshPaused = true;
        this.ui.showOfflineBanner();
        // Skip initial loadData() call — will be triggered when 'online' fires.
        log('Dashboard loaded while offline — waiting for network');
      } else {
        // Load data (async operations, don't wait)
        this.loadData();
      }

      // Initialize charts immediately (don't wait for data)
      try {
        this.initCharts();
      } catch (_error) {
        // Try again after a delay in case DOM isn't ready
        setTimeout(() => {
          try {
            this.initCharts();
          } catch (_e) {}
        }, 1000);
      }

      // Setup auto-refresh when returning to stale dashboard
      this.setupStaleDataRefresh();

      // Setup event listeners for new dashboard features (2025-10-24)
      this.setupNewFeatureListeners();

      // Initialize custom glassmorphic dropdowns
      this.initCustomDropdowns();

      // Initialize search history modal and tabs
      this.initSearchHistoryModal();
      this.initSearchTabs();
    },

    // ---------------------------------------------------------------------------
    // Progressive Loading — Critical Path + Progress Indicator
    //
    // CRITICAL sections (revenue + orders) load first as a prioritised pair.
    // All other sections are SUPPLEMENTARY and load asynchronously after the
    // critical data is visible. On slow connections this means the user sees
    // meaningful data after 2 RTTs instead of waiting for all 10+ requests.
    //
    // Total section count used by the progress indicator (see _updateLoadProgress):
    //   Critical: 2  (today-stats / recent-orders)
    //   Supplementary: 7 groups
    //   Total: 9 logical units
    // ---------------------------------------------------------------------------

    // Section count constants (used by the progress indicator)
    _LOAD_TOTAL_SECTIONS: 9,
    _LOAD_CRITICAL_SECTIONS: 2,

    /**
     * Update the loading progress indicator in the DOM.
     * Renders "Loading critical data... (N/M sections loaded)" until critical
     * sections complete, then "Loading additional data... (N/M sections loaded)",
     * then hides once all sections have reported.
     *
     * @param {number} loaded - Sections that have returned (success or error)
     * @param {number} total  - Total sections expected
     * @param {string} phase  - 'critical' | 'supplementary' | 'done'
     */
    _updateLoadProgress(loaded, total, phase) {
      const el = document.getElementById('load-progress-indicator');
      if (!el) return;
      if (phase === 'done' || loaded >= total) {
        el.style.display = 'none';
        return;
      }
      el.style.display = '';
      const label = phase === 'critical' ? 'Loading critical data…' : 'Loading additional data…';
      el.textContent = `${label} (${loaded}/${total} sections loaded)`;
    },

    /**
     * Increment the progress counter and refresh the indicator.
     * Safe to call from any async section loader — each call represents
     * one logical section completing (success or error).
     */
    _sectionLoaded() {
      const prog = this.state.loadingProgress;
      prog.loaded = Math.min(prog.loaded + 1, prog.total);
      const isDone = prog.loaded >= prog.total;
      if (isDone) prog.phase = 'done';
      this._updateLoadProgress(prog.loaded, prog.total, prog.phase);
    },

    /**
     * Wrap a supplementary section loader so that non-critical failures show
     * a bandwidth-aware placeholder instead of a blocking error state.
     *
     * Callers pass the async loader fn and optionally a container element ID
     * to insert the low-bandwidth message into when the loader rejects/throws.
     *
     * @param {function(): Promise<any>} loaderFn - Section loader
     * @param {string|null} [containerId] - Optional container for the placeholder
     * @returns {Promise<void>}
     */
    async _loadSupplementary(loaderFn, containerId) {
      try {
        await loaderFn.call(this);
      } catch (_err) {
        if (containerId) {
          const el = document.getElementById(containerId);
          if (el) {
            const p = document.createElement('p');
            p.className = 'dash-bandwidth-msg';
            p.style.cssText =
              'color:rgba(255,255,255,0.5);padding:16px;text-align:center;font-size:0.85em;';
            p.textContent = 'This section is unavailable — low bandwidth?';
            while (el.firstChild) el.removeChild(el.firstChild);
            el.appendChild(p);
          }
        }
      } finally {
        this._sectionLoaded();
      }
    },

    // Load all data
    // Public entry point. Tracks in-flight loads so isDataStale() does not report a
    // still-running load as stale and trigger a duplicate. try/finally is mandatory:
    // a mid-flight throw that left the counter above zero would permanently disable the
    // visibility/focus recovery path, which is exactly the failure class this fixes.
    // Record the outcome of a single dashboard request. `ok` marks the load as having produced at
    // least one real response; a 401 marks it as an authentication failure so the UI can say
    // "session expired" instead of the misleading "Setup Required / deploy the proxy".
    noteLoadOutcome: function (ok, status) {
      if (!this.state) return;
      if (ok) this.state.loadHadSuccess = true;
      if (status === 401) this.state.loadHadAuthFailure = true;
    },

    loadData: async function () {
      if (!this.state.dataLoadDepth) {
        // Reset per-load outcome tracking. A load in which no request succeeded must NOT be
        // stamped as fresh (see _loadDataInner), or isDataStale() reports fresh for 20 minutes
        // and every recovery handler (onResume / visibilitychange / focus / pageshow) no-ops.
        this.state.loadHadSuccess = false;
        this.state.loadHadAuthFailure = false;
      }
      this.state.dataLoadDepth = (this.state.dataLoadDepth || 0) + 1;
      this.state.dataLoadInFlight = true;
      try {
        await this._loadDataInner();
      } finally {
        this.state.dataLoadDepth = Math.max(0, (this.state.dataLoadDepth || 1) - 1);
        this.state.dataLoadInFlight = this.state.dataLoadDepth > 0;
      }
    },

    _loadDataInner: async function () {
      // Initialise progress state
      this.state.loadingProgress = {
        loaded: 0,
        total: this._LOAD_TOTAL_SECTIONS,
        phase: 'critical',
      };
      this._updateLoadProgress(0, this._LOAD_TOTAL_SECTIONS, 'critical');

      // -----------------------------------------------------------------------
      // CRITICAL PATH — revenue + orders (first 2 sections).
      // These are awaited so the indicator advances to "supplementary" only
      // after real data (or an error) is visible for the most important metrics.
      // -----------------------------------------------------------------------
      await Promise.allSettled([
        this.loadShopifyData().finally(() => this._sectionLoaded()),
        this.loadAllRecentOrders().finally(() => this._sectionLoaded()),
      ]);

      // Transition to supplementary phase
      this.state.loadingProgress.phase = 'supplementary';
      this._updateLoadProgress(
        this.state.loadingProgress.loaded,
        this._LOAD_TOTAL_SECTIONS,
        'supplementary'
      );

      // Start visitor tracking alongside supplementary (no RTT dependency)
      this.loadAIAgentData();
      this.trackVisitors();

      // -----------------------------------------------------------------------
      // SUPPLEMENTARY sections — load asynchronously in staggered groups.
      // Each group is wrapped with _loadSupplementary so failures show a
      // bandwidth-aware placeholder rather than a blocking error state.
      // -----------------------------------------------------------------------

      // Group 1: Analytics & Metrics (500ms after critical)
      await this.delay(500);
      this._loadSupplementary(async function () {
        this.loadAnalyticsData();
        this.loadNewMetrics();
        this.loadAdvancedMetrics();
        this.loadPerformanceMetrics();
      });

      // Group 2: Traffic & Attribution (1s after critical)
      await this.delay(500);
      this._loadSupplementary(async function () {
        this.trackTrafficAttribution();
        this.loadSubscriptionRenewals();
        this.updateRealtimeActivityDate('today');
      });

      // Group 3: Campaign Data (1.5s after critical)
      await this.delay(500);
      this._loadSupplementary(async function () {
        const campaignPeriodSelector = document.getElementById('campaign-revenue-period');
        const campaignPeriod = campaignPeriodSelector ? campaignPeriodSelector.value : '7d';
        this.trackCampaignRevenuePerformance(campaignPeriod);
        this.loadKlaviyoCampaigns();
      });

      // Group 4: GraphQL Analytics (2s after critical)
      await this.delay(500);
      this._loadSupplementary(async function () {
        this.loadCollectionsPerformance();
        this.loadGiftCardAnalytics();
        this.loadPaymentMethodsBreakdown();
      });

      // Group 5: Extended Analytics (2.5s after critical)
      await this.delay(500);
      this._loadSupplementary(async function () {
        this.loadShippingAnalytics();
        this.loadCustomerTagsDistribution();
        this.loadBlogPerformance();
        this.loadVideoLibraryData();
      });

      // Group 6: Social & External (3s after critical)
      await this.delay(500);
      this._loadSupplementary(async function () {
        this.loadSocialData();
        this.loadPinterestAnalytics();
      });

      // Group 7: Advanced Features + Additional Metrics (3.5s after critical)
      await this.delay(500);
      this._loadSupplementary(async function () {
        this.loadEnhancedAbandonedCheckout();
        this.loadDiscountROITracker();
        this.loadHighVariantProducts();
        this.loadTopConvertingProducts();
        this.loadCartRecoveryMetrics();
        this.loadTrafficSourceROI();
        this.loadSmartAlerts();
        this.loadTotalTips90d();
      });

      // Only a load in which at least one request actually succeeded counts as a load. Stamping
      // unconditionally made a fully-401'd load look fresh for 20 minutes, which disabled every
      // self-healing path and pinned the dashboard at "0"/"Setup Required" until a manual refresh.
      if (this.state.loadHadSuccess) {
        this.updateLastUpdateTime();
        this.state.lastDataFetchTimestamp = Date.now();
      }
    },

    // Helper function to add delay between API call groups
    delay: function (ms) {
      return new Promise((resolve) => {
        setTimeout(resolve, ms);
      });
    },

    // Load REAL Shopify store data via secure proxy
    loadShopifyData: async function () {
      try {
        // Fetch today's stats from proxy (with cache buster if refresh clicked)
        const cacheBuster = this.config.cacheBuster ? `&_cb=${this.config.cacheBuster}` : '';
        const response = await fetch(
          `${this.config.apiProxyUrl}?endpoint=today-stats${cacheBuster}`
        );

        this.noteLoadOutcome(response.ok, response.status);

        if (response.ok) {
          const result = await response.json();

          if (result.success) {
            const { revenue, orderCount, aov, conversionRate } = result.data;

            safeSetHTML(document.getElementById('today-revenue'), `$${this.formatNumber(revenue)}`);
            document.getElementById('order-count').textContent = `${orderCount} orders`;
            document.getElementById('aov').textContent = `$${this.formatNumber(aov)} AOV`;

            // Fetch conversion funnel to populate conversion rate meta
            const funnelResponse = await fetch(
              `${this.config.apiProxyUrl}?endpoint=conversion-funnel`
            );
            if (funnelResponse.ok) {
              const funnelResult = await funnelResponse.json();
              if (funnelResult.success && funnelResult.data) {
                const rates = _calculateConversionRates(funnelResult.data);

                safeSetHTML(
                  document.getElementById('conversion-rate'),
                  `${rates.overallConversionRate}%`
                );
                document.getElementById('cart-rate').textContent = `${rates.cartRate}% to cart`;
                document.getElementById('checkout-rate').textContent =
                  `${rates.checkoutRate}% to checkout`;
              } else {
                safeSetHTML(
                  document.getElementById('conversion-rate'),
                  `${conversionRate || '0.00'}%`
                );
              }
            } else {
              safeSetHTML(
                document.getElementById('conversion-rate'),
                `${conversionRate || '0.00'}%`
              );
            }
          }
        } else {
          await response.text();
          // A 401 means the session is gone, not that the proxy was never deployed. Rendering
          // "Setup Required / See server/README.md" for an expired session sent the user chasing
          // a deployment problem that did not exist.
          if (response.status === 401) {
            this.showSessionExpiredMessage();
          } else {
            this.showProxySetupMessage();
          }
        }
      } catch (_error) {
        this.showProxySetupMessage();
      }

      // Load additional data
      this.loadTopProducts();
      this.loadInventoryAlerts();
    },

    // Show setup message if proxy not configured
    showProxySetupMessage: function () {
      safeSetHTML(
        document.getElementById('today-revenue'),
        `<span style="font-size: 0.8em;">Setup Required</span>`
      );
      document.getElementById('order-count').textContent = 'See server/README.md';
      document.getElementById('aov').textContent = 'Deploy proxy';
      safeSetHTML(
        document.getElementById('conversion-rate'),
        `<span style="font-size: 0.8em;">Deploy proxy</span>`
      );
    },

    // Show an expired-session state. Distinct from showProxySetupMessage(): the proxy is deployed
    // and answering, the caller just is not signed in any more. Every card loader is covered by a
    // single 'ccc:auth-failed' listener (registered in init) rather than a per-loader 401 branch,
    // because the fetch wrapper in snippets/admin-dashboard.liquid is the one choke point every
    // dashboard request passes through.
    showSessionExpiredMessage: function () {
      this.noteLoadOutcome(false, 401);
      const revenue = document.getElementById('today-revenue');
      if (revenue) {
        safeSetHTML(revenue, `<span style="font-size: 0.8em;">Session expired</span>`);
      }
      const orderCount = document.getElementById('order-count');
      if (orderCount) orderCount.textContent = 'Sign in again to continue';
      const aov = document.getElementById('aov');
      if (aov) aov.textContent = 'Session expired';
      const conversion = document.getElementById('conversion-rate');
      if (conversion) {
        safeSetHTML(conversion, `<span style="font-size: 0.8em;">Session expired</span>`);
      }
    },

    // Load AI agent tracking data from server
    loadAIAgentData: async function () {
      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=ai-bot-traffic`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            const { summary, botDetails } = result.data;

            // Update summary stats
            const aiTraffic = document.getElementById('ai-traffic');
            if (aiTraffic) safeSetHTML(aiTraffic, summary.botOrders || 0);

            const aiConversions = document.getElementById('ai-conversions');
            if (aiConversions) aiConversions.textContent = `${summary.botOrders || 0} orders`;

            const aiRevenue = document.getElementById('ai-revenue');
            if (aiRevenue) {
              aiRevenue.textContent = `$${this.formatNumber(parseFloat(summary.botRevenue || 0))}`;
            }

            // Transform botDetails into agent-based stats for breakdown
            const agentStats = {};
            botDetails.forEach((bot) => {
              if (!agentStats[bot.botType]) {
                agentStats[bot.botType] = {
                  visits: 0,
                  conversions: 0,
                  revenue: 0,
                };
              }
              agentStats[bot.botType].visits++;
              agentStats[bot.botType].conversions++;
              agentStats[bot.botType].revenue += parseFloat(bot.revenue || 0);
            });

            // Build AI agent breakdown
            this.buildAIAgentBreakdown(agentStats);

            // Update bot counts
            const goodBots = document.getElementById('good-bots');
            if (goodBots) goodBots.textContent = `${summary.botOrders || 0} orders`;
          } else {
            this.showAIAgentFallback();
          }
        } else {
          this.showAIAgentFallback();
        }
      } catch (_error) {
        this.showAIAgentFallback();
      }
    },

    // Attribution tab: AI Referral Traffic. The card shipped with markup but no loader of
    // any kind, so #ai-attribution-summary and #ai-referral-table-body had nothing that
    // could ever replace their placeholders regardless of what the server returned.
    loadAIReferralTraffic: async function () {
      const TABLE_COLUMN_COUNT = 4;

      const summaryEl = document.getElementById('ai-attribution-summary');
      const tableBodyEl = document.getElementById('ai-referral-table-body');
      if (!summaryEl && !tableBodyEl) return;

      const showMessage = (message) => {
        if (summaryEl) safeSetHTML(summaryEl, `<span>${this.escapeHtml(message)}</span>`);
        if (tableBodyEl) {
          safeSetHTML(
            tableBodyEl,
            `<tr><td colspan="${TABLE_COLUMN_COUNT}">${this.escapeHtml(message)}</td></tr>`
          );
        }
      };

      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=ai-bot-traffic`);
        if (!response.ok) {
          showMessage(`AI referral data unavailable (HTTP ${response.status})`);
          return;
        }

        const result = await response.json();
        if (!result?.success || !result.data) {
          showMessage(result?.error || 'AI referral data unavailable');
          return;
        }

        const data = result.data;
        const platforms = Array.isArray(data.platforms) ? data.platforms : [];
        const sessions = Number(data.sessions) || 0;
        const users = Number(data.users) || 0;
        const revenue = Number(data.revenue) || 0;

        this.core.scheduleDomWrite(() => {
          if (summaryEl) {
            safeSetHTML(
              summaryEl,
              `<span class="metric-value">${this.formatNumber(sessions, 0)} sessions</span>` +
                ' <span class="separator">•</span> ' +
                `<span class="metric-value">${this.formatNumber(users, 0)} visitors</span>` +
                ' <span class="separator">•</span> ' +
                `<span class="metric-value">$${this.formatNumber(revenue, 2)}</span>`
            );
          }

          if (tableBodyEl) {
            safeSetHTML(
              tableBodyEl,
              platforms.length === 0
                ? `<tr><td colspan="${TABLE_COLUMN_COUNT}">No AI referral traffic in this period</td></tr>`
                : platforms
                    .map(
                      (platform) => `
              <tr>
                <td>${this.escapeHtml(platform.name || 'Unknown')}</td>
                <td>${this.formatNumber(platform.sessions || 0, 0)}</td>
                <td>${this.formatNumber(platform.users || 0, 0)}</td>
                <td>$${this.formatNumber(Number(platform.revenue) || 0, 2)}</td>
              </tr>
            `
                    )
                    .join('')
            );
          }
        });
      } catch (error) {
        showMessage('AI referral data failed to load');
        AdminDashboardCore.logError('[Admin] loadAIReferralTraffic error:', error);
      }
    },

    // Show fallback message when AI agent data unavailable
    showAIAgentFallback: function () {
      const aiTraffic = document.getElementById('ai-traffic');
      if (aiTraffic) safeSetHTML(aiTraffic, '0');

      const aiConv = document.getElementById('ai-conversions');
      if (aiConv) aiConv.textContent = '0 orders';

      const aiRev = document.getElementById('ai-revenue');
      if (aiRev) aiRev.textContent = '$0';

      const goodBots = document.getElementById('good-bots');
      if (goodBots) goodBots.textContent = '0 orders';

      const container = document.getElementById('ai-agent-breakdown');
      if (container) {
        safeSetHTML(
          container,
          '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">No AI bot orders detected in the last 7 days</p>'
        );

        // Hide the entire AI Agent card when no data
        const aiCard = container.closest('.glass-card');
        if (aiCard) {
          aiCard.style.display = 'none';
        }
      }
    },

    // Build AI agent breakdown UI
    buildAIAgentBreakdown: function (agents) {
      const container = document.getElementById('ai-agent-breakdown');
      if (!container) return; // Container doesn't exist, skip rendering

      safeSetHTML(container, '');

      const agentIcons = {
        ChatGPT: '💬',
        Claude: '🧠',
        Perplexity: '🔍',
        'Google Gemini': '✨',
        'Microsoft Copilot': '🤖',
      };

      Object.entries(agents).forEach(([name, data]) => {
        const item = document.createElement('div');
        item.className = 'ai-agent-item';
        safeSetHTML(
          item,
          `
          <div class="ai-agent-icon">${agentIcons[name] || '🤖'}</div>
          <div class="ai-agent-data">
            <h4>${name}</h4>
            <p>${data.visits} visits • ${((data.conversions / data.visits) * 100 || 0).toFixed(1)}% conversion</p>
          </div>
          <div class="ai-agent-stats">
            <div class="revenue">$${this.formatNumber(data.revenue || 0)}</div>
            <div class="conversions">${data.conversions || 0} sales</div>
          </div>
        `
        );
        container.appendChild(item);
      });

      if (Object.keys(agents).length === 0) {
        safeSetHTML(
          container,
          '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">No AI agent data yet</p>'
        );
        // Hide the entire AI Agent card when no data
        const aiCard = container.closest('.glass-card');
        if (aiCard) {
          aiCard.style.display = 'none';
        }
      } else {
        // Show the card if it was hidden
        const aiCard = container.closest('.glass-card');
        if (aiCard) {
          aiCard.style.display = '';
        }
      }
    },

    // Track live visitors
    trackVisitors: async function () {
      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=live-visitors`);

        if (response.ok) {
          const result = await response.json();

          if (result.success && result.data) {
            const { total, human, bots, botsBreakdown, devices, locations, pages } = result.data;

            // Validate data
            const totalNum = parseInt(total, 10) || 0;
            const humanNum = parseInt(human, 10) || 0;
            let botsNum = parseInt(bots, 10) || 0;

            // Fallback to AIAgentTracker if API returns 0 bots but we know there are bots
            if (botsNum === 0 && window.AIAgentTracker) {
              const stats = AIAgentTracker.getStats();
              const crawlerVisits = stats.search_crawlers?.total_visits || 0;
              const aiVisits = Object.values(stats.by_agent || {}).reduce(
                (sum, agent) => sum + (agent.visits || 0),
                0
              );
              botsNum = crawlerVisits + aiVisits;
            }

            // Store bot count for bot chart to use
            this.state.liveVisitorBots = botsNum;

            // Update UI
            safeSetHTML(document.getElementById('live-visitors'), totalNum);
            document.getElementById('human-count').textContent = `${humanNum} humans`;
            document.getElementById('bot-count').textContent = `${botsNum} bots`;

            // Build comprehensive tooltip and attach click handler
            if (totalNum > 0) {
              this.liveVisitorsData = { total: totalNum, devices, locations, pages, botsBreakdown };
              const liveVisitorsCard = document
                .getElementById('live-visitors')
                .closest('.stat-card');
              if (liveVisitorsCard && !liveVisitorsCard.hasAttribute('data-tooltip-initialized')) {
                liveVisitorsCard.setAttribute('data-tooltip-initialized', 'true');
                liveVisitorsCard.style.cursor = 'pointer';

                // Add expand indicator
                const expandIndicator = document.createElement('div');
                expandIndicator.className = 'card-expand-indicator';
                safeSetHTML(expandIndicator, '⤢');
                expandIndicator.title = 'Click to expand details';
                liveVisitorsCard.appendChild(expandIndicator);

                liveVisitorsCard.addEventListener('click', (e) => {
                  e.stopPropagation();
                  this.showLiveVisitorsModal();
                });
              }
            }

            // Warning if all values are 0 (might indicate API issue)
          } else {
            safeSetHTML(
              document.getElementById('live-visitors'),
              `<span style="font-size: 0.8em;">API Error</span>`
            );
            document.getElementById('human-count').textContent = 'Check console';
            document.getElementById('bot-count').textContent = 'for details';
          }
        } else {
          // Fallback message if proxy not configured
          safeSetHTML(
            document.getElementById('live-visitors'),
            `<span style="font-size: 0.8em;">GA Required</span>`
          );
          document.getElementById('human-count').textContent = 'Setup Google Analytics';
          document.getElementById('bot-count').textContent = 'for live tracking';
        }
      } catch (_error) {
        safeSetHTML(
          document.getElementById('live-visitors'),
          `<span style="font-size: 0.8em;">GA Required</span>`
        );
        document.getElementById('human-count').textContent = 'Setup Google Analytics';
        document.getElementById('bot-count').textContent = 'for live tracking';
      }
    },

    // Build comprehensive tooltip for Live Visitors card
    buildLiveVisitorsTooltip: function (total, devices, locations, pages) {
      let html = `
        <div class="visitors-tooltip-header">
          <div class="visitors-count-large">
            <span class="visitors-icon">👥</span>
            <span class="visitors-number">${parseInt(total, 10) || 0}</span>
          </div>
          <div class="visitors-label">Active Visitors Right Now</div>
        </div>
      `;

      // Devices section
      if (devices && Object.keys(devices).length > 0) {
        const deviceEntries = Object.entries(devices)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5);

        html += `
          <div class="visitors-section">
            <div class="visitors-section-header">
              <span class="section-icon">📱</span>
              <span class="section-title">Devices</span>
            </div>
            <div class="visitors-items">
        `;

        deviceEntries.forEach(([device, count]) => {
          const deviceNameRaw = device.charAt(0).toUpperCase() + device.slice(1);
          const deviceName = this.escapeHtml(deviceNameRaw);
          const deviceIcon = _getDeviceIcon(device);

          html += `
            <div class="visitors-item">
              <span class="item-icon">${deviceIcon}</span>
              <span class="item-label">${deviceName}</span>
              <span class="item-count">${parseInt(count, 10) || 0}</span>
            </div>
          `;
        });

        html += `
            </div>
          </div>
        `;
      }

      // Locations section
      if (locations && Object.keys(locations).length > 0) {
        const locationEntries = Object.entries(locations)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5);

        html += `
          <div class="visitors-section">
            <div class="visitors-section-header">
              <span class="section-icon">🌎</span>
              <span class="section-title">Top Locations</span>
            </div>
            <div class="visitors-items">
        `;

        locationEntries.forEach(([country, count]) => {
          const flag = this.getCountryFlag(country);
          const countryTrunc = _truncateLabel(country, 22);
          const countryName = this.escapeHtml(countryTrunc);

          html += `
            <div class="visitors-item">
              <span class="item-icon">${flag}</span>
              <span class="item-label">${countryName}</span>
              <span class="item-count">${parseInt(count, 10) || 0}</span>
            </div>
          `;
        });

        html += `
            </div>
          </div>
        `;
      }

      // Pages section
      if (pages && Object.keys(pages).length > 0) {
        const pageEntries = Object.entries(pages)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6);

        html += `
          <div class="visitors-section">
            <div class="visitors-section-header">
              <span class="section-icon">👀</span>
              <span class="section-title">Currently Viewing</span>
            </div>
            <div class="visitors-items">
        `;

        pageEntries.forEach(([page, count]) => {
          const pageIcon = page.includes('/products/')
            ? '🛍️'
            : page.includes('/collections/')
              ? '📂'
              : page === '/'
                ? '🏠'
                : page.includes('/cart')
                  ? '🛒'
                  : '📄';
          const pageNameRaw =
            page === '/'
              ? 'Homepage'
              : page.length > 25
                ? '...' + page.substring(page.length - 25)
                : page;
          const pageName = this.escapeHtml(pageNameRaw);

          html += `
            <div class="visitors-item">
              <span class="item-icon">${pageIcon}</span>
              <span class="item-label page-path">${pageName}</span>
              <span class="item-count">${parseInt(count, 10) || 0}</span>
            </div>
          `;
        });

        html += `
            </div>
          </div>
        `;
      }

      return html;
    },

    // Show Live Visitors Modal (click-to-toggle with close button)
    showLiveVisitorsModal: function () {
      // Remove existing modal if any
      const existingModal = document.getElementById('live-visitors-modal');
      if (existingModal) {
        existingModal.remove();
        return; // Toggle off
      }

      if (!this.liveVisitorsData) return;

      const { total, devices, locations, pages, botsBreakdown } = this.liveVisitorsData;

      // Create modal
      const modal = document.createElement('div');
      modal.id = 'live-visitors-modal';
      modal.className = 'visitors-modal';

      let html = `
        <div class="visitors-modal-content">
          <button class="visitors-modal-close" aria-label="Close">&times;</button>
          <div class="visitors-tooltip-header">
            <div class="visitors-count-large">
              <span class="visitors-icon">👥</span>
              <span class="visitors-number">${total}</span>
            </div>
            <div class="visitors-label">Active Visitors Right Now</div>
          </div>
      `;

      // Devices section
      if (devices && Object.keys(devices).length > 0) {
        const deviceEntries = Object.entries(devices)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5);

        html += `
          <div class="visitors-section">
            <div class="visitors-section-header">
              <span class="section-icon">📱</span>
              <span class="section-title">Devices</span>
            </div>
            <div class="visitors-items">
        `;

        deviceEntries.forEach(([device, count]) => {
          const deviceNameRaw = device.charAt(0).toUpperCase() + device.slice(1);
          const deviceName = this.escapeHtml(deviceNameRaw);
          const deviceIcon = _getDeviceIcon(device);

          html += `
            <div class="visitors-item">
              <span class="item-icon">${deviceIcon}</span>
              <span class="item-label">${deviceName}</span>
              <span class="item-count">${parseInt(count, 10) || 0}</span>
            </div>
          `;
        });

        html += `
            </div>
          </div>
        `;
      }

      // Locations section
      if (locations && Object.keys(locations).length > 0) {
        const locationEntries = Object.entries(locations)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5);

        html += `
          <div class="visitors-section">
            <div class="visitors-section-header">
              <span class="section-icon">🌎</span>
              <span class="section-title">Top Locations</span>
            </div>
            <div class="visitors-items">
        `;

        locationEntries.forEach(([country, count]) => {
          const flag = this.getCountryFlag(country);
          const countryTrunc = _truncateLabel(country, 22);
          const countryName = this.escapeHtml(countryTrunc);

          html += `
            <div class="visitors-item">
              <span class="item-icon">${flag}</span>
              <span class="item-label">${countryName}</span>
              <span class="item-count">${parseInt(count, 10) || 0}</span>
            </div>
          `;
        });

        html += `
            </div>
          </div>
        `;
      }

      // Pages section with nested tooltips
      if (pages && Object.keys(pages).length > 0) {
        const pageEntries = Object.entries(pages)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6);

        html += `
          <div class="visitors-section">
            <div class="visitors-section-header">
              <span class="section-icon">👀</span>
              <span class="section-title">Currently Viewing</span>
            </div>
            <div class="visitors-items">
        `;

        pageEntries.forEach(([page, count]) => {
          // Strip domain from URL (handle both full URLs and paths)
          let cleanPath = page;
          try {
            // If it's a full URL, extract just the pathname
            if (page.includes('://')) {
              const url = new URL(page);
              cleanPath = url.pathname;
            } else if (page.includes('coracreacrafts') || page.includes('.com')) {
              // Handle domain without protocol (e.g., "coracreacrafts.com/products/mug")
              cleanPath = page.replace(/^[^/]*coracreacrafts[^/]*\/?/, '/');
            }

            // Remove site name prefix/suffix from page titles (e.g., "CoraCreaCrafts | Products" → "Products")
            cleanPath = cleanPath.replace(/^CoraCreaCrafts\s*\|\s*/i, '');
            cleanPath = cleanPath.replace(/^Cora Crea Crafts\s*\|\s*/i, '');
            cleanPath = cleanPath.replace(/\s*\|\s*CoraCreaCrafts$/i, '');
            cleanPath = cleanPath.replace(/\s*\|\s*Cora Crea Crafts$/i, '');
          } catch (_e) {
            // If URL parsing fails, use as-is
            cleanPath = page;
          }

          const pageIcon = cleanPath.includes('/products/')
            ? '🛍️'
            : cleanPath.includes('/collections/')
              ? '📂'
              : cleanPath === '/'
                ? '🏠'
                : cleanPath.includes('/cart')
                  ? '🛒'
                  : '📄';
          const pageNameRaw =
            cleanPath === '/'
              ? 'Homepage'
              : cleanPath.length > 50
                ? '...' + cleanPath.substring(cleanPath.length - 50)
                : cleanPath;
          const pageName = this.escapeHtml(pageNameRaw);
          const fullPath = this.escapeHtml(cleanPath);

          html += `
            <div class="visitors-item page-item" data-full-path="${fullPath}">
              <span class="item-icon">${pageIcon}</span>
              <span class="item-label page-path">${pageName}</span>
              <span class="item-count">${parseInt(count, 10) || 0}</span>
            </div>
          `;
        });

        html += `
            </div>
          </div>
        `;
      }

      // Bots section
      if (botsBreakdown && Array.isArray(botsBreakdown) && botsBreakdown.length > 0) {
        // Sort by count descending
        const sortedBots = botsBreakdown.sort((a, b) => b.count - a.count).slice(0, 15);

        html += `
          <div class="visitors-section">
            <div class="visitors-section-header">
              <span class="section-icon">🤖</span>
              <span class="section-title">Active Bots</span>
            </div>
            <div class="visitors-items">
        `;

        sortedBots.forEach((bot) => {
          const botIcon =
            bot.botType.toLowerCase().includes('gpt') ||
            bot.botType.toLowerCase().includes('chatgpt')
              ? '🤖'
              : bot.botType.toLowerCase().includes('claude')
                ? '🧠'
                : bot.botType.toLowerCase().includes('google') ||
                    bot.botType.toLowerCase().includes('gemini')
                  ? '🔍'
                  : bot.botType.toLowerCase().includes('bing')
                    ? '🌐'
                    : bot.botType.toLowerCase().includes('facebook') ||
                        bot.botType.toLowerCase().includes('meta')
                      ? '👥'
                      : bot.botType.toLowerCase().includes('perplexity')
                        ? '💡'
                        : bot.botType.toLowerCase().includes('crawler') ||
                            bot.botType.toLowerCase().includes('spider')
                          ? '🕷️'
                          : '🤖';

          const flag = this.getCountryFlag(bot.location);
          const deviceRaw =
            bot.device.length > 50 ? bot.device.substring(0, 47) + '...' : bot.device;
          const deviceName = this.escapeHtml(deviceRaw);
          const safeLocation = this.escapeHtml(bot.location);
          const safeBotType = this.escapeHtml(bot.botType);
          const safeDeviceAttr = this.escapeHtml(bot.device);

          html += `
            <div class="visitors-item bot-item" data-tooltip="${safeDeviceAttr}">
              <span class="item-icon">${botIcon}</span>
              <div class="bot-details">
                <span class="bot-device">${deviceName}</span>
                <span class="bot-meta">${flag} ${safeLocation} • ${safeBotType}</span>
              </div>
              <span class="item-count">${parseInt(bot.count, 10) || 0}</span>
            </div>
          `;
        });

        html += `
            </div>
          </div>
        `;
      }

      html += `</div>`;
      safeSetHTML(modal, html);
      document.body.appendChild(modal);

      // Add event listeners
      const closeBtn = modal.querySelector('.visitors-modal-close');
      closeBtn.addEventListener('click', () => modal.remove());

      // Close on outside click
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          modal.remove();
        }
      });

      // Add nested tooltips for page items
      const pageItems = modal.querySelectorAll('.page-item');
      pageItems.forEach((item) => {
        item.addEventListener('mouseenter', (_e) => {
          const fullPath = item.getAttribute('data-full-path');
          if (!fullPath) return;

          // Remove any existing tooltips first
          document.querySelectorAll('.page-tooltip').forEach((t) => t.remove());

          const pageTooltip = document.createElement('div');
          pageTooltip.className = 'page-tooltip';

          // Show full URL path
          pageTooltip.textContent = fullPath;

          document.body.appendChild(pageTooltip);

          // Position tooltip above the item
          const rect = item.getBoundingClientRect();
          const tooltipRect = pageTooltip.getBoundingClientRect();

          // Center horizontally on the item
          const left = rect.left + rect.width / 2 - tooltipRect.width / 2;
          const top = rect.top - tooltipRect.height - 12;

          pageTooltip.style.top = `${Math.max(10, top)}px`;
          pageTooltip.style.left = `${Math.max(10, Math.min(left, window.innerWidth - tooltipRect.width - 10))}px`;
        });

        item.addEventListener('mouseleave', () => {
          document.querySelectorAll('.page-tooltip').forEach((t) => t.remove());
        });
      });

      // Animate in
      requestAnimationFrame(() => {
        modal.classList.add('visible');
      });
    },

    // Track traffic attribution (medium, campaign, channel grouping)
    trackTrafficAttribution: async function () {
      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=traffic-attribution`
        );

        if (response.ok) {
          if (result.success && result.data) {
            const { mediums, campaigns, channels } = result.data;

            // Populate Traffic Medium Table
            this.populateAttributionTable(
              'medium-table-body',
              'medium-total-count',
              mediums.breakdown,
              mediums.total,
              'medium'
            );

            // Populate Campaign Table
            this.populateAttributionTable(
              'campaign-table-body',
              'campaign-total-count',
              campaigns.breakdown,
              campaigns.total,
              'campaign'
            );

            // Populate Channel Table
            this.populateAttributionTable(
              'channel-table-body',
              'channel-total-count',
              channels.breakdown,
              channels.total,
              'channel'
            );
          } else {
            this.setTrafficAttributionError('API Error');
          }
        } else {
          this.setTrafficAttributionError('GA Required');
        }
      } catch (error) {
        AdminDashboardCore.logError('[Admin] trackTrafficAttribution error:', error);
        this.setTrafficAttributionError('GA Required');
      }
    },

    // Track campaign revenue performance (actual sales attributed to campaigns)
    trackCampaignRevenuePerformance: async function (period = '7d') {
      // Show loading state
      const tableBody = document.getElementById('campaign-revenue-table-body');
      const totalCountEl = document.getElementById('campaign-revenue-total');

      if (tableBody) {
        safeSetHTML(
          tableBody,
          `
          <tr class="loading-row">
            <td colspan="4">
              <div class="loading-container">
                <div class="loading-spinner small"></div>
                <span class="loading-text">Loading campaign revenue data...</span>
              </div>
            </td>
          </tr>
        `
        );
      }

      if (totalCountEl) {
        totalCountEl.textContent = 'Loading...';
      }

      try {
        const response = await fetch(
          `${this.config.apiProxyUrl}?endpoint=campaign-revenue-performance&period=${period}`
        );

        if (response.ok) {
          const result = await response.json();

          if (result.success && result.data && result.data.campaigns) {
            const { campaigns, totalOrders } = result.data;

            // Populate Campaign Revenue Performance Table
            this.populateCampaignRevenueTable(
              'campaign-revenue-table-body',
              'campaign-revenue-total',
              campaigns,
              totalOrders
            );
          } else {
            this.setCampaignRevenueError('No data');
          }
        } else {
          this.setCampaignRevenueError('API Error');
        }
      } catch (error) {
        AdminDashboardCore.logError('[Admin] trackCampaignRevenuePerformance error:', error);
        this.setCampaignRevenueError('Error');
      }
    },

    // Populate campaign revenue table with actual sales data
    populateCampaignRevenueTable: function (tableBodyId, totalCountId, campaigns, totalOrders) {
      const tableBody = document.getElementById(tableBodyId);
      const totalCountEl = document.getElementById(totalCountId);

      if (!tableBody || !totalCountEl) {
        return;
      }

      // Update total count
      totalCountEl.textContent = `${totalOrders.toLocaleString()} orders`;

      if (!campaigns || campaigns.length === 0) {
        safeSetHTML(
          tableBody,
          '<tr class="loading-row"><td colspan="5">No campaign sales data available</td></tr>'
        );
        return;
      }

      // Build table rows (campaigns already sorted by revenue)
      const rows = campaigns
        .slice(0, 10)
        .map((campaign) => {
          const revenue = parseFloat(campaign.totalRevenue || 0);
          const orders = parseInt(campaign.orderCount || 0, 10);
          const aov = campaign.avgOrderValue || 0;
          const confidence = campaign.confidenceScore
            ? (campaign.confidenceScore * 100).toFixed(0)
            : 0;

          // Source badge with color
          const sourceBadge = campaign.sourceBadge || campaign.source?.toUpperCase() || 'UNKNOWN';
          const badgeColor = PLATFORM_BADGE_COLORS[sourceBadge] || '#6b7280';

          // Confidence indicator
          const confEmoji = _getConfidenceEmoji(confidence);
          const confColor = _getConfidenceColor(confidence);

          return `
          <tr>
            <td>
              <div style="display: flex; flex-direction: column; gap: 4px;">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <span style="background: ${badgeColor}; color: white; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: bold;">${sourceBadge}</span>
                  <span>${campaign.campaignName}</span>
                </div>
                <div style="font-size: 11px; color: rgba(255,255,255,0.5);">
                  <span style="color: ${confColor};">${confEmoji}</span> ${confidence}% high confidence
                </div>
              </div>
            </td>
            <td>$${revenue.toFixed(2)}</td>
            <td>${orders}</td>
            <td>$${aov.toFixed(2)}</td>
          </tr>
        `;
        })
        .join('');

      safeSetHTML(tableBody, rows);
    },

    // Helper to set error state for campaign revenue table
    setCampaignRevenueError: function (errorText) {
      const tableBody = document.getElementById('campaign-revenue-table-body');
      const totalCountEl = document.getElementById('campaign-revenue-total');

      if (tableBody) {
        safeSetHTML(
          tableBody,
          `<tr class="loading-row"><td colspan="4">${errorText} - Campaign revenue data unavailable</td></tr>`
        );
      }

      if (totalCountEl) {
        totalCountEl.textContent = '-- orders';
      }
    },

    // Helper to set error state for all traffic attribution tables
    setTrafficAttributionError: function (errorText) {
      const errorRow = `<tr class="loading-row"><td colspan="3">${errorText} - Traffic attribution data unavailable</td></tr>`;

      const mediumBody = document.getElementById('medium-table-body');
      const campaignBody = document.getElementById('campaign-table-body');
      const channelBody = document.getElementById('channel-table-body');

      if (mediumBody) safeSetHTML(mediumBody, errorRow);
      if (campaignBody) safeSetHTML(campaignBody, errorRow);
      if (channelBody) safeSetHTML(channelBody, errorRow);

      const mediumCount = document.getElementById('medium-total-count');
      const campaignCount = document.getElementById('campaign-total-count');
      const channelCount = document.getElementById('channel-total-count');

      if (mediumCount) mediumCount.textContent = '-- sessions';
      if (campaignCount) campaignCount.textContent = '-- sessions';
      if (channelCount) channelCount.textContent = '-- sessions';
    },

    // Populate an attribution table with data
    populateAttributionTable: function (tableBodyId, totalCountId, breakdown, total, type) {
      const tableBody = document.getElementById(tableBodyId);
      const totalCountEl = document.getElementById(totalCountId);

      if (!tableBody || !totalCountEl) {
        return;
      }

      // Update total count
      totalCountEl.textContent = `${total.toLocaleString()} sessions`;

      // Sort entries by count (descending)
      const sortedEntries = Object.entries(breakdown || {}).sort((a, b) => b[1] - a[1]);

      if (sortedEntries.length === 0) {
        safeSetHTML(
          tableBody,
          '<tr class="loading-row"><td colspan="3">No data available</td></tr>'
        );
        return;
      }

      // Build table rows
      const rows = sortedEntries
        .map(([name, count]) => {
          const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';

          // Transform "(none)" and "(not set)" to user-friendly names
          let displayName = name;
          if (name === '(none)' || name === '(not set)') {
            if (type === 'campaign') {
              displayName = 'No Campaign';
            } else {
              displayName = 'Direct';
            }
          }

          // Add color coding for campaigns
          let nameColor = '';
          if (type === 'campaign') {
            const nameLower = name.toLowerCase();
            if (nameLower.includes('google')) {
              nameColor = 'style="color: #7dd3fc"'; // light blue
            } else if (nameLower.includes('klaviyo')) {
              nameColor = 'style="color: #fde047"'; // yellow
            } else if (nameLower.includes('facebook')) {
              nameColor = 'style="color: #86efac"'; // light green
            } else if (nameLower.includes('instagram')) {
              nameColor = 'style="color: #f9a8d4"'; // pink
            }
          }

          return `
          <tr>
            <td><span ${nameColor}>${displayName}</span></td>
            <td>${count.toLocaleString()}</td>
            <td>
              <div class="percentage-bar-wrapper">
                <div class="percentage-bar">
                  <div class="percentage-fill" style="width: ${percentage}%"></div>
                </div>
                <span class="percentage-text">${percentage}%</span>
              </div>
            </td>
          </tr>
        `;
        })
        .join('');

      safeSetHTML(tableBody, rows);
    },

    // Build tooltip for traffic attribution cards
    buildTrafficTooltip: function (title, breakdown, total) {
      const lines = [title, '━━━━━━━━━━━━━━━'];

      if (!breakdown || Object.keys(breakdown).length === 0) {
        lines.push('No data available');
        return lines.join('\n');
      }

      // Show ALL entries, sorted by count
      const sortedEntries = Object.entries(breakdown).sort((a, b) => b[1] - a[1]);

      sortedEntries.forEach(([name, count]) => {
        const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
        // Transform "(none)" and "(not set)" for tooltip display
        let displayName = name;
        if (name === '(none)' || name === '(not set)') {
          displayName =
            title.includes('Medium') || title.includes('Channel') ? 'Direct' : 'No Campaign';
        }
        lines.push(`  ${displayName}: ${count} (${percentage}%)`);
      });

      return lines.join('\n');
    },

    // Format date as "31 Dec 2025" (no commas)
    formatDateShort: function (date) {
      const day = date.getDate();
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    },

    // Update Real-Time Activity dropdown with dates
    updateRealtimeActivityDate: function (_period) {
      const selector = document.getElementById('realtime-activity-period');
      if (!selector) return;

      const now = new Date();
      const today = new Date(now);
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 6);

      // Format date as "31 Dec" (short format for dropdown)
      const formatShort = (date) => {
        const day = date.getDate();
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        return `${day} ${month}`;
      };

      // Build label map
      const labelMap = {
        today: `Today (${formatShort(today)})`,
        yesterday: `Yesterday (${formatShort(yesterday)})`,
        week: 'Past 7 Days',
      };

      // Update option text to show "Today (date)" format
      const options = selector.options;
      for (let i = 0; i < options.length; i++) {
        const option = options[i];
        if (labelMap[option.value]) {
          option.textContent = labelMap[option.value];
        }
      }

      // Also update the custom glassmorphic dropdown if it exists
      const customDropdown = selector.parentElement?.querySelector('.custom-dropdown');
      if (customDropdown) {
        // Update trigger text for current selection
        const triggerText = customDropdown.querySelector('.custom-dropdown-text');
        if (triggerText && labelMap[selector.value]) {
          triggerText.textContent = labelMap[selector.value];
        }
        // Update menu options
        customDropdown.querySelectorAll('.custom-dropdown-option').forEach((opt) => {
          const val = opt.dataset.value;
          if (labelMap[val]) {
            opt.textContent = labelMap[val];
          }
        });
      }
    },

    // Unified loader for Live Activity + Retail Orders (single API call)
    // This avoids duplicate calls to the recent-orders endpoint
    loadAllRecentOrders: async function (period = 'today') {
      try {
        const cacheBuster = this.config.cacheBuster ? `&_cb=${this.config.cacheBuster}` : '';
        const response = await fetch(
          `${this.config.apiProxyUrl}?endpoint=recent-orders&period=${period}${cacheBuster}`
        );

        // Discard if user switched to a different period while this fetch was in-flight
        if (this.state.currentActivityPeriod && this.state.currentActivityPeriod !== period) {
          return;
        }

        if (response.ok) {
          const result = await response.json();
          // Cache the result so the three feed loaders below can share it
          this.state.cachedRecentOrders = {
            data: result,
            period: period,
            timestamp: Date.now(),
          };

          // Now call the feed loaders - they will use the cached data
          await Promise.all([
            this.loadRecentActivity(period),
            this.loadRetailOrders(period),
            this.loadCustomSales(period),
          ]);
        } else {
          // Clear cache on error
          this.state.cachedRecentOrders = null;
          await Promise.all([
            this.loadRecentActivity(period),
            this.loadRetailOrders(period),
            this.loadCustomSales(period),
          ]);
        }
      } catch (_error) {
        this.state.cachedRecentOrders = null;
        await Promise.all([
          this.loadRecentActivity(period),
          this.loadRetailOrders(period),
          this.loadCustomSales(period),
        ]);
      }
    },

    // Show loading indicator for Real-Time Activity cards
    showRealtimeActivityLoading: function () {
      const activityList = document.getElementById('activity-list');
      const subscriptionList = document.getElementById('subscription-list');
      const retailList = document.getElementById('retail-list');

      const loadingHTML = `
        <div class="loading-container">
          <div class="loading-spinner large"></div>
          <div class="loading-text">Loading...</div>
        </div>
      `;

      if (activityList) safeSetHTML(activityList, loadingHTML);
      if (subscriptionList) safeSetHTML(subscriptionList, loadingHTML);
      if (retailList) safeSetHTML(retailList, loadingHTML);
    },

    // Load subscription renewals
    loadSubscriptionRenewals: async function (period = 'today') {
      const subscriptionList = document.getElementById('subscription-list');

      try {
        const cacheBuster = this.config.cacheBuster ? `&_cb=${this.config.cacheBuster}` : '';
        const response = await fetch(
          `${this.config.apiProxyUrl}?endpoint=subscription-renewals&period=${period}${cacheBuster}`
        );

        // Discard if user switched to a different period while this fetch was in-flight
        if (this.state.currentActivityPeriod && this.state.currentActivityPeriod !== period) {
          return;
        }

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data.length > 0) {
            safeSetHTML(subscriptionList, '');

            // Calculate and display total dollar amount
            const total = result.data.reduce((sum, order) => sum + parseFloat(order.total || 0), 0);
            const subscriptionTotalEl = document.getElementById('subscription-total');
            if (subscriptionTotalEl) {
              subscriptionTotalEl.textContent = `$${Math.round(total).toLocaleString('en-US')}`;
            }

            result.data.forEach((order) => {
              const item = document.createElement('div');

              // Determine value tier for color coding
              const orderValue = parseFloat(order.total) || 0;
              const { valueTier, valueClass } = AdminDashboardCore.getOrderValueTier(orderValue);

              item.className = `activity-item ${valueTier}`;

              // Store full order data for tooltip
              item.dataset.orderData = JSON.stringify(order);

              // Calculate time ago
              const timeAgo = this.formatTimeAgo(order.createdAt);

              // Extract discount code
              let discountCode = '';
              if (order.discountCode) {
                discountCode = order.discountCode;
              } else if (order.discountCodes && order.discountCodes.length > 0) {
                discountCode = order.discountCodes
                  .map((d) => d.code)
                  .filter(Boolean)
                  .join(', ');
              } else if (order.discountApplications && order.discountApplications.length > 0) {
                discountCode = order.discountApplications
                  .map((d) => d.code || d.title)
                  .filter(Boolean)
                  .join(', ');
              }

              // Check for international shipping
              let isInternational = false;
              if (order.shippingAddress && order.shippingAddress.country) {
                const country = order.shippingAddress.country.toUpperCase();
                isInternational =
                  country !== 'US' && country !== 'USA' && country !== 'UNITED STATES';
              }

              // Check if order has a note
              const hasNote =
                (order.note && order.note.trim()) || (order.notes && order.notes.trim());

              // Format order count with styling
              const orderCountDisplay = order.customerOrderCount
                ? ` (${order.customerOrderCount})`
                : '';

              // Format timing information (for repeat customers)
              const timingDisplay = this.core.formatOrderTiming(
                order.customerOrderCount,
                order.firstOrderDate,
                order.lastOrderDate
              );

              // Traffic-source pill via the shared resolver (same 3-tier fallback as the popup).
              const trafficSourceBadge = AdminDashboardCore.trafficSourceRowBadge(order);

              safeSetHTML(
                item,
                `
                <div class="activity-avatar">🔄</div>
                <div class="activity-details">
                  <h4>${this.escapeHtml(order.customerName)}${orderCountDisplay}${timingDisplay} <span class="subscription-badge">RENEWAL</span></h4>
                  <p>#${order.orderNumber} • <span class="order-value ${valueClass}">$${order.total}</span>${trafficSourceBadge}${discountCode ? ` • <span class="discount-badge">${discountCode}</span>` : ''}${isInternational ? ' • <span class="international-badge">INTL</span>' : ''}${hasNote ? ' • <span class="note-badge">NOTE</span>' : ''}</p>
                </div>
                <div class="activity-time">${timeAgo}</div>
              `
              );

              this.attachOrderRowInteractions(item, order);

              subscriptionList.appendChild(item);
            });

            document.getElementById('subscription-count').textContent =
              `${result.data.length} renewals`;
          } else {
            // No subscription renewals found
            safeSetHTML(
              subscriptionList,
              '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">No subscription renewals</p>'
            );
            document.getElementById('subscription-count').textContent = '0 renewals';
            const subscriptionTotalEl = document.getElementById('subscription-total');
            if (subscriptionTotalEl) {
              subscriptionTotalEl.textContent = '$0';
            }
          }
        } else {
          safeSetHTML(
            subscriptionList,
            '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">Deploy proxy to see renewals</p>'
          );
          document.getElementById('subscription-count').textContent = '0 renewals';
          const subscriptionTotalEl = document.getElementById('subscription-total');
          if (subscriptionTotalEl) {
            subscriptionTotalEl.textContent = '$0';
          }
        }
      } catch (_error) {
        safeSetHTML(
          subscriptionList,
          '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">Deploy proxy to see renewals</p>'
        );
        document.getElementById('subscription-count').textContent = '0 renewals';
        const subscriptionTotalEl = document.getElementById('subscription-total');
        if (subscriptionTotalEl) {
          subscriptionTotalEl.textContent = '$0';
        }
      }
    },

    // Load retail/POS orders
    loadRetailOrders: async function (period = 'today') {
      const retailList = document.getElementById('retail-list');

      try {
        // Check for cached data from loadAllRecentOrders (avoids duplicate API calls)
        let result;
        if (
          this.state.cachedRecentOrders &&
          this.state.cachedRecentOrders.period === period &&
          Date.now() - this.state.cachedRecentOrders.timestamp < 5000
        ) {
          result = this.state.cachedRecentOrders.data;
        } else {
          // No cache, fetch directly
          const cacheBuster = this.config.cacheBuster ? `&_cb=${this.config.cacheBuster}` : '';
          const response = await fetch(
            `${this.config.apiProxyUrl}?endpoint=recent-orders&period=${period}${cacheBuster}`
          );
          if (!response.ok) throw new Error('API error');
          result = await response.json();
        }

        if (result.success && result.data.length > 0) {
          // Filter for retail orders only, excluding POS custom sales (own section).
          // `!== true` so an older proxy build that omits isCustomSale degrades to
          // the previous behavior rather than emptying this feed.
          const retailOrders = result.data.filter(
            (order) => order.isRetailOrder === true && order.isCustomSale !== true
          );

          if (retailOrders.length > 0) {
            safeSetHTML(retailList, '');

            // Calculate and display total dollar amount
            const total = retailOrders.reduce(
              (sum, order) => sum + parseFloat(order.total || 0),
              0
            );
            const retailTotalEl = document.getElementById('retail-total');
            if (retailTotalEl) {
              retailTotalEl.textContent = `$${Math.round(total).toLocaleString('en-US')}`;
            }

            retailOrders.forEach((order) => {
              const item = document.createElement('div');

              // Determine value tier for color coding
              const orderValue = parseFloat(order.total) || 0;
              const { valueTier, valueClass } = AdminDashboardCore.getOrderValueTier(orderValue);

              item.className = `activity-item ${valueTier}`;

              // Store full order data for tooltip
              item.dataset.orderData = JSON.stringify(order);

              // Calculate time ago
              const timeAgo = this.formatTimeAgo(order.createdAt);

              // Extract discount code
              let discountCode = '';
              if (order.discountCode) {
                discountCode = order.discountCode;
              } else if (order.discountCodes && order.discountCodes.length > 0) {
                discountCode = order.discountCodes
                  .map((d) => d.code)
                  .filter(Boolean)
                  .join(', ');
              } else if (order.discountApplications && order.discountApplications.length > 0) {
                discountCode = order.discountApplications
                  .map((d) => d.code || d.title)
                  .filter(Boolean)
                  .join(', ');
              }

              // Check for international shipping
              let isInternational = false;
              if (order.shippingAddress && order.shippingAddress.country) {
                const country = order.shippingAddress.country.toUpperCase();
                isInternational =
                  country !== 'US' && country !== 'USA' && country !== 'UNITED STATES';
              }

              // Check if order has a note
              const hasNote =
                (order.note && order.note.trim()) || (order.notes && order.notes.trim());

              // Check if this is a first-time customer
              const isNewCustomer = parseInt(order.customerOrderCount, 10) === 1;

              // Format order count with styling (red for first-time customers)
              const orderCountDisplay = order.customerOrderCount
                ? parseInt(order.customerOrderCount, 10) === 1
                  ? ` <span style="color: #ff1493; font-weight: 700;">(${order.customerOrderCount})</span>`
                  : ` (${order.customerOrderCount})`
                : '';

              // Format timing information (for repeat customers)
              const timingDisplay = this.core.formatOrderTiming(
                order.customerOrderCount,
                order.firstOrderDate,
                order.lastOrderDate
              );

              // Traffic-source pill via the shared resolver (same 3-tier fallback as the popup).
              const trafficSourceBadge = AdminDashboardCore.trafficSourceRowBadge(order);

              safeSetHTML(
                item,
                `
                  <div class="activity-avatar">🏪</div>
                  <div class="activity-details">
                    <h4>${this.escapeHtml(order.customerName)}${orderCountDisplay}${timingDisplay}${isNewCustomer ? ' <span class="new-customer-badge">NEW</span>' : ''} <span class="retail-badge">RETAIL</span></h4>
                    <p>#${order.orderNumber} • <span class="order-value ${valueClass}">$${order.total}</span>${trafficSourceBadge}${discountCode ? ` • <span class="discount-badge">${discountCode}</span>` : ''}${isInternational ? ' • <span class="international-badge">INTL</span>' : ''}${hasNote ? ' • <span class="note-badge">NOTE</span>' : ''}</p>
                  </div>
                  <div class="activity-time">${timeAgo}</div>
                `
              );

              this.attachOrderRowInteractions(item, order);

              retailList.appendChild(item);
            });

            document.getElementById('retail-count').textContent = `${retailOrders.length} orders`;
          } else {
            // No retail orders found
            safeSetHTML(
              retailList,
              '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">No retail orders</p>'
            );
            document.getElementById('retail-count').textContent = '0 orders';
            const retailTotalEl = document.getElementById('retail-total');
            if (retailTotalEl) {
              retailTotalEl.textContent = '$0';
            }
          }
        } else {
          // No orders at all
          safeSetHTML(
            retailList,
            '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">No retail orders</p>'
          );
          document.getElementById('retail-count').textContent = '0 orders';
          const retailTotalEl = document.getElementById('retail-total');
          if (retailTotalEl) {
            retailTotalEl.textContent = '$0';
          }
        }
      } catch (_error) {
        safeSetHTML(
          retailList,
          '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">Deploy proxy to see orders</p>'
        );
        document.getElementById('retail-count').textContent = '0 orders';
        const retailTotalEl = document.getElementById('retail-total');
        if (retailTotalEl) {
          retailTotalEl.textContent = '$0';
        }
      }
    },

    // Hide the Custom Sales card entirely when there is nothing to show.
    // The card ships with display:none in the markup, so it never flashes an
    // empty shell during load; this only toggles it back on when populated.
    setCustomSalesCardVisible: function (visible) {
      const list = document.getElementById('custom-sales-list');
      if (!list) return;
      const card = list.closest('.glass-card');
      if (card) card.style.display = visible ? '' : 'none';
    },

    // Load POS custom-sale orders (every line item rung up by hand at the
    // register). Shares the loadAllRecentOrders fetch cache with the Live
    // Activity and Retail Orders feeds.
    loadCustomSales: async function (period = 'today') {
      const customSalesList = document.getElementById('custom-sales-list');
      if (!customSalesList) return;

      try {
        // Check for cached data from loadAllRecentOrders (avoids duplicate API calls)
        let result;
        if (
          this.state.cachedRecentOrders &&
          this.state.cachedRecentOrders.period === period &&
          Date.now() - this.state.cachedRecentOrders.timestamp < 5000
        ) {
          result = this.state.cachedRecentOrders.data;
        } else {
          // No cache, fetch directly
          const cacheBuster = this.config.cacheBuster ? `&_cb=${this.config.cacheBuster}` : '';
          const response = await fetch(
            `${this.config.apiProxyUrl}?endpoint=recent-orders&period=${period}${cacheBuster}`
          );
          if (!response.ok) throw new Error('API error');
          result = await response.json();
        }

        // `=== true` so an older proxy build that omits isCustomSale leaves the
        // card hidden rather than rendering a broken or duplicated feed.
        const customSales =
          result.success && Array.isArray(result.data)
            ? result.data.filter((order) => order.isCustomSale === true)
            : [];

        if (customSales.length === 0) {
          this.setCustomSalesCardVisible(false);
          return;
        }

        safeSetHTML(customSalesList, '');

        const total = customSales.reduce((sum, order) => sum + parseFloat(order.total || 0), 0);
        const customSalesTotalEl = document.getElementById('custom-sales-total');
        if (customSalesTotalEl) {
          customSalesTotalEl.textContent = `$${Math.round(total).toLocaleString('en-US')}`;
        }

        customSales.forEach((order) => {
          const item = document.createElement('div');

          // Determine value tier for color coding
          const orderValue = parseFloat(order.total) || 0;
          const { valueTier, valueClass } = AdminDashboardCore.getOrderValueTier(orderValue);

          item.className = `activity-item ${valueTier}`;

          // Store full order data for the shared order drawer
          item.dataset.orderData = JSON.stringify(order);

          const timeAgo = this.formatTimeAgo(order.createdAt);

          // Extract discount code
          let discountCode = '';
          if (order.discountCode) {
            discountCode = order.discountCode;
          } else if (order.discountCodes && order.discountCodes.length > 0) {
            discountCode = order.discountCodes
              .map((d) => d.code)
              .filter(Boolean)
              .join(', ');
          } else if (order.discountApplications && order.discountApplications.length > 0) {
            discountCode = order.discountApplications
              .map((d) => d.code || d.title)
              .filter(Boolean)
              .join(', ');
          }

          // Check if order has a note
          const hasNote = (order.note && order.note.trim()) || (order.notes && order.notes.trim());

          // Check if this is a first-time customer
          const isNewCustomer = parseInt(order.customerOrderCount, 10) === 1;

          const orderCountDisplay = order.customerOrderCount
            ? parseInt(order.customerOrderCount, 10) === 1
              ? ` <span style="color: #ff1493; font-weight: 700;">(${order.customerOrderCount})</span>`
              : ` (${order.customerOrderCount})`
            : '';

          const timingDisplay = this.core.formatOrderTiming(
            order.customerOrderCount,
            order.firstOrderDate,
            order.lastOrderDate
          );

          // Traffic-source pill via the shared resolver (same 3-tier fallback as the popup).
          const trafficSourceBadge = AdminDashboardCore.trafficSourceRowBadge(order);

          safeSetHTML(
            item,
            `
                  <div class="activity-avatar">🧾</div>
                  <div class="activity-details">
                    <h4>${this.escapeHtml(order.customerName)}${orderCountDisplay}${timingDisplay}${isNewCustomer ? ' <span class="new-customer-badge">NEW</span>' : ''} <span class="custom-sale-badge">CUSTOM</span></h4>
                    <p>#${order.orderNumber} • <span class="order-value ${valueClass}">$${order.total}</span>${trafficSourceBadge}${discountCode ? ` • <span class="discount-badge">${discountCode}</span>` : ''}${hasNote ? ' • <span class="note-badge">NOTE</span>' : ''}</p>
                  </div>
                  <div class="activity-time">${timeAgo}</div>
                `
          );

          this.attachOrderRowInteractions(item, order);

          customSalesList.appendChild(item);
        });

        const customSalesCountEl = document.getElementById('custom-sales-count');
        if (customSalesCountEl) {
          customSalesCountEl.textContent = `${customSales.length} orders`;
        }

        this.setCustomSalesCardVisible(true);
      } catch (_error) {
        // Never surface an error shell for this card - it is hidden by contract
        // whenever it has no content.
        this.setCustomSalesCardVisible(false);
      }
    },

    // Load recent activity from real orders
    loadRecentActivity: async function (period = 'today') {
      const activityList = document.getElementById('activity-list');

      try {
        // Check for cached data from loadAllRecentOrders (avoids duplicate API calls)
        let result;
        if (
          this.state.cachedRecentOrders &&
          this.state.cachedRecentOrders.period === period &&
          Date.now() - this.state.cachedRecentOrders.timestamp < 5000
        ) {
          result = this.state.cachedRecentOrders.data;
        } else {
          // No cache, fetch directly
          const cacheBuster = this.config.cacheBuster ? `&_cb=${this.config.cacheBuster}` : '';
          const response = await fetch(
            `${this.config.apiProxyUrl}?endpoint=recent-orders&period=${period}${cacheBuster}`
          );
          if (!response.ok) throw new Error('API error');
          result = await response.json();
        }

        if (result.success && result.data.length > 0) {
          // Create a hash of the current data to detect changes
          const dataHash = JSON.stringify(result.data.map((o) => o.id));

          // Only update if data has changed
          if (this.state.lastActivityHash !== dataHash) {
            this.state.lastActivityHash = dataHash;

            // Detect new orders and show animated notification
            const currentOrderCount = result.data.length;
            const newOrdersCount = currentOrderCount - this.state.lastActivityCount;

            if (!this.state.isFirstLoad && newOrdersCount > 0) {
              this.showNewOrdersNotification(newOrdersCount);
            }
            this.state.lastActivityCount = currentOrderCount;

            safeSetHTML(activityList, '');

            // IMPORTANT: Filter out retail orders, custom sales AND subscription renewals.
            // recent-orders returns ALL order types; each type has its own panel:
            //   isCustomSale === true          → Custom Sales section (loadCustomSales)
            //   isRetailOrder === true         → Retail Orders section (loadRetailOrders)
            //   isSubscriptionRenewal === true → Subscription Renewals section (loadSubscriptionRenewals)
            // Custom sales are always POS, so isRetailOrder already excludes them; the
            // explicit check keeps this list an exhaustive statement of the contract.
            // Omitting any condition causes duplicates across sections.
            const nonRetailOrders = result.data.filter(
              (order) =>
                order.isRetailOrder !== true &&
                order.isCustomSale !== true &&
                order.isSubscriptionRenewal !== true
            );

            // Calculate and display total dollar amount (excluding retail)
            const total = nonRetailOrders.reduce(
              (sum, order) => sum + parseFloat(order.total || 0),
              0
            );
            const activityTotalEl = document.getElementById('activity-total');
            if (activityTotalEl) {
              activityTotalEl.textContent = `$${Math.round(total).toLocaleString('en-US')}`;
            }

            // Handle case where all orders are retail (shown in separate section)
            if (nonRetailOrders.length === 0) {
              safeSetHTML(
                activityList,
                '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">No online orders (check Retail section)</p>'
              );
              document.getElementById('activity-count').textContent = '0 recent orders';
            }

            nonRetailOrders.forEach((order) => {
              const item = document.createElement('div');

              // Determine value tier for color coding
              const orderValue = parseFloat(order.total) || 0;
              const { valueTier, valueClass } = AdminDashboardCore.getOrderValueTier(orderValue);

              item.className = `activity-item ${valueTier}`;

              // Store full order data for tooltip
              item.dataset.orderData = JSON.stringify(order);

              // Calculate time ago
              const timeAgo = this.formatTimeAgo(order.createdAt);

              // Extract discount code properly
              let discountCode = '';
              if (order.discountCode) {
                discountCode = order.discountCode;
              } else if (order.discountCodes && order.discountCodes.length > 0) {
                // discountCodes is an array of objects like { code: "SAVE10", amount: "10.00" }
                discountCode = order.discountCodes
                  .map((d) => d.code)
                  .filter(Boolean)
                  .join(', ');
              } else if (order.discountApplications && order.discountApplications.length > 0) {
                discountCode = order.discountApplications
                  .map((d) => d.code || d.title)
                  .filter(Boolean)
                  .join(', ');
              }

              // Check if coupon code contains "SHIP" for upgrade badge
              const hasShipUpgrade = discountCode && discountCode.toUpperCase().includes('SHIP');

              // Check for pre-orders in line items
              let hasPreOrder = false;
              if (order.lineItems && order.lineItems.length > 0) {
                hasPreOrder = order.lineItems.some(
                  (item) => item.name && item.name.toUpperCase().includes('PRE-ORDER')
                );
              }

              // Check if order contains subscription items
              let hasSubscription = false;
              if (order.lineItems && order.lineItems.length > 0) {
                hasSubscription = order.lineItems.some(
                  (item) => item.name && item.name.toUpperCase().includes('SUBSCRIPTION')
                );
              }

              // Check if order contains Faire Payout Fee
              let hasFaire = false;
              if (order.lineItems && order.lineItems.length > 0) {
                hasFaire = order.lineItems.some(
                  (item) => item.name && item.name.toLowerCase().includes('faire payout fee')
                );
              }

              // Check if shipping address is international (non-US)
              let isInternational = false;
              if (order.shippingAddress && order.shippingAddress.country) {
                const country = order.shippingAddress.country.toUpperCase();
                isInternational =
                  country !== 'US' && country !== 'USA' && country !== 'UNITED STATES';
              }

              // Check if order has a note
              const hasNote =
                (order.note && order.note.trim()) || (order.notes && order.notes.trim());

              // Check if order has a tip (tips appear as line items with "Tip" in the name)
              let hasTip = false;
              if (order.lineItems && order.lineItems.length > 0) {
                hasTip = order.lineItems.some(
                  (item) => item.name && item.name.toLowerCase().includes('tip')
                );
              }

              // Check if order came from gift quiz
              let hasQuiz = false;
              if (order.attributes && order.attributes.length > 0) {
                hasQuiz = order.attributes.some(
                  (attr) =>
                    (attr.name === '_quiz_source' && attr.value === 'Gift-quiz') ||
                    (attr.name === '_utm_source' && attr.value === 'gift-quiz')
                );
              }

              // Check if order was attributed to Rebuy Smart Cart
              const isRebuyOrder = order.isRebuyOrder === true;

              // Check if customer is an email marketing subscriber
              const isNewsletterSubscriber =
                order.customerEmailSubscribed === true ||
                (Array.isArray(order.customerTags) &&
                  order.customerTags.some((t) => t.toLowerCase() === 'newsletter'));

              // Check if this is a first-time customer based on order count
              const isNewCustomer = parseInt(order.customerOrderCount, 10) === 1;

              // Format order count with styling
              const orderCountDisplay = order.customerOrderCount
                ? parseInt(order.customerOrderCount, 10) === 1
                  ? ` <span style="color: #ff1493; font-weight: 700;">(${order.customerOrderCount})</span>`
                  : ` (${order.customerOrderCount})`
                : '';

              // Format timing information (for repeat customers)
              const timingDisplay = this.core.formatOrderTiming(
                order.customerOrderCount,
                order.firstOrderDate,
                order.lastOrderDate
              );

              // Check if this is a retail/POS order
              const isRetailOrder = order.isRetailOrder === true;

              // Traffic-source pill via the shared resolver (same 3-tier fallback as the popup).
              const trafficSourceBadge = AdminDashboardCore.trafficSourceRowBadge(order);

              safeSetHTML(
                item,
                `
                  <div class="activity-avatar">🛍️</div>
                  <div class="activity-details">
                    <h4>${this.escapeHtml(order.customerName)}${orderCountDisplay}${timingDisplay}${isNewCustomer ? ' <span class="new-customer-badge">NEW</span>' : ''}${hasTip ? ' <span class="tip-badge">TIP</span>' : ''}${hasQuiz ? ' <span class="quiz-badge">QUIZ</span>' : ''}${hasFaire ? ' <span class="faire-badge">FAIRE</span>' : ''}${isRetailOrder ? ' <span class="retail-badge">RETAIL</span>' : ''}${isRebuyOrder ? ' <span class="rebuy-badge">REBUY</span>' : ''}${isNewsletterSubscriber ? ' 📩' : ''}</h4>
                    <p>#${order.orderNumber} • <span class="order-value ${valueClass}">$${order.total}</span>${trafficSourceBadge}${discountCode ? ` • <span class="discount-badge">${discountCode}</span>` : ''}${hasShipUpgrade ? ' • <span class="upgrade-badge">UPGRADE</span>' : ''}${hasPreOrder ? ' • <span class="preorder-badge">PRE-ORDER</span>' : ''}${hasSubscription && !isNewCustomer ? ' • <span class="subscription-badge">SUB</span>' : ''}${isInternational ? ' • <span class="international-badge">INTL</span>' : ''}${hasNote ? ' • <span class="note-badge">NOTE</span>' : ''}${hasFaire ? ' • <span class="faire-badge">FAIRE</span>' : ''}</p>
                  </div>
                  <div class="activity-time">${timeAgo}</div>
                `
              );

              // Add tooltip event listeners
              // Track if this is a touch device to prevent duplicate events
              this.attachOrderRowInteractions(item, order);

              activityList.appendChild(item);
            });

            document.getElementById('activity-count').textContent =
              `${nonRetailOrders.length} recent orders`;
          }
        } else {
          // Handle empty data - no orders for this period
          safeSetHTML(
            activityList,
            '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">No recent orders</p>'
          );
          document.getElementById('activity-count').textContent = '0 recent orders';
          const activityTotalEl = document.getElementById('activity-total');
          if (activityTotalEl) {
            activityTotalEl.textContent = '$0';
          }
        }
      } catch (error) {
        AdminDashboardCore.logError('[Admin] loadRecentActivity error:', error);
        if (!this.state.lastActivityHash) {
          safeSetHTML(
            activityList,
            '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">Deploy proxy to see real activity</p>'
          );
          document.getElementById('activity-count').textContent = '0 events';
          const activityTotalEl = document.getElementById('activity-total');
          if (activityTotalEl) {
            activityTotalEl.textContent = '$0';
          }
        }
      }
    },

    // Load top products
    loadTopProducts: async function (period = '7d') {
      const container = document.getElementById('top-products');

      // Show loading state
      if (container) {
        safeSetHTML(
          container,
          `
          <div class="loading-container">
            <div class="loading-spinner large"></div>
            <div class="loading-text">Loading products...</div>
          </div>
        `
        );
      }

      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=top-products&period=${period}`
        );
        if (response.ok) {
          if (result.success && result.data.length > 0) {
            safeSetHTML(container, '');
            result.data.forEach((product, index) => {
              const item = document.createElement('div');
              item.className = 'product-item';
              const productTitle = product.title || product.name || 'Unknown Product';
              safeSetHTML(
                item,
                `
                <div class="product-rank">${index + 1}</div>
                <div class="product-info">
                  <h4>${productTitle}</h4>
                  <p>${this.formatNumber(product.quantity, 0)} sales</p>
                </div>
                <div class="product-revenue">$${this.formatNumber(product.revenue)}</div>
              `
              );
              container.appendChild(item);
            });
          } else {
            safeSetHTML(
              container,
              '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">No product data available</p>'
            );
          }
        } else {
          // Handle HTTP errors (400, 500, etc.)
          await response.text();
          safeSetHTML(
            container,
            `<p style="color: rgba(239, 68, 68, 0.8); padding: 20px; text-align: center;">API Error ${response.status}: ${response.statusText}</p>`
          );
        }
      } catch (_error) {
        safeSetHTML(
          container,
          '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">Deploy proxy to see products</p>'
        );
      }
    },

    // Load inventory alerts
    loadInventoryAlerts: async function () {
      const container = document.getElementById('inventory-alerts');

      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=inventory-alerts`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data.length > 0) {
            safeSetHTML(container, '');
            result.data.forEach((alert) => {
              const item = document.createElement('div');
              item.className = 'alert-item';
              safeSetHTML(
                item,
                `
                <div class="alert-product">${alert.productTitle}${alert.variantTitle !== 'Default Title' ? ' - ' + alert.variantTitle : ''}</div>
                <div class="alert-stock">${alert.stock} left</div>
              `
              );
              container.appendChild(item);
            });
          } else {
            safeSetHTML(
              container,
              '<p style="color: rgba(255,255,255,0.6); padding: 10px; text-align: center; font-size: 12px;">No low stock alerts</p>'
            );
          }
        }
      } catch (_error) {
        safeSetHTML(
          container,
          '<p style="color: rgba(255,255,255,0.6); padding: 10px; text-align: center; font-size: 12px;">Deploy proxy</p>'
        );
      }
    },

    // Load analytics data
    loadAnalyticsData: async function () {
      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=performance-metrics`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data.pageLoad) {
            const pageLoadEl = document.getElementById('page-load');
            const bounceRateEl = document.getElementById('bounce-rate');
            if (pageLoadEl) pageLoadEl.textContent = `${result.data.pageLoad} ms`;
            if (bounceRateEl) bounceRateEl.textContent = `${result.data.bounceRate}%`;
          } else {
            const bounceRateEl = document.getElementById('bounce-rate');
            const pageLoadEl = document.getElementById('page-load');
            if (bounceRateEl) bounceRateEl.textContent = 'GA Required';
            if (pageLoadEl) pageLoadEl.textContent = 'GA Required';
          }
        }
      } catch (_error) {
        const bounceRateEl = document.getElementById('bounce-rate');
        const pageLoadEl = document.getElementById('page-load');
        if (bounceRateEl) bounceRateEl.textContent = 'GA Required';
        if (pageLoadEl) pageLoadEl.textContent = 'GA Required';
      }

      // Visitor locations also require GA
      this.loadVisitorLocations();

      // Load user agent summary
      this.loadUserAgents();
    },

    // Convert country code to flag emoji
    getCountryFlag: function (countryCodeOrName) {
      if (!countryCodeOrName) return '🌍';

      // If it's a 2-letter country code, convert to flag emoji
      if (countryCodeOrName.length === 2) {
        const codePoints = countryCodeOrName
          .toUpperCase()
          .split('')
          .map((char) => 127397 + char.charCodeAt(0));
        return String.fromCodePoint(...codePoints);
      }

      // For longer strings, check if it's a known country name
      const countryToFlag = {
        'United States': '🇺🇸',
        Canada: '🇨🇦',
        'United Kingdom': '🇬🇧',
        Australia: '🇦🇺',
        Germany: '🇩🇪',
        France: '🇫🇷',
        Spain: '🇪🇸',
        Italy: '🇮🇹',
        Japan: '🇯🇵',
        China: '🇨🇳',
        India: '🇮🇳',
        Brazil: '🇧🇷',
        Mexico: '🇲🇽',
        Netherlands: '🇳🇱',
        Sweden: '🇸🇪',
        Norway: '🇳🇴',
        Denmark: '🇩🇰',
        Finland: '🇫🇮',
        Poland: '🇵🇱',
        Russia: '🇷🇺',
        'South Korea': '🇰🇷',
        Singapore: '🇸🇬',
        'Hong Kong': '🇭🇰',
        Taiwan: '🇹🇼',
        Thailand: '🇹🇭',
        Vietnam: '🇻🇳',
        Indonesia: '🇮🇩',
        Philippines: '🇵🇭',
        Malaysia: '🇲🇾',
        'New Zealand': '🇳🇿',
        Ireland: '🇮🇪',
        Belgium: '🇧🇪',
        Switzerland: '🇨🇭',
        Austria: '🇦🇹',
        Portugal: '🇵🇹',
        Greece: '🇬🇷',
        Turkey: '🇹🇷',
        Israel: '🇮🇱',
        'Saudi Arabia': '🇸🇦',
        UAE: '🇦🇪',
        'United Arab Emirates': '🇦🇪',
        'South Africa': '🇿🇦',
        Argentina: '🇦🇷',
        Chile: '🇨🇱',
        Colombia: '🇨🇴',
      };

      return countryToFlag[countryCodeOrName] || '🌍';
    },

    // Load visitor locations from Google Analytics
    loadVisitorLocations: async function () {
      const container = document.getElementById('location-list');
      try {
        // Add timeout to prevent infinite loading
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=visitor-locations`, {
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (response.ok) {
          const result = await response.json();

          if (result.success && result.data && result.data.length > 0) {
            const html = result.data
              .map((location) => {
                // Get flag: use provided flag from backend (preferred), or fallback to conversion
                const flag = location.flag || '🌍';

                // Count total bots for this location
                const botCount = location.bots
                  ? Object.values(location.bots).reduce((sum, count) => sum + count, 0)
                  : 0;

                const escLoc = (v) => this.escapeHtml(v);
                const cityText = location.city ? `, ${escLoc(location.city)}` : '';
                const safeCountry = escLoc(location.country);

                // Tooltip payload travels as JSON, never as markup. getAttribute
                // decodes HTML entities, so markup packed into an attribute is
                // re-parsed as live HTML on read and any escaping applied here
                // would be inert. The reader builds DOM nodes from this data.
                const tooltipData = { userAgents: [], bots: [] };
                if (location.userAgents && Object.keys(location.userAgents).length > 0) {
                  Object.entries(location.userAgents)
                    .sort((a, b) => b[1] - a[1])
                    .forEach(([ua, count]) => {
                      const uaLower = String(ua).toLowerCase();
                      let deviceEmoji = '💻';
                      if (
                        uaLower.includes('ipad') ||
                        uaLower.includes('tablet') ||
                        (uaLower.includes('android') && !uaLower.includes('mobile'))
                      ) {
                        deviceEmoji = '📋';
                      } else if (
                        uaLower.includes('mobile') ||
                        uaLower.includes('iphone') ||
                        uaLower.includes('android')
                      ) {
                        deviceEmoji = '📱';
                      }
                      tooltipData.userAgents.push({
                        emoji: deviceEmoji,
                        label: String(ua),
                        count: parseInt(count, 10) || 0,
                      });
                    });

                  if (location.bots && Object.keys(location.bots).length > 0) {
                    Object.entries(location.bots).forEach(([bot, count]) => {
                      tooltipData.bots.push({
                        emoji: '🤖',
                        label: String(bot),
                        count: parseInt(count, 10) || 0,
                      });
                    });
                  }
                }

                const tooltipAttr = tooltipData.userAgents.length
                  ? ` data-tooltip-data="${escLoc(JSON.stringify(tooltipData))}"`
                  : '';

                return `
              <div class="location-item"${tooltipAttr}>
                <span class="location-flag">${flag}</span>
                <div class="location-info">
                  <strong>${safeCountry}${cityText}</strong>
                </div>
                <div class="location-stats">
                  <span class="location-visitors">${parseInt(location.visitors, 10) || 0} visitors</span>
                  ${botCount > 0 ? `<span class="location-bots">🤖 ${parseInt(botCount, 10)} bots</span>` : ''}
                </div>
              </div>
            `;
              })
              .join('');
            safeSetHTML(container, html);

            // Add hover event listeners for tooltips
            this.initLocationTooltips();
          } else {
            safeSetHTML(
              container,
              '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center; font-size: 12px;">No location data available</p>'
            );
          }
        } else {
          safeSetHTML(
            container,
            '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center; font-size: 12px;">Unable to load location data</p>'
          );
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          safeSetHTML(
            container,
            '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center; font-size: 12px;">⏱️ Request timed out. Check API connection.</p>'
          );
        } else {
          safeSetHTML(
            container,
            '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center; font-size: 12px;">Unable to load location data</p>'
          );
        }
      }
    },

    // Initialize location tooltips
    initLocationTooltips: function () {
      const locationItems = document.querySelectorAll('.location-item[data-tooltip-data]');
      let tooltip = null;

      locationItems.forEach((item) => {
        // Remove default tooltip to prevent interference
        item.removeAttribute('data-tooltip');

        item.addEventListener('mouseenter', (e) => {
          e.stopPropagation(); // Prevent parent tooltip from showing

          const payload = item.getAttribute('data-tooltip-data');
          if (!payload) return;
          let tooltipData;
          try {
            tooltipData = JSON.parse(payload);
          } catch (_err) {
            return;
          }

          // Remove any existing tooltips first
          document.querySelectorAll('.location-tooltip').forEach((t) => t.remove());

          // Build the tooltip as DOM nodes. Nothing is ever parsed as HTML.
          tooltip = document.createElement('div');
          tooltip.className = 'location-tooltip';
          tooltip.appendChild(window.AdminDashboardCore.buildLocationTooltip(tooltipData));
          document.body.appendChild(tooltip);

          // Position tooltip
          const rect = item.getBoundingClientRect();
          tooltip.style.position = 'fixed';
          tooltip.style.left = `${rect.right + 10}px`;
          tooltip.style.top = `${rect.top}px`;

          // Adjust if tooltip goes off screen
          setTimeout(() => {
            const tooltipRect = tooltip.getBoundingClientRect();
            if (tooltipRect.right > window.innerWidth) {
              tooltip.style.left = `${rect.left - tooltipRect.width - 10}px`;
            }
            if (tooltipRect.bottom > window.innerHeight) {
              tooltip.style.top = `${window.innerHeight - tooltipRect.height - 10}px`;
            }
          }, 0);
        });

        item.addEventListener('mouseleave', () => {
          if (tooltip) {
            tooltip.remove();
            tooltip = null;
          }
        });
      });
    },

    // Load user agent summary
    loadUserAgents: async function () {
      const container = document.getElementById('user-agent-list');
      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=user-agent-summary`);

        if (response.ok) {
          const result = await response.json();

          if (result.success && result.data) {
            const userAgents = result.data.userAgents || [];
            const totalBots = result.data.totalBots || 0;
            const totalHumans = result.data.totalHumans || 0;

            if (userAgents.length === 0) {
              safeSetHTML(
                container,
                '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center; font-size: 12px;">No user agent data available yet. Check back after GA4 has collected data.</p>'
              );
              return;
            }

            // Create HTML for user agents (show top 10)
            const topAgents = userAgents.slice(0, 10);
            let html = '<div class="user-agent-summary">';

            // Summary stats
            html += `
            <div class="ua-summary">
              <div class="ua-stat">
                <span class="ua-icon">👤</span>
                <div>
                  <strong>${this.formatNumber(totalHumans, 0)}</strong>
                  <small>Human</small>
                </div>
              </div>
              <div class="ua-stat">
                <span class="ua-icon">🤖</span>
                <div>
                  <strong>${this.formatNumber(totalBots, 0)}</strong>
                  <small>Bots</small>
                </div>
              </div>
            </div>
          `;

            html += '<div class="ua-items">';
            topAgents.forEach((ua) => {
              const icon = ua.isBot ? '🤖' : '💻';
              const botClass = ua.isBot ? 'is-bot' : '';
              html += `
              <div class="ua-item ${botClass}">
                <span class="ua-item-icon">${icon}</span>
                <div class="ua-item-info">
                  <span class="ua-name">${ua.name}</span>
                </div>
                <div class="ua-item-count">${this.formatNumber(ua.count, 0)}</div>
              </div>
            `;
            });
            html += '</div></div>';

            safeSetHTML(container, html);
          } else {
            safeSetHTML(
              container,
              '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center; font-size: 12px;">No user agent data available</p>'
            );
          }
        } else {
          safeSetHTML(
            container,
            '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center; font-size: 12px;">Unable to load user agent data</p>'
          );
        }
      } catch (_error) {
        safeSetHTML(
          container,
          '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center; font-size: 12px;">Unable to load user agent data</p>'
        );
      }
    },

    // Load advanced metrics (all new visualizations)
    loadAdvancedMetrics: async function () {
      // Get current dropdown selections
      const discountPeriod = document.getElementById('discount-codes-period')?.value || '30d';
      const abandonedCartPeriod = document.getElementById('abandoned-cart-period')?.value || '7d';
      const devicePeriod = document.getElementById('device-performance-period')?.value || '7d';
      const searchPeriod = document.getElementById('search-performance-period')?.value || '7d';
      const topProductsPeriod = document.getElementById('top-products-period')?.value || '7d';
      const conversionFunnelPeriod =
        document.getElementById('conversion-funnel-period')?.value || '7d';

      // Load all new endpoints in parallel with current dropdown selections
      await Promise.all([
        this.loadConversionFunnel(conversionFunnelPeriod),
        this.loadRevenueVelocity(),
        this.loadDiscountPerformance(discountPeriod),
        this.loadRevenuePerVisitor(),
        this.loadItemsPerOrder(),
        this.loadItemsPerOrderNoSubs(),
        this.loadAddToCartRate(),
        this.loadCheckoutAbandonment(),
        this.loadCustomerSplit(),
        this.loadFulfillmentMetrics(),
        this.loadRefundMetrics(),
        this.loadProductTrends(),
        this.loadInventoryVelocity(),
        this.loadAbandonedCartProducts(abandonedCartPeriod),
        this.loadDevicePerformance(devicePeriod),
        this.loadSearchMetrics(searchPeriod),
        this.loadTopProducts(topProductsPeriod),
      ]);
    },

    // Conversion Funnel
    loadConversionFunnel: async function (period = '7d') {
      const container = document.getElementById('funnel-split-bar');
      if (!container) {
        return;
      }

      // Show loading state
      safeSetHTML(
        container,
        `
        <div class="loading-container">
          <div class="loading-spinner"></div>
          <div class="loading-text">Loading conversion funnel...</div>
        </div>
      `
      );

      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=conversion-funnel&period=${period}`
        );
        if (response.ok) {
          if (result.success && result.data) {
            const visitors = parseInt(result.data.visitors, 10) || 0;
            const addToCart = parseInt(result.data.addToCart, 10) || 0;
            const checkout = parseInt(result.data.checkout, 10) || 0;
            const purchases = parseInt(result.data.purchases, 10) || 0;

            // Calculate percentages
            const _cartRate = visitors > 0 && addToCart >= 0 ? (addToCart / visitors) * 100 : 0;
            const _checkoutRate = visitors > 0 && checkout >= 0 ? (checkout / visitors) * 100 : 0;
            const purchaseRate = visitors > 0 && purchases >= 0 ? (purchases / visitors) * 100 : 0;

            // Build stages array - Split bar style where segments add up to 100%
            const stages = [];
            const totalVisitors = visitors || 1;

            // Calculate non-converting visitors (those who didn't purchase)
            const nonConvertingVisitors = visitors - purchases;
            const nonConvertingPercentage = (nonConvertingVisitors / totalVisitors) * 100;

            // Always include non-converting visitors (those who didn't complete purchase)
            if (nonConvertingVisitors > 0) {
              stages.push({
                label: 'Visitors',
                icon: '👥',
                count: nonConvertingVisitors,
                percentage: nonConvertingPercentage,
                color: '#3b82f6',
              });
            }

            // Add purchases segment if > 0
            if (purchaseRate > 0) {
              stages.push({
                label: 'Purchase',
                icon: '✅',
                count: purchases,
                percentage: purchaseRate,
                color: '#10b981',
              });
            }

            // Build Customer Split-style visualization
            if (stages.length === 0) {
              safeSetHTML(
                container,
                '<p style="color: rgba(255,255,255,0.6); padding: 40px; text-align: center;">No conversion data available</p>'
              );
              return;
            }

            safeSetHTML(
              container,
              stages
                .map(
                  (stage) => `
              <div class="funnel-segment" style="flex: ${stage.percentage}; background: ${stage.color};">
                <span class="funnel-segment-icon">${stage.icon}</span>
                <span class="funnel-segment-label">${stage.label}</span>
                <span class="funnel-segment-value">${this.formatNumber(stage.count, 0)} (${stage.percentage.toFixed(1)}%)</span>
              </div>
            `
                )
                .join('')
            );
          } else {
            safeSetHTML(
              container,
              '<p style="color: rgba(255,255,255,0.6); padding: 40px; text-align: center;">Unable to load conversion funnel</p>'
            );
          }
        } else {
          safeSetHTML(
            container,
            '<p style="color: rgba(255,255,255,0.6); padding: 40px; text-align: center;">Unable to load conversion funnel</p>'
          );
        }
      } catch (_error) {
        safeSetHTML(
          container,
          '<p style="color: rgba(255,255,255,0.6); padding: 40px; text-align: center;">Failed to load conversion funnel</p>'
        );
      }
    },

    // Revenue Velocity
    loadRevenueVelocity: async function () {
      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=revenue-velocity`
        );
        if (response.ok) {
          if (result.success) {
            const { percentChange, direction } = result.data;
            const percent = parseFloat(percentChange);

            // Set direction emoji
            let emoji = '📊';
            let className = 'velocity-stable';
            if (direction === 'up') {
              emoji = '📈';
              className = 'velocity-up';
            } else if (direction === 'down') {
              emoji = '📉';
              className = 'velocity-down';
            }

            const label =
              direction === 'up'
                ? 'Revenue accelerating (vs last 7 days)'
                : direction === 'down'
                  ? 'Revenue slowing (vs last 7 days)'
                  : 'Revenue steady (vs last 7 days)';
            const percentText = `${Math.abs(percent).toFixed(1)}%`;
            // Batch all DOM writes into a single rAF to avoid multiple reflows
            this.core.scheduleDomWrite(() => {
              const dirEl = document.getElementById('revenue-velocity-direction');
              if (dirEl) {
                dirEl.textContent = emoji;
                dirEl.className = className;
              }
              const pctEl = document.getElementById('revenue-velocity-percent');
              if (pctEl) pctEl.textContent = percentText;
              const lblEl = document.getElementById('revenue-velocity-label');
              if (lblEl) lblEl.textContent = label;
            });
          }
        }
      } catch (error) {
        _err('[Admin] loadRevenueVelocity error:', error);
        _showMetricError('revenue-velocity-percent', '--');
        _showMetricError('revenue-velocity-label', 'Unable to load');
      }
    },

    // Discount Performance
    loadDiscountPerformance: async function (period = '30d') {
      const list = document.getElementById('discount-list');
      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=discount-performance&period=${period}`
        );
        if (response.ok) {
          if (result.success && result.data.discounts && result.data.discounts.length > 0) {
            const html = result.data.discounts
              .map(
                (discount) => `
              <div class="discount-item">
                <div class="discount-code">
                  <span class="discount-code-name">${discount.code}</span>
                  <span class="discount-meta">${discount.uses} uses</span>
                </div>
                <div class="discount-stats">
                  <div class="discount-revenue">$${this.formatNumber(discount.revenue)}</div>
                  <div class="discount-uses">${discount.type}</div>
                </div>
              </div>
            `
              )
              .join('');
            safeSetHTML(list, html);
          } else {
            safeSetHTML(
              list,
              '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center; font-size: 12px;">No discount codes used recently</p>'
            );
          }
        }
      } catch (_error) {
        safeSetHTML(
          list,
          '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center; font-size: 12px;">Unable to load discount data</p>'
        );
      }
    },

    // Revenue Per Visitor
    loadRevenuePerVisitor: async function () {
      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=revenue-per-visitor`
        );
        if (response.ok) {
          if (result.success) {
            const rpvText = `$${this.formatNumber(result.data.rpv)}`;
            const visitorsText = `${this.formatNumber(result.data.visitors, 0)} visitors`;
            // Batch both DOM writes into a single rAF to avoid two separate reflows
            this.core.scheduleDomWrite(() => {
              const rpvEl = document.getElementById('rpv-value');
              if (rpvEl) rpvEl.textContent = rpvText;
              const visEl = document.getElementById('rpv-visitors');
              if (visEl) visEl.textContent = visitorsText;
            });
          }
        } else {
          _err('[Admin] loadRevenuePerVisitor HTTP', response.status);
          _showMetricError('rpv-value', '--');
          _showMetricError('rpv-visitors', 'API error');
        }
      } catch (error) {
        _err('[Admin] loadRevenuePerVisitor error:', error);
        _showMetricError('rpv-value', '--');
        _showMetricError('rpv-visitors', 'Unable to load');
      }
    },

    // Items Per Order
    loadItemsPerOrder: async function () {
      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=items-per-order`
        );
        if (response.ok) {
          if (result.success) {
            const avgText = result.data.average.toFixed(1);
            const totalText = `${this.formatNumber(result.data.totalItems, 0)} items total`;
            this.core.scheduleDomWrite(() => {
              const avgEl = document.getElementById('items-per-order-value');
              if (avgEl) avgEl.textContent = avgText;
              const totEl = document.getElementById('items-per-order-total');
              if (totEl) totEl.textContent = totalText;
            });
          }
        } else {
          _err('[Admin] loadItemsPerOrder HTTP', response.status);
          _showMetricError('items-per-order-value', '--');
        }
      } catch (error) {
        _err('[Admin] loadItemsPerOrder error:', error);
        _showMetricError('items-per-order-value', '--');
      }
    },

    // Items Per Order (excluding subscriptions)
    loadItemsPerOrderNoSubs: async function () {
      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=items-per-order-no-subs`
        );
        if (response.ok) {
          if (result.success) {
            const avgNoSubsText = result.data.average.toFixed(1);
            const totalNoSubsText = `${this.formatNumber(result.data.totalItems, 0)} items total`;
            this.core.scheduleDomWrite(() => {
              const avgEl = document.getElementById('items-per-order-no-subs-value');
              if (avgEl) avgEl.textContent = avgNoSubsText;
              const totEl = document.getElementById('items-per-order-no-subs-total');
              if (totEl) totEl.textContent = totalNoSubsText;
            });
          }
        } else {
          _err('[Admin] loadItemsPerOrderNoSubs HTTP', response.status);
          _showMetricError('items-per-order-no-subs-value', '--');
        }
      } catch (error) {
        _err('[Admin] loadItemsPerOrderNoSubs error:', error);
        _showMetricError('items-per-order-no-subs-value', '--');
      }
    },

    // Add to Cart Rate
    loadAddToCartRate: async function () {
      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=add-to-cart-rate`
        );
        if (response.ok) {
          if (result.success) {
            const cartRateText = `${result.data.rate}%`;
            const cartMetaText = `${this.formatNumber(result.data.cartAdditions, 0)} of ${this.formatNumber(result.data.productViews, 0)} views`;
            this.core.scheduleDomWrite(() => {
              const rateEl = document.getElementById('cart-rate-value');
              if (rateEl) rateEl.textContent = cartRateText;
              const metaEl = document.getElementById('cart-rate-meta');
              if (metaEl) metaEl.textContent = cartMetaText;
            });
          }
        } else {
          _err('[Admin] loadAddToCartRate HTTP', response.status);
          _showMetricError('cart-rate-value', '--');
        }
      } catch (error) {
        _err('[Admin] loadAddToCartRate error:', error);
        _showMetricError('cart-rate-value', '--');
      }
    },

    // Checkout Abandonment
    loadCheckoutAbandonment: async function () {
      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=checkout-abandonment`
        );
        if (response.ok) {
          if (result.success) {
            const dropValText = `$${this.formatNumber(result.data.potentialRevenue)}`;
            const dropMetaText = `${result.data.rate}% drop rate`;
            this.core.scheduleDomWrite(() => {
              const valEl = document.getElementById('checkout-drop-value');
              if (valEl) valEl.textContent = dropValText;
              const metaEl = document.getElementById('checkout-drop-meta');
              if (metaEl) metaEl.textContent = dropMetaText;
            });
          }
        } else {
          _err('[Admin] loadCheckoutAbandonment HTTP', response.status);
          _showMetricError('checkout-drop-value', '--');
        }
      } catch (error) {
        _err('[Admin] loadCheckoutAbandonment error:', error);
        _showMetricError('checkout-drop-value', '--');
      }
    },

    // Customer Split
    loadCustomerSplit: async function () {
      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=customer-split`
        );
        if (response.ok) {
          if (result.success) {
            const { firstTime, total } = result.data;
            let { returning } = result.data;

            // Calculate returning if not provided
            if (typeof returning === 'undefined' || returning === null) {
              returning = Math.max(0, (total || 0) - (firstTime || 0));
            }

            const firstTimePercent = total > 0 ? Math.round((firstTime / total) * 100) : 50;
            const returningPercent = total > 0 ? Math.round((returning / total) * 100) : 50;

            // Batch all 4 DOM writes (2 style + 2 text) into a single rAF to avoid multiple reflows
            const ftPct = `${firstTimePercent}%`;
            const retPct = `${returningPercent}%`;
            this.core.scheduleDomWrite(() => {
              const ftSeg = document.getElementById('first-time-segment');
              if (ftSeg) ftSeg.style.width = ftPct;
              const retSeg = document.getElementById('returning-segment');
              if (retSeg) retSeg.style.width = retPct;
              const ftVal = document.getElementById('first-time-value');
              if (ftVal) ftVal.textContent = ftPct;
              const retVal = document.getElementById('returning-value');
              if (retVal) retVal.textContent = retPct;
            });
          }
        } else {
          _err('[Admin] loadCustomerSplit HTTP', response.status);
          _showMetricError('first-time-value', '--');
          _showMetricError('returning-value', '--');
        }
      } catch (error) {
        _err('[Admin] loadCustomerSplit error:', error);
        _showMetricError('first-time-value', '--');
        _showMetricError('returning-value', '--');
      }
    },

    // Load fulfillment metrics
    loadFulfillmentMetrics: async function () {
      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=fulfillment-metrics`
        );
        if (response.ok) {
          if (result.success) {
            const unfulfilledCountEl = document.getElementById('unfulfilled-count');
            unfulfilledCountEl.textContent = result.data.unfulfilled;

            // Add tooltip to unfulfilled count showing oldest order
            if (result.data.oldestUnfulfilled) {
              unfulfilledCountEl.style.cursor = 'pointer';
              unfulfilledCountEl.addEventListener('mouseenter', (e) => {
                this.showUnfulfilledTooltip(e, result.data.oldestUnfulfilled);
              });
              unfulfilledCountEl.addEventListener('mouseleave', () => {
                this.hideUnfulfilledTooltip();
              });
            }

            document.getElementById('avg-fulfillment-time').textContent =
              `${result.data.avgFulfillmentDays} days`;
            document.getElementById('partially-fulfilled-count').textContent =
              result.data.partiallyFulfilled;
          }
        } else {
          _err('[Admin] loadFulfillmentMetrics HTTP', response.status);
          _showMetricError('unfulfilled-count', '--');
          _showMetricError('avg-fulfillment-time', '--');
          _showMetricError('partially-fulfilled-count', '--');
        }
      } catch (error) {
        _err('[Admin] loadFulfillmentMetrics error:', error);
        _showMetricError('unfulfilled-count', '--');
        _showMetricError('avg-fulfillment-time', '--');
        _showMetricError('partially-fulfilled-count', '--');
      }
    },

    // Load refund metrics
    loadRefundMetrics: async function () {
      const container = document.getElementById('refund-products-list');
      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=refund-metrics`
        );
        if (response.ok) {
          if (result.success) {
            const refundRateText = `${result.data.refundRate}%`;
            const refundAmountText = `$${this.formatNumber(result.data.totalRefundAmount)}`;
            this.core.scheduleDomWrite(() => {
              const rrEl = document.getElementById('refund-rate-value');
              if (rrEl) rrEl.textContent = refundRateText;
              const raEl = document.getElementById('total-refund-amount');
              if (raEl) raEl.textContent = refundAmountText;
            });

            if (result.data.mostReturnedProducts.length > 0) {
              safeSetHTML(container, '');
              result.data.mostReturnedProducts.forEach((product) => {
                const item = document.createElement('div');
                item.className = 'refund-product-item';
                safeSetHTML(
                  item,
                  `
                  <div class="refund-product-info">
                    <span class="refund-product-name">${product.title}</span>
                    <span class="refund-count">${product.refundCount} refunds</span>
                  </div>
                  <div class="refund-amount">$${product.refundAmount}</div>
                `
                );
                container.appendChild(item);
              });
            } else {
              safeSetHTML(
                container,
                '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">No refunds in this period</p>'
              );
            }
          }
        }
      } catch (_error) {
        safeSetHTML(
          container,
          '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">Unable to load refund data</p>'
        );
      }
    },

    // Load product trends
    loadProductTrends: async function () {
      const trendingContainer = document.getElementById('trending-products-list');
      const decliningContainer = document.getElementById('declining-products-list');

      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=product-trends`
        );
        if (response.ok) {
          if (result.success) {
            // Trending products
            if (result.data.trendingProducts.length > 0) {
              safeSetHTML(trendingContainer, '');
              result.data.trendingProducts.forEach((product) => {
                const item = document.createElement('div');
                item.className = 'trend-item';
                safeSetHTML(
                  item,
                  `
                  <div class="trend-product-info">
                    <span class="trend-product-name">${product.title}</span>
                    <span class="trend-change positive">+${product.quantityChange.toFixed(1)}%</span>
                  </div>
                  <div class="trend-quantity">${product.currentQuantity} units</div>
                `
                );
                trendingContainer.appendChild(item);
              });
            } else {
              safeSetHTML(
                trendingContainer,
                '<p style="color: rgba(255,255,255,0.6); padding: 10px; text-align: center; font-size: 12px;">No trending products</p>'
              );
            }

            // Declining products
            if (result.data.decliningProducts.length > 0) {
              safeSetHTML(decliningContainer, '');
              result.data.decliningProducts.forEach((product) => {
                const item = document.createElement('div');
                item.className = 'trend-item';
                safeSetHTML(
                  item,
                  `
                  <div class="trend-product-info">
                    <span class="trend-product-name">${product.title}</span>
                    <span class="trend-change negative">${product.quantityChange.toFixed(1)}%</span>
                  </div>
                  <div class="trend-quantity">${product.currentQuantity} units</div>
                `
                );
                decliningContainer.appendChild(item);
              });
            } else {
              safeSetHTML(
                decliningContainer,
                '<p style="color: rgba(255,255,255,0.6); padding: 10px; text-align: center; font-size: 12px;">No declining products</p>'
              );
            }
          }
        }
      } catch (_error) {
        safeSetHTML(
          trendingContainer,
          '<p style="color: rgba(255,255,255,0.6); padding: 10px; text-align: center; font-size: 12px;">Unable to load trends</p>'
        );
        safeSetHTML(
          decliningContainer,
          '<p style="color: rgba(255,255,255,0.6); padding: 10px; text-align: center; font-size: 12px;">Unable to load trends</p>'
        );
      }
    },

    // Load inventory velocity
    loadInventoryVelocity: async function () {
      const deadStockContainer = document.getElementById('dead-stock-list');
      const fastMovingContainer = document.getElementById('fast-moving-list');

      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=inventory-velocity`
        );
        if (response.ok) {
          if (result.success) {
            // Dead stock (slow moving)
            if (result.data.deadStock.length > 0) {
              safeSetHTML(deadStockContainer, '');
              result.data.deadStock.slice(0, 5).forEach((item) => {
                const div = document.createElement('div');
                div.className = 'velocity-item';
                const daysDisplay =
                  item.daysRemaining >= 999 ? '999+' : item.daysRemaining.toFixed(0);
                safeSetHTML(
                  div,
                  `
                  <div class="velocity-product-info">
                    <span class="velocity-product-name">${item.title}</span>
                    <span class="velocity-days">${daysDisplay} days</span>
                  </div>
                  <div class="velocity-stock">${item.currentStock} in stock</div>
                `
                );
                deadStockContainer.appendChild(div);
              });
            } else {
              safeSetHTML(
                deadStockContainer,
                '<p style="color: rgba(255,255,255,0.6); padding: 10px; text-align: center; font-size: 12px;">No slow-moving items</p>'
              );
            }

            // Fast moving
            if (result.data.fastMoving.length > 0) {
              safeSetHTML(fastMovingContainer, '');
              result.data.fastMoving.slice(0, 5).forEach((item) => {
                const div = document.createElement('div');
                div.className = 'velocity-item';
                const daysDisplay =
                  item.daysRemaining >= 999 ? '999+' : item.daysRemaining.toFixed(0);
                safeSetHTML(
                  div,
                  `
                  <div class="velocity-product-info">
                    <span class="velocity-product-name">${item.title}</span>
                    <span class="velocity-days">${daysDisplay} days</span>
                  </div>
                  <div class="velocity-stock">${item.currentStock} left</div>
                `
                );
                fastMovingContainer.appendChild(div);
              });
            } else {
              safeSetHTML(
                fastMovingContainer,
                '<p style="color: rgba(255,255,255,0.6); padding: 10px; text-align: center; font-size: 12px;">No fast-moving items</p>'
              );
            }
          }
        }
      } catch (_error) {
        safeSetHTML(
          deadStockContainer,
          '<p style="color: rgba(255,255,255,0.6); padding: 10px; text-align: center; font-size: 12px;">Unable to load data</p>'
        );
        safeSetHTML(
          fastMovingContainer,
          '<p style="color: rgba(255,255,255,0.6); padding: 10px; text-align: center; font-size: 12px;">Unable to load data</p>'
        );
      }
    },

    // Load abandoned cart products
    loadAbandonedCartProducts: async function (period = '7d') {
      const container = document.getElementById('abandoned-products-list');
      const avgCartEl = document.getElementById('avg-abandoned-cart-value');
      const totalCartsEl = document.getElementById('total-abandoned-carts');

      // Show loading state
      if (container) {
        safeSetHTML(
          container,
          `
          <div class="loading-container">
            <div class="loading-spinner"></div>
            <div class="loading-text">Loading abandoned cart data...</div>
          </div>
        `
        );
      }

      if (avgCartEl) avgCartEl.textContent = '--';
      if (totalCartsEl) totalCartsEl.textContent = '--';

      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=abandoned-cart-products&period=${period}`
        );
        if (response.ok) {
          if (result.success) {
            const avgCartEl = document.getElementById('avg-abandoned-cart-value');
            const totalCartsEl = document.getElementById('total-abandoned-carts');
            if (avgCartEl) {
              avgCartEl.textContent = '$' + this.formatNumber(result.data.avgCartValue);
            }
            if (totalCartsEl) {
              totalCartsEl.textContent = this.formatNumber(result.data.totalAbandonedCarts, 0);
            }

            if (result.data.mostAbandonedProducts.length > 0) {
              safeSetHTML(container, '');
              result.data.mostAbandonedProducts.slice(0, 5).forEach((product) => {
                const item = document.createElement('div');
                item.className = 'abandoned-product-item';
                safeSetHTML(
                  item,
                  `
                  <div class="abandoned-product-info">
                    <span class="abandoned-product-name">${product.title}</span>
                    <span class="abandoned-count">${product.abandonmentCount.toLocaleString()} times</span>
                  </div>
                  <div class="abandoned-revenue">$${this.formatNumber(product.potentialRevenue)}</div>
                `
                );
                container.appendChild(item);
              });
            } else {
              safeSetHTML(
                container,
                '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">No abandoned cart data</p>'
              );
            }
          }
        }
      } catch (_error) {
        safeSetHTML(
          container,
          '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">Unable to load data</p>'
        );
      }
    },

    // Load device performance
    loadDevicePerformance: async function (period = '7d') {
      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=device-performance&period=${period}`
        );
        if (response.ok) {
          if (result.success) {
            // Pre-compute all values before touching the DOM
            const mobileOrders = result.data.mobile.orders;
            const mobileRevenue = `$${this.formatNumber(result.data.mobile.revenue)}`;
            const mobileAov = `$${result.data.mobile.aov}`;
            const desktopOrders = result.data.desktop.orders;
            const desktopRevenue = `$${this.formatNumber(result.data.desktop.revenue)}`;
            const desktopAov = `$${result.data.desktop.aov}`;
            // Batch all 6 DOM writes into a single rAF to avoid 6 separate reflows
            this.core.scheduleDomWrite(() => {
              const moEl = document.getElementById('mobile-orders');
              if (moEl) moEl.textContent = mobileOrders;
              const mrEl = document.getElementById('mobile-revenue');
              if (mrEl) mrEl.textContent = mobileRevenue;
              const maEl = document.getElementById('mobile-aov');
              if (maEl) maEl.textContent = mobileAov;
              const doEl = document.getElementById('desktop-orders');
              if (doEl) doEl.textContent = desktopOrders;
              const drEl = document.getElementById('desktop-revenue');
              if (drEl) drEl.textContent = desktopRevenue;
              const daEl = document.getElementById('desktop-aov');
              if (daEl) daEl.textContent = desktopAov;
            });
          }
        }
      } catch (_error) {}
    },

    _searchData: null,
    _gapAnalysis: null,

    renderSearchList: function (activeTab) {
      const container = document.getElementById('search-queries-list');
      if (!container || !this._searchData) return;
      const data = this._searchData;
      let searches;
      if (activeTab === 'zero') {
        searches = (data.topZeroResultSearches || [])
          .filter((s) => s.query && s.query.length >= 3)
          .slice();
      } else {
        searches = (data.topSearches || []).slice(0, 20);
      }
      searches.sort((a, b) => b.searchCount - a.searchCount);
      if (searches.length === 0) {
        const msg =
          activeTab === 'zero'
            ? 'No zero-result searches in this period.'
            : 'No search queries recorded yet.';
        safeSetHTML(
          container,
          `<p style="color:rgba(255,255,255,0.6);padding:16px;text-align:center;font-size:13px;">${msg}</p>`
        );
        return;
      }
      safeSetHTML(container, '');
      searches.forEach((search, index) => {
        const item = document.createElement('div');
        item.className = 'search-query-item actual-search';
        const zeroTag = search.hasZeroResults
          ? '<span style="font-size:10px;background:rgba(239,68,68,0.3);padding:1px 5px;border-radius:4px;margin-left:6px;">0 results</span>'
          : '';
        const cvrDisplay =
          search.conversionRate !== null && search.conversionRate !== undefined
            ? `${parseFloat(search.conversionRate).toFixed(1)}% CVR`
            : `${search.searchCount.toLocaleString()} search${search.searchCount !== 1 ? 'es' : ''}`;

        // Gap analysis badge (Zero Results tab only)
        let gapBadge = '';
        let adminUrl = null;
        if (activeTab === 'zero' && this._gapAnalysis) {
          const a = this._gapAnalysis[search.query.toLowerCase()];
          if (a) {
            adminUrl = a.adminUrl || null;
            if (a.applied) {
              gapBadge =
                '<span class="search-gap-badge" style="background:rgba(16,185,129,0.18);border:1px solid rgba(16,185,129,0.4);color:rgba(74,222,128,0.9);cursor:default;">✓ Synonym added</span>';
            } else {
              const badgeCfg = {
                gap: {
                  bg: 'rgba(239,68,68,0.18)',
                  border: 'rgba(239,68,68,0.45)',
                  color: '#fca5a5',
                  label: 'Product gap',
                },
                synonym: {
                  bg: 'rgba(245,158,11,0.18)',
                  border: 'rgba(245,158,11,0.45)',
                  color: '#fcd34d',
                  label: 'Add synonym',
                },
                visibility: {
                  bg: 'rgba(96,165,250,0.18)',
                  border: 'rgba(96,165,250,0.45)',
                  color: '#93c5fd',
                  label: 'Check visibility',
                },
                unknown: {
                  bg: 'rgba(148,163,184,0.15)',
                  border: 'rgba(148,163,184,0.3)',
                  color: '#cbd5e1',
                  label: 'Unknown',
                },
              };
              const cfg = badgeCfg[a.action] || badgeCfg.unknown;
              const hint = a.product
                ? `· ${a.product.title.slice(0, 22)}${a.product.title.length > 22 ? '…' : ''}`
                : '';
              const cursor = adminUrl ? 'pointer' : 'default';
              // escapeHtml the adminUrl before embedding in an HTML attribute to prevent attribute injection
              const safeAdminUrl = adminUrl ? this.escapeHtml(adminUrl) : '';
              gapBadge = `<span class="search-gap-badge" style="background:${cfg.bg};border:1px solid ${cfg.border};color:${cfg.color};cursor:${cursor};" data-admin-url="${safeAdminUrl}">${cfg.label}<span style="opacity:0.65;margin-left:4px;font-weight:400;">${hint}</span></span>`;
            }
          }
        }

        safeSetHTML(
          item,
          `<div class="search-query-info">
            <div class="keyword-rank-container">
              <span class="keyword-rank">#${index + 1}</span>
              <span class="search-actual-query">"${this.escapeHtml(search.query)}"</span>${zeroTag}
            </div>
          </div>
          ${gapBadge}
          <div class="search-conversion">${cvrDisplay}</div>`
        );
        container.appendChild(item);
      });

      // Wire gap badge clicks (event delegation after render)
      if (activeTab === 'zero') {
        container.querySelectorAll('.search-gap-badge[data-admin-url]').forEach(function (badge) {
          const url = badge.dataset.adminUrl;
          if (!url) return;
          badge.addEventListener('click', function (e) {
            e.stopPropagation();
            // Validate URL protocol at click time — prevent javascript: open-redirect from API data
            if (/^https?:\/\//i.test(url)) {
              window.open(url, '_blank', 'noopener,noreferrer');
            }
          });
        });
      }
    },

    loadSearchGapAnalysis: async function () {
      const btn = document.getElementById('search-gap-btn');
      if (!btn || btn.disabled) return;
      btn.disabled = true;
      const origText = btn.textContent;
      btn.textContent = 'Analyzing…';
      const period = document.getElementById('search-performance-period')?.value || '7d';
      try {
        const resp = await fetch(
          `${this.config.apiProxyUrl}?endpoint=search-gap-analysis&period=${period}`
        );
        if (!resp.ok) throw new Error('Request failed');
        const result = await resp.json();
        if (!result.success) throw new Error('API error');
        this._gapAnalysis = {};
        (result.data.items || []).forEach((item) => {
          this._gapAnalysis[item.query.toLowerCase()] = item;
        });
        const activeTab = document.querySelector('.search-tab.active')?.dataset?.tab || 'all';
        if (activeTab === 'zero') this.renderSearchList('zero');
        btn.textContent = '✓ Done';
        btn.style.cssText +=
          ';background:rgba(16,185,129,0.15);border-color:rgba(16,185,129,0.3);color:rgba(16,185,129,0.9);';

        // Show apply-synonyms button if there are actionable synonym items
        const synonymCount = Object.values(this._gapAnalysis).filter(
          (v) => v.action === 'synonym' && v.product
        ).length;
        const applyBtn = document.getElementById('search-apply-synonyms-btn');
        if (applyBtn && synonymCount > 0) {
          applyBtn.style.display = '';
          applyBtn.textContent = `↑ Apply ${synonymCount} synonym${synonymCount !== 1 ? 's' : ''}`;
          applyBtn.disabled = false;
          applyBtn.removeAttribute('data-applied');
        }
      } catch (_e) {
        btn.disabled = false;
        btn.textContent = origText;
      }
    },

    /* eslint-disable require-atomic-updates */
    applySearchSynonyms: async function () {
      const btn = document.getElementById('search-apply-synonyms-btn');
      if (!btn || btn.disabled || !this._gapAnalysis) return;

      const synonymItems = Object.values(this._gapAnalysis).filter(
        (item) => item.action === 'synonym' && item.product && !item.applied
      );
      if (synonymItems.length === 0) return;

      btn.disabled = true;
      const origText = btn.textContent;
      btn.textContent = 'Applying…';

      try {
        const resp = await fetch(`${this.config.apiProxyUrl}?endpoint=create-search-synonyms`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'omit',
          body: JSON.stringify({
            items: synonymItems.map((i) => ({ query: i.query, product: i.product })),
          }),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.error || `HTTP ${resp.status}`);
        }
        const result = await resp.json();
        if (!result.success) throw new Error('API error');

        const { created, skipped, errors } = result.data;

        // Mark applied items so badge updates to "✓ Synonym added"
        (result.data.items || []).forEach((item) => {
          const key = item.query.toLowerCase();
          if (this._gapAnalysis[key] && item.status === 'created') {
            this._gapAnalysis[key].applied = true;
          }
        });

        // Re-render to reflect updated badges
        const activeTab = document.querySelector('.search-tab.active')?.dataset?.tab || 'all';
        if (activeTab === 'zero') this.renderSearchList('zero');

        const parts = [`✓ ${created} created`];
        if (skipped > 0) parts.push(`${skipped} skipped`);
        if (errors > 0) parts.push(`${errors} errors`);
        btn.textContent = parts.join(', ');
        btn.dataset.applied = 'true';
        btn.style.cssText +=
          ';background:rgba(16,185,129,0.15);border-color:rgba(16,185,129,0.3);color:rgba(16,185,129,0.9);';
      } catch (err) {
        btn.disabled = false;
        btn.textContent = `Error: ${err.message.slice(0, 40)}`;
        btn.style.cssText +=
          ';background:rgba(239,68,68,0.12);border-color:rgba(239,68,68,0.3);color:rgba(252,165,165,0.9);';
        setTimeout(() => {
          btn.textContent = origText;
          btn.style.cssText = btn.style.cssText
            .replace(/background:[^;]+;/g, '')
            .replace(/border-color:[^;]+;/g, '')
            .replace(/color:[^;]+;/g, '');
          btn.disabled = false;
        }, 4000);
      }
    },
    /* eslint-enable require-atomic-updates */

    // Load search metrics
    loadSearchMetrics: async function (period = '7d') {
      const container = document.getElementById('search-queries-list');

      // Common stopwords to filter out (not meaningful keywords) — reserved for future use
      const _stopwords = new Set([
        'the',
        'and',
        'for',
        'are',
        'but',
        'not',
        'you',
        'all',
        'can',
        'her',
        'was',
        'one',
        'our',
        'out',
        'day',
        'get',
        'has',
        'him',
        'his',
        'how',
        'man',
        'new',
        'now',
        'old',
        'see',
        'two',
        'way',
        'who',
        'boy',
        'did',
        'its',
        'let',
        'put',
        'say',
        'she',
        'too',
        'use',
        'with',
        'from',
        'have',
        'this',
        'that',
        'what',
        'when',
        'where',
        'which',
        'will',
        'about',
        'after',
        'before',
        'other',
        'there',
        'these',
        'those',
        'their',
        'would',
        'could',
        'should',
        'search',
        'find',
        'looking',
        'want',
        'need',
        'buy',
        'shop',
        'store',
      ]);

      try {
        const { response, data: result } = await this.queuedFetch(
          `${this.config.apiProxyUrl}?endpoint=search-metrics&period=${period}`
        );
        if (response.ok) {
          if (result.success) {
            const totalEl = document.getElementById('total-searches');
            if (totalEl) totalEl.textContent = this.formatNumber(result.data.totalSearches, 0);

            const zeroEl = document.getElementById('zero-result-searches');
            if (zeroEl) {
              const zeroCount = result.data.zeroResultCount || 0;
              const total = result.data.totalSearches || 0;
              const zeroPct = total > 0 ? ((zeroCount / total) * 100).toFixed(0) : 0;
              zeroEl.textContent = `${zeroCount} (${zeroPct}%)`;
            }

            this._searchData = result.data;
            const activeTab = document.querySelector('.search-tab.active')?.dataset?.tab || 'all';
            if (result.data.message && result.data.message.includes('Configure')) {
              safeSetHTML(
                container,
                '<p style="color:rgba(255,255,255,0.6);padding:16px;text-align:center;font-size:13px;">Configure GA4 environment variables for search tracking.</p>'
              );
            } else {
              this.renderSearchList(activeTab);
            }
          } else {
            safeSetHTML(
              container,
              '<p style="color:rgba(255,255,255,0.6);padding:20px;text-align:center;">Unable to load search data</p>'
            );
          }
        } else {
          safeSetHTML(
            container,
            '<p style="color:rgba(255,255,255,0.6);padding:20px;text-align:center;">Unable to load search data</p>'
          );
        }
      } catch (_error) {
        safeSetHTML(
          container,
          '<p style="color:rgba(255,255,255,0.6);padding:20px;text-align:center;">Unable to load search data</p>'
        );
      }
    },

    // Search tab toggle
    initSearchTabs: function () {
      const self = this;
      document.querySelectorAll('.search-tab').forEach(function (btn) {
        btn.addEventListener('click', function () {
          _setButtonGroupActive('.search-tab', btn, false);
          self.renderSearchList(btn.dataset.tab);
        });
      });

      document.getElementById('search-gap-btn')?.addEventListener('click', function () {
        self.loadSearchGapAnalysis();
      });

      document.getElementById('search-apply-synonyms-btn')?.addEventListener('click', function () {
        self.applySearchSynonyms();
      });

      document.getElementById('search-export-btn')?.addEventListener('click', function () {
        if (!self._searchData) return;
        const activeTab = document.querySelector('.search-tab.active')?.dataset?.tab || 'all';
        const rows =
          activeTab === 'zero'
            ? self._searchData.topZeroResultSearches || []
            : self._searchData.topSearches || [];
        const period = document.getElementById('search-performance-period')?.value || '7d';
        const header = 'Query,Search Count,Zero Results';
        const csv = [header]
          .concat(
            rows.map(
              (r) =>
                `"${(r.query || '').replace(/"/g, '""')}",${r.searchCount},${r.hasZeroResults ? 'Yes' : 'No'}`
            )
          )
          .join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `search-${activeTab === 'zero' ? 'zero-results' : 'queries'}-${period}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      });

      document.getElementById('search-clear-btn')?.addEventListener('click', function () {
        const btn = this;
        if (btn.dataset.confirming !== 'true') {
          btn.dataset.confirming = 'true';
          btn.textContent = 'Sure?';
          btn.style.background = 'rgba(239,68,68,0.3)';
          btn.style.borderColor = 'rgba(239,68,68,0.6)';
          const reset = function () {
            btn.dataset.confirming = '';
            btn.textContent = '✕ Clear';
            btn.style.background = 'rgba(239,68,68,0.12)';
            btn.style.borderColor = 'rgba(239,68,68,0.3)';
          };
          btn._clearReset = setTimeout(reset, 3000);
          return;
        }
        clearTimeout(btn._clearReset);
        btn.dataset.confirming = '';
        btn.textContent = 'Clearing…';
        btn.disabled = true;
        btn.style.background = 'rgba(239,68,68,0.12)';
        btn.style.borderColor = 'rgba(239,68,68,0.3)';

        self._searchData = null;
        self._gapAnalysis = {};
        const container = document.getElementById('search-queries-list');
        if (container) {
          safeSetHTML(
            container,
            '<div class="loading-container"><div class="loading-spinner"></div><div class="loading-text">Clearing search data…</div></div>'
          );
        }
        const applyBtn = document.getElementById('search-apply-synonyms-btn');
        if (applyBtn) applyBtn.style.display = 'none';
        const period = document.getElementById('search-performance-period')?.value || '7d';
        fetch(
          `${self.config.apiProxyUrl}?endpoint=clear-search-data&period=${encodeURIComponent(period)}`,
          {
            method: 'POST',
          }
        )
          .then(function (res) {
            return res.ok ? res.json() : Promise.reject(res.status);
          })
          .catch(function () {
            return { success: false };
          })
          .finally(function () {
            btn.textContent = '✕ Clear';
            btn.disabled = false;
            self.loadSearchMetrics(period);
          });
      });
    },

    // Search history modal
    initSearchHistoryModal: function () {
      const btn = document.getElementById('search-history-btn');
      const modal = document.getElementById('search-history-modal');
      const closeBtn = document.getElementById('close-search-history-modal');
      const searchBtn = document.getElementById('sh-search-btn');

      if (!btn || !modal) return;

      const self = this;
      btn.addEventListener('click', function () {
        modal.style.display = 'flex';
        self.loadSearchHistory(1);
      });
      closeBtn?.addEventListener('click', function () {
        modal.style.display = 'none';
      });
      modal.addEventListener('click', function (e) {
        if (e.target === modal) modal.style.display = 'none';
      });
      searchBtn?.addEventListener('click', function () {
        self.loadSearchHistory(1);
      });
      document.getElementById('sh-query')?.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') self.loadSearchHistory(1);
      });
    },

    loadSearchHistory: async function (page) {
      const content = document.getElementById('search-history-content');
      if (!content) return;
      safeSetHTML(
        content,
        '<div class="loading-container"><div class="loading-spinner large"></div><div class="loading-text">Loading...</div></div>'
      );

      const period = document.getElementById('sh-period')?.value || '30d';
      const q = document.getElementById('sh-query')?.value || '';
      const type = document.getElementById('sh-type')?.value || '';
      const p = page || 1;

      try {
        const params = new URLSearchParams({ endpoint: 'search-history', period, page: p });
        if (q) params.set('q', q);
        if (type) params.set('type', type);
        const response = await fetch(`${this.config.apiProxyUrl}?${params}`);
        if (!response.ok) throw new Error('Request failed');
        const result = await response.json();
        if (!result.success) throw new Error('API error');

        const { rows, total, pages } = result.data;
        const self = this;

        if (rows.length === 0) {
          safeSetHTML(
            content,
            '<p style="color:rgba(255,255,255,0.6);padding:30px;text-align:center;">No searches found for the selected filters.</p>'
          );
          return;
        }

        const tableEl = document.createElement('div');

        // Summary row
        const summaryEl = document.createElement('div');
        summaryEl.style.cssText = 'margin-bottom:12px;color:rgba(255,255,255,0.6);font-size:12px;';
        summaryEl.textContent = `${total.toLocaleString()} total searches — page ${p} of ${pages}`;
        tableEl.appendChild(summaryEl);

        // Table
        const table = document.createElement('table');
        table.style.cssText = 'width:100%;border-collapse:collapse;font-size:13px;';
        const thead = document.createElement('thead');
        safeSetHTML(
          thead,
          '<tr style="border-bottom:1px solid rgba(255,255,255,0.15);">' +
            '<th style="text-align:left;padding:8px 6px;color:rgba(255,255,255,0.5);font-weight:500;">Query</th>' +
            '<th style="text-align:center;padding:8px 6px;color:rgba(255,255,255,0.5);font-weight:500;width:90px;">Type</th>' +
            '<th style="text-align:center;padding:8px 6px;color:rgba(255,255,255,0.5);font-weight:500;width:70px;">Results</th>' +
            '<th style="text-align:left;padding:8px 6px;color:rgba(255,255,255,0.5);font-weight:500;width:130px;">When</th>' +
            '</tr>'
        );
        table.appendChild(thead);

        const tbody = document.createElement('tbody');
        rows.forEach(function (row) {
          const tr = document.createElement('tr');
          tr.style.cssText = 'border-bottom:1px solid rgba(255,255,255,0.07);';
          const resultsVal = row.results === null || row.results === undefined ? '—' : row.results;
          const resultStyle = row.results === 0 ? 'color:#f87171;' : '';
          const typeLabel = row.type === 'full' ? '🔎 full' : '⚡ predict';
          const when = new Date(row.created_at).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
          safeSetHTML(
            tr,
            `<td style="padding:7px 6px;">${self.escapeHtml(row.query)}</td>` +
              `<td style="padding:7px 6px;text-align:center;font-size:11px;color:rgba(255,255,255,0.6);">${typeLabel}</td>` +
              `<td style="padding:7px 6px;text-align:center;${resultStyle}">${resultsVal}</td>` +
              `<td style="padding:7px 6px;color:rgba(255,255,255,0.5);font-size:11px;">${when}</td>`
          );
          tbody.appendChild(tr);
        });
        table.appendChild(tbody);
        tableEl.appendChild(table);

        // Pagination
        if (pages > 1) {
          const pager = document.createElement('div');
          pager.style.cssText = 'display:flex;gap:8px;justify-content:center;margin-top:16px;';
          for (let i = 1; i <= Math.min(pages, 10); i++) {
            const btn = document.createElement('button');
            btn.textContent = i;
            btn.style.cssText = `padding:4px 10px;border-radius:4px;border:1px solid rgba(255,255,255,0.2);background:${i === p ? 'rgba(99,102,241,0.6)' : 'rgba(255,255,255,0.1)'};color:#fff;cursor:pointer;font-size:12px;`;
            btn.addEventListener('click', function () {
              self.loadSearchHistory(i);
            });
            pager.appendChild(btn);
          }
          tableEl.appendChild(pager);
        }

        safeSetHTML(content, '');
        content.appendChild(tableEl);
      } catch (_err) {
        safeSetHTML(
          content,
          '<p style="color:rgba(255,255,255,0.6);padding:20px;text-align:center;">Unable to load search history.</p>'
        );
      }
    },

    // Load new metrics (cart abandonment, return customers, LTV, session duration, devices)
    loadNewMetrics: async function () {
      try {
        // Cart Abandonment
        const cartResponse = await fetch(`${this.config.apiProxyUrl}?endpoint=cart-abandonment`);
        if (cartResponse.ok) {
          const cartData = await cartResponse.json();
          if (cartData.success) {
            document.getElementById('abandonment-rate').textContent = `${cartData.data.rate}%`;
            document.getElementById('potential-revenue').textContent =
              `$${this.formatNumber(cartData.data.potentialRevenue)} potential`;
          }
        }

        // Return Customers
        const returnResponse = await fetch(`${this.config.apiProxyUrl}?endpoint=return-customers`);
        if (returnResponse.ok) {
          const returnData = await returnResponse.json();
          if (returnData.success) {
            // Update Overview tab Return Customers card
            document.getElementById('return-rate').textContent = `${returnData.data.rate}%`;
            document.getElementById('returning-count').textContent =
              `${returnData.data.returning} returning`;

            // Update Customers tab Return Customers card
            document.getElementById('return-customers-percent').textContent =
              `${returnData.data.rate}%`;
            document.getElementById('return-customers-total').textContent =
              `${returnData.data.returning} returning`;
          }
        }

        // Customer Lifetime Value (with retry logic)
        const loadLTV = async (_isRetry = false) => {
          try {
            const ltvResponse = await fetch(
              `${this.config.apiProxyUrl}?endpoint=customer-lifetime-value`
            );
            if (ltvResponse.ok) {
              const ltvData = await ltvResponse.json();
              if (ltvData.success) {
                document.getElementById('ltv-value').textContent =
                  `$${this.formatNumber(ltvData.data.value)}`;
                document.getElementById('ltv-customers').textContent =
                  `${this.formatNumber(ltvData.data.totalCustomers, 0)} customers`;
                return true;
              } else {
                return false;
              }
            } else {
              return false;
            }
          } catch (_error) {
            return false;
          }
        };

        const ltvSuccess = await loadLTV(false);
        if (!ltvSuccess) {
          await new Promise((resolve) => {
            setTimeout(resolve, 3000);
          });
          const retrySuccess = await loadLTV(true);
          if (!retrySuccess) {
            document.getElementById('ltv-value').textContent = 'Error';
            document.getElementById('ltv-customers').textContent = 'Failed to load';
          }
        }

        // Average Session Duration
        const sessionResponse = await fetch(
          `${this.config.apiProxyUrl}?endpoint=avg-session-duration`
        );
        if (sessionResponse.ok) {
          const sessionData = await sessionResponse.json();
          if (sessionData.success) {
            document.getElementById('session-duration').textContent = sessionData.data.formatted;
          }
        }

        // Device Breakdown
        const deviceResponse = await fetch(`${this.config.apiProxyUrl}?endpoint=device-breakdown`);
        if (deviceResponse.ok) {
          const deviceData = await deviceResponse.json();
          if (deviceData.success) {
            const total = deviceData.data.mobile + deviceData.data.desktop + deviceData.data.tablet;
            const mobilePercent =
              total > 0 ? Math.round((deviceData.data.mobile / total) * 100) : 0;
            document.getElementById('device-breakdown').textContent = `${mobilePercent}% mobile`;
          }
        }
      } catch (_error) {}
    },

    // Load social media data
    loadSocialData: async function () {
      // Load Pinterest metrics
      try {
        const pinterestResponse = await fetch(
          `${this.config.apiProxyUrl}?endpoint=pinterest-metrics`
        );
        if (pinterestResponse.ok) {
          const result = await pinterestResponse.json();
          // Handle double-nested response structure
          const pinData = result.data?.data || result.data;
          if (result.success && pinData && pinData.followers !== undefined) {
            const pinFollowers = pinData.followers || 0;
            const pinSaves = pinData.saves || 0;
            const saveRatio = pinFollowers > 0 ? (pinSaves / pinFollowers) * 100 : 0;

            const pinFollowersEl = document.getElementById('pin-followers');
            const pinSavesEl = document.getElementById('pin-saves');
            if (pinFollowersEl) pinFollowersEl.textContent = this.formatNumber(pinFollowers, 0);
            if (pinSavesEl) pinSavesEl.textContent = this.formatNumber(pinSaves, 0);

            // Industry benchmark comparison for Pinterest
            const pinComparison = document.getElementById('pin-comparison');
            if (pinComparison) {
              const indicator = pinComparison.querySelector('.comparison-indicator');
              if (indicator) {
                // Pinterest benchmarks: saves should be 2-5% of followers for healthy engagement
                if (saveRatio >= 5) {
                  indicator.textContent = '🔥 Excellent saves/follower ratio';
                  indicator.className = 'comparison-indicator excellent';
                } else if (saveRatio >= 2) {
                  indicator.textContent = '✓ Good saves/follower ratio';
                  indicator.className = 'comparison-indicator above-average';
                } else if (saveRatio >= 1) {
                  indicator.textContent = '✓ Average saves/follower ratio';
                  indicator.className = 'comparison-indicator average';
                } else {
                  indicator.textContent = '↓ Low saves/follower ratio';
                  indicator.className = 'comparison-indicator below-average';
                }
              }
            }
          } else {
            const pinFollowersEl = document.getElementById('pin-followers');
            const pinSavesEl = document.getElementById('pin-saves');
            if (pinFollowersEl) pinFollowersEl.textContent = '--';
            if (pinSavesEl) pinSavesEl.textContent = '--';
          }
        } else {
          const pinFollowersEl = document.getElementById('pin-followers');
          const pinSavesEl = document.getElementById('pin-saves');
          if (pinFollowersEl) pinFollowersEl.textContent = '--';
          if (pinSavesEl) pinSavesEl.textContent = '--';
        }
      } catch (_error) {
        const pinFollowersEl = document.getElementById('pin-followers');
        const pinSavesEl = document.getElementById('pin-saves');
        if (pinFollowersEl) pinFollowersEl.textContent = '--';
        if (pinSavesEl) pinSavesEl.textContent = '--';
      }

      // Load Instagram metrics
      try {
        const instagramResponse = await fetch(
          `${this.config.apiProxyUrl}?endpoint=instagram-metrics`
        );
        if (instagramResponse.ok) {
          const result = await instagramResponse.json();
          // Handle double-nested response structure
          const igData = result.data?.data || result.data;
          if (result.success && igData && igData.followers !== undefined) {
            // Small social media card
            const engagement = igData.engagement || 0;
            const igFollowersEl = document.getElementById('ig-followers');
            const igEngagementEl = document.getElementById('ig-engagement');
            if (igFollowersEl) {
              igFollowersEl.textContent = this.formatNumber(igData.followers || 0, 0);
            }
            if (igEngagementEl) igEngagementEl.textContent = `${this.formatNumber(engagement, 1)}%`;

            // Industry benchmark comparison for Instagram engagement
            const igComparison = document.getElementById('ig-comparison');
            if (igComparison) {
              const indicator = igComparison.querySelector('.comparison-indicator');
              if (engagement >= 5) {
                indicator.textContent = '🔥 Excellent (Industry: 1-3%)';
                indicator.className = 'comparison-indicator excellent';
              } else if (engagement >= 3) {
                indicator.textContent = '↑ Above Average (Industry: 1-3%)';
                indicator.className = 'comparison-indicator above-average';
              } else if (engagement >= 1) {
                indicator.textContent = '✓ Average (Industry: 1-3%)';
                indicator.className = 'comparison-indicator average';
              } else {
                indicator.textContent = '↓ Below Average (Industry: 1-3%)';
                indicator.className = 'comparison-indicator below-average';
              }
            }

            // Comprehensive Instagram Analytics widget
            this.renderInstagramAnalytics(igData);
          } else {
            const igFollowersEl = document.getElementById('ig-followers');
            const igEngagementEl = document.getElementById('ig-engagement');
            if (igFollowersEl) igFollowersEl.textContent = '--';
            if (igEngagementEl) igEngagementEl.textContent = '--%';
          }
        } else {
          const igFollowersEl = document.getElementById('ig-followers');
          const igEngagementEl = document.getElementById('ig-engagement');
          if (igFollowersEl) igFollowersEl.textContent = '--';
          if (igEngagementEl) igEngagementEl.textContent = '--%';
        }
      } catch (_error) {
        const igFollowersEl = document.getElementById('ig-followers');
        const igEngagementEl = document.getElementById('ig-engagement');
        if (igFollowersEl) igFollowersEl.textContent = '--';
        if (igEngagementEl) igEngagementEl.textContent = '--%';
      }

      // Load Instagram Ads metrics
      this.loadInstagramAdsMetrics();

      // Load Google Ads metrics
      this.loadGoogleAdsMetrics();

      // Load Groupie Bot stats
      this.loadGroupieBotStats();

      // Load TikTok metrics
      this.loadTikTokMetrics();

      // Load Facebook metrics
      try {
        const facebookResponse = await fetch(
          `${this.config.apiProxyUrl}?endpoint=facebook-metrics`
        );
        if (facebookResponse.ok) {
          const result = await facebookResponse.json();
          // Handle double-nested response structure
          const fbData = result.data?.data || result.data;
          if (result.success && fbData && fbData.likes !== undefined) {
            // Social Media card (basic metrics)
            const fbLikes = fbData.likes || 0;
            const fbReach = fbData.reach || 0;
            const fbEngagement = fbData.engagementRate || 0;

            const fbLikesEl = document.getElementById('fb-likes');
            const fbReachEl = document.getElementById('fb-reach');
            if (fbLikesEl) fbLikesEl.textContent = this.formatNumber(fbLikes, 0);
            if (fbReachEl) fbReachEl.textContent = this.formatNumber(fbReach, 0);

            // Industry benchmark comparison for Facebook - show actual percentage
            const fbComparison = document.getElementById('fb-comparison');
            if (fbComparison) {
              const indicator = fbComparison.querySelector('.comparison-indicator');
              const actualPercent = fbEngagement.toFixed(2) + '%';
              // Facebook engagement benchmarks: <0.5% = low, 0.5-1% = average, 1-2% = good, >2% = excellent
              if (fbEngagement >= 2) {
                indicator.textContent = `🔥 ${actualPercent} - Excellent (Industry: 0.5-1%)`;
                indicator.className = 'comparison-indicator excellent';
              } else if (fbEngagement >= 1) {
                indicator.textContent = `↑ ${actualPercent} - Above Average (Industry: 0.5-1%)`;
                indicator.className = 'comparison-indicator above-average';
              } else if (fbEngagement >= 0.5) {
                indicator.textContent = `✓ ${actualPercent} - Average (Industry: 0.5-1%)`;
                indicator.className = 'comparison-indicator average';
              } else {
                indicator.textContent = `↓ ${actualPercent} - Below Average (Industry: 0.5-1%)`;
                indicator.className = 'comparison-indicator below-average';
              }
            }

            // Facebook Analytics card (detailed metrics)
            // Audience Growth
            const fbNewFollowersEl = document.getElementById('fb-new-followers');
            const fbUnfollowsEl = document.getElementById('fb-unfollows');
            if (fbNewFollowersEl) {
              fbNewFollowersEl.textContent = this.formatNumber(fbData.newFollowers || 0, 0);
            }
            if (fbUnfollowsEl) {
              fbUnfollowsEl.textContent = this.formatNumber(fbData.unfollows || 0, 0);
            }

            const netGrowth = fbData.netGrowth || 0;
            const netGrowthEl = document.getElementById('fb-net-growth');
            if (netGrowthEl) {
              netGrowthEl.textContent =
                (netGrowth >= 0 ? '+' : '') + this.formatNumber(netGrowth, 0);
              netGrowthEl.style.color =
                netGrowth >= 0 ? 'var(--accent-success)' : 'var(--accent-danger)';
            }

            // Growth Rate percentage
            const fbGrowthRateEl = document.getElementById('fb-growth-rate');
            if (fbGrowthRateEl) {
              fbGrowthRateEl.textContent = this.formatNumber(fbData.growthRate || 0, 2) + '%';
            }

            // Post Performance
            const fbPostImpressionsEl = document.getElementById('fb-post-impressions');
            const fbPostImpressionsUniqueEl = document.getElementById('fb-post-impressions-unique');
            const fbPostReactionsEl = document.getElementById('fb-post-reactions');
            const fbVideoViewsEl = document.getElementById('fb-video-views');
            if (fbPostImpressionsEl) {
              fbPostImpressionsEl.textContent = this.formatNumber(fbData.postImpressions || 0, 0);
            }
            if (fbPostImpressionsUniqueEl) {
              fbPostImpressionsUniqueEl.textContent = this.formatNumber(
                fbData.postImpressionsUnique || 0,
                0
              );
            }
            if (fbPostReactionsEl) {
              fbPostReactionsEl.textContent = this.formatNumber(fbData.postReactions || 0, 0);
            }
            if (fbVideoViewsEl) {
              fbVideoViewsEl.textContent = this.formatNumber(fbData.videoViews || 0, 0);
            }

            // Engagement Overview with percentages
            const fbEngagementRateEl = document.getElementById('fb-engagement-rate');
            const fbPostEngagementsEl = document.getElementById('fb-post-engagements');
            const fbVideoEngagementRateEl = document.getElementById('fb-video-engagement-rate');
            if (fbEngagementRateEl) {
              fbEngagementRateEl.textContent =
                this.formatNumber(fbData.engagementRate || 0, 2) + '%';
            }
            if (fbPostEngagementsEl) {
              fbPostEngagementsEl.textContent = this.formatNumber(fbData.postEngagements || 0, 0);
            }
            if (fbVideoEngagementRateEl) {
              fbVideoEngagementRateEl.textContent =
                this.formatNumber(fbData.videoEngagementRate || 0, 2) + '%';
            }
          } else {
            // Set all to default '--' if API fails
            this.setFacebookMetricsDefault();
          }
        } else {
          this.setFacebookMetricsDefault();
        }
      } catch (_error) {
        this.setFacebookMetricsDefault();
      }

      // Load Facebook Ads metrics
      this.loadFacebookAdsMetrics();
    },

    setFacebookMetricsDefault: function () {
      // Social Media card
      document.getElementById('fb-likes').textContent = '--';
      document.getElementById('fb-reach').textContent = '--';

      // Facebook Analytics card
      [
        'fb-new-followers',
        'fb-unfollows',
        'fb-net-growth',
        'fb-growth-rate',
        'fb-post-impressions',
        'fb-post-impressions-unique',
        'fb-post-reactions',
        'fb-video-views',
        'fb-engagement-rate',
        'fb-post-engagements',
        'fb-video-engagement-rate',
      ].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.textContent = '--';
      });
    },

    loadFacebookAdsMetrics: async function () {
      try {
        const adsResponse = await fetch(`${this.config.apiProxyUrl}?endpoint=facebook-ads-metrics`);
        if (adsResponse.ok) {
          const result = await adsResponse.json();
          const adsData = result.data?.data || result.data;

          if (result.success && adsData) {
            // Spend & ROAS
            document.getElementById('fb-ads-spend').textContent =
              '$' + this.formatNumber(adsData.spend || 0, 2);
            document.getElementById('fb-ads-roas').textContent =
              this.formatNumber(adsData.roas || 0, 2) + 'x';
            document.getElementById('fb-ads-purchase-value').textContent =
              '$' + this.formatNumber(adsData.purchaseValue || 0, 2);
            document.getElementById('fb-ads-conversions').textContent = this.formatNumber(
              adsData.conversions || 0,
              0
            );

            // Performance metrics
            document.getElementById('fb-ads-impressions').textContent = this.formatNumber(
              adsData.impressions || 0,
              0
            );
            document.getElementById('fb-ads-reach').textContent = this.formatNumber(
              adsData.reach || 0,
              0
            );
            document.getElementById('fb-ads-clicks').textContent = this.formatNumber(
              adsData.clicks || 0,
              0
            );
            document.getElementById('fb-ads-ctr').textContent =
              this.formatNumber(adsData.ctr || 0, 2) + '%';
            document.getElementById('fb-ads-cpc').textContent =
              '$' + this.formatNumber(adsData.cpc || 0, 2);
            document.getElementById('fb-ads-cpm').textContent =
              '$' + this.formatNumber(adsData.cpm || 0, 2);
            document.getElementById('fb-ads-cost-per-conversion').textContent =
              '$' + this.formatNumber(adsData.costPerConversion || 0, 2);
          } else {
            this.setFacebookAdsMetricsDefault();
          }
        } else {
          this.setFacebookAdsMetricsDefault();
        }
      } catch (_error) {
        this.setFacebookAdsMetricsDefault();
      }
    },

    setFacebookAdsMetricsDefault: function () {
      [
        'fb-ads-spend',
        'fb-ads-roas',
        'fb-ads-purchase-value',
        'fb-ads-conversions',
        'fb-ads-impressions',
        'fb-ads-reach',
        'fb-ads-clicks',
        'fb-ads-ctr',
        'fb-ads-cpc',
        'fb-ads-cpm',
        'fb-ads-cost-per-conversion',
      ].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.textContent = '--';
      });
    },

    // Render comprehensive Instagram Analytics widget
    renderInstagramAnalytics: function (igData) {
      try {
        // Audience Growth Section
        const followers = igData.followers || 0;
        const following = igData.following || 0;
        const posts = igData.posts || 0;

        document.getElementById('ig-total-followers').textContent = this.formatNumber(followers, 0);
        document.getElementById('ig-following').textContent = this.formatNumber(following, 0);

        // Calculate follower ratio (followers / following)
        const followerRatio = following > 0 ? (followers / following).toFixed(1) : '0.0';
        document.getElementById('ig-follower-ratio').textContent = followerRatio + ':1';

        // Growth rate placeholder (would need historical data)
        document.getElementById('ig-follower-growth').textContent = '+0.0%';

        // Content Performance Section
        document.getElementById('ig-total-posts').textContent = this.formatNumber(posts, 0);
        document.getElementById('ig-reach').textContent = this.formatNumber(igData.reach || 0, 0);
        document.getElementById('ig-impressions').textContent = this.formatNumber(
          igData.impressions || 0,
          0
        );

        // Calculate average reach per post
        const avgReach = posts > 0 ? Math.round((igData.reach || 0) / posts) : 0;
        document.getElementById('ig-avg-reach').textContent = this.formatNumber(avgReach, 0);

        // Engagement Overview Section
        document.getElementById('ig-engagement-rate').textContent =
          this.formatNumber(igData.engagement || 0, 2) + '%';

        // Calculate accounts engaged from engagement rate and followers
        const accountsEngaged = Math.round((followers * (igData.engagement || 0)) / 100);
        document.getElementById('ig-accounts-engaged').textContent = this.formatNumber(
          accountsEngaged,
          0
        );

        // Update profile link and username
        const username = igData.username || 'coracreacrafts';
        document.getElementById('ig-username').textContent = username;
        const profileLink = document.getElementById('ig-profile-link');
        if (profileLink) {
          profileLink.href = `https://instagram.com/${username}`;
        }
      } catch (_error) {
        this.setInstagramAnalyticsDefault();
      }
    },

    // Set default values for Instagram Analytics widget
    setInstagramAnalyticsDefault: function () {
      [
        'ig-total-followers',
        'ig-following',
        'ig-follower-ratio',
        'ig-follower-growth',
        'ig-total-posts',
        'ig-reach',
        'ig-impressions',
        'ig-avg-reach',
        'ig-engagement-rate',
        'ig-accounts-engaged',
      ].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.textContent = '--';
      });
    },

    // Load Instagram Ads metrics
    loadInstagramAdsMetrics: async function () {
      try {
        const adsResponse = await fetch(
          `${this.config.apiProxyUrl}?endpoint=instagram-ads-metrics`
        );
        if (adsResponse.ok) {
          const result = await adsResponse.json();
          const adsData = result.data?.data || result.data;

          if (result.success && adsData) {
            // Spend & ROAS
            document.getElementById('ig-ads-spend').textContent =
              '$' + this.formatNumber(adsData.spend || 0, 2);
            document.getElementById('ig-ads-roas').textContent =
              this.formatNumber(adsData.roas || 0, 2) + 'x';
            document.getElementById('ig-ads-purchase-value').textContent =
              '$' + this.formatNumber(adsData.purchaseValue || 0, 2);
            document.getElementById('ig-ads-conversions').textContent = this.formatNumber(
              adsData.conversions || 0,
              0
            );

            // Performance metrics
            document.getElementById('ig-ads-impressions').textContent = this.formatNumber(
              adsData.impressions || 0,
              0
            );
            document.getElementById('ig-ads-reach').textContent = this.formatNumber(
              adsData.reach || 0,
              0
            );
            document.getElementById('ig-ads-clicks').textContent = this.formatNumber(
              adsData.clicks || 0,
              0
            );
            document.getElementById('ig-ads-ctr').textContent =
              this.formatNumber(adsData.ctr || 0, 2) + '%';
            document.getElementById('ig-ads-cpc').textContent =
              '$' + this.formatNumber(adsData.cpc || 0, 2);
            document.getElementById('ig-ads-cpm').textContent =
              '$' + this.formatNumber(adsData.cpm || 0, 2);
            document.getElementById('ig-ads-cost-per-conversion').textContent =
              '$' + this.formatNumber(adsData.costPerConversion || 0, 2);
          } else {
            this.setInstagramAdsMetricsDefault();
          }
        } else {
          this.setInstagramAdsMetricsDefault();
        }
      } catch (_error) {
        this.setInstagramAdsMetricsDefault();
      }
    },

    setInstagramAdsMetricsDefault: function () {
      [
        'ig-ads-spend',
        'ig-ads-roas',
        'ig-ads-purchase-value',
        'ig-ads-conversions',
        'ig-ads-impressions',
        'ig-ads-reach',
        'ig-ads-clicks',
        'ig-ads-ctr',
        'ig-ads-cpc',
        'ig-ads-cpm',
        'ig-ads-cost-per-conversion',
      ].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.textContent = '--';
      });
    },

    // Load Google Ads metrics
    loadGoogleAdsMetrics: async function () {
      try {
        const adsResponse = await fetch(`${this.config.apiProxyUrl}?endpoint=google-ads-metrics`);
        if (adsResponse.ok) {
          const result = await adsResponse.json();
          const adsData = result.data?.data || result.data;

          if (result.success && adsData) {
            // Spend & ROAS
            document.getElementById('google-ads-spend').textContent =
              '$' + this.formatNumber(adsData.spend || 0, 2);
            document.getElementById('google-ads-roas').textContent =
              this.formatNumber(adsData.roas || 0, 2) + 'x';
            document.getElementById('google-ads-conversion-value').textContent =
              '$' + this.formatNumber(adsData.conversionValue || 0, 2);
            document.getElementById('google-ads-conversions').textContent = this.formatNumber(
              adsData.conversions || 0,
              0
            );

            // Performance metrics
            document.getElementById('google-ads-impressions').textContent = this.formatNumber(
              adsData.impressions || 0,
              0
            );
            document.getElementById('google-ads-clicks').textContent = this.formatNumber(
              adsData.clicks || 0,
              0
            );
            document.getElementById('google-ads-ctr').textContent =
              this.formatNumber(adsData.ctr || 0, 2) + '%';
            document.getElementById('google-ads-cpc').textContent =
              '$' + this.formatNumber(adsData.cpc || 0, 2);
            document.getElementById('google-ads-cpm').textContent =
              '$' + this.formatNumber(adsData.cpm || 0, 2);
            document.getElementById('google-ads-cost-per-conversion').textContent =
              '$' + this.formatNumber(adsData.costPerConversion || 0, 2);
          } else {
            this.setGoogleAdsMetricsDefault();
          }
        } else {
          this.setGoogleAdsMetricsDefault();
        }
      } catch (_error) {
        this.setGoogleAdsMetricsDefault();
      }
    },

    setGoogleAdsMetricsDefault: function () {
      [
        'google-ads-spend',
        'google-ads-roas',
        'google-ads-conversion-value',
        'google-ads-conversions',
        'google-ads-impressions',
        'google-ads-clicks',
        'google-ads-ctr',
        'google-ads-cpc',
        'google-ads-cpm',
        'google-ads-cost-per-conversion',
      ].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.textContent = '--';
      });
    },

    loadGroupieBotStats: async function () {
      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=groupie-bot-stats`);
        if (response.ok) {
          const result = await response.json();
          // Handle nested data structure: result.data.data
          const botData = result.data?.data || result.data;

          if (result.success && botData && botData.stats) {
            // Update stats
            document.getElementById('groupie-total-comments').textContent =
              botData.stats.totalComments || 0;
            document.getElementById('groupie-avg-likes').textContent =
              botData.stats.avgLikes || '0.00';
            document.getElementById('groupie-avg-replies').textContent =
              botData.stats.avgReplies || '0.00';

            const pendingEl = document.getElementById('groupie-pending-approvals');
            if (pendingEl) {
              pendingEl.textContent = botData.stats.pendingApprovals || 0;
              // Highlight if there are pending approvals
              pendingEl
                .closest('.groupie-stat-card')
                .classList.toggle('has-pending', botData.stats.pendingApprovals > 0);
            }

            // Render recent comments
            this.renderGroupieBotComments(botData.recentComments || []);

            // Render trending hashtags
            this.renderGroupieBotHashtags(botData.trendingHashtags || []);
          } else {
            this.setGroupieBotDefaults();
          }
        } else {
          this.setGroupieBotDefaults();
        }
      } catch (_error) {
        this.setGroupieBotDefaults();
      }
    },

    renderGroupieBotComments: function (comments) {
      const container = document.getElementById('groupie-recent-comments');
      if (!container) return;

      if (!comments || comments.length === 0) {
        safeSetHTML(container, '<div class="groupie-empty">No recent comments</div>');
        return;
      }

      const html = comments
        .map((comment) => {
          // Try multiple possible URL field names from Instagram API
          const postUrl =
            comment.postUrl ||
            comment.permalink ||
            comment.postPermalink ||
            comment.url ||
            comment.post_url ||
            comment.media_url;

          return `
        <div class="groupie-comment-item" ${postUrl ? `data-post-url="${this.escapeHtml(postUrl)}"` : ''}>
          <div class="groupie-comment-header">
            <span class="groupie-comment-username">@${this.escapeHtml(comment.username)}</span>
            <span class="groupie-comment-time">${this.formatTimeAgo(comment.postedAt)}</span>
          </div>
          <div class="groupie-comment-text" ${postUrl ? `data-post-url="${this.escapeHtml(postUrl)}"` : ''}>
            ${this.escapeHtml(comment.comment)}
            ${postUrl ? '<span class="comment-link-icon" title="Click to view on Instagram">🔗</span>' : ''}
          </div>
          <div class="groupie-comment-stats">
            <span class="groupie-comment-stat">❤️ ${comment.likes}</span>
            <span class="groupie-comment-stat">💭 ${comment.replies}</span>
          </div>
          <div class="groupie-comment-tooltip">
            <div class="full-comment-text">${this.escapeHtml(comment.comment)}</div>
          </div>
        </div>
      `;
        })
        .join('');

      safeSetHTML(container, html);

      // Add click handlers to comments with URLs
      container.querySelectorAll('.groupie-comment-text[data-post-url]').forEach((commentEl) => {
        commentEl.addEventListener('click', function (e) {
          e.preventDefault();
          const url = this.getAttribute('data-post-url');
          // Validate protocol at click time — defense in depth against javascript: in API data
          if (url && /^https?:\/\//i.test(url)) {
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        });
      });
    },

    renderGroupieBotHashtags: function (hashtags) {
      const container = document.getElementById('groupie-trending-hashtags');
      if (!container) return;

      if (!hashtags || hashtags.length === 0) {
        safeSetHTML(container, '<div class="groupie-empty">No trending hashtags</div>');
        return;
      }

      const html = hashtags
        .map(
          (tag) => `
        <div class="groupie-hashtag-item">
          <span class="groupie-hashtag-tag">#${this.escapeHtml(tag.tag)}</span>
          <span class="groupie-hashtag-popularity">${Math.round(tag.popularity * 100)}%</span>
        </div>
      `
        )
        .join('');

      safeSetHTML(container, html);
    },

    setGroupieBotDefaults: function () {
      const statIds = [
        'groupie-total-comments',
        'groupie-avg-likes',
        'groupie-avg-replies',
        'groupie-pending-approvals',
      ];
      statIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.textContent = '--';
      });

      const commentsEl = document.getElementById('groupie-recent-comments');
      if (commentsEl) safeSetHTML(commentsEl, '<div class="groupie-empty">Unavailable</div>');

      const hashtagsEl = document.getElementById('groupie-trending-hashtags');
      if (hashtagsEl) safeSetHTML(hashtagsEl, '<div class="groupie-empty">Unavailable</div>');
    },

    // Load TikTok metrics
    loadTikTokMetrics: async function () {
      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=tiktok-metrics`);
        if (response.ok) {
          const result = await response.json();
          const ttData = result.data?.data || result.data;

          if (result.success && ttData) {
            // Audience Growth
            document.getElementById('tt-total-followers').textContent = this.formatNumber(
              ttData.followers || 0,
              0
            );
            document.getElementById('tt-follower-growth').textContent = '+0%'; // Will need historical data for growth %
            document.getElementById('tt-following').textContent = this.formatNumber(
              ttData.following || 0,
              0
            );
            document.getElementById('tt-total-likes').textContent = this.formatNumber(
              ttData.totalLikes || 0,
              0
            );

            // Content Performance
            document.getElementById('tt-total-videos').textContent = this.formatNumber(
              ttData.totalVideos || 0,
              0
            );
            document.getElementById('tt-total-views').textContent = this.formatNumber(
              ttData.totalViews || 0,
              0
            );
            document.getElementById('tt-avg-views').textContent = this.formatNumber(
              ttData.avgViews || 0,
              0
            );
            document.getElementById('tt-shares').textContent = this.formatNumber(
              ttData.shares || 0,
              0
            );

            // Engagement Overview
            document.getElementById('tt-engagement-rate').textContent =
              this.formatNumber(ttData.engagementRate || 0, 2) + '%';
            document.getElementById('tt-total-comments').textContent = this.formatNumber(
              ttData.comments || 0,
              0
            );
            document.getElementById('tt-username').textContent =
              ttData.username || 'coracreacrafts';

            // Update profile link
            const profileLink = document.getElementById('tt-profile-link');
            if (profileLink && ttData.username) {
              profileLink.href = `https://tiktok.com/@${ttData.username}`;
            }

            // Render top videos
            this.renderTikTokVideos(ttData.topVideos || []);
          } else {
            this.setTikTokDefaults();
          }
        } else {
          this.setTikTokDefaults();
        }
      } catch (_error) {
        this.setTikTokDefaults();
      }
    },

    renderTikTokVideos: function (videos) {
      const container = document.getElementById('tt-top-videos');
      if (!container) return;

      if (!videos || videos.length === 0) {
        safeSetHTML(container, '<div class="tt-empty">No videos available</div>');
        return;
      }

      const html = videos
        .map((video) => {
          // Validate the URL — only allow https/http to prevent javascript: protocol injection
          const safeLink = this.safeUrl(video.embedLink);
          return `
        <div class="tt-video-item"${safeLink ? ` data-video-url="${this.escapeHtml(safeLink)}"` : ''} style="cursor:${safeLink ? 'pointer' : 'default'}">
          <div class="tt-video-header">
            <div class="tt-video-description">${this.escapeHtml(video.description || video.title)}</div>
            <div class="tt-video-views">${this.formatNumber(video.views, 0)} views</div>
          </div>
          <div class="tt-video-stats">
            <span class="tt-video-stat">❤️ ${this.formatNumber(video.likes, 0)}</span>
            <span class="tt-video-stat">💬 ${this.formatNumber(video.comments, 0)}</span>
            <span class="tt-video-stat">🔄 ${this.formatNumber(video.shares, 0)}</span>
          </div>
        </div>
      `;
        })
        .join('');

      safeSetHTML(container, html);

      // Wire click handlers via event delegation — no inline onclick, CSP-safe
      container.querySelectorAll('.tt-video-item[data-video-url]').forEach((item) => {
        item.addEventListener('click', function () {
          const url = this.getAttribute('data-video-url');
          // Re-validate at click time — defense in depth
          if (url && /^https?:\/\//i.test(url)) {
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        });
      });
    },

    setTikTokDefaults: function () {
      const metricIds = [
        'tt-total-followers',
        'tt-follower-growth',
        'tt-following',
        'tt-total-likes',
        'tt-total-videos',
        'tt-total-views',
        'tt-avg-views',
        'tt-shares',
        'tt-engagement-rate',
        'tt-total-comments',
      ];

      metricIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.textContent = '--';
      });

      const videosEl = document.getElementById('tt-top-videos');
      if (videosEl) safeSetHTML(videosEl, '<div class="tt-empty">Unavailable</div>');
    },

    formatTimeAgo: function (timestamp) {
      if (!timestamp) return '--';
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);

      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays}d ago`;
    },

    // Proxy escapeHtml to core module (implementation moved to core)
    escapeHtml(text) {
      return this.core.escapeHtml(text);
    },

    // Proxy safeUrl to core module — validates http/https, rejects javascript: and data: protocols
    safeUrl(url) {
      return this.core.safeUrl(url);
    },

    // Initialize charts
    initCharts: function () {
      // Initialize charts object to store chart instances
      this.charts = {};

      this.initTrafficSourcesChart();
      this.initRevenueChart();
      this.initBotChart();
      this.setupDropdownListeners();
    },

    // Setup all dropdown event listeners using event delegation
    setupDropdownListeners: function () {
      const self = this;

      // Debounce helper for period-selector change handlers.
      // Rapid changes (e.g., keyboard scrolling through a <select>) would fire one API
      // request per intermediate value. A 120 ms debounce collapses that burst into a
      // single call for the final settled value, preventing redundant in-flight fetches.
      // Additionally, each debounce cancels any in-flight requests for the selector's
      // view key so slow responses for the previous period never overwrite fresh data.
      const _debouncedCalls = new Map(); // selectorId → timeoutId
      function debounceCall(id, fn, ms) {
        clearTimeout(_debouncedCalls.get(id));
        // Cancel in-flight requests for this selector's view (stale period = stale data).
        if (window.AdminDashboardAPI) {
          window.AdminDashboardAPI.cancelView(id);
        }
        _debouncedCalls.set(id, setTimeout(fn, ms));
      }
      const PERIOD_DEBOUNCE_MS = 120;

      // Use event delegation on document to handle all period selector changes
      document.addEventListener('change', function (e) {
        const target = e.target;
        const id = target.id;
        const value = target.value;

        // Revenue period
        if (id === 'revenue-period') {
          debounceCall(id, () => self.updateRevenueChart(value), PERIOD_DEBOUNCE_MS);
        }
        // Traffic period
        else if (id === 'traffic-period') {
          debounceCall(id, () => self.updateTrafficSourcesChart(value), PERIOD_DEBOUNCE_MS);
        }
        // Abandoned cart period
        else if (id === 'abandoned-cart-period') {
          debounceCall(id, () => self.loadAbandonedCartProducts(value), PERIOD_DEBOUNCE_MS);
        }
        // Search performance period
        else if (id === 'search-performance-period') {
          // Synchronous UI resets happen immediately (no debounce needed — no API call)
          self._gapAnalysis = null;
          const gapBtn = document.getElementById('search-gap-btn');
          if (gapBtn) {
            gapBtn.disabled = false;
            gapBtn.textContent = '⚡ Analyze';
            gapBtn.style.cssText = '';
          }
          const applyBtn = document.getElementById('search-apply-synonyms-btn');
          if (applyBtn) {
            applyBtn.style.display = 'none';
            applyBtn.disabled = false;
            applyBtn.textContent = '↑ Apply synonyms';
            applyBtn.style.cssText =
              'display:none;font-size:11px;padding:4px 8px;border-radius:6px;background:rgba(139,92,246,0.15);border:1px solid rgba(139,92,246,0.35);color:rgba(167,139,250,0.9);cursor:pointer;white-space:nowrap;';
            delete applyBtn.dataset.applied;
          }
          // Debounce the actual API call
          debounceCall(id, () => self.loadSearchMetrics(value), PERIOD_DEBOUNCE_MS);
        }
        // Campaign revenue period
        else if (id === 'campaign-revenue-period') {
          debounceCall(id, () => self.trackCampaignRevenuePerformance(value), PERIOD_DEBOUNCE_MS);
        }
        // Top products period
        else if (id === 'top-products-period') {
          debounceCall(id, () => self.loadTopProducts(value), PERIOD_DEBOUNCE_MS);
        }
        // Discount codes period
        else if (id === 'discount-codes-period') {
          debounceCall(id, () => self.loadDiscountPerformance(value), PERIOD_DEBOUNCE_MS);
        }
        // Device performance period
        else if (id === 'device-performance-period') {
          debounceCall(id, () => self.loadDevicePerformance(value), PERIOD_DEBOUNCE_MS);
        }
        // Payment methods period
        else if (id === 'payment-methods-period') {
          debounceCall(id, () => self.loadPaymentMethodsBreakdown(value), PERIOD_DEBOUNCE_MS);
        }
        // Shipping analytics period
        else if (id === 'shipping-analytics-period') {
          debounceCall(id, () => self.loadShippingAnalytics(value), PERIOD_DEBOUNCE_MS);
        }
        // Collections performance period
        else if (id === 'collections-performance-period') {
          debounceCall(id, () => self.loadCollectionsPerformance(value), PERIOD_DEBOUNCE_MS);
        }
        // Conversion funnel period
        else if (id === 'conversion-funnel-period') {
          debounceCall(id, () => self.loadConversionFunnel(value), PERIOD_DEBOUNCE_MS);
        }
        // Enhanced abandoned checkout period
        else if (id === 'enhanced-checkout-period') {
          debounceCall(id, () => self.loadEnhancedAbandonedCheckout(value), PERIOD_DEBOUNCE_MS);
        }
        // Discount ROI period
        else if (id === 'discount-roi-period') {
          debounceCall(id, () => self.loadDiscountROITracker(value), PERIOD_DEBOUNCE_MS);
        }
        // Profit Margin Trend period
        else if (id === 'profit-margin-period') {
          debounceCall(id, () => self.loadProfitMetrics(value), PERIOD_DEBOUNCE_MS);
        }
        // Real-Time Activity period (Today/Yesterday/Past 7 Days) - controls all three cards
        else if (id === 'realtime-activity-period') {
          const newPeriod = value;
          // Track the active period so in-flight requests for the old period are discarded
          self.state.currentActivityPeriod = newPeriod;
          // Clear hash guard so the new period always re-renders (fixes spinner-stuck on period switch)
          self.state.lastActivityHash = null;
          // Clear stale cache so we never serve the previous period's data
          self.state.cachedRecentOrders = null;
          // Synchronous UI updates happen immediately
          self.showRealtimeActivityLoading();
          self.updateRealtimeActivityDate(newPeriod);
          // Debounce the actual API calls to avoid duplicate requests on rapid period switches
          debounceCall(
            id,
            () => {
              self.loadAllRecentOrders(newPeriod);
              self.loadSubscriptionRenewals(newPeriod);
            },
            PERIOD_DEBOUNCE_MS
          );
        }
      });

      // Add event listener for View Referrals button
      const viewReferralsBtn = document.getElementById('view-referrals-btn');
      if (viewReferralsBtn) {
        viewReferralsBtn.addEventListener('click', () => {
          this.showReferralModal();
        });
      }

      // Add event listener for closing referral modal
      const closeModalBtn = document.getElementById('close-referral-modal');
      const modalOverlay = document.getElementById('referral-modal');
      if (closeModalBtn) {
        closeModalBtn.addEventListener('click', () => {
          modalOverlay.style.display = 'none';
        });
      }
      if (modalOverlay) {
        modalOverlay.addEventListener('click', (e) => {
          if (e.target === modalOverlay) {
            modalOverlay.style.display = 'none';
          }
        });
      }
    },

    // Loading indicator helpers
    showChartLoading: function (chartContainerId) {
      const container = document.getElementById(chartContainerId)?.parentElement;
      if (!container || container.querySelector('.chart-loading-overlay')) return;

      const overlay = document.createElement('div');
      overlay.className = 'chart-loading-overlay';
      safeSetHTML(overlay, '<div class="spinner"></div>');
      container.appendChild(overlay);
    },

    hideChartLoading: function (chartContainerId) {
      const container = document.getElementById(chartContainerId)?.parentElement;
      if (!container) return;

      const overlay = container.querySelector('.chart-loading-overlay');
      if (overlay) {
        overlay.remove();
      }
    },

    // Traffic sources chart
    initTrafficSourcesChart: async function () {
      await this.updateTrafficSourcesChart('7days');
    },

    // Update traffic sources chart with different time periods
    updateTrafficSourcesChart: async function (period) {
      this.showChartLoading('traffic-sources-chart');
      this.showChartLoading('traffic-sources-chart-no-direct');

      try {
        // Destroy existing chart if exists
        if (this.state.charts.trafficChart) {
          this.state.charts.trafficChart.destroy();
        }

        const ctx = document.getElementById('traffic-sources-chart');

        // Handle trend comparison - fetch all four periods (queued to prevent throttling)
        if (period === 'trend') {
          const [todayResult, week7Result, days30Result, days90Result] = await Promise.all([
            this.queuedFetch(`${this.config.apiProxyUrl}?endpoint=today-traffic-sources`),
            this.queuedFetch(`${this.config.apiProxyUrl}?endpoint=7day-traffic-sources`),
            this.queuedFetch(`${this.config.apiProxyUrl}?endpoint=30day-traffic-sources`),
            this.queuedFetch(`${this.config.apiProxyUrl}?endpoint=90day-traffic-sources`),
          ]);

          const todayData = todayResult.data;
          const week7Data = week7Result.data;
          const days30Data = days30Result.data;
          const days90Data = days90Result.data;

          if (todayData.success && week7Data.success && days30Data.success && days90Data.success) {
            // Get all unique traffic sources
            const allSources = new Set();
            todayData.data.forEach((d) => allSources.add(d.source));
            week7Data.data.forEach((d) => allSources.add(d.source));
            days30Data.data.forEach((d) => allSources.add(d.source));
            days90Data.data.forEach((d) => allSources.add(d.source));

            const labels = Array.from(allSources);

            // Calculate totals for each period
            const todayTotal = todayData.data.reduce((sum, d) => sum + d.visits, 0);
            const week7Total = week7Data.data.reduce((sum, d) => sum + d.visits, 0);
            const days30Total = days30Data.data.reduce((sum, d) => sum + d.visits, 0);
            const days90Total = days90Data.data.reduce((sum, d) => sum + d.visits, 0);

            // Create datasets for each period (converted to percentages)
            const todayValues = labels.map((source) => {
              const item = todayData.data.find((d) => d.source === source);
              const visits = item ? item.visits : 0;
              return todayTotal > 0 ? (visits / todayTotal) * 100 : 0;
            });

            const week7Values = labels.map((source) => {
              const item = week7Data.data.find((d) => d.source === source);
              const visits = item ? item.visits : 0;
              return week7Total > 0 ? (visits / week7Total) * 100 : 0;
            });

            const days30Values = labels.map((source) => {
              const item = days30Data.data.find((d) => d.source === source);
              const visits = item ? item.visits : 0;
              return days30Total > 0 ? (visits / days30Total) * 100 : 0;
            });

            const days90Values = labels.map((source) => {
              const item = days90Data.data.find((d) => d.source === source);
              const visits = item ? item.visits : 0;
              return days90Total > 0 ? (visits / days90Total) * 100 : 0;
            });

            // Dynamically adjust canvas height based on number of data points
            const container = ctx.parentElement;
            if (labels.length > 8) {
              const newHeight = Math.max(300, labels.length * 50);
              container.style.height = `${newHeight}px`;
              ctx.style.height = `${newHeight}px`;
            } else {
              container.style.height = '300px';
            }

            // Guard: ensure Chart.js library is loaded before instantiation
            if (!window.Chart) {
              console.warn(
                '[AdminDashboard] Chart.js library not loaded; skipping chart initialization'
              );
              return;
            }

            this.state.charts.trafficChart = new Chart(ctx, {
              type: 'bar',
              data: {
                labels,
                datasets: [
                  {
                    label: 'Last 90 Days',
                    data: days90Values,
                    backgroundColor: 'rgba(34, 197, 94, 0.8)',
                    borderColor: 'rgba(34, 197, 94, 1)',
                    borderWidth: 2,
                    total: days90Total,
                  },
                  {
                    label: 'Last 30 Days',
                    data: days30Values,
                    backgroundColor: 'rgba(236, 72, 153, 0.8)',
                    borderColor: 'rgba(236, 72, 153, 1)',
                    borderWidth: 2,
                    total: days30Total,
                  },
                  {
                    label: 'Last 7 Days',
                    data: week7Values,
                    backgroundColor: 'rgba(251, 146, 60, 0.8)',
                    borderColor: 'rgba(251, 146, 60, 1)',
                    borderWidth: 2,
                    total: week7Total,
                  },
                  {
                    label: 'Today',
                    data: todayValues,
                    backgroundColor: 'rgba(59, 130, 246, 0.8)',
                    borderColor: 'rgba(59, 130, 246, 1)',
                    borderWidth: 2,
                    total: todayTotal,
                  },
                ],
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  x: {
                    grid: {
                      color: 'rgba(255, 255, 255, 0.1)',
                    },
                    ticks: {
                      color: 'rgba(255, 255, 255, 0.9)',
                      font: {
                        weight: 'bold',
                        size: 12,
                      },
                    },
                  },
                  y: {
                    beginAtZero: true,
                    max: 100,
                    grid: {
                      color: 'rgba(255, 255, 255, 0.1)',
                    },
                    ticks: {
                      color: 'rgba(255, 255, 255, 0.9)',
                      font: {
                        weight: 'bold',
                        size: 12,
                      },
                      callback: function (value) {
                        return value + '%';
                      },
                    },
                    title: {
                      display: true,
                      text: 'Percentage (%)',
                      color: 'rgba(255, 255, 255, 0.9)',
                      font: {
                        weight: 'bold',
                        size: 13,
                      },
                    },
                  },
                },
                plugins: {
                  legend: {
                    display: true,
                    position: 'top',
                    labels: {
                      color: '#ffffff',
                      font: {
                        weight: 'bold',
                        size: 13,
                      },
                      padding: 15,
                      usePointStyle: true,
                      pointStyle: 'rectRounded',
                      generateLabels: function (chart) {
                        const datasets = chart.data.datasets;
                        return datasets.map((dataset, i) => ({
                          text: `${dataset.label} (${dataset.total ? dataset.total.toLocaleString() : 0} visits)`,
                          fillStyle: dataset.backgroundColor,
                          strokeStyle: dataset.borderColor,
                          lineWidth: dataset.borderWidth,
                          hidden: !chart.isDatasetVisible(i),
                          index: i,
                          datasetIndex: i,
                          fontColor: '#ffffff',
                        }));
                      },
                    },
                  },
                  datalabels: {
                    display: true,
                    color: '#ffffff',
                    font: {
                      weight: 'bold',
                      size: 11,
                    },
                    anchor: 'end',
                    align: 'end',
                    formatter: function (value, context) {
                      // Get actual visit count (not percentage)
                      const dataset = context.chart.data.datasets[context.datasetIndex];
                      const total = dataset.total || 0;
                      const actualVisits = Math.round((value / 100) * total);
                      return actualVisits > 0 ? actualVisits.toLocaleString() : '';
                    },
                  },
                  tooltip: {
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    titleColor: 'rgba(255, 255, 255, 1)',
                    bodyColor: 'rgba(255, 255, 255, 0.9)',
                    borderColor: 'rgba(99, 102, 241, 0.5)',
                    borderWidth: 1,
                    padding: 12,
                    titleFont: {
                      weight: 'bold',
                      size: 14,
                    },
                    bodyFont: {
                      weight: 'bold',
                      size: 13,
                    },
                    callbacks: {
                      label: function (context) {
                        const label = context.dataset.label || '';
                        const value = context.parsed.y || 0;
                        const total = context.dataset.total || 0;
                        return `${label}: ${value.toFixed(1)}% (${total} total visits)`;
                      },
                    },
                  },
                },
              },
            });

            // Create second chart (excluding Direct traffic) for trend comparison
            if (this.state.charts.trafficChartNoDirect) {
              this.state.charts.trafficChartNoDirect.destroy();
            }

            const ctxNoDirect = document.getElementById('traffic-sources-chart-no-direct');
            if (ctxNoDirect) {
              // Filter out Direct traffic from all sources
              const allSourcesNoDirect = new Set();
              todayData.data
                .filter((d) => d.source !== 'Direct')
                .forEach((d) => allSourcesNoDirect.add(d.source));
              week7Data.data
                .filter((d) => d.source !== 'Direct')
                .forEach((d) => allSourcesNoDirect.add(d.source));
              days30Data.data
                .filter((d) => d.source !== 'Direct')
                .forEach((d) => allSourcesNoDirect.add(d.source));
              days90Data.data
                .filter((d) => d.source !== 'Direct')
                .forEach((d) => allSourcesNoDirect.add(d.source));

              const labelsNoDirect = Array.from(allSourcesNoDirect);

              // Calculate totals for each period (excluding Direct)
              const todayTotalNoDirect = todayData.data
                .filter((d) => d.source !== 'Direct')
                .reduce((sum, d) => sum + d.visits, 0);
              const week7TotalNoDirect = week7Data.data
                .filter((d) => d.source !== 'Direct')
                .reduce((sum, d) => sum + d.visits, 0);
              const days30TotalNoDirect = days30Data.data
                .filter((d) => d.source !== 'Direct')
                .reduce((sum, d) => sum + d.visits, 0);
              const days90TotalNoDirect = days90Data.data
                .filter((d) => d.source !== 'Direct')
                .reduce((sum, d) => sum + d.visits, 0);

              // Create datasets for each period (converted to percentages)
              const todayValuesNoDirect = labelsNoDirect.map((source) => {
                const item = todayData.data.find(
                  (d) => d.source === source && d.source !== 'Direct'
                );
                const visits = item ? item.visits : 0;
                return todayTotalNoDirect > 0 ? (visits / todayTotalNoDirect) * 100 : 0;
              });

              const week7ValuesNoDirect = labelsNoDirect.map((source) => {
                const item = week7Data.data.find(
                  (d) => d.source === source && d.source !== 'Direct'
                );
                const visits = item ? item.visits : 0;
                return week7TotalNoDirect > 0 ? (visits / week7TotalNoDirect) * 100 : 0;
              });

              const days30ValuesNoDirect = labelsNoDirect.map((source) => {
                const item = days30Data.data.find(
                  (d) => d.source === source && d.source !== 'Direct'
                );
                const visits = item ? item.visits : 0;
                return days30TotalNoDirect > 0 ? (visits / days30TotalNoDirect) * 100 : 0;
              });

              const days90ValuesNoDirect = labelsNoDirect.map((source) => {
                const item = days90Data.data.find(
                  (d) => d.source === source && d.source !== 'Direct'
                );
                const visits = item ? item.visits : 0;
                return days90TotalNoDirect > 0 ? (visits / days90TotalNoDirect) * 100 : 0;
              });

              // Adjust canvas height based on number of data points
              const containerNoDirect = ctxNoDirect.parentElement;
              if (labelsNoDirect.length > 8) {
                const newHeight = Math.max(300, labelsNoDirect.length * 50);
                containerNoDirect.style.height = `${newHeight}px`;
                ctxNoDirect.style.height = `${newHeight}px`;
              } else {
                containerNoDirect.style.height = '300px';
              }

              // Guard: ensure Chart.js library is loaded before instantiation
              if (!window.Chart) {
                console.warn(
                  '[AdminDashboard] Chart.js library not loaded; skipping chart initialization'
                );
                return;
              }

              this.state.charts.trafficChartNoDirect = new Chart(ctxNoDirect, {
                type: 'bar',
                data: {
                  labels: labelsNoDirect,
                  datasets: [
                    {
                      label: 'Last 90 Days',
                      data: days90ValuesNoDirect,
                      backgroundColor: 'rgba(34, 197, 94, 0.8)',
                      borderColor: 'rgba(34, 197, 94, 1)',
                      borderWidth: 2,
                      total: days90TotalNoDirect,
                    },
                    {
                      label: 'Last 30 Days',
                      data: days30ValuesNoDirect,
                      backgroundColor: 'rgba(236, 72, 153, 0.8)',
                      borderColor: 'rgba(236, 72, 153, 1)',
                      borderWidth: 2,
                      total: days30TotalNoDirect,
                    },
                    {
                      label: 'Last 7 Days',
                      data: week7ValuesNoDirect,
                      backgroundColor: 'rgba(251, 146, 60, 0.8)',
                      borderColor: 'rgba(251, 146, 60, 1)',
                      borderWidth: 2,
                      total: week7TotalNoDirect,
                    },
                    {
                      label: 'Today',
                      data: todayValuesNoDirect,
                      backgroundColor: 'rgba(59, 130, 246, 0.8)',
                      borderColor: 'rgba(59, 130, 246, 1)',
                      borderWidth: 2,
                      total: todayTotalNoDirect,
                    },
                  ],
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: {
                      grid: {
                        color: 'rgba(255, 255, 255, 0.1)',
                      },
                      ticks: {
                        color: 'rgba(255, 255, 255, 0.9)',
                        font: {
                          weight: 'bold',
                          size: 12,
                        },
                      },
                    },
                    y: {
                      beginAtZero: true,
                      max: 100,
                      grid: {
                        color: 'rgba(255, 255, 255, 0.1)',
                      },
                      ticks: {
                        color: 'rgba(255, 255, 255, 0.9)',
                        font: {
                          weight: 'bold',
                          size: 12,
                        },
                        callback: function (value) {
                          return value + '%';
                        },
                      },
                      title: {
                        display: true,
                        text: 'Percentage (%)',
                        color: 'rgba(255, 255, 255, 0.9)',
                        font: {
                          weight: 'bold',
                          size: 13,
                        },
                      },
                    },
                  },
                  plugins: {
                    legend: {
                      display: true,
                      position: 'top',
                      labels: {
                        color: '#ffffff',
                        font: {
                          weight: 'bold',
                          size: 13,
                        },
                        padding: 15,
                        usePointStyle: true,
                        pointStyle: 'rectRounded',
                        generateLabels: function (chart) {
                          const datasets = chart.data.datasets;
                          return datasets.map((dataset, i) => ({
                            text: `${dataset.label} (${dataset.total ? dataset.total.toLocaleString() : 0} visits)`,
                            fillStyle: dataset.backgroundColor,
                            strokeStyle: dataset.borderColor,
                            lineWidth: dataset.borderWidth,
                            hidden: !chart.isDatasetVisible(i),
                            index: i,
                            datasetIndex: i,
                            fontColor: '#ffffff',
                          }));
                        },
                      },
                    },
                    datalabels: {
                      display: true,
                      color: '#ffffff',
                      font: {
                        weight: 'bold',
                        size: 11,
                      },
                      anchor: 'end',
                      align: 'end',
                      formatter: function (value, context) {
                        const dataset = context.chart.data.datasets[context.datasetIndex];
                        const total = dataset.total || 0;
                        const actualVisits = Math.round((value / 100) * total);
                        return actualVisits > 0 ? actualVisits.toLocaleString() : '';
                      },
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.9)',
                      titleColor: 'rgba(255, 255, 255, 1)',
                      bodyColor: 'rgba(255, 255, 255, 0.9)',
                      borderColor: 'rgba(99, 102, 241, 0.5)',
                      borderWidth: 1,
                      padding: 12,
                      titleFont: {
                        weight: 'bold',
                        size: 14,
                      },
                      bodyFont: {
                        weight: 'bold',
                        size: 13,
                      },
                      callbacks: {
                        label: function (context) {
                          const label = context.dataset.label || '';
                          const value = context.parsed.y || 0;
                          const total = context.dataset.total || 0;
                          return `${label}: ${value.toFixed(1)}% (${total} total visits)`;
                        },
                      },
                    },
                  },
                },
              });
            }
          }
        } else {
          // Single period view - show doughnut chart
          const endpointMap = {
            today: 'today-traffic-sources',
            '7days': '7day-traffic-sources',
            '30days': '30day-traffic-sources',
            '90days': '90day-traffic-sources',
          };

          const response = await fetch(
            `${this.config.apiProxyUrl}?endpoint=${endpointMap[period]}`
          );
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data.length > 0) {
              const labels = result.data.map((d) => d.source);
              const data = result.data.map((d) => d.visits);

              // Guard: ensure Chart.js library is loaded before instantiation
              if (!window.Chart) {
                console.warn(
                  '[AdminDashboard] Chart.js library not loaded; skipping chart initialization'
                );
                return;
              }

              this.state.charts.trafficChart = new Chart(ctx, {
                type: 'doughnut',
                data: {
                  labels,
                  datasets: [
                    {
                      data,
                      backgroundColor: labels.map((label, index) => {
                        // Comprehensive color palette for 25 distinct traffic sources (vibrant, high contrast)
                        const fallbackColors = [
                          '#8b5cf6',
                          '#f59e0b',
                          '#10b981',
                          '#ef4444',
                          '#3b82f6',
                          '#ec4899',
                          '#14b8a6',
                          '#f97316',
                          '#a855f7',
                          '#84cc16',
                          '#06b6d4',
                          '#f43f5e',
                          '#6366f1',
                          '#22c55e',
                          '#0ea5e9',
                          '#eab308',
                          '#f472b6',
                          '#2dd4bf',
                          '#fb923c',
                          '#c084fc',
                          '#4ade80',
                          '#fbbf24',
                          '#e879f9',
                          '#38bdf8',
                          '#94a3b8',
                        ];

                        // Email/Klaviyo: Distinctive purple-pink
                        if (label === 'Klaviyo' || label.includes('Klaviyo:')) {
                          return '#d84e89'; // Klaviyo brand purple-pink
                        }
                        // Social platforms: Individual brand colors (high contrast)
                        else if (label === 'Facebook') {
                          return '#1877f2'; // Facebook blue
                        } else if (label === 'Twitter' || label === 'X') {
                          return '#1da1f2'; // Twitter blue
                        } else if (label === 'Instagram') {
                          return '#e1306c'; // Instagram pink
                        } else if (label === 'TikTok') {
                          return '#25f4ee'; // TikTok cyan
                        } else if (label === 'Pinterest') {
                          return '#e60023'; // Pinterest red
                        } else if (label === 'YouTube') {
                          return '#ff0000'; // YouTube red
                        } else if (label === 'LinkedIn') {
                          return '#0077b5'; // LinkedIn blue
                        } else if (label === 'Reddit') {
                          return '#ff4500'; // Reddit orange
                        } else if (label === 'Snapchat') {
                          return '#fffc00'; // Snapchat yellow
                        } else if (label === 'WhatsApp') {
                          return '#25d366'; // WhatsApp green
                        }
                        // Search engines
                        else if (
                          label === 'Google' ||
                          label === 'Google Search' ||
                          label === 'Organic Search'
                        ) {
                          return '#34a853'; // Google green (organic)
                        } else if (label === 'Google Ads') {
                          return '#4285f4'; // Google Ads blue (paid)
                        } else if (label === 'Bing') {
                          return '#008373'; // Bing teal
                        } else if (label === 'Yahoo') {
                          return '#720e9e'; // Yahoo purple
                        } else if (label === 'DuckDuckGo') {
                          return '#de5833'; // DuckDuckGo orange
                        }
                        // AI Assistants
                        else if (
                          label.includes('AI:') ||
                          label.includes('ChatGPT') ||
                          label.includes('Claude') ||
                          label.includes('Gemini')
                        ) {
                          return '#a78bfa'; // Purple for AI
                        }
                        // Shopping & Marketplaces
                        else if (label === 'Amazon' || label.includes('Amazon')) {
                          return '#ff9900'; // Amazon orange
                        } else if (label === 'Etsy' || label.includes('Etsy')) {
                          return '#f56400'; // Etsy orange
                        } else if (label === 'eBay' || label.includes('eBay')) {
                          return '#e53238'; // eBay red
                        }
                        // Direct & Unknown
                        else if (label === 'Direct') {
                          return '#06b6d4'; // Cyan for direct
                        } else if (label === 'Unknown') {
                          return '#6b7280'; // Gray for unknown
                        }
                        // Fallback: Use palette with index-based assignment for other sources
                        else {
                          return fallbackColors[index % fallbackColors.length];
                        }
                      }),
                    },
                  ],
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: true,
                      position: 'bottom',
                      labels: {
                        color: '#ffffff',
                        font: {
                          weight: 'bold',
                          size: 13,
                        },
                        generateLabels: function (chart) {
                          const data = chart.data;
                          if (data.labels.length && data.datasets.length) {
                            const dataset = data.datasets[0];
                            const total = dataset.data.reduce((sum, val) => sum + val, 0);
                            return data.labels.map((label, i) => {
                              const value = dataset.data[i];
                              const percentage = ((value / total) * 100).toFixed(1);
                              return {
                                text: `${label}: ${value.toLocaleString()} visits (${percentage}%)`,
                                fillStyle: dataset.backgroundColor[i],
                                hidden: false,
                                index: i,
                                fontColor: '#ffffff',
                              };
                            });
                          }
                          return [];
                        },
                      },
                    },
                    datalabels: {
                      display: true,
                      color: '#ffffff',
                      font: {
                        weight: 'bold',
                        size: 12,
                      },
                      formatter: function (value, _context) {
                        return value > 0 ? value.toLocaleString() : '';
                      },
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const label = context.label || '';
                          const value = context.parsed || 0;
                          const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
                          const percentage = ((value / total) * 100).toFixed(1);
                          return `${label}: ${value} visits (${percentage}%)`;
                        },
                      },
                      titleFont: {
                        weight: 'bold',
                        size: 14,
                      },
                      bodyFont: {
                        weight: 'bold',
                        size: 13,
                      },
                    },
                  },
                },
              });

              // Create second chart excluding Direct traffic
              const ctxNoDirect = document.getElementById('traffic-sources-chart-no-direct');
              if (ctxNoDirect) {
                // Destroy existing chart if exists
                if (this.state.charts.trafficChartNoDirect) {
                  this.state.charts.trafficChartNoDirect.destroy();
                }

                // Filter out Direct traffic
                const filteredData = result.data.filter((d) => d.source !== 'Direct');
                if (filteredData.length > 0) {
                  const labelsNoDirect = filteredData.map((d) => d.source);
                  const dataNoDirect = filteredData.map((d) => d.visits);

                  // Guard: ensure Chart.js library is loaded before instantiation
                  if (!window.Chart) {
                    console.warn(
                      '[AdminDashboard] Chart.js library not loaded; skipping chart initialization'
                    );
                    return;
                  }

                  this.state.charts.trafficChartNoDirect = new Chart(ctxNoDirect, {
                    type: 'doughnut',
                    data: {
                      labels: labelsNoDirect,
                      datasets: [
                        {
                          data: dataNoDirect,
                          backgroundColor: labelsNoDirect.map((label, index) => {
                            // Use same color mapping as main chart
                            const fallbackColors = [
                              '#8b5cf6',
                              '#f59e0b',
                              '#10b981',
                              '#ef4444',
                              '#3b82f6',
                              '#ec4899',
                              '#14b8a6',
                              '#f97316',
                              '#a855f7',
                              '#84cc16',
                              '#06b6d4',
                              '#f43f5e',
                              '#6366f1',
                              '#22c55e',
                              '#0ea5e9',
                              '#eab308',
                              '#f472b6',
                              '#2dd4bf',
                              '#fb923c',
                              '#c084fc',
                              '#4ade80',
                              '#fbbf24',
                              '#e879f9',
                              '#38bdf8',
                              '#94a3b8',
                            ];

                            if (label === 'Klaviyo' || label.includes('Klaviyo:')) return '#d84e89';
                            else if (label === 'Facebook') return '#1877f2';
                            else if (label === 'Twitter' || label === 'X') return '#1da1f2';
                            else if (label === 'Instagram') return '#e1306c';
                            else if (label === 'TikTok') return '#25f4ee';
                            else if (label === 'Pinterest') return '#e60023';
                            else if (label === 'YouTube') return '#ff0000';
                            else if (label === 'LinkedIn') return '#0077b5';
                            else if (label === 'Reddit') return '#ff4500';
                            else if (label === 'Snapchat') return '#fffc00';
                            else if (label === 'WhatsApp') return '#25d366';
                            else if (
                              label === 'Google' ||
                              label === 'Google Search' ||
                              label === 'Organic Search'
                            ) {
                              return '#34a853';
                            } else if (label === 'Google Ads') return '#4285f4';
                            else if (label === 'Bing') return '#008373';
                            else if (label === 'Yahoo') return '#720e9e';
                            else if (label === 'DuckDuckGo') return '#de5833';
                            else if (
                              label.includes('AI:') ||
                              label.includes('ChatGPT') ||
                              label.includes('Claude') ||
                              label.includes('Gemini')
                            ) {
                              return '#a78bfa';
                            } else if (label === 'Amazon' || label.includes('Amazon')) {
                              return '#ff9900';
                            } else if (label === 'Etsy' || label.includes('Etsy')) return '#f56400';
                            else if (label === 'eBay' || label.includes('eBay')) return '#e53238';
                            else if (label === 'Unknown') return '#6b7280';
                            else return fallbackColors[index % fallbackColors.length];
                          }),
                        },
                      ],
                    },
                    options: {
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'bottom',
                          labels: {
                            color: '#ffffff',
                            font: {
                              weight: 'bold',
                              size: 13,
                            },
                            generateLabels: function (chart) {
                              const data = chart.data;
                              if (data.labels.length && data.datasets.length) {
                                const dataset = data.datasets[0];
                                const total = dataset.data.reduce((sum, val) => sum + val, 0);
                                return data.labels.map((label, i) => {
                                  const value = dataset.data[i];
                                  const percentage = ((value / total) * 100).toFixed(1);
                                  return {
                                    text: `${label}: ${value.toLocaleString()} visits (${percentage}%)`,
                                    fillStyle: dataset.backgroundColor[i],
                                    hidden: false,
                                    index: i,
                                    fontColor: '#ffffff',
                                  };
                                });
                              }
                              return [];
                            },
                          },
                        },
                        datalabels: {
                          display: true,
                          color: '#ffffff',
                          font: {
                            weight: 'bold',
                            size: 12,
                          },
                          formatter: function (value, _context) {
                            return value > 0 ? value.toLocaleString() : '';
                          },
                        },
                        tooltip: {
                          callbacks: {
                            label: function (context) {
                              const label = context.label || '';
                              const value = context.parsed || 0;
                              const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
                              const percentage = ((value / total) * 100).toFixed(1);
                              return `${label}: ${value} visits (${percentage}%)`;
                            },
                          },
                          titleFont: {
                            weight: 'bold',
                            size: 14,
                          },
                          bodyFont: {
                            weight: 'bold',
                            size: 13,
                          },
                        },
                      },
                    },
                  });
                }
              }
            }
          }
        }
      } catch (error) {
        AdminDashboardCore.logError('[Admin] updateTrafficSourcesChart error:', error);
      } finally {
        this.hideChartLoading('traffic-sources-chart');
        this.hideChartLoading('traffic-sources-chart-no-direct');
      }
    },

    // Show referral details modal
    showReferralModal: async function () {
      const modal = document.getElementById('referral-modal');
      const modalContent = document.getElementById('referral-modal-content');

      // Show modal with loading state
      modal.style.display = 'flex';
      safeSetHTML(
        modalContent,
        `
        <div class="loading-container">
          <div class="loading-spinner large"></div>
          <div class="loading-text">Loading referral data...</div>
        </div>
      `
      );

      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=referral-details`);
        const result = await response.json();

        if (result.success && result.data) {
          const { referrals, totalReferralSources, totalOrders, totalRevenue } = result.data;

          if (referrals.length === 0) {
            safeSetHTML(
              modalContent,
              `
              <div class="empty-state">
                <p>No referral traffic found in the last 90 days.</p>
                <p class="empty-hint">Referral traffic comes from websites other than major platforms (Google, Facebook, Instagram, Pinterest, TikTok, Twitter, YouTube).</p>
              </div>
            `
            );
            return;
          }

          // Store data for sorting
          this.referralData = referrals;
          this.referralSort = { column: 'orderCount', direction: 'desc' };

          // Build summary and table
          const html = `
            <div class="referral-summary">
              <div class="summary-stat">
                <span class="summary-label">Total Referral Sources</span>
                <span class="summary-value">${totalReferralSources}</span>
              </div>
              <div class="summary-stat">
                <span class="summary-label">Total Orders</span>
                <span class="summary-value">${totalOrders}</span>
              </div>
              <div class="summary-stat">
                <span class="summary-label">Total Revenue</span>
                <span class="summary-value">$${totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </div>
            <div id="referral-table-wrapper"></div>
          `;

          safeSetHTML(modalContent, html);
          this.renderReferralTable();
        } else {
          throw new Error(result.error || 'Failed to load referral data');
        }
      } catch (error) {
        safeSetHTML(
          modalContent,
          `
          <div class="error-state">
            <p>❌ Failed to load referral data</p>
            <p class="error-message">${error.message}</p>
          </div>
        `
        );
      }
    },

    // Render referral table with current sort
    renderReferralTable: function () {
      const wrapper = document.getElementById('referral-table-wrapper');
      if (!wrapper || !this.referralData) return;

      // Sort data
      const sortedData = [...this.referralData].sort((a, b) => {
        let aVal = a[this.referralSort.column];
        let bVal = b[this.referralSort.column];

        // Handle dates
        if (
          this.referralSort.column === 'firstOrderDate' ||
          this.referralSort.column === 'lastOrderDate'
        ) {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        }

        if (this.referralSort.direction === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });

      // Build table HTML
      let tableHTML = `
        <div class="referral-table-container">
          <table class="referral-table">
            <thead>
              <tr>
                <th>#</th>
                <th class="sortable" data-column="url">Referral Source <span class="sort-indicator">${this.referralSort.column === 'url' ? (this.referralSort.direction === 'asc' ? '▲' : '▼') : ''}</span></th>
                <th class="sortable" data-column="orderCount">Orders <span class="sort-indicator">${this.referralSort.column === 'orderCount' ? (this.referralSort.direction === 'asc' ? '▲' : '▼') : ''}</span></th>
                <th class="sortable" data-column="totalRevenue">Revenue <span class="sort-indicator">${this.referralSort.column === 'totalRevenue' ? (this.referralSort.direction === 'asc' ? '▲' : '▼') : ''}</span></th>
                <th class="sortable" data-column="avgOrderValue">Avg Order <span class="sort-indicator">${this.referralSort.column === 'avgOrderValue' ? (this.referralSort.direction === 'asc' ? '▲' : '▼') : ''}</span></th>
                <th class="sortable" data-column="lastOrderDate">Last Order <span class="sort-indicator">${this.referralSort.column === 'lastOrderDate' ? (this.referralSort.direction === 'asc' ? '▲' : '▼') : ''}</span></th>
                <th class="sortable" data-column="firstOrderDate">First Order <span class="sort-indicator">${this.referralSort.column === 'firstOrderDate' ? (this.referralSort.direction === 'asc' ? '▲' : '▼') : ''}</span></th>
              </tr>
            </thead>
            <tbody>
      `;

      sortedData.forEach((ref, index) => {
        const firstDate = this.formatDate(ref.firstOrderDate);
        const lastDate = this.formatDate(ref.lastOrderDate);
        const displayUrl = ref.url.replace(/^https?:\/\//, ''); // Strip protocol

        tableHTML += `
          <tr>
            <td class="rank-cell">${index + 1}</td>
            <td class="url-cell" title="${this.escapeHtml(ref.url)}">${this.escapeHtml(displayUrl)}</td>
            <td class="number-cell">${ref.orderCount}</td>
            <td class="revenue-cell">$${ref.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
            <td class="number-cell">$${ref.avgOrderValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
            <td class="date-cell">${lastDate}</td>
            <td class="date-cell">${firstDate}</td>
          </tr>
        `;
      });

      tableHTML += `
            </tbody>
          </table>
        </div>
      `;

      safeSetHTML(wrapper, tableHTML);

      // Add click handlers to sortable headers
      const sortableHeaders = wrapper.querySelectorAll('th.sortable');
      sortableHeaders.forEach((header) => {
        header.addEventListener('click', () => {
          const column = header.getAttribute('data-column');

          // Toggle direction if same column, otherwise default to desc
          if (this.referralSort.column === column) {
            this.referralSort.direction = this.referralSort.direction === 'asc' ? 'desc' : 'asc';
          } else {
            this.referralSort.column = column;
            this.referralSort.direction =
              column === 'url' || column === 'firstOrderDate' ? 'asc' : 'desc';
          }

          this.renderReferralTable();
        });
      });
    },

    // Revenue chart
    initRevenueChart: async function () {
      await this.updateRevenueChart('7d');
    },

    // Update 24-hour revenue display (always shows last 24 hours)
    update24HourRevenue: async function () {
      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=24h-revenue`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data.length > 0) {
            const total24h = result.data.reduce((sum, d) => sum + parseFloat(d.revenue), 0);
            document.getElementById('revenue-growth').textContent =
              `$${this.formatNumber(total24h)}`;
          }
        }
      } catch (_error) {}
    },

    // Update revenue chart with different time periods
    updateRevenueChart: async function (period, retryCount = 0) {
      this.showChartLoading('revenue-chart');

      try {
        const endpointMap = {
          '24h': '24h-revenue',
          '7d': '7day-revenue',
          '30d': '30day-revenue',
        };

        const endpoint = endpointMap[period];

        // Add cache-busting timestamp to ensure fresh data
        const timestamp = Date.now();
        const url = `${this.config.apiProxyUrl}?endpoint=${endpoint}&_cb=${timestamp}`;
        const response = await fetch(url);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data.length > 0) {
            // Validate data quality - check if data has meaningful values
            const totalRevenue = result.data.reduce(
              (sum, d) => sum + parseFloat(d.revenue || 0),
              0
            );
            const _nonZeroCount = result.data.filter((d) => parseFloat(d.revenue || 0) > 0).length;

            const hasValidData =
              totalRevenue > 0 ||
              result.data.some(
                (d) =>
                  parseFloat(d.renewals || 0) > 0 ||
                  parseFloat(d.retail || 0) > 0 ||
                  parseFloat(d.online || 0) > 0
              );

            // Self-healing: If data is all zeros and we haven't retried too many times, retry
            if (!hasValidData && retryCount < 3) {
              this.hideChartLoading('revenue-chart');

              // Exponential backoff: 1s, 2s, 4s
              await new Promise((resolve) => {
                setTimeout(resolve, Math.pow(2, retryCount) * 1000);
              });

              return this.updateRevenueChart(period, retryCount + 1);
            }

            // If still no valid data after retries, render anyway
            const ctx = document.getElementById('revenue-chart');

            // Destroy existing chart if exists
            if (this.state.charts.revenueChart) {
              this.state.charts.revenueChart.destroy();
            }

            // Format labels based on period
            const labels = result.data.map((d) => {
              if (period === '24h') {
                // Extract hour from ISO timestamp like "2025-10-06T14"
                const hour = parseInt(d.date.split('T')[1] || d.date.split('-')[3], 10);
                const ampm = hour >= 12 ? 'PM' : 'AM';
                const displayHour = hour % 12 || 12;
                return `${displayHour}${ampm}`;
              } else {
                // Parse date in local timezone to avoid UTC offset issues
                const [year, month, day] = d.date.split('-');
                const date = new Date(year, month - 1, day);
                const dayNum = date.getDate();
                const monthName = date.toLocaleDateString('en-US', { month: 'short' });
                return `${dayNum} ${monthName}`;
              }
            });
            const data = result.data.map((d) => parseFloat(d.revenue));
            const renewalsData = result.data.map((d) => parseFloat(d.renewals || 0));
            const retailData = result.data.map((d) => parseFloat(d.retail || 0));
            const onlineData = result.data.map((d) => parseFloat(d.online || 0));

            // Calculate cumulative revenue
            const cumulativeData = [];
            let runningTotal = 0;
            data.forEach((value) => {
              runningTotal += value;
              cumulativeData.push(runningTotal);
            });

            // Calculate simple moving average (SMA) of cumulative revenue
            // 24h: 3-hour SMA, 7d: 3-day SMA, 30d: 14-day SMA (2 weeks)
            const smaWindow = period === '24h' ? 3 : period === '7d' ? 3 : 14;
            const smaData = cumulativeData.map((_, index) => {
              if (index < smaWindow - 1) return null;
              const window = cumulativeData.slice(index - smaWindow + 1, index + 1);
              const sum = window.reduce((acc, val) => acc + val, 0);
              return sum / smaWindow;
            });

            const total = data.reduce((sum, val) => sum + val, 0);
            const totalRenewals = renewalsData.reduce((sum, val) => sum + val, 0);
            const totalRetail = retailData.reduce((sum, val) => sum + val, 0);
            const totalOnline = onlineData.reduce((sum, val) => sum + val, 0);

            // Calculate daily average based on period
            const daysInPeriod = period === '24h' ? 1 : period === '7d' ? 7 : 30;
            const dailyAverage = total / daysInPeriod;

            // Calculate percentages
            const renewalsPercent = total > 0 ? ((totalRenewals / total) * 100).toFixed(1) : 0;
            const retailPercent = total > 0 ? ((totalRetail / total) * 100).toFixed(1) : 0;
            const onlinePercent = total > 0 ? ((totalOnline / total) * 100).toFixed(1) : 0;

            document.getElementById('total-revenue').textContent = `$${this.formatNumber(total)}`;
            safeSetHTML(
              document.getElementById('renewals-revenue'),
              `$${this.formatNumber(totalRenewals)} <span class="revenue-percent">(${renewalsPercent}%)</span>`
            );
            safeSetHTML(
              document.getElementById('retail-revenue'),
              `$${this.formatNumber(totalRetail)} <span class="revenue-percent">(${retailPercent}%)</span>`
            );
            safeSetHTML(
              document.getElementById('online-revenue'),
              `$${this.formatNumber(totalOnline)} <span class="revenue-percent">(${onlinePercent}%)</span>`
            );
            document.getElementById('daily-avg-revenue').textContent =
              `$${this.formatNumber(dailyAverage)}`;

            // Guard: ensure Chart.js library is loaded before instantiation
            if (!window.Chart) {
              console.warn(
                '[AdminDashboard] Chart.js library not loaded; skipping chart initialization'
              );
              return;
            }

            this.state.charts.revenueChart = new Chart(ctx, {
              type: 'line',
              data: {
                labels,
                datasets: [
                  {
                    label: 'Total Revenue',
                    data,
                    borderColor: '#10b981',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    tension: 0.4,
                    fill: true,
                    borderWidth: 2,
                  },
                  {
                    label: 'Renewals',
                    data: renewalsData,
                    borderColor: '#3b82f6',
                    backgroundColor: 'transparent',
                    tension: 0.4,
                    borderWidth: 2,
                    pointRadius: 3,
                    pointHoverRadius: 5,
                  },
                  {
                    label: 'Retail',
                    data: retailData,
                    borderColor: '#a855f7',
                    backgroundColor: 'transparent',
                    tension: 0.4,
                    borderWidth: 2,
                    pointRadius: 3,
                    pointHoverRadius: 5,
                  },
                  {
                    label: 'Online Sales',
                    data: onlineData,
                    borderColor: '#14b8a6',
                    backgroundColor: 'transparent',
                    tension: 0.4,
                    borderWidth: 2,
                    pointRadius: 3,
                    pointHoverRadius: 5,
                  },
                  {
                    label: 'Cumulative Revenue',
                    data: cumulativeData,
                    borderColor: '#f59e0b',
                    backgroundColor: 'rgba(245, 158, 11, 0.05)',
                    tension: 0.4,
                    borderWidth: 3,
                    borderDash: [8, 4],
                    pointRadius: 0,
                    pointHoverRadius: 6,
                    fill: false,
                    yAxisID: 'y1',
                  },
                  {
                    label: `${smaWindow}-Period SMA (Cumulative)`,
                    data: smaData,
                    borderColor: '#ef4444',
                    backgroundColor: 'transparent',
                    tension: 0.1,
                    borderWidth: 2,
                    borderDash: [5, 5],
                    pointRadius: 0,
                    pointHoverRadius: 5,
                    fill: false,
                    spanGaps: true,
                  },
                ],
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                  mode: 'index',
                  intersect: false,
                },
                plugins: {
                  legend: {
                    display: true,
                    position: 'top',
                    align: 'end',
                    labels: {
                      color: 'rgba(255, 255, 255, 0.9)',
                      font: { size: 12, weight: '500' },
                      padding: 15,
                      usePointStyle: true,
                      pointStyle: 'line',
                    },
                  },
                  tooltip: {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    titleColor: 'white',
                    bodyColor: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    borderWidth: 1,
                    callbacks: {
                      label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) {
                          label += ': ';
                        }
                        if (context.parsed.y !== null) {
                          label +=
                            '$' + context.parsed.y.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                        }
                        return label;
                      },
                    },
                  },
                },
                scales: {
                  y: {
                    type: 'linear',
                    display: true,
                    position: 'left',
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Period Revenue',
                      color: 'rgba(255, 255, 255, 0.7)',
                      font: { size: 11, weight: '500' },
                    },
                    ticks: {
                      color: 'rgba(255,255,255,0.9)',
                      font: { size: 12, weight: '500' },
                      callback: function (value) {
                        return '$' + value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                      },
                    },
                    grid: { color: 'rgba(255,255,255,0.15)' },
                  },
                  y1: {
                    type: 'linear',
                    display: true,
                    position: 'right',
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Cumulative Total',
                      color: 'rgba(245, 158, 11, 0.9)',
                      font: { size: 11, weight: '500' },
                    },
                    ticks: {
                      color: 'rgba(245, 158, 11, 0.9)',
                      font: { size: 12, weight: '500' },
                      callback: function (value) {
                        return '$' + value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                      },
                    },
                    grid: {
                      drawOnChartArea: false,
                    },
                  },
                  x: {
                    ticks: {
                      color: 'rgba(255,255,255,0.9)',
                      font: { size: 11, weight: '500' },
                      maxRotation: 0,
                      autoSkip: true,
                      maxTicksLimit: period === '24h' ? 12 : 10,
                    },
                    grid: { color: 'rgba(255,255,255,0.1)' },
                  },
                },
              },
            });
          }
        } else if (retryCount < 3) {
          // Self-healing: Retry on failed response
          this.hideChartLoading('revenue-chart');

          await new Promise((resolve) => {
            setTimeout(resolve, Math.pow(2, retryCount) * 1000);
          });
          return this.updateRevenueChart(period, retryCount + 1);
        }
      } catch (_error) {
        // Self-healing: Retry on error
        if (retryCount < 3) {
          this.hideChartLoading('revenue-chart');

          await new Promise((resolve) => {
            setTimeout(resolve, Math.pow(2, retryCount) * 1000);
          });
          return this.updateRevenueChart(period, retryCount + 1);
        }
      } finally {
        this.hideChartLoading('revenue-chart');
      }
    },

    // Bot traffic chart
    initBotChart: function (retryCount = 0) {
      const ctx = document.getElementById('bot-chart');
      if (!ctx) return;

      // If chart already exists, don't retry initialization
      if (this.state.charts.botChart) return;

      // Try to get bot data from API first (from live-visitors endpoint)
      const apiBotsDetected = this.state.liveVisitorBots || 0;

      // Get bot data from AIAgentTracker (detailed breakdown)
      let aiVisits = 0;
      let crawlerVisits = 0;
      let blockedAttempts = 0;
      let botStats = null;

      if (window.AIAgentTracker) {
        const stats = AIAgentTracker.getStats();
        aiVisits = Object.values(stats.by_agent || {}).reduce(
          (sum, agent) => sum + (agent.visits || 0),
          0
        );
        crawlerVisits = stats.search_crawlers?.total_visits || 0;
        blockedAttempts = stats.blocked_bots?.total_attempts || 0;
        botStats = stats;
      }

      // Use API data if available, otherwise use AIAgentTracker data
      const displayBotCount = apiBotsDetected > 0 ? apiBotsDetected : crawlerVisits;

      // Update bot metrics
      document.getElementById('search-bots').textContent =
        `${displayBotCount} visit${displayBotCount !== 1 ? 's' : ''}`;
      document.getElementById('blocked-bots').textContent =
        `${blockedAttempts} attempt${blockedAttempts !== 1 ? 's' : ''}`;

      // Create simple bar chart showing bot types
      // Use AIAgentTracker data for breakdown if available, otherwise show API total
      const chartData = botStats
        ? [aiVisits, crawlerVisits, blockedAttempts]
        : [0, displayBotCount, blockedAttempts];

      // Guard: ensure Chart.js library is loaded before instantiation
      if (!window.Chart) {
        console.warn('[AdminDashboard] Chart.js library not loaded; skipping chart initialization');
        return;
      }

      this.state.charts.botChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['AI Shopping Bots', 'Search Crawlers', 'Blocked'],
          datasets: [
            {
              data: chartData,
              backgroundColor: [
                'rgba(99, 102, 241, 0.7)',
                'rgba(139, 92, 246, 0.7)',
                'rgba(239, 68, 68, 0.7)',
              ],
              borderColor: [
                'rgba(99, 102, 241, 1)',
                'rgba(139, 92, 246, 1)',
                'rgba(239, 68, 68, 1)',
              ],
              borderWidth: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: 'Bot Views & Attempts',
              color: 'rgba(255,255,255,0.9)',
              font: {
                size: 14,
                weight: 'bold',
              },
              padding: {
                top: 5,
                bottom: 10,
              },
            },
            legend: {
              display: true,
              position: 'bottom',
              labels: {
                color: 'rgba(255,255,255,0.9)',
                font: { size: 11 },
                padding: 10,
              },
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  const label = context.label || '';
                  const value = context.parsed || 0;
                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                  return `${label}: ${value} (${percentage}%)`;
                },
                afterLabel: function (context) {
                  const label = context.label || '';
                  const lines = [];

                  // Show individual bot names based on category
                  if (label === 'AI Shopping Bots') {
                    const agents = botStats.by_agent || {};
                    Object.entries(agents).forEach(([name, data]) => {
                      if (data.visits > 0) {
                        lines.push(
                          `  • ${name}: ${data.visits} visit${data.visits !== 1 ? 's' : ''}`
                        );
                      }
                    });
                    if (lines.length === 0) {
                      lines.push('  (No AI bot visits yet)');
                    }
                  } else if (label === 'Search Crawlers') {
                    const crawlers = botStats.search_crawlers?.by_crawler || {};
                    Object.entries(crawlers).forEach(([name, count]) => {
                      if (count > 0) {
                        lines.push(`  • ${name}: ${count} visit${count !== 1 ? 's' : ''}`);
                      }
                    });
                    if (lines.length === 0) {
                      lines.push('  (No crawler visits yet)');
                    }
                  } else if (label === 'Blocked') {
                    const blocked = botStats.blocked_bots?.by_bot || {};
                    Object.entries(blocked).forEach(([name, data]) => {
                      if (data.count > 0) {
                        lines.push(
                          `  • ${name}: ${data.count} attempt${data.count !== 1 ? 's' : ''}`
                        );
                      }
                    });
                    if (lines.length === 0) {
                      lines.push('  (No blocked bots yet)');
                    }
                  }

                  return lines;
                },
              },
            },
            datalabels: {
              color: '#ffffff',
              font: {
                size: 16,
                weight: 'bold',
              },
              textAlign: 'center',
              anchor: 'center',
              align: 'center',
              offset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              borderRadius: 4,
              padding: 6,
              formatter: (value, context) => {
                if (value === 0) return '';
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                return `${value}\n${percentage}%`;
              },
              display: function (context) {
                return context.dataset.data[context.dataIndex] > 0;
              },
            },
          },
        },
      });

      // If we have API data but no AIAgentTracker, still show the chart with API data
      if (apiBotsDetected > 0 && !window.AIAgentTracker) {
        const parent = ctx.parentElement;
        if (!this.state.charts.botChart) {
          safeSetHTML(
            parent,
            '<p style="color: rgba(255,255,255,0.6); padding: 40px; text-align: center; font-size: 14px;">Displaying bot data from Google Analytics. Install AI Agent Tracker for detailed breakdown.</p>'
          );
        }
      }

      // Only retry if we have no data from either source
      if (apiBotsDetected === 0 && !window.AIAgentTracker && retryCount < 5) {
        const parent = ctx.parentElement;
        safeSetHTML(
          parent,
          '<p style="color: rgba(255,255,255,0.6); padding: 40px; text-align: center; font-size: 14px;">Bot tracking initializing...</p>'
        );

        // Retry after a short delay
        setTimeout(() => {
          this.initBotChart(retryCount + 1);
        }, 900);
      } else if (apiBotsDetected === 0 && !window.AIAgentTracker && retryCount >= 5) {
        // After 5 retries with no data, show message
        const parent = ctx.parentElement;
        safeSetHTML(
          parent,
          '<p style="color: rgba(255,255,255,0.6); padding: 40px; text-align: center; font-size: 14px;">No bot data available. Check GA4 configuration.</p>'
        );
      }
    },

    // Update last update time
    // Proxy time utilities to core module (implementations moved to core)
    updateLastUpdateTime() {
      return this.core.updateLastUpdateTime();
    },

    isDataStale() {
      return this.core.isDataStale();
    },

    // Setup auto-refresh when page becomes visible after being stale
    setupStaleDataRefresh: function () {
      const handleVisibilityChange = () => {
        if (!document.hidden && this.isDataStale()) {
          this.config.cacheBuster = Date.now();
          this.loadData();
          this.showToast('🔄 Dashboard refreshed (data was stale)');
        }
      };

      const handleRefreshIfStale = () => {
        if (this.isDataStale()) {
          this.config.cacheBuster = Date.now();
          this.loadData();
          this.showToast('🔄 Dashboard refreshed (data was stale)');
        }
      };

      // Listen for page show (critical for detecting wake from sleep/hibernation)
      // This event fires when page is restored from bfcache (back-forward cache)
      // which includes after system sleep, even if tab was visible the whole time
      const handlePageShow = (event) => {
        // event.persisted indicates the page was restored from bfcache
        if (event.persisted || this.isDataStale()) {
          this.config.cacheBuster = Date.now();
          this.loadData();
          this.showToast('🔄 Dashboard refreshed after page restoration');
        }
      };

      // Listen for visibility change (tab switching)
      document.addEventListener('visibilitychange', handleVisibilityChange);
      this.core.registerListener(document, 'visibilitychange', handleVisibilityChange);

      // Listen for window focus (app/browser becoming active)
      window.addEventListener('focus', handleRefreshIfStale);
      this.core.registerListener(window, 'focus', handleRefreshIfStale);

      // Listen for page show - see handlePageShow definition above
      window.addEventListener('pageshow', handlePageShow);
      this.core.registerListener(window, 'pageshow', handlePageShow);

      // online/offline handling is now in setupConnectionMonitoring() which is
      // called from init().  The legacy 'online' handler is intentionally removed
      // here to avoid double-firing a loadData() call; setupConnectionMonitoring
      // owns the full lifecycle.
    },

    // ---------------------------------------------------------------------------
    // Connection monitoring — online/offline events + Network Information API
    // ---------------------------------------------------------------------------

    /**
     * Set up all connection-state event listeners.
     *
     * Responsibilities:
     *   - offline event: pause auto-refresh, show offline banner
     *   - online  event: clear offline state, hide banner, trigger fresh load
     *   - Network Information API change event: detect 2g/slow-2g and show
     *     slow-connection advisory banner; disable auto-refresh on very slow
     *     connections
     *
     * Also performs the initial Network Information API probe so slow connections
     * are detected even if no 'change' event fires before the first fetch.
     */
    setupConnectionMonitoring: function () {
      const health = this.core.state.connectionHealth;

      // --- offline ---
      const handleOffline = () => {
        health.isOffline = true;
        health.autoRefreshPaused = true;
        this.ui.showOfflineBanner();
        log('[Connection] Offline — pausing dashboard refresh');
      };
      window.addEventListener('offline', handleOffline);
      this.core.registerListener(window, 'offline', handleOffline);

      // --- online ---
      const handleOnline = () => {
        health.isOffline = false;
        // Only un-pause auto-refresh if we are not in connection-problem mode
        if (!health.connectionProblem) {
          health.autoRefreshPaused = false;
        }
        this.ui.hideOfflineBanner();
        // Always trigger a fresh load when connectivity returns
        this.config.cacheBuster = Date.now();
        this.loadData();
        log('[Connection] Online — refreshing dashboard');
      };
      window.addEventListener('online', handleOnline);
      this.core.registerListener(window, 'online', handleOnline);

      // --- Network Information API (initial probe + change listener) ---
      const applyNetworkInfo = () => {
        const isNowSlow = this.core.isSlowConnection();
        health.slowConnection = isNowSlow;
        if (isNowSlow) {
          const et = this.core.getEffectiveConnectionType();
          this.ui.showSlowConnectionWarning(et);
          // Disable auto-refresh on extremely slow connections to avoid saturating
          // the link with polling requests.
          if (!health.isOffline && !health.connectionProblem) {
            health.autoRefreshPaused = true;
          }
          log('[Connection] Slow connection detected:', et);
        } else {
          this.ui.hideSlowConnectionWarning();
          // Re-enable auto-refresh if we left slow-connection mode and no other
          // pause reason is active.
          if (!health.isOffline && !health.connectionProblem) {
            health.autoRefreshPaused = false;
          }
        }
      };

      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (conn) {
        conn.addEventListener('change', applyNetworkInfo);
        this.core.registerListener(conn, 'change', applyNetworkInfo);
        // Probe immediately so the banner shows on first load if already on 2g
        applyNetworkInfo();
      }
    },

    /**
     * Record a successful fetch result against the connection health tracker.
     * Clears the consecutive-timeout counter and hides the problem banner if
     * we were previously in connection-problem mode.
     */
    recordFetchSuccess: function () {
      const wasProblem = this.core.resetConnectionHealth();
      if (wasProblem) {
        this.ui.hideConnectionProblemWarning();
        log('[ConnectionHealth] Connection recovered — resuming normal operation');
      }
    },

    /**
     * Record a TimeoutError result against the connection health tracker.
     * If the threshold (3 consecutive timeouts) is crossed, shows the
     * connection-problem banner and switches to manual-refresh-only mode.
     */
    recordFetchTimeout: function () {
      const crossedThreshold = this.core.recordTimeoutError();
      if (crossedThreshold) {
        this.ui.showConnectionProblemWarning();
        log('[ConnectionHealth] Switched to manual-refresh-only mode after 3 consecutive timeouts');
      }
    },

    // Initialize custom glassmorphic dropdowns to replace native selects
    initCustomDropdowns: function () {
      // Transform these selectors to custom glassmorphic dropdowns
      const selectorsToTransform = [
        'realtime-activity-period',
        'revenue-period',
        'campaign-revenue-period',
        'conversion-funnel-period',
        'profit-margin-period',
        'cac-channel-period',
        'roas-platform-period',
        'traffic-period',
        'search-performance-period',
        'top-products-period',
        'discount-codes-period',
        'discount-roi-period',
        'sellthrough-period',
        'enhanced-checkout-period',
        'abandoned-cart-period',
        'device-performance-period',
        'payment-methods-period',
        'shipping-analytics-period',
        'cart-recovery-period',
        'traffic-roi-period',
        'klaviyo-period',
      ];

      selectorsToTransform.forEach((selectorId) => {
        const select = document.getElementById(selectorId);
        if (!select || select.dataset.customized) return;

        // Verify this is actually a select element with options
        if (select.tagName !== 'SELECT' || !select.options || select.options.length === 0) return;

        // Mark as customized to prevent double initialization
        select.dataset.customized = 'true';

        // Create custom dropdown wrapper
        const wrapper = document.createElement('div');
        wrapper.className = 'custom-dropdown';

        // Create trigger button
        const trigger = document.createElement('div');
        trigger.className = 'custom-dropdown-trigger';

        // Get selected option text (safely handle -1 selectedIndex)
        const selectedIndex = select.selectedIndex >= 0 ? select.selectedIndex : 0;
        const selectedOption = select.options[selectedIndex];
        safeSetHTML(
          trigger,
          `
          <span class="custom-dropdown-text">${selectedOption ? selectedOption.textContent : 'Select'}</span>
          <svg class="custom-dropdown-arrow" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1.5L6 6.5L11 1.5" stroke="#a78bfa" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `
        );

        // Create dropdown menu
        const menu = document.createElement('div');
        menu.className = 'custom-dropdown-menu';

        // Populate options
        Array.from(select.options).forEach((option, index) => {
          const optionEl = document.createElement('div');
          optionEl.className = 'custom-dropdown-option';
          if (index === selectedIndex) {
            optionEl.classList.add('selected');
          }
          optionEl.dataset.value = option.value;
          optionEl.textContent = option.textContent;

          optionEl.addEventListener('click', () => {
            // Update native select
            select.value = option.value;

            // Trigger change event on native select
            select.dispatchEvent(new Event('change', { bubbles: true }));

            // Update trigger text
            wrapper.querySelector('.custom-dropdown-text').textContent = option.textContent;

            // Update selected state using DRY helper
            _setButtonGroupActive('.custom-dropdown-option', optionEl, false);

            // Close dropdown and reset parent z-index
            wrapper.classList.remove('open');
            const parent = wrapper.closest('.realtime-controls') || wrapper.closest('.glass-card');
            if (parent) parent.classList.remove('dropdown-open');
          });

          menu.appendChild(optionEl);
        });

        // Append elements
        wrapper.appendChild(trigger);
        wrapper.appendChild(menu);

        // Insert wrapper after select
        select.parentNode.insertBefore(wrapper, select.nextSibling);

        // Hide original select
        select.classList.add('hidden-select');

        // Helper to get parent element that needs z-index elevation
        const getDropdownParent = (el) => {
          return el.closest('.realtime-controls') || el.closest('.glass-card');
        };

        // Toggle dropdown on click
        trigger.addEventListener('click', (e) => {
          e.stopPropagation();
          const wasOpen = wrapper.classList.contains('open');

          // Close all other dropdowns and reset their parent z-index
          document.querySelectorAll('.custom-dropdown.open').forEach((d) => {
            d.classList.remove('open');
            const parent = getDropdownParent(d);
            if (parent) parent.classList.remove('dropdown-open');
          });

          if (!wasOpen) {
            wrapper.classList.add('open');
            // Elevate parent z-index when dropdown is open
            const parent = getDropdownParent(wrapper);
            if (parent) parent.classList.add('dropdown-open');
          }
        });

        // Close on outside click
        document.addEventListener('click', (e) => {
          if (!wrapper.contains(e.target)) {
            wrapper.classList.remove('open');
            const parent = getDropdownParent(wrapper);
            if (parent) parent.classList.remove('dropdown-open');
          }
        });

        // Close on escape key
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            wrapper.classList.remove('open');
            const parent = getDropdownParent(wrapper);
            if (parent) parent.classList.remove('dropdown-open');
          }
        });
      });
    },

    // Quick Actions handlers
    setupQuickActions: function () {
      // Refresh button
      const refreshBtn = document.getElementById('refresh-btn');
      if (refreshBtn) {
        refreshBtn.addEventListener('click', () => {
          // Force server cache refresh by appending timestamp
          this.config.cacheBuster = Date.now();
          this.loadData();
          this.showToast('✅ Dashboard refreshed');
        });
      }

      // Export CSV button
      const exportBtn = document.getElementById('export-btn');
      if (exportBtn) {
        exportBtn.addEventListener('click', () => this.exportToCSV());
      }

      // Keyboard shortcuts
      document.addEventListener('keydown', (e) => {
        // Only trigger if not typing in input
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

        switch (e.key.toLowerCase()) {
          case 'r':
            e.preventDefault();
            this.loadData();
            this.showToast('🔄 Refreshed');
            break;
          case 'e':
            e.preventDefault();
            this.exportToCSV();
            break;
        }
      });
    },

    // Export dashboard data to CSV
    exportToCSV: function () {
      try {
        const data = [];

        // Add headers
        data.push(['Category', 'Metric', 'Value']);

        // Core Metrics
        data.push([
          'Core',
          'Today Revenue',
          document.getElementById('today-revenue')?.textContent || '--',
        ]);
        data.push([
          'Core',
          'Order Count',
          document.getElementById('order-count')?.textContent || '--',
        ]);
        data.push(['Core', 'AOV', document.getElementById('aov')?.textContent || '--']);
        data.push([
          'Core',
          'Conversion Rate',
          document.getElementById('conversion-rate')?.textContent || '--',
        ]);
        data.push(['Core', 'Cart Rate', document.getElementById('cart-rate')?.textContent || '--']);
        data.push([
          'Core',
          'Checkout Rate',
          document.getElementById('checkout-rate')?.textContent || '--',
        ]);

        // Visitor Metrics
        data.push([
          'Visitors',
          'Live Visitors',
          document.getElementById('live-visitors')?.textContent || '--',
        ]);
        data.push([
          'Visitors',
          'Human Count',
          document.getElementById('human-count')?.textContent || '--',
        ]);
        data.push([
          'Visitors',
          'Bot Count',
          document.getElementById('bot-count')?.textContent || '--',
        ]);
        data.push([
          'Visitors',
          'Session Duration',
          document.getElementById('session-duration')?.textContent || '--',
        ]);
        data.push([
          'Visitors',
          'Device Breakdown',
          document.getElementById('device-breakdown')?.textContent || '--',
        ]);
        data.push([
          'Visitors',
          'Bounce Rate',
          document.getElementById('bounce-rate')?.textContent || '--',
        ]);

        // AI Agent Metrics
        data.push([
          'AI Agents',
          'AI Traffic',
          document.getElementById('ai-traffic')?.textContent || '--',
        ]);
        data.push([
          'AI Agents',
          'AI Conversions',
          document.getElementById('ai-conversions')?.textContent || '--',
        ]);
        data.push([
          'AI Agents',
          'AI Revenue',
          document.getElementById('ai-revenue')?.textContent || '--',
        ]);
        data.push([
          'AI Agents',
          'Good Bots',
          document.getElementById('good-bots')?.textContent || '--',
        ]);
        data.push([
          'AI Agents',
          'Search Bots',
          document.getElementById('search-bots')?.textContent || '--',
        ]);
        data.push([
          'AI Agents',
          'Blocked Bots',
          document.getElementById('blocked-bots')?.textContent || '--',
        ]);

        // Cart & Checkout Metrics
        data.push([
          'Cart/Checkout',
          'Cart Abandonment Rate',
          document.getElementById('abandonment-rate')?.textContent || '--',
        ]);
        data.push([
          'Cart/Checkout',
          'Potential Revenue Lost',
          document.getElementById('potential-revenue')?.textContent || '--',
        ]);
        data.push([
          'Cart/Checkout',
          'Cart Rate Value',
          document.getElementById('cart-rate-value')?.textContent || '--',
        ]);
        data.push([
          'Cart/Checkout',
          'Cart Rate Meta',
          document.getElementById('cart-rate-meta')?.textContent || '--',
        ]);
        data.push([
          'Cart/Checkout',
          'Checkout Drop Rate',
          document.getElementById('checkout-drop-value')?.textContent || '--',
        ]);
        data.push([
          'Cart/Checkout',
          'Checkout Drop Meta',
          document.getElementById('checkout-drop-meta')?.textContent || '--',
        ]);

        // Customer Metrics
        data.push([
          'Customers',
          'Return Customer Rate',
          document.getElementById('return-rate')?.textContent || '--',
        ]);
        data.push([
          'Customers',
          'Returning Count',
          document.getElementById('returning-count')?.textContent || '--',
        ]);
        data.push([
          'Customers',
          'Customer LTV',
          document.getElementById('ltv-value')?.textContent || '--',
        ]);
        data.push([
          'Customers',
          'LTV Customers',
          document.getElementById('ltv-customers')?.textContent || '--',
        ]);
        data.push([
          'Customers',
          'New Customer Percentage',
          document.getElementById('first-time-value')?.textContent || '--',
        ]);
        data.push([
          'Customers',
          'Returning Customer Percentage',
          document.getElementById('returning-value')?.textContent || '--',
        ]);

        // Revenue Metrics
        data.push([
          'Revenue',
          'Revenue Velocity Direction',
          document.getElementById('revenue-velocity-direction')?.textContent || '--',
        ]);
        data.push([
          'Revenue',
          'Revenue Velocity Percent',
          document.getElementById('revenue-velocity-percent')?.textContent || '--',
        ]);
        data.push([
          'Revenue',
          'Total Revenue',
          document.getElementById('total-revenue')?.textContent || '--',
        ]);
        data.push([
          'Revenue',
          'Renewals',
          document.getElementById('renewals-revenue')?.textContent || '--',
        ]);
        data.push([
          'Revenue',
          'Retail',
          document.getElementById('retail-revenue')?.textContent || '--',
        ]);
        data.push([
          'Revenue',
          'Online Sales',
          document.getElementById('online-revenue')?.textContent || '--',
        ]);
        data.push([
          'Revenue',
          'Daily Average',
          document.getElementById('daily-avg-revenue')?.textContent || '--',
        ]);
        data.push([
          'Revenue',
          'RPV (Revenue Per Visitor)',
          document.getElementById('rpv-value')?.textContent || '--',
        ]);
        data.push([
          'Revenue',
          'RPV Visitors',
          document.getElementById('rpv-visitors')?.textContent || '--',
        ]);

        // Conversion Funnel
        data.push([
          'Funnel',
          'Visitors',
          document.getElementById('funnel-visitors-count')?.textContent || '--',
        ]);
        data.push([
          'Funnel',
          'Add to Cart',
          document.getElementById('funnel-cart-count')?.textContent || '--',
        ]);
        data.push([
          'Funnel',
          'Cart Conversion Rate',
          document.getElementById('funnel-cart-rate')?.textContent || '--',
        ]);
        data.push([
          'Funnel',
          'Checkout',
          document.getElementById('funnel-checkout-count')?.textContent || '--',
        ]);
        data.push([
          'Funnel',
          'Checkout Conversion Rate',
          document.getElementById('funnel-checkout-rate')?.textContent || '--',
        ]);
        data.push([
          'Funnel',
          'Purchases',
          document.getElementById('funnel-purchase-count')?.textContent || '--',
        ]);
        data.push([
          'Funnel',
          'Purchase Conversion Rate',
          document.getElementById('funnel-purchase-rate')?.textContent || '--',
        ]);

        // Order Metrics
        data.push([
          'Orders',
          'Items Per Order',
          document.getElementById('items-per-order-value')?.textContent || '--',
        ]);
        data.push([
          'Orders',
          'Items Per Order Total',
          document.getElementById('items-per-order-total')?.textContent || '--',
        ]);
        data.push([
          'Orders',
          'Items Per Order (No Subs)',
          document.getElementById('items-per-order-no-subs-value')?.textContent || '--',
        ]);
        data.push([
          'Orders',
          'Items Per Order Total (No Subs)',
          document.getElementById('items-per-order-no-subs-total')?.textContent || '--',
        ]);

        // Performance Metrics
        data.push([
          'Performance',
          'Page Load Time',
          document.getElementById('page-load')?.textContent || '--',
        ]);
        data.push([
          'Performance',
          'Uptime',
          document.getElementById('uptime')?.textContent || '--',
        ]);

        // Social Media Metrics
        data.push([
          'Social Media',
          'Instagram Followers',
          document.getElementById('ig-followers')?.textContent || '--',
        ]);
        data.push([
          'Social Media',
          'Instagram Engagement',
          document.getElementById('ig-engagement')?.textContent || '--',
        ]);
        data.push([
          'Social Media',
          'Facebook Likes',
          document.getElementById('fb-likes')?.textContent || '--',
        ]);
        data.push([
          'Social Media',
          'Facebook Reach',
          document.getElementById('fb-reach')?.textContent || '--',
        ]);
        data.push([
          'Social Media',
          'Pinterest Followers',
          document.getElementById('pin-followers')?.textContent || '--',
        ]);
        data.push([
          'Social Media',
          'Pinterest Saves',
          document.getElementById('pin-saves')?.textContent || '--',
        ]);

        // Convert to CSV
        const csv = data.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');

        // Download
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dashboard-export-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        this.showToast('📥 Data exported successfully');
      } catch (_error) {
        this.showToast('❌ Export failed');
      }
    },

    // Show toast notification
    showToast: function (message) {
      const toast = document.createElement('div');
      toast.textContent = message;
      toast.style.cssText = `
        position: fixed; bottom: 24px; right: 24px; z-index: 10000;
        background: rgba(30, 41, 59, 0.95); backdrop-filter: blur(20px);
        border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 12px;
        padding: 16px 24px; color: white; font-weight: 600;
        animation: slideIn 0.3s ease;
      `;

      document.body.appendChild(toast);

      setTimeout(() => {
        toast.style.animation = 'slideOut 0.3s ease';
        setTimeout(() => toast.remove(), 300);
      }, 2000);
    },

    // Show large animated notification for new orders
    showNewOrdersNotification: function (count) {
      const notification = document.createElement('div');
      notification.id = 'new-orders-notification';
      notification.style.cssText = `
        position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
        z-index: 20000; pointer-events: none;
        font-size: 64px; font-weight: 900; color: white;
        text-shadow: 0 0 20px rgba(16, 185, 129, 0.8);
        animation: newOrdersPopIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      `;
      const pluralS = count !== 1 ? 's' : '';
      notification.textContent = `🎉 ${count} new order${pluralS}!`;

      document.body.appendChild(notification);

      setTimeout(() => {
        notification.style.animation = 'newOrdersFadeOut 0.5s ease';
        setTimeout(() => notification.remove(), 500);
      }, 2000);
    },

    // Attach hover/tap interactions to an order row. Tap-vs-scroll aware: on touch we open the
    // drawer only for a genuine tap (finger barely moved between touchstart and touchend), so
    // scrolling the list never opens a drawer. Desktop uses mouseenter/mouseleave. Shared by all
    // three feeds (online, retail, subscription) -- previously three copy-pasted blocks.
    attachOrderRowInteractions: function (item, order) {
      const TAP_MOVE_THRESHOLD_PX = 10;
      let touchHandled = false;
      let touchMoved = false;
      let touchStartX = 0;
      let touchStartY = 0;

      item.addEventListener(
        'touchstart',
        (e) => {
          const existingDrawer = document.getElementById('order-drawer');
          if (existingDrawer || this.state.drawerClosing) return;
          const touch = e.touches[0];
          touchStartX = touch.clientX;
          touchStartY = touch.clientY;
          touchMoved = false;
        },
        { passive: true }
      );

      item.addEventListener(
        'touchmove',
        (e) => {
          const touch = e.touches[0];
          if (
            Math.abs(touch.clientX - touchStartX) > TAP_MOVE_THRESHOLD_PX ||
            Math.abs(touch.clientY - touchStartY) > TAP_MOVE_THRESHOLD_PX
          ) {
            touchMoved = true;
          }
        },
        { passive: true }
      );

      item.addEventListener('touchend', (e) => {
        if (touchMoved) return; // was a scroll/drag, not a tap
        const existingDrawer = document.getElementById('order-drawer');
        if (existingDrawer || this.state.drawerClosing) return;
        touchHandled = true;
        e.preventDefault(); // suppress the synthetic click that follows a tap
        e.stopPropagation();
        this.showOrderTooltip(e, order);
        setTimeout(() => {
          touchHandled = false;
        }, 500);
      });

      item.addEventListener('click', (e) => {
        if (touchHandled) {
          e.preventDefault();
          e.stopPropagation();
        }
      });

      item.addEventListener('mouseenter', (e) => {
        if (touchHandled) return;
        if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;
        const existingDrawer = document.getElementById('order-drawer');
        if (existingDrawer || this.state.drawerClosing) return;
        e.stopPropagation();
        this.showOrderTooltip(e, order);
      });

      item.addEventListener('mouseleave', () => {
        if (touchHandled) return;
        this.hideOrderTooltip();
      });
    },

    // Show order tooltip on hover or drawer if would be cut off
    showOrderTooltip: function (event, order) {
      // Remove existing tooltip/drawer if any
      this.hideOrderTooltip();
      this.hideOrderDrawer();

      // Check if mobile/touch device (screen width < 769px OR touch event)
      const isMobile =
        window.innerWidth < 769 ||
        event.type === 'touchstart' ||
        event.type === 'touchend' ||
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0;

      // Format order date
      const orderDate = new Date(order.createdAt);
      const day = orderDate.getDate();
      const month = orderDate.toLocaleDateString('en-US', { month: 'short' });
      const year = orderDate.getFullYear();
      const time = orderDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      const formattedDate = `${day} ${month} ${year} ${time}`;

      // Build tooltip content with all available order details
      // Pre-escape all API-sourced values before HTML interpolation
      const esc = (v) => this.escapeHtml(v);
      const hasNote = (order.note && order.note.trim()) || (order.notes && order.notes.trim());
      const safeOrderNumber = esc(order.orderNumber);
      const safeCustomerName = esc(order.customerName || 'Unknown');
      const safeOrderCount = order.customerOrderCount
        ? parseInt(order.customerOrderCount, 10)
        : null;
      const safeFormattedDate = esc(formattedDate);
      let tooltipHTML = `
        <div class="tooltip-header">
          <h3>Order #${safeOrderNumber}</h3>
          <div class="tooltip-header-right">
            ${hasNote ? '<span class="tooltip-note-indicator">NOTE</span>' : ''}
            <span class="tooltip-amount">${this.formatCurrency(order.total)}</span>
          </div>
        </div>
        <div class="tooltip-row">
          <div class="tooltip-section tooltip-section-half">
            <div class="tooltip-label">👤 Customer</div>
            <div class="tooltip-value">${safeCustomerName}${safeOrderCount !== null ? (safeOrderCount === 1 ? ` <span style="color: #ff1493; font-weight: 700;">(${safeOrderCount})</span>` : ` (${safeOrderCount})`) : ''}</div>
          </div>
          <div class="tooltip-section tooltip-section-half">
            <div class="tooltip-label">📅 Order Date</div>
            <div class="tooltip-value">${safeFormattedDate}</div>
          </div>
        </div>
      `;

      // Traffic source display (icon + label) resolved from backend attribution.
      // Single source of truth shared with the mobile drawer; see
      // AdminDashboardCore.resolveOrderTrafficSource.
      const { icon: sourceIcon, label: sourceValueDisplay } =
        window.AdminDashboardCore.resolveOrderTrafficSource(order);

      tooltipHTML += `
        <div class="tooltip-section">
          <div class="tooltip-label">${sourceIcon} Traffic Source</div>
          <div class="tooltip-value">${esc(sourceValueDisplay)}</div>
        </div>
      `;

      // Add device and browser side by side if available
      if (order.device || order.browser) {
        const deviceVal = esc(order.device || '—');
        const browserVal = esc(order.browser || '—');
        tooltipHTML += `
          <div class="tooltip-row">
            <div class="tooltip-section tooltip-section-half">
              <div class="tooltip-label">📱 Device</div>
              <div class="tooltip-value">${deviceVal}</div>
            </div>
            <div class="tooltip-section tooltip-section-half">
              <div class="tooltip-label">🌐 Browser</div>
              <div class="tooltip-value">${browserVal}</div>
            </div>
          </div>
        `;
      }

      // Add line items if available (always full width)
      if (order.lineItems && order.lineItems.length > 0) {
        tooltipHTML += `
          <div class="tooltip-section">
            <div class="tooltip-label">🛍️ Items (${order.lineItems.length})</div>
            <div class="tooltip-items">
        `;
        order.lineItems.forEach((item) => {
          const quantityClass = item.quantity > 1 ? 'item-quantity-highlight' : '';
          const quantityText =
            item.quantity > 1
              ? `<span class="${quantityClass}">${item.quantity}x</span>`
              : `${item.quantity}x`;
          tooltipHTML += `
            <div class="tooltip-item">
              <span>${quantityText} ${esc(item.name)}</span>
              <span>${this.formatCurrency(item.price)}</span>
            </div>
          `;
        });
        tooltipHTML += `</div></div>`;
      }

      // Add email and shipping side by side if both available
      const hasEmail = order.email;
      const hasShipping = order.shippingAddress;

      if (hasEmail && hasShipping) {
        const addr = order.shippingAddress;
        const addrCity = addr.city ? esc(addr.city) + ', ' : '';
        const addrProvince = esc(addr.province || addr.state || '');
        const addrCountry = addr.country ? ' ' + esc(addr.country) : '';
        tooltipHTML += `
          <div class="tooltip-row">
            <div class="tooltip-section tooltip-section-half">
              <div class="tooltip-label">📧 Email</div>
              <div class="tooltip-value">${esc(order.email)}</div>
            </div>
            <div class="tooltip-section tooltip-section-half">
              <div class="tooltip-label">📦 Shipping</div>
              <div class="tooltip-value">
                ${addrCity}${addrProvince}${addrCountry}
              </div>
            </div>
          </div>
        `;
      } else {
        // Add email if available (full width)
        if (hasEmail) {
          tooltipHTML += `
            <div class="tooltip-section">
              <div class="tooltip-label">📧 Email</div>
              <div class="tooltip-value">${esc(order.email)}</div>
            </div>
          `;
        }

        // Add shipping address if available (full width)
        if (hasShipping) {
          const addr = order.shippingAddress;
          const addrCity = addr.city ? esc(addr.city) + ', ' : '';
          const addrProvince = esc(addr.province || addr.state || '');
          const addrCountry = addr.country ? ' ' + esc(addr.country) : '';
          tooltipHTML += `
            <div class="tooltip-section">
              <div class="tooltip-label">📦 Shipping</div>
              <div class="tooltip-value">
                ${addrCity}${addrProvince}${addrCountry}
              </div>
            </div>
          `;
        }
      }

      // Add fulfillment and discount code side by side if both available
      const hasFulfillment = order.fulfillmentStatus;
      let hasDiscount = false;
      let discountText = '';

      if (order.discountCode || order.discountCodes || order.discountApplications) {
        if (order.discountCode) {
          discountText = order.discountCode;
        } else if (order.discountCodes && order.discountCodes.length > 0) {
          discountText = order.discountCodes
            .map((d) => d.code)
            .filter(Boolean)
            .join(', ');
        } else if (order.discountApplications && order.discountApplications.length > 0) {
          discountText = order.discountApplications
            .map((d) => d.code || d.title)
            .filter(Boolean)
            .join(', ');
        }
        hasDiscount = !!discountText;
      }

      const safeFulfillment = esc(order.fulfillmentStatus || 'unfulfilled');
      const safeDiscountText = esc(discountText);
      if (hasFulfillment && hasDiscount) {
        const statusEmoji =
          order.fulfillmentStatus === 'fulfilled'
            ? '✅'
            : order.fulfillmentStatus === 'partial'
              ? '📦'
              : '⏳';
        tooltipHTML += `
          <div class="tooltip-row">
            <div class="tooltip-section tooltip-section-half">
              <div class="tooltip-label">${statusEmoji} Fulfillment</div>
              <div class="tooltip-value">${safeFulfillment}</div>
            </div>
            <div class="tooltip-section tooltip-section-half">
              <div class="tooltip-label">🎟️ Discount Code</div>
              <div class="tooltip-value tooltip-discount">${safeDiscountText}</div>
            </div>
          </div>
        `;
      } else {
        // Add fulfillment status if available (full width)
        if (hasFulfillment) {
          const statusEmoji =
            order.fulfillmentStatus === 'fulfilled'
              ? '✅'
              : order.fulfillmentStatus === 'partial'
                ? '📦'
                : '⏳';
          tooltipHTML += `
            <div class="tooltip-section">
              <div class="tooltip-label">${statusEmoji} Fulfillment</div>
              <div class="tooltip-value">${safeFulfillment}</div>
            </div>
          `;
        }

        // Add discount code if available (full width)
        if (hasDiscount) {
          tooltipHTML += `
            <div class="tooltip-section">
              <div class="tooltip-label">🎟️ Discount Code</div>
              <div class="tooltip-value tooltip-discount">${safeDiscountText}</div>
            </div>
          `;
        }
      }

      // Add order notes if available (filter out Shopify's attribution notes)
      if (order.note || order.notes) {
        const noteText = order.note || order.notes;
        if (noteText && noteText.trim()) {
          // Skip Shopify's auto-generated attribution notes
          const isAttributionNote =
            noteText.toLowerCase().includes('converted after a visit from') ||
            noteText.toLowerCase().includes('st session from') ||
            noteText.toLowerCase().includes('nd session from') ||
            noteText.toLowerCase().includes('rd session from') ||
            noteText.toLowerCase().includes('th session from');

          if (!isAttributionNote) {
            tooltipHTML += `
              <div class="tooltip-section">
                <div class="tooltip-label">📝 Notes</div>
                <div class="tooltip-value tooltip-notes">${esc(noteText)}</div>
              </div>
            `;
          }
        }
      }

      // On mobile, always use drawer
      if (isMobile) {
        this.showOrderDrawer(tooltipHTML);
        return;
      }

      // Create tooltip element (desktop only)
      const tooltip = document.createElement('div');
      tooltip.id = 'order-tooltip';
      tooltip.className = 'order-tooltip';
      safeSetHTML(tooltip, tooltipHTML);
      document.body.appendChild(tooltip);

      // Use requestAnimationFrame to ensure browser has calculated layout
      // Capture the target element before async callback
      const targetElement = event.currentTarget || event.target;
      requestAnimationFrame(() => {
        // Position tooltip near the hovered item
        const rect = targetElement.getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();

        // Check if tooltip would be significantly cut off at bottom
        const bottomSpace = window.innerHeight - rect.bottom;
        const topSpace = rect.top;
        const wouldBeCutOff =
          bottomSpace < tooltipRect.height + 20 && topSpace < tooltipRect.height + 20;

        // If would be cut off, use drawer instead
        if (wouldBeCutOff) {
          tooltip.remove();
          this.showOrderDrawer(tooltipHTML);
          return;
        }

        // Position to the right of the item, or left if not enough space
        let left = rect.right + 10;
        if (left + tooltipRect.width > window.innerWidth) {
          left = rect.left - tooltipRect.width - 10;
        }

        // Ensure left doesn't go off-screen
        if (left < 10) {
          left = 10;
        }
        if (left + tooltipRect.width > window.innerWidth) {
          left = window.innerWidth - tooltipRect.width - 10;
        }

        // Vertically position: prefer above the item for better UX
        let top = rect.top - tooltipRect.height - 10;

        // If not enough space above, place below
        if (top < 10) {
          top = rect.bottom + 10;
        }

        // If still going off-screen bottom, move up from bottom
        if (top + tooltipRect.height > window.innerHeight - 10) {
          top = window.innerHeight - tooltipRect.height - 10;
        }

        // Final safety: ensure top is never too high
        if (top < 10) {
          top = 10;
        }

        tooltip.style.left = `${left}px`;
        tooltip.style.top = `${top}px`;
        tooltip.style.visibility = 'visible';
        tooltip.style.opacity = '1';
        tooltip.style.transform = 'translateY(0) scale(1)';
      });
    },

    // Hide order tooltip
    hideOrderTooltip: function () {
      const tooltip = document.getElementById('order-tooltip');
      if (tooltip) {
        tooltip.remove();
      }
    },

    // Show order drawer (for bottom cutoff prevention)
    showOrderDrawer: function (content) {
      // Create overlay
      const overlay = document.createElement('div');
      overlay.className = 'order-tooltip-drawer-overlay';
      overlay.id = 'order-drawer-overlay';

      // Create drawer
      const drawer = document.createElement('div');
      drawer.className = 'order-tooltip-drawer';
      drawer.id = 'order-drawer';

      // Create drawer handle for mobile tap-to-close
      const drawerHandle = document.createElement('div');
      drawerHandle.className = 'order-tooltip-drawer-handle';
      safeSetHTML(drawerHandle, '<div class="drawer-handle-bar"></div>');

      // Create drawer content
      const drawerContent = document.createElement('div');
      drawerContent.className = 'order-tooltip-drawer-content';
      safeSetHTML(drawerContent, content);

      drawer.appendChild(drawerHandle);
      drawer.appendChild(drawerContent);

      // Explicit close button: an always-visible, large tap target. The handle-bar tap and the
      // backdrop strip were easy to miss on mobile, so closing felt unreliable.
      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'order-tooltip-drawer-close';
      closeBtn.setAttribute('aria-label', 'Close order details');
      closeBtn.textContent = '\u00D7';
      const closeFromButton = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.hideOrderDrawer();
      };
      closeBtn.addEventListener('click', closeFromButton);
      closeBtn.addEventListener('touchstart', closeFromButton, { passive: false });
      drawer.appendChild(closeBtn);

      document.body.appendChild(overlay);
      document.body.appendChild(drawer);

      // Trigger animation
      requestAnimationFrame(() => {
        overlay.classList.add('active');
        drawer.classList.add('active');
      });

      // Close on overlay touch/click - prevent event propagation to avoid opening other orders
      overlay.addEventListener('touchstart', (e) => {
        e.preventDefault(); // Prevent mouse events from firing
        e.stopPropagation();
        this.hideOrderDrawer();
      });

      overlay.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.hideOrderDrawer();
      });

      // Passive touchend for performance (no preventDefault needed)
      overlay.addEventListener(
        'touchend',
        (e) => {
          e.stopPropagation();
        },
        { passive: true }
      );

      // Close on handle tap (mobile)
      drawerHandle.addEventListener('touchstart', (e) => {
        e.preventDefault(); // Prevent mouse events from firing
        e.stopPropagation();
        this.hideOrderDrawer();
      });

      drawerHandle.addEventListener(
        'touchend',
        (e) => {
          e.stopPropagation();
        },
        { passive: true }
      );

      drawerHandle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.hideOrderDrawer();
      });

      // Close on escape key
      const escapeHandler = (e) => {
        if (e.key === 'Escape') {
          this.hideOrderDrawer();
          document.removeEventListener('keydown', escapeHandler);
        }
      };
      document.addEventListener('keydown', escapeHandler);
    },

    // Hide order drawer
    hideOrderDrawer: function () {
      const overlay = document.getElementById('order-drawer-overlay');
      const drawer = document.getElementById('order-drawer');

      if (overlay && drawer) {
        // Set flag to prevent reopening during close animation
        this.state.drawerClosing = true;

        overlay.classList.remove('active');
        drawer.classList.remove('active');

        setTimeout(() => {
          overlay.remove();
          drawer.remove();
          // Clear flag after animation completes plus buffer time
          setTimeout(() => {
            this.state.drawerClosing = false;
          }, 100);
        }, 400);
      }
    },

    // Show visitors tooltip on hover
    showVisitorsTooltip: function (event, tooltipHTML) {
      // Remove existing tooltip if any
      this.hideVisitorsTooltip();

      // Create tooltip element
      const tooltip = document.createElement('div');
      tooltip.id = 'visitors-tooltip';
      tooltip.className = 'visitors-tooltip';

      // Set tooltip content
      safeSetHTML(tooltip, tooltipHTML);

      // Append to body
      document.body.appendChild(tooltip);

      // Position tooltip centered on screen
      const tooltipRect = tooltip.getBoundingClientRect();
      const left = (window.innerWidth - tooltipRect.width) / 2;
      const top = (window.innerHeight - tooltipRect.height) / 2;

      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;
      tooltip.style.opacity = '1';
    },

    // Hide visitors tooltip
    hideVisitorsTooltip: function () {
      const tooltip = document.getElementById('visitors-tooltip');
      if (tooltip) {
        tooltip.remove();
      }
    },

    // Setup Live Visitors tooltip
    setupVisitorsTooltip: function () {
      const liveVisitorsCard = document.querySelector('[data-tooltip*="Number of visitors"]');
      if (liveVisitorsCard) {
        liveVisitorsCard.addEventListener('mouseenter', (e) => {
          const tooltipAttr = liveVisitorsCard.getAttribute('data-tooltip');
          // Only show custom tooltip if it's HTML (contains tags)
          if (tooltipAttr && tooltipAttr.includes('<div')) {
            e.stopPropagation();
            // Temporarily remove data-tooltip to prevent default tooltip
            const originalTooltip = tooltipAttr;
            liveVisitorsCard.removeAttribute('data-tooltip');
            this.showVisitorsTooltip(e, originalTooltip);

            // Restore data-tooltip when custom tooltip is hidden
            const restoreTooltip = () => {
              liveVisitorsCard.setAttribute('data-tooltip', originalTooltip);
            };
            liveVisitorsCard.addEventListener('mouseleave', restoreTooltip, { once: true });
          }
        });

        liveVisitorsCard.addEventListener('mouseleave', () => {
          this.hideVisitorsTooltip();
        });
      }
    },

    // Show keyword tooltip on hover
    showKeywordTooltip: function (event, keywordData, _element) {
      this.hideSearchTooltip();

      const tooltip = document.createElement('div');
      tooltip.id = 'search-tooltip';
      tooltip.className = 'search-tooltip';

      // Sort queries by search count
      const topQueries = keywordData.queries
        .sort((a, b) => b.searchCount - a.searchCount)
        .slice(0, 8);

      const totalRevenue = keywordData.queries
        .filter((q) => q.revenue)
        .reduce((sum, q) => sum + parseFloat(q.revenue || 0), 0);

      let tooltipHTML = `
        <div class="search-tooltip-header">
          <div class="search-header-title">
            <span class="search-icon">🏷️</span>
            <span class="search-label">Keyword</span>
          </div>
          <div class="search-query-full">"${this.escapeHtml(keywordData.keyword)}"</div>
        </div>

        <div class="search-keywords-section">
          <div class="keywords-label">
            <span class="keywords-icon">📊</span>
            <span>Top Queries Containing This Keyword</span>
          </div>
          <div class="keyword-queries-list">
            ${topQueries
              .map(
                (query, idx) => `
              <div class="keyword-query-item">
                <span class="query-rank">${idx + 1}.</span>
                <span class="query-text">"${this.escapeHtml(query.query)}"</span>
                <span class="query-count">${query.searchCount} searches</span>
              </div>
            `
              )
              .join('')}
            ${keywordData.queries.length > 8 ? `<div class="more-queries">+${keywordData.queries.length - 8} more queries</div>` : ''}
          </div>
        </div>

        <div class="search-tooltip-body">
          <div class="search-stats-grid">
            <div class="search-stat-card">
              <div class="stat-icon">🔢</div>
              <div class="stat-content">
                <div class="stat-label">Total Searches</div>
                <div class="stat-value">${keywordData.count.toLocaleString()}</div>
              </div>
            </div>

            <div class="search-stat-card">
              <div class="stat-icon">📝</div>
              <div class="stat-content">
                <div class="stat-label">Unique Queries</div>
                <div class="stat-value">${keywordData.queries.length}</div>
              </div>
            </div>
      `;

      if (totalRevenue > 0) {
        tooltipHTML += `
            <div class="search-stat-card">
              <div class="stat-icon">💵</div>
              <div class="stat-content">
                <div class="stat-label">Total Revenue</div>
                <div class="stat-value has-value">$${this.formatNumber(totalRevenue)}</div>
              </div>
            </div>
        `;
      }

      tooltipHTML += `
          </div>
        </div>
      `;

      safeSetHTML(tooltip, tooltipHTML);
      document.body.appendChild(tooltip);

      // Wait for next frame to get accurate dimensions
      requestAnimationFrame(() => {
        this.updateTooltipPosition(event);
        tooltip.style.opacity = '1';
        tooltip.style.visibility = 'visible';
      });
    },

    // Update tooltip position (called on mousemove)
    updateTooltipPosition: function (event) {
      const tooltip = document.getElementById('search-tooltip');
      if (!tooltip) return;

      const tooltipRect = tooltip.getBoundingClientRect();
      const padding = 25;
      const cursorOffset = 20;

      // Calculate viewport boundaries
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Default position: to the right and slightly below cursor
      let left = event.clientX + cursorOffset;
      let top = event.clientY + cursorOffset;

      // Horizontal positioning
      if (left + tooltipRect.width + padding > viewportWidth) {
        // Not enough space on right, try left
        left = event.clientX - tooltipRect.width - cursorOffset;

        // If still doesn't fit on left, center it
        if (left < padding) {
          left = Math.max(padding, (viewportWidth - tooltipRect.width) / 2);
        }
      }

      // Ensure doesn't go past left edge
      left = Math.max(padding, left);

      // Vertical positioning
      if (top + tooltipRect.height + padding > viewportHeight) {
        // Not enough space below, position above cursor
        top = event.clientY - tooltipRect.height - cursorOffset;

        // If still doesn't fit above, position at top with some padding
        if (top < padding) {
          top = padding;
        }
      }

      // Ensure doesn't go past top edge
      top = Math.max(padding, top);

      // Apply position
      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;
    },

    // Show search tooltip on hover (legacy - keeping for backwards compatibility)
    showSearchTooltip: function (event, searchData) {
      this.showKeywordTooltip(event, searchData);
    },

    // Hide search tooltip
    hideSearchTooltip: function () {
      const tooltip = document.getElementById('search-tooltip');
      if (tooltip) {
        tooltip.remove();
      }
    },

    // Show unfulfilled order tooltip (styled like Live Activity tooltips)
    showUnfulfilledTooltip: function (event, orderData) {
      this.hideUnfulfilledTooltip();

      const tooltip = document.createElement('div');
      tooltip.id = 'unfulfilled-tooltip';
      tooltip.className = 'live-tooltip';

      const daysUnfulfilled = Math.floor(
        (Date.now() - new Date(orderData.createdAt)) / (1000 * 60 * 60 * 24)
      );

      safeSetHTML(
        tooltip,
        `
        <div class="tooltip-header">
          📦 Oldest Unfulfilled Order
        </div>
        <div class="tooltip-row">
          <div class="tooltip-label">Order #</div>
          <div class="tooltip-value">${orderData.orderNumber || orderData.name}</div>
        </div>
        <div class="tooltip-row">
          <div class="tooltip-label">Customer</div>
          <div class="tooltip-value">${this.escapeHtml(orderData.customerName || 'Unknown')}</div>
        </div>
        <div class="tooltip-row">
          <div class="tooltip-label">Created</div>
          <div class="tooltip-value">${daysUnfulfilled} days ago</div>
        </div>
        <div class="tooltip-row">
          <div class="tooltip-label">Amount</div>
          <div class="tooltip-value">$${orderData.totalPrice || '0.00'}</div>
        </div>
      `
      );

      document.body.appendChild(tooltip);

      // Position tooltip
      requestAnimationFrame(() => {
        const rect = tooltip.getBoundingClientRect();
        const targetRect = event.target.getBoundingClientRect();
        const padding = 10;

        let left = targetRect.right + padding;
        let top = targetRect.top;

        // Keep within viewport
        if (left + rect.width > window.innerWidth - padding) {
          left = targetRect.left - rect.width - padding;
        }

        if (top + rect.height > window.innerHeight - padding) {
          top = window.innerHeight - rect.height - padding;
        }

        tooltip.style.left = `${left}px`;
        tooltip.style.top = `${top}px`;
      });
    },

    hideUnfulfilledTooltip: function () {
      const tooltip = document.getElementById('unfulfilled-tooltip');
      if (tooltip) {
        tooltip.remove();
      }
    },

    /**
     * ==============================================
     * GraphQL Analytics Widget Functions
     * ==============================================
     */

    // Load Collections Performance
    loadCollectionsPerformance: async function (period = '30d') {
      try {
        const container = document.getElementById('collections-performance');
        if (!container) return;

        // Show loading indicator
        safeSetHTML(
          container,
          `
          <div class="loading-container">
            <div class="loading-spinner"></div>
            <div class="loading-text">Loading collections performance...</div>
          </div>
        `
        );

        const response = await fetch(
          `${this.config.apiProxyUrl}?endpoint=collections-performance&period=${period}`
        );
        const result = await response.json();

        if (result.success && result.data) {
          const data = result.data.data || result.data;

          // Top 5 collections by sales
          const topCollections = (data.collections || []).slice(0, 5);

          let html = '<div class="collections-list">';

          topCollections.forEach((collection) => {
            const stockPercentage = parseFloat(collection.stockPercentage) || 0;
            const statusColor =
              stockPercentage > 80 ? '#22c55e' : stockPercentage > 50 ? '#f59e0b' : '#ef4444';
            // Use totalSales from API response
            const salesValue = parseFloat(collection.totalSales) || 0;

            html += `
              <div class="collection-item">
                <div class="collection-info">
                  <div class="collection-name">${collection.title}</div>
                  <div class="collection-meta">${collection.productsCount} products • ${collection.inStockProducts} in stock • ${stockPercentage}% stocked</div>
                </div>
                <div class="collection-stats">
                  <div class="collection-value">$${this.formatNumber(salesValue, 0)}</div>
                  <div class="collection-stock" style="color: ${statusColor}; font-size: 11px; opacity: 0.8;">Sales</div>
                </div>
              </div>
            `;
          });

          html += '</div>';

          if (data.summary) {
            const totalSales = parseFloat(data.summary.totalSales) || 0;
            html += `
              <div class="widget-footer">
                <small>Total Sales: $${this.formatNumber(totalSales, 0)}</small>
              </div>
            `;
          }

          safeSetHTML(container, html);
        }
      } catch (_error) {
        const container = document.getElementById('collections-performance');
        if (container) {
          safeSetHTML(
            container,
            `
            <div class="error-message">
              <p>⚠️ Failed to load collections performance data</p>
            </div>
          `
          );
        }
      }
    },

    // Load Gift Card Analytics
    loadGiftCardAnalytics: async function () {
      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=gift-card-analytics`);
        const result = await response.json();

        if (result.success && result.data) {
          const data = result.data.data || result.data;
          const container = document.getElementById('gift-card-analytics');

          if (!container) return;

          const redemptionRate = parseFloat(data.redemptionRate) || 0;
          const redemptionColor =
            redemptionRate > 70 ? '#22c55e' : redemptionRate > 50 ? '#f59e0b' : '#ef4444';

          let html = '<div class="gift-card-grid">';

          html += `
            <div class="stat-box">
              <div class="stat-label">Total Sold</div>
              <div class="stat-value">$${this.formatNumber(parseFloat(data.totalSold))}</div>
            </div>
            <div class="stat-box">
              <div class="stat-label">Outstanding</div>
              <div class="stat-value">$${this.formatNumber(parseFloat(data.totalBalance))}</div>
            </div>
            <div class="stat-box">
              <div class="stat-label">Redeemed</div>
              <div class="stat-value" style="color: ${redemptionColor}">${redemptionRate}%</div>
            </div>
            <div class="stat-box">
              <div class="stat-label">Active Cards</div>
              <div class="stat-value">${data.activeCards || 0}</div>
            </div>
          `;

          html += '</div>';

          if (data.breakdown) {
            html += `
              <div class="widget-footer">
                <small>
                  ${data.breakdown.fullyRedeemed || 0} fully redeemed •
                  ${data.breakdown.partiallyRedeemed || 0} partial •
                  ${data.breakdown.untouched || 0} untouched
                </small>
              </div>
            `;
          }

          safeSetHTML(container, html);
        }
      } catch (_error) {}
    },

    // Load Enhanced Abandoned Checkout Analytics
    loadEnhancedAbandonedCheckout: async function (period = '7d') {
      try {
        // Check if required elements exist before proceeding
        const totalEl = document.getElementById('enhanced-checkout-total');
        const valueEl = document.getElementById('enhanced-checkout-value');
        const timeEl = document.getElementById('enhanced-checkout-time');

        if (!totalEl || !valueEl || !timeEl) {
          return; // Exit silently if elements don't exist
        }

        const response = await fetch(
          `${this.config.apiProxyUrl}?endpoint=enhanced-abandoned-checkout&period=${period}`
        );
        const result = await response.json();

        if (result.success && result.data) {
          const data = result.data.data || result.data;

          // Update summary stats
          totalEl.textContent = data.totalAbandoned || 0;
          const totalValue = parseFloat(data.totalValue) || 0;
          const avgTime = parseFloat(data.avgTimeToAbandonment) || 0;
          valueEl.textContent = `$${this.formatNumber(totalValue)}`;
          timeEl.textContent = `${avgTime.toFixed(1)} min`;

          // Render stage breakdown
          const container = document.getElementById('abandonment-stages');
          if (!container) return;

          const stages = data.stageBreakdown || {};
          let html = '<div class="stage-breakdown">';

          const stageConfig = [
            { key: 'cart', icon: '🛒', label: 'Cart' },
            { key: 'information', icon: '📝', label: 'Information' },
            { key: 'shipping', icon: '🚚', label: 'Shipping' },
            { key: 'payment', icon: '💳', label: 'Payment' },
          ];

          stageConfig.forEach((stage) => {
            const stageData = stages[stage.key] || { count: 0, percentage: 0 };
            html += `
              <div class="stage-item">
                <div class="stage-icon">${stage.icon}</div>
                <div class="stage-info">
                  <h4>${stage.label}</h4>
                  <p class="stage-count">${stageData.count} checkouts</p>
                  <div class="stage-bar">
                    <div class="stage-bar-fill" style="width: ${stageData.percentage}%; background: #6366f1;"></div>
                  </div>
                  <p class="stage-percentage">${stageData.percentage}% of total</p>
                </div>
              </div>
            `;
          });

          html += '</div>';
          safeSetHTML(container, html);
        }
      } catch (_error) {}
    },

    // Load Discount ROI Tracker
    loadDiscountROITracker: async function (period = '30d') {
      try {
        // Check if required elements exist before proceeding
        const platformTotalEl = document.getElementById('platform-discount-total');
        const platformRevenueEl = document.getElementById('platform-discount-revenue');
        const platformRoiEl = document.getElementById('platform-discount-roi');
        const manualTotalEl = document.getElementById('manual-discount-total');
        const manualRevenueEl = document.getElementById('manual-discount-revenue');
        const manualRoiEl = document.getElementById('manual-discount-roi');

        if (
          !platformTotalEl ||
          !platformRevenueEl ||
          !platformRoiEl ||
          !manualTotalEl ||
          !manualRevenueEl ||
          !manualRoiEl
        ) {
          return; // Exit silently if elements don't exist
        }

        const response = await fetch(
          `${this.config.apiProxyUrl}?endpoint=discount-roi-tracker&period=${period}`
        );
        const result = await response.json();

        if (result.success && result.data) {
          const data = result.data.data || result.data;

          // Update platform discounts
          const platform = data.platformDiscounts || {};
          const platformTotal = parseFloat(platform.total) || 0;
          const platformRevenue = parseFloat(platform.revenue) || 0;
          const platformRoi = parseFloat(platform.roi) || 0;
          platformTotalEl.textContent = `$${this.formatNumber(platformTotal)}`;
          platformRevenueEl.textContent = `$${this.formatNumber(platformRevenue)}`;
          platformRoiEl.textContent = `${platformRoi.toFixed(1)}x`;

          // Update manual discounts
          const manual = data.manualDiscounts || {};
          const manualTotal = parseFloat(manual.total) || 0;
          const manualRevenue = parseFloat(manual.revenue) || 0;
          const manualRoi = parseFloat(manual.roi) || 0;
          manualTotalEl.textContent = `$${this.formatNumber(manualTotal)}`;
          manualRevenueEl.textContent = `$${this.formatNumber(manualRevenue)}`;
          manualRoiEl.textContent = `${manualRoi.toFixed(1)}x`;

          // Render top performing codes
          const container = document.getElementById('top-performing-codes');
          if (!container) return;

          const topCodes = data.topPerformingCodes || [];

          if (topCodes.length === 0) {
            safeSetHTML(
              container,
              '<div class="no-data-message"><span class="no-data-icon">📊</span><p>No discount codes used in this period</p></div>'
            );
            return;
          }

          let html = '<div class="compact-codes-list">';

          topCodes.slice(0, 5).forEach((code, index) => {
            const codeRevenue = parseFloat(code.revenue) || 0;
            const codeRoi = parseFloat(code.roi) || 0;
            const codeUses = parseInt(code.uses, 10) || 0;

            // Determine badge color based on ROI
            const roiClass = codeRoi > 10 ? 'roi-excellent' : codeRoi > 5 ? 'roi-good' : 'roi-ok';

            html += `
              <div class="compact-code-item">
                <div class="code-left">
                  <span class="code-position">#${index + 1}</span>
                  <div class="code-details">
                    <div class="code-title">${code.code}</div>
                    <div class="code-metrics">
                      <span class="metric">${codeUses.toLocaleString()} uses</span>
                      <span class="metric-dot">•</span>
                      <span class="metric">$${this.formatNumber(codeRevenue)}</span>
                    </div>
                  </div>
                </div>
                <div class="code-right">
                  <span class="roi-badge ${roiClass}">${codeRoi.toFixed(1)}x ROI</span>
                </div>
              </div>
            `;
          });

          html += '</div>';

          if (topCodes.length > 5) {
            html += `<div class="codes-footer">+${topCodes.length - 5} more codes</div>`;
          }

          safeSetHTML(container, html);
        }
      } catch (_error) {}
    },

    // Load High-Variant Products
    loadHighVariantProducts: async function () {
      try {
        // Check if required elements exist before proceeding
        const countEl = document.getElementById('high-variant-count');
        const totalEl = document.getElementById('high-variant-total');
        const avgEl = document.getElementById('high-variant-avg');

        if (!countEl || !totalEl || !avgEl) {
          return; // Exit silently if elements don't exist
        }

        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=high-variant-products`);
        const result = await response.json();

        if (result.success && result.data) {
          const data = result.data.data || result.data;

          // Update summary stats
          countEl.textContent = data.totalProducts || 0;
          totalEl.textContent = data.totalVariants || 0;
          avgEl.textContent = data.avgVariantsPerProduct || 0;

          // Render product list
          const container = document.getElementById('high-variant-products');
          if (!container) return;

          const products = data.products || [];

          if (products.length === 0) {
            safeSetHTML(container, '<p class="no-data">No products with 10+ variants found</p>');
            return;
          }

          let html = '<div class="high-variant-products-list">';

          products.slice(0, 10).forEach((product, _index) => {
            const stockStatus = product.inStock > product.outOfStock ? 'good' : 'warning';
            html += `
              <div class="variant-product-item">
                <div class="variant-product-header">
                  <h4>${product.title}</h4>
                  <span class="variant-count-badge">${product.variantCount} variants</span>
                </div>
                <div class="variant-product-stats">
                  <div class="variant-stat-item ${stockStatus}">
                    <span class="stat-icon">✓</span>
                    <span>${product.inStock} in stock</span>
                  </div>
                  <div class="variant-stat-item">
                    <span class="stat-icon">✗</span>
                    <span>${product.outOfStock} out of stock</span>
                  </div>
                  <div class="variant-stat-item">
                    <span class="stat-icon">💲</span>
                    <span>$${product.priceRange.min} - $${product.priceRange.max}</span>
                  </div>
                </div>
                <div class="variant-options">
                  <small>Options: ${product.options.join(', ')}</small>
                </div>
              </div>
            `;
          });

          html += '</div>';
          safeSetHTML(container, html);
        }
      } catch (_error) {}
    },

    // Load Payment Methods Breakdown
    loadPaymentMethodsBreakdown: async function (period = '30d') {
      try {
        const container = document.getElementById('payment-methods');
        if (!container) return;

        // Show loading indicator
        safeSetHTML(
          container,
          `
          <div class="loading-container">
            <div class="loading-spinner"></div>
            <div class="loading-text">Loading payment methods...</div>
          </div>
        `
        );

        const response = await fetch(
          `${this.config.apiProxyUrl}?endpoint=payment-methods-breakdown&period=${period}`
        );
        const result = await response.json();

        if (result.success && result.data) {
          const data = result.data.data || result.data;

          const methods = data.methods || [];
          const totalRevenue = parseFloat(data.totalRevenue) || 0;

          // Removed duplicate header
          let html = '<div class="payment-methods-list">';

          methods.slice(0, 5).forEach((method) => {
            const percentage = parseFloat(method.percentage) || 0;
            const barWidth = Math.min(percentage, 100); // Cap at 100%
            const gatewayName = this.formatGatewayName(method.gateway);
            const aov = parseFloat(method.averageOrderValue) || 0;

            html += `
              <div class="chart-bar-item payment-item">
                <div class="chart-bar-label">
                  <span class="chart-bar-name">${gatewayName}</span>
                  <span class="chart-bar-meta">${method.count} orders • $${this.formatNumber(aov, 2)} AOV</span>
                </div>
                <div class="chart-bar-track">
                  <div class="chart-bar-fill payment-bar" style="width: ${barWidth}%"></div>
                  <span class="chart-bar-value">${percentage.toFixed(1)}%</span>
                </div>
              </div>
            `;
          });

          html += '</div>';
          html += `
            <div class="widget-footer">
              <small>${data.totalOrders || 0} orders • $${this.formatNumber(totalRevenue)} total</small>
            </div>
          `;

          safeSetHTML(container, html);
        }
      } catch (_error) {
        const container = document.getElementById('payment-methods');
        if (container) {
          safeSetHTML(
            container,
            `
            <div class="error-message">
              <p>⚠️ Failed to load payment methods data</p>
            </div>
          `
          );
        }
      }
    },

    // Format gateway names for display
    formatGatewayName: function (gateway) {
      const names = {
        shopify_payments: 'Shopify Payments',
        tiktok_shop: 'TikTok Shop',
        paypal: 'PayPal',
        manual: 'Manual Payment',
        gift_card: 'Gift Card',
        shop_pay: 'Shop Pay',
      };
      return names[gateway] || gateway.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    },

    // Load Shipping Analytics
    loadShippingAnalytics: async function (period = '30d') {
      try {
        const container = document.getElementById('shipping-analytics');
        if (!container) return;

        // Show loading indicator
        safeSetHTML(
          container,
          `
          <div class="loading-container">
            <div class="loading-spinner"></div>
            <div class="loading-text">Loading shipping analytics...</div>
          </div>
        `
        );

        const response = await fetch(
          `${this.config.apiProxyUrl}?endpoint=shipping-analytics&period=${period}`
        );
        const result = await response.json();

        if (result.success && result.data) {
          const data = result.data.data || result.data;

          const methods = data.methods || [];
          const zones = data.zones || [];

          let html = '';

          // Shipping methods with bar chart - SUBCARD 1
          html += '<div class="shipping-subcard">';
          html += '<h4 class="section-subtitle">📦 Top Methods</h4>';
          html += '<div class="chart-container">';

          methods.slice(0, 5).forEach((method) => {
            const percentage = parseFloat(method.percentage) || 0;
            const barWidth = Math.min(percentage, 100); // Cap at 100%
            const avgCost = parseFloat(method.averageCost) || 0;
            const methodName = method.method.toLowerCase();

            // Industry benchmark comparison for shipping methods
            let benchmarkIndicator = '';
            if (methodName.includes('free')) {
              // Free shipping: 60-80% is optimal for conversions
              if (percentage >= 60) {
                benchmarkIndicator = '<span class="shipping-benchmark excellent">✓ Optimal</span>';
              } else if (percentage >= 40) {
                benchmarkIndicator =
                  '<span class="shipping-benchmark average">Consider more free shipping</span>';
              } else {
                benchmarkIndicator =
                  '<span class="shipping-benchmark below-average">Low (industry: 60-80%)</span>';
              }
            } else if (methodName.includes('standard') || methodName.includes('ground')) {
              // Standard shipping: 15-30% is typical
              if (percentage >= 15 && percentage <= 35) {
                benchmarkIndicator = '<span class="shipping-benchmark excellent">✓ Typical</span>';
              } else if (percentage > 35) {
                benchmarkIndicator =
                  '<span class="shipping-benchmark average">High paid shipping %</span>';
              }
            } else if (
              methodName.includes('express') ||
              methodName.includes('expedited') ||
              methodName.includes('priority')
            ) {
              // Express: 5-15% is healthy premium option usage
              if (percentage >= 5 && percentage <= 15) {
                benchmarkIndicator =
                  '<span class="shipping-benchmark excellent">✓ Good premium uptake</span>';
              } else if (percentage < 5) {
                benchmarkIndicator =
                  '<span class="shipping-benchmark average">Low premium usage</span>';
              }
            }

            html += `
              <div class="chart-bar-item">
                <div class="chart-bar-label">
                  <span class="chart-bar-name">${method.method} ${benchmarkIndicator}</span>
                  <span class="chart-bar-meta">${method.count} orders • $${this.formatNumber(avgCost, 2)} avg</span>
                </div>
                <div class="chart-bar-track">
                  <div class="chart-bar-fill" style="width: ${barWidth}%"></div>
                  <span class="chart-bar-value">${percentage.toFixed(1)}%</span>
                </div>
              </div>
            `;
          });

          html += '</div></div>';

          // Shipping zones with bar chart - SUBCARD 2
          html += '<div class="shipping-subcard">';
          html += '<h4 class="section-subtitle">🌍 Top Destinations</h4>';
          html += '<div class="chart-container">';

          zones.slice(0, 5).forEach((zone) => {
            const percentage = parseFloat(zone.percentage) || 0;
            const barWidth = Math.min(percentage, 100); // Cap at 100%

            html += `
              <div class="chart-bar-item">
                <div class="chart-bar-label">
                  <span class="chart-bar-icon">${this.getCountryFlag(zone.country)}</span>
                  <span class="chart-bar-name">${zone.countryName}</span>
                  <span class="chart-bar-meta">${zone.count} orders</span>
                </div>
                <div class="chart-bar-track">
                  <div class="chart-bar-fill destination-bar" style="width: ${barWidth}%"></div>
                  <span class="chart-bar-value">${percentage.toFixed(1)}%</span>
                </div>
              </div>
            `;
          });

          html += '</div></div>';

          html += `
            <div class="widget-footer">
              <small>${data.freeShippingPercentage}% free shipping • $${this.formatNumber(parseFloat(data.averageShippingCost))} avg cost</small>
            </div>
          `;

          safeSetHTML(container, html);
        }
      } catch (_error) {
        const container = document.getElementById('shipping-analytics');
        if (container) {
          safeSetHTML(
            container,
            `
            <div class="error-message">
              <p>⚠️ Failed to load shipping analytics data</p>
            </div>
          `
          );
        }
      }
    },

    // Load Customer Tags Distribution
    loadCustomerTagsDistribution: async function () {
      try {
        const response = await fetch(
          `${this.config.apiProxyUrl}?endpoint=customer-tags-distribution`
        );
        const result = await response.json();

        if (result.success && result.data) {
          const data = result.data.data || result.data;
          const container = document.getElementById('customer-tags');

          if (!container) return;

          const tags = data.tags || [];
          const vipSegments = data.vipSegments || [];

          // Removed duplicate header
          let html = '';

          // VIP segments first
          if (vipSegments.length > 0) {
            html += '<div class="shipping-section">';
            html += '<h4 class="section-subtitle vip-subtitle">⭐ VIP Segments</h4>';

            vipSegments.slice(0, 3).forEach((segment) => {
              const revenue = parseFloat(segment.totalRevenue) || 0;
              const avgLTV = parseFloat(segment.averageRevenuePerCustomer) || 0;
              html += `
                <div class="collection-item vip-item">
                  <div class="collection-info">
                    <div class="collection-name">${segment.tag}</div>
                    <div class="collection-meta">${segment.count} customers • $${this.formatNumber(avgLTV, 2)} avg LTV</div>
                  </div>
                  <div class="collection-stats">
                    <div class="collection-value vip-value">$${this.formatNumber(revenue, 0)}</div>
                  </div>
                </div>
              `;
            });
            html += '</div>';
          }

          // All tags
          html += '<div class="shipping-section">';
          html += '<h4 class="section-subtitle">All Segments</h4>';

          tags.slice(0, 8).forEach((tag) => {
            const percentage = parseFloat(tag.percentage) || 0;
            html += `
              <div class="tag-item">
                <span class="tag-name">${tag.tag}</span>
                <span class="tag-count">${tag.count} customers</span>
                <span class="tag-percentage">${percentage.toFixed(1)}%</span>
              </div>
            `;
          });

          html += '</div>';

          html += `
            <div class="widget-footer">
              <small>${data.totalTags || 0} tags • ${data.totalCustomers || 0} customers</small>
            </div>
          `;

          safeSetHTML(container, html);
        }
      } catch (_error) {}
    },

    // Load Blog Performance
    loadBlogPerformance: async function () {
      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=blog-performance`);
        const result = await response.json();

        if (result.success && result.data) {
          const data = result.data.data || result.data;

          // Update Blog Articles stat card
          const blogViewsEl = document.getElementById('blog-views-total');
          if (blogViewsEl) {
            blogViewsEl.textContent = data.hasGAData
              ? (data.totalViews || 0).toLocaleString()
              : '--';
          }
          const blogUsersEl = document.getElementById('blog-views-users');
          if (blogUsersEl) {
            blogUsersEl.textContent = data.hasGAData
              ? `${(data.totalConversions || 0).toLocaleString()} conversions`
              : `${data.totalArticles || 0} articles`;
          }

          const container = document.getElementById('blog-performance');

          if (!container) return;

          const blogs = data.blogs || [];
          const contentHealth = data.contentHealth || 'Unknown';
          const healthColor = contentHealth === 'Active' ? '#22c55e' : '#ef4444';

          // Update subtitle with period
          const subtitleEl = document.getElementById('blog-performance-subtitle');
          if (subtitleEl) {
            subtitleEl.textContent = 'Last 30 days • Content health';
          }

          // Removed duplicate header
          let html = '';

          // Show analytics data or helpful message
          if (!data.hasGAData && data.totalViews === 0) {
            html += `
              <div style="padding: 20px; background: rgba(255, 193, 7, 0.1); border: 1px solid rgba(255, 193, 7, 0.3); border-radius: 12px; margin-bottom: 20px; text-align: center;">
                <div style="color: #ffc107; font-size: 14px; margin-bottom: 8px;">⚠️ Analytics Data Unavailable</div>
                <div style="color: var(--text-light); font-size: 12px;">
                  Google Analytics data is being collected. Check back later for blog performance metrics.
                </div>
              </div>
            `;
          }

          html += `
            <div class="gift-card-grid">
              <div class="stat-box">
                <div class="stat-label">Total Views</div>
                <div class="stat-value">${data.hasGAData ? (data.totalViews || 0).toLocaleString() : '—'}</div>
              </div>
              <div class="stat-box">
                <div class="stat-label">Conversions</div>
                <div class="stat-value">${data.hasGAData ? (data.totalConversions || 0).toLocaleString() : '—'}</div>
              </div>
              <div class="stat-box">
                <div class="stat-label">Revenue</div>
                <div class="stat-value">${data.hasGAData ? '$' + this.formatNumber(parseFloat(data.totalRevenue || 0)) : '—'}</div>
              </div>
              <div class="stat-box">
                <div class="stat-label">Conv. Rate</div>
                <div class="stat-value">${data.hasGAData ? (data.conversionRate || '0.00') + '%' : '—'}</div>
              </div>
              <div class="stat-box">
                <div class="stat-label">Total Articles</div>
                <div class="stat-value">${(data.totalArticles || 0).toLocaleString()}</div>
              </div>
              <div class="stat-box">
                <div class="stat-label">Content Health</div>
                <div class="stat-value" style="color: ${healthColor}">${contentHealth}</div>
              </div>
            </div>
          `;

          html += '<div class="collections-list">';

          blogs.forEach((blog) => {
            const hasRecent = blog.recentArticles > 0;
            const statusClass = hasRecent ? 'blog-active' : 'blog-quiet';
            const views = blog.views || 0;
            const conversions = blog.conversions || 0;
            const revenue = parseFloat(blog.revenue || 0);
            const _convRate = views > 0 ? ((conversions / views) * 100).toFixed(2) : '0.00';

            const analyticsText = data.hasGAData
              ? `${views.toLocaleString()} views • ${conversions.toLocaleString()} conversions • $${this.formatNumber(revenue)} revenue`
              : 'Analytics data collecting...';

            // Sort all articles by published date (newest first)
            const latestArticles = blog.latestArticles || [];
            const allArticlesByDate = [...latestArticles].sort(
              (a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)
            );

            // Use all articles
            const combinedArticles = allArticlesByDate;

            // Store article data for drawer
            const blogId = `blog-${blog.title.replace(/\s+/g, '-').toLowerCase()}`;

            html += `
              <div class="collection-item blog-item blog-item-clickable" data-blog-id="${blogId}">
                <div class="collection-info">
                  <div class="collection-name">${blog.title}</div>
                  <div class="collection-meta">
                    ${analyticsText}
                  </div>
                  <div class="collection-meta" style="margin-top: 4px;">
                    ${blog.totalArticles} articles • ${blog.publicationFrequency}
                  </div>
                  ${
                    blog.topTags && blog.topTags.length > 0
                      ? `
                    <div class="blog-tags">
                      ${blog.topTags
                        .slice(0, 3)
                        .map((tag) => `<span class="blog-tag">${tag.tag}</span>`)
                        .join('')}
                    </div>
                  `
                      : ''
                  }
                </div>
                <div class="collection-stats">
                  <span class="blog-status ${statusClass}">
                    ${hasRecent ? '● Active' : '○ Quiet'}
                  </span>
                  <span class="blog-drawer-icon">›</span>
                </div>
                <div class="blog-articles-data" style="display: none;">
                  ${this.escapeHtml(JSON.stringify({ articles: combinedArticles, blogTitle: blog.title, blogHandle: blog.handle }))}
                </div>
              </div>
            `;
          });

          html += '</div>';

          safeSetHTML(container, html);

          // Add click handlers for blog items to open drawer
          const blogItems = container.querySelectorAll('.blog-item-clickable');
          blogItems.forEach((item) => {
            item.addEventListener('click', (_e) => {
              const dataEl = item.querySelector('.blog-articles-data');
              if (dataEl) {
                try {
                  const data = JSON.parse(dataEl.textContent);
                  this.openBlogArticlesDrawer(data);
                } catch (parseError) {
                  _err('[Admin] loadBlogPerformance drawer data parse error:', parseError);
                  this.showToast('⚠️ Unable to open blog details');
                }
              }
            });
          });
        } else {
          // result.success is false — clear shimmers so cards don't spin forever
          const blogViewsEl2 = document.getElementById('blog-views-total');
          if (blogViewsEl2) blogViewsEl2.textContent = '--';
          const blogPerfEl2 = document.getElementById('blog-performance');
          if (blogPerfEl2) {
            safeSetHTML(
              blogPerfEl2,
              '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">Blog data unavailable</p>'
            );
          }
        }
      } catch (_error) {
        // Clear loading shimmers so cards don't spin forever
        const blogViewsEl = document.getElementById('blog-views-total');
        if (blogViewsEl) blogViewsEl.textContent = '--';
        const blogPerfEl = document.getElementById('blog-performance');
        if (blogPerfEl) {
          safeSetHTML(
            blogPerfEl,
            '<p style="color: rgba(255,255,255,0.6); padding: 20px; text-align: center;">Blog data unavailable</p>'
          );
        }
      }
    },

    // Load Video Library page-view stats from GA4
    loadVideoLibraryData: async function () {
      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=video-library-views`);
        const result = await response.json();
        const data = result.data || result;

        const viewsEl = document.getElementById('video-library-views');
        if (viewsEl) viewsEl.textContent = (data.views || 0).toLocaleString();

        const usersEl = document.getElementById('video-library-users');
        if (usersEl) usersEl.textContent = `${(data.users || 0).toLocaleString()} visitors`;

        const realtimeEl = document.getElementById('video-library-realtime');
        if (realtimeEl) realtimeEl.textContent = `${data.realtimeOnPage || 0} now`;
      } catch (_error) {
        const viewsEl = document.getElementById('video-library-views');
        if (viewsEl) viewsEl.textContent = '--';
        const usersEl = document.getElementById('video-library-users');
        if (usersEl) usersEl.textContent = '-- visitors';
      }
    },

    // Open drawer with blog articles
    openBlogArticlesDrawer: function (data) {
      const { articles, blogTitle, blogHandle } = data;

      // Get saved sort preference or default to 'date'
      const savedSort = localStorage.getItem('blog-articles-sort') || 'date';

      // Identify top 3 most viewed articles (for medals)
      const articlesByViews = [...articles].sort((a, b) => (b.views || 0) - (a.views || 0));
      const _top3Handles = new Set(
        [articlesByViews[0]?.handle, articlesByViews[1]?.handle, articlesByViews[2]?.handle].filter(
          Boolean
        )
      );

      // Mark each article with its view rank
      const viewRankMap = new Map();
      articlesByViews.forEach((article, index) => {
        if (index < 3) {
          viewRankMap.set(article.handle, index);
        }
      });

      // Sort articles based on preference
      let sortedArticles = [...articles];
      if (savedSort === 'date') {
        sortedArticles.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
      } else {
        sortedArticles.sort((a, b) => (b.views || 0) - (a.views || 0));
        // Filter out 0-view articles when sorting by views
        sortedArticles = sortedArticles.filter((article) => (article.views || 0) > 0);
      }

      // Create drawer HTML
      let drawerHTML = `
        <div class="blog-drawer-overlay" id="blog-drawer-overlay">
          <div class="blog-drawer" id="blog-drawer">
            <div class="blog-drawer-header">
              <h3>${blogTitle} - Articles</h3>
              <button class="blog-drawer-close" id="blog-drawer-close">×</button>
            </div>
            <div class="blog-drawer-content">
      `;

      if (articles.length > 0) {
        // Add sorting toggle buttons
        drawerHTML += `
          <div class="blog-drawer-sort-toggle" style="display: flex; gap: 8px; margin-bottom: 16px; padding: 0 8px;">
            <button class="blog-sort-btn ${savedSort === 'date' ? 'active' : ''}" data-sort="date" style="flex: 1; padding: 8px 16px; background: ${savedSort === 'date' ? 'var(--accent)' : 'rgba(255,255,255,0.1)'}; border: 1px solid ${savedSort === 'date' ? 'var(--accent)' : 'rgba(255,255,255,0.2)'}; border-radius: 6px; color: white; cursor: pointer; font-size: 14px; font-weight: 500; transition: all 0.2s;">
              📅 Sort by Date
            </button>
            <button class="blog-sort-btn ${savedSort === 'views' ? 'active' : ''}" data-sort="views" style="flex: 1; padding: 8px 16px; background: ${savedSort === 'views' ? 'var(--accent)' : 'rgba(255,255,255,0.1)'}; border: 1px solid ${savedSort === 'views' ? 'var(--accent)' : 'rgba(255,255,255,0.2)'}; border-radius: 6px; color: white; cursor: pointer; font-size: 14px; font-weight: 500; transition: all 0.2s;">
              📊 Sort by Views
            </button>
          </div>
        `;

        // All Articles section
        const sortLabel = savedSort === 'date' ? 'by Published Date' : 'by Views';
        drawerHTML += `
          <div class="blog-drawer-section">
            <h4 class="blog-drawer-section-title">📝 All Articles (${sortLabel})</h4>
            <div class="blog-drawer-articles" id="blog-drawer-articles-list">
              ${sortedArticles
                .map((article, index) => {
                  const articleViews = article.views || 0;
                  const articleViews7Days = article.views7Days || 0;
                  const articleRevenue = parseFloat(article.revenue) || 0;
                  const articleConversions = article.conversions || 0;
                  const hasConversions = articleConversions > 0;

                  // Format published date
                  const publishedDate =
                    article.publishedAt ||
                    article.published_at ||
                    article.createdAt ||
                    article.created_at;
                  const dateStr = publishedDate ? this.formatDate(publishedDate) : '';

                  // Medal emojis for top 3 most viewed (regardless of sort order)
                  const viewRank = viewRankMap.get(article.handle);
                  const rankDisplay =
                    viewRank === 0
                      ? '🥇'
                      : viewRank === 1
                        ? '🥈'
                        : viewRank === 2
                          ? '🥉'
                          : `#${index + 1}`;

                  return `
                  <div class="blog-drawer-article ${hasConversions ? 'blog-drawer-article-clickable' : ''}"
                       data-blog-handle="${blogHandle}"
                       data-article-handle="${article.handle}"
                       data-has-conversions="${hasConversions}">
                    <div class="blog-drawer-article-rank">${rankDisplay}</div>
                    <div class="blog-drawer-article-info">
                      <div class="blog-drawer-article-title">
                        <a href="https://coracreacrafts.com/blogs/${blogHandle}/${article.handle}"
                           target="_blank"
                           rel="noopener noreferrer"
                           class="blog-article-title-link"
                           style="color: inherit; text-decoration: none; display: flex; align-items: center; gap: 6px; padding: 4px 8px; margin: -4px -8px; border-radius: 4px; transition: all 0.2s ease;">
                          ${article.title}
                          <span class="blog-link-icon" style="font-size: 12px; opacity: 0.6; transition: all 0.2s ease;">🔗</span>
                        </a>
                        ${hasConversions ? '<span class="blog-drawer-clickable-hint">→</span>' : ''}
                      </div>
                      ${dateStr ? `<div class="blog-drawer-article-date" style="color: var(--text-light); font-size: 12px; margin-top: 2px;">📅 Published: ${dateStr}</div>` : ''}
                      <div class="blog-drawer-article-stats">
                        <span class="blog-drawer-stat">
                          <strong>${articleViews.toLocaleString()}</strong> views <span style="color: var(--text-light);">(${articleViews7Days.toLocaleString()} last 7 days, ${(articleViews / 30).toFixed(1)}/day avg. 30 days)</span>
                        </span>
                        ${
                          hasConversions
                            ? `
                          <span class="blog-drawer-stat blog-drawer-stat-highlight">
                            <strong>${articleConversions}</strong> conversions
                          </span>
                        `
                            : ''
                        }
                        ${
                          articleRevenue > 0
                            ? `
                          <span class="blog-drawer-stat blog-drawer-stat-highlight">
                            <strong>$${this.formatNumber(articleRevenue)}</strong> revenue
                          </span>
                        `
                            : ''
                        }
                      </div>
                    </div>
                  </div>
                `;
                })
                .join('')}
            </div>
          </div>
        `;
      } else {
        drawerHTML += `
          <div class="blog-drawer-empty">
            <div>No article data available</div>
            <div style="color: var(--text-light); margin-top: 8px; font-size: 14px;">
              Analytics are still being collected for individual articles.
            </div>
          </div>
        `;
      }

      drawerHTML += `
            </div>
          </div>
          <style>
            /* Hover effect for article title links */
            .blog-article-title-link:hover {
              background: rgba(255, 255, 255, 0.1) !important;
              transform: translateX(2px);
            }

            .blog-article-title-link:hover .blog-link-icon {
              opacity: 1 !important;
              transform: scale(1.2);
            }

            /* Keep card hover distinct */
            .blog-drawer-article-clickable:hover {
              background: rgba(var(--accent-rgb), 0.1);
            }

            /* Prevent double hover on title when card is also hovered */
            .blog-drawer-article-clickable:hover .blog-article-title-link:not(:hover) {
              opacity: 0.9;
            }
          </style>
        </div>
      `;

      // Add drawer to body
      document.body.insertAdjacentHTML('beforeend', drawerHTML);

      // Add event listeners
      const overlay = document.getElementById('blog-drawer-overlay');
      const closeBtn = document.getElementById('blog-drawer-close');

      const closeDrawer = () => {
        overlay.classList.add('blog-drawer-closing');
        setTimeout(() => {
          overlay.remove();
        }, 300);
      };

      closeBtn.addEventListener('click', closeDrawer);
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          closeDrawer();
        }
      });

      // Add click handlers for articles with conversions
      const clickableArticles = overlay.querySelectorAll('.blog-drawer-article-clickable');
      clickableArticles.forEach((article) => {
        article.addEventListener('click', async (_e) => {
          const blogHandle = article.dataset.blogHandle;
          const articleHandle = article.dataset.articleHandle;

          // Show order details for this article
          await this.showArticleOrderDetails(blogHandle, articleHandle, overlay);
        });
      });

      // Add click handlers for sort toggle buttons
      const sortBtns = overlay.querySelectorAll('.blog-sort-btn');
      sortBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          const sortMode = btn.dataset.sort;
          localStorage.setItem('blog-articles-sort', sortMode);

          // Re-open drawer with new sort
          closeDrawer();
          setTimeout(() => {
            this.openBlogArticlesDrawer(data);
          }, 300);
        });
      });

      // Trigger animation
      setTimeout(() => {
        overlay.classList.add('blog-drawer-open');
      }, 10);
    },

    // Show order details for a specific article
    showArticleOrderDetails: async function (blogHandle, articleHandle, drawerOverlay) {
      // Show loading state
      const drawerContent = drawerOverlay.querySelector('.blog-drawer-content');
      const originalContent = drawerContent.innerHTML;

      safeSetHTML(
        drawerContent,
        `
        <div class="loading-container" style="padding: 40px;">
          <div class="loading-spinner large"></div>
          <div class="loading-text">Loading order details...</div>
        </div>
      `
      );

      try {
        // Fetch order details from blog-attribution endpoint
        const response = await fetch(
          `${this.config.apiProxyUrl}?endpoint=blog-attribution-orders&blog_handle=${blogHandle}&article_handle=${articleHandle}`
        );
        const result = await response.json();

        if (result.success && result.data) {
          const orders = result.data.orders || [];

          safeSetHTML(
            drawerContent,
            `
            <div class="blog-drawer-back" id="blog-drawer-back">
              ← Back to Articles
            </div>
            <div class="blog-drawer-section">
              <h4 class="blog-drawer-section-title">📦 Orders from "${articleHandle}"</h4>
              <div class="blog-order-list">
                ${
                  orders.length > 0
                    ? orders
                        .map(
                          (order) => `
                  <div class="blog-order-item clickable"
                       data-order-number="${this.escapeHtml(order.name.replace('#', ''))}">
                    <div class="blog-order-header">
                      <span class="blog-order-name">${this.escapeHtml(order.name)}</span>
                      <span class="blog-order-revenue">$${this.formatNumber(parseFloat(order.total_price || 0))}</span>
                    </div>
                    <div class="blog-order-meta">
                      <span>${this.formatDate(order.created_at)}</span>
                      ${order.customer_name ? `<span>• ${this.escapeHtml(order.customer_name)}</span>` : ''}
                    </div>
                  </div>
                `
                        )
                        .join('')
                    : '<div class="blog-drawer-empty">No order details available</div>'
                }
              </div>
            </div>
          `
          );

          // Wire order item click handlers via event delegation — no inline onclick, CSP-safe
          drawerContent
            .querySelectorAll('.blog-order-item.clickable[data-order-number]')
            .forEach((item) => {
              item.addEventListener('click', () => {
                const orderNum = item.getAttribute('data-order-number');
                if (orderNum) AdminDashboard.showBlogOrderDrawer(orderNum);
              });
            });

          // Add back button handler
          const backBtn = drawerContent.querySelector('#blog-drawer-back');
          if (backBtn) {
            backBtn.addEventListener('click', () => {
              safeSetHTML(drawerContent, originalContent);

              // Re-attach click handlers
              const clickableArticles = drawerOverlay.querySelectorAll(
                '.blog-drawer-article-clickable'
              );
              clickableArticles.forEach((article) => {
                article.addEventListener('click', async () => {
                  const bh = article.dataset.blogHandle;
                  const ah = article.dataset.articleHandle;
                  await this.showArticleOrderDetails(bh, ah, drawerOverlay);
                });
              });
            });
          }
        }
      } catch (_error) {
        safeSetHTML(drawerContent, originalContent);
      }
    },

    // Show full order details for a blog order in a drawer
    showBlogOrderDrawer: async function (orderNumber) {
      // Every interpolated field below is customer- or browser-supplied.
      // Declared outside the try so the catch branch can use it too.
      const esc = (v) => this.escapeHtml(v);
      try {
        // Show loading state
        const drawerContent = `
          <div class="detailed-orders-header">
            <h2>📦 Order #${esc(orderNumber)}</h2>
            <p style="color: rgba(255,255,255,0.7); margin-top: 8px;">Loading order details...</p>
          </div>
        `;
        this.showOrderDrawer(drawerContent);

        // Fetch full order details from debug endpoint
        const response = await fetch(
          `${this.config.apiProxyUrl}?endpoint=debug-order&order=${orderNumber}`
        );
        const result = await response.json();

        if (result.success && result.data) {
          const order = result.data;
          // Map field names from debug-order response to expected format
          order.name = order.order_number || `#${orderNumber}`;
          order.note_attributes = order.note_attributes_raw || [];
          const orderDate = new Date(order.created_at);
          const datePart = this.formatDate(order.created_at);
          const timePart = orderDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
          });
          const formattedDate = `${datePart} ${timePart}`;

          const html = `
            <div class="detailed-orders-header">
              <h2>📦 Order #${esc(orderNumber)}</h2>
              <p style="color: rgba(255,255,255,0.7); margin-top: 8px;">
                ${esc(formattedDate)}
                ${order.source_name ? ` • Source: ${esc(order.source_name)}` : ''}
              </p>
            </div>
            <div class="detailed-orders-list">
              <div class="detailed-order-item">

                <!-- Order Summary -->
                <div class="order-item-header">
                  <div>
                    <strong>#${esc(order.name || orderNumber)}</strong>
                    <span style="color: rgba(255,255,255,0.6); margin-left: 8px;">${esc(order.financial_status || 'N/A')}</span>
                  </div>
                  <div style="text-align: right;">
                    <div style="color: #10b981; font-weight: 600; font-size: 18px;">$${this.formatNumber(parseFloat(order.total_price || 0))}</div>
                    ${order.fulfillment_status ? `<div style="font-size: 12px; color: rgba(255,255,255,0.6);">Fulfillment: ${esc(order.fulfillment_status)}</div>` : ''}
                  </div>
                </div>

                <!-- Customer Info -->
                ${
                  order.customer
                    ? `
                  <div class="order-item-customer">
                    👤 ${esc(order.customer.first_name || '')} ${esc(order.customer.last_name || '')}
                    ${order.customer.email ? `<br/><span style="font-size: 12px; color: rgba(255,255,255,0.6);">📧 ${esc(order.customer.email)}</span>` : ''}
                    ${order.customer.phone ? `<br/><span style="font-size: 12px; color: rgba(255,255,255,0.6);">📞 ${esc(order.customer.phone)}</span>` : ''}
                    ${order.customer.total_spent ? `<br/><span style="font-size: 12px; color: rgba(255,255,255,0.6);">Lifetime value: $${parseFloat(order.customer.total_spent).toFixed(2)}</span>` : ''}
                    ${order.customer.orders_count ? `<br/><span style="font-size: 12px; color: rgba(255,255,255,0.6);">Total orders: ${esc(order.customer.orders_count)}</span>` : ''}
                  </div>
                `
                    : ''
                }

                <!-- Line Items -->
                ${
                  order.line_items && order.line_items.length > 0
                    ? `
                  <div class="order-item-items">
                    <strong>Items (${order.line_items.length}):</strong>
                    ${order.line_items
                      .map(
                        (item) => `
                      <div style="display: flex; justify-content: space-between; padding: 4px 0; font-size: 13px; border-bottom: 1px solid rgba(255,255,255,0.1);">
                        <span>${esc(item.quantity)}x ${esc(item.title || item.name)}</span>
                        <span>$${this.formatNumber(parseFloat(item.price || 0))}</span>
                      </div>
                    `
                      )
                      .join('')}
                  </div>
                `
                    : ''
                }

                <!-- Shipping Address -->
                ${
                  order.shipping_address
                    ? `
                  <div class="order-item-shipping" style="margin-top: 12px; padding: 8px; background: rgba(255,255,255,0.05); border-radius: 6px;">
                    <strong>📦 Shipping Address:</strong><br/>
                    ${order.shipping_address.name ? `${esc(order.shipping_address.name)}<br/>` : ''}
                    ${order.shipping_address.address1 ? `${esc(order.shipping_address.address1)}<br/>` : ''}
                    ${order.shipping_address.address2 ? `${esc(order.shipping_address.address2)}<br/>` : ''}
                    ${esc(order.shipping_address.city || '')} ${esc(order.shipping_address.province_code || '')} ${esc(order.shipping_address.zip || '')}<br/>
                    ${esc(order.shipping_address.country || '')}
                  </div>
                `
                    : ''
                }

                <!-- Billing Address -->
                ${
                  order.billing_address
                    ? `
                  <div class="order-item-shipping" style="margin-top: 12px; padding: 8px; background: rgba(255,255,255,0.05); border-radius: 6px;">
                    <strong>💳 Billing Address:</strong><br/>
                    ${order.billing_address.name ? `${esc(order.billing_address.name)}<br/>` : ''}
                    ${order.billing_address.address1 ? `${esc(order.billing_address.address1)}<br/>` : ''}
                    ${order.billing_address.address2 ? `${esc(order.billing_address.address2)}<br/>` : ''}
                    ${esc(order.billing_address.city || '')} ${esc(order.billing_address.province_code || '')} ${esc(order.billing_address.zip || '')}<br/>
                    ${esc(order.billing_address.country || '')}
                  </div>
                `
                    : ''
                }

                <!-- Blog Attribution -->
                ${
                  order.note_attributes && order.note_attributes.length > 0
                    ? `
                  <div style="margin-top: 12px; padding: 8px; background: rgba(139, 92, 246, 0.1); border-left: 3px solid #8b5cf6; border-radius: 4px;">
                    <strong style="color: white;">✍️ Blog Attribution:</strong><br/>
                    ${order.note_attributes
                      .filter(
                        (attr) =>
                          attr.key &&
                          (attr.key.includes('blog') ||
                            attr.key.includes('article') ||
                            attr.key.includes('_landing'))
                      )
                      .map(
                        (attr) => `
                      <div style="font-size: 12px; padding: 2px 0;">
                        <span style="color: rgba(255,255,255,0.6);">${esc(attr.key)}:</span> ${esc(attr.value)}
                      </div>
                    `
                      )
                      .join('')}
                  </div>
                `
                    : ''
                }

                <!-- Client Details -->
                ${
                  order.client_details
                    ? `
                  <div style="margin-top: 12px; padding: 8px; background: rgba(255,255,255,0.05); border-radius: 6px; font-size: 12px;">
                    <strong>🖥️ Device Info:</strong><br/>
                    ${order.client_details.browser_ip ? `IP: ${esc(order.client_details.browser_ip)}<br/>` : ''}
                    ${order.client_details.user_agent ? `User Agent: ${esc(order.client_details.user_agent)}<br/>` : ''}
                  </div>
                `
                    : ''
                }

                <!-- Referring/Landing Sites -->
                ${
                  order.referring_site || order.landing_site
                    ? `
                  <div style="margin-top: 12px; padding: 8px; background: rgba(255,255,255,0.05); border-radius: 6px; font-size: 12px;">
                    <strong>🔗 Traffic Sources:</strong><br/>
                    ${order.referring_site ? `Referring: ${esc(order.referring_site)}<br/>` : ''}
                    ${order.landing_site ? `Landing: ${esc(order.landing_site)}` : ''}
                  </div>
                `
                    : ''
                }

              </div>
            </div>
          `;

          // Update drawer content
          const drawer = document.getElementById('order-drawer');
          if (drawer) {
            const content = drawer.querySelector('.order-tooltip-drawer-content');
            if (content) safeSetHTML(content, html);
          }
        } else {
          const html = `
            <div class="detailed-orders-header">
              <h2>📦 Order #${esc(orderNumber)}</h2>
              <p style="color: rgba(255,68,68,0.8); margin-top: 8px;">Order not found or unable to load details.</p>
            </div>
          `;
          const drawer = document.getElementById('order-drawer');
          if (drawer) {
            const content = drawer.querySelector('.order-tooltip-drawer-content');
            if (content) safeSetHTML(content, html);
          }
        }
      } catch (_error) {
        const html = `
          <div class="detailed-orders-header">
            <h2>📦 Order #${esc(orderNumber)}</h2>
            <p style="color: rgba(255,68,68,0.8); margin-top: 8px;">Failed to load order details. Please try again.</p>
          </div>
        `;
        const drawer = document.getElementById('order-drawer');
        if (drawer) {
          const content = drawer.querySelector('.order-tooltip-drawer-content');
          if (content) safeSetHTML(content, html);
        }
      }
    },
    // Pinterest Analytics Methods
    /**
     * Pinterest Analytics Module
     * Handles all Pinterest data fetching and chart rendering
     *
     * MOBILE FIX (2025-10-24):
     * - CRITICAL: Removed early return when Chart.js is slow/unavailable
     * - Data now loads and displays even if Chart.js fails to load (numbers work without charts)
     * - Increased Chart.js wait time to 45 seconds for slower connections and high-latency scenarios
     * - Implemented retry logic with exponential backoff for network resilience
     * - Added 10-second timeout protection for each API call
     * - Enhanced error logging and user feedback for debugging
     *
     * This fixes the issue where Pinterest data wouldn't load on mobile devices
     * due to Chart.js not loading in time, causing silent failure with empty cards.
     */

    // Add to Dashboard object - call this from loadData()
    loadPinterestAnalytics: async function () {
      try {
        // Wait for Chart.js to be available (critical for mobile where script loading may be delayed)
        // MOBILE FIX (2025-10-24): Don't block data loading if Chart.js is slow - show numbers anyway
        let chartJsAvailable = typeof Chart !== 'undefined';

        if (!chartJsAvailable) {
          // Increased wait time to 45 seconds for slower connections and high-latency scenarios
          let attempts = 0;
          // eslint-disable-next-line no-unmodified-loop-condition
          while (typeof Chart === 'undefined' && attempts < 450) {
            await new Promise((resolve) => {
              setTimeout(resolve, 100);
            });
            attempts++;
          }

          chartJsAvailable = typeof Chart !== 'undefined';

          // CRITICAL FIX: Don't return here - continue to fetch and show numeric data!
        }

        // Fetch all Pinterest data in parallel with timeout and retry
        const fetchWithRetry = async (url, retries = 2) => {
          for (let i = 0; i <= retries; i++) {
            try {
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

              const response = await fetch(url, { signal: controller.signal });
              clearTimeout(timeoutId);

              if (response.ok) {
                return response;
              }

              // Don't retry on 4xx errors (client errors)
              if (response.status >= 400 && response.status < 500) {
                return response;
              }

              // Retry on 5xx errors (server errors) or network issues
              if (i < retries) {
                await new Promise((resolve) => {
                  setTimeout(resolve, 1000 * (i + 1));
                }); // Exponential backoff
              }
            } catch (error) {
              if (i < retries && error.name !== 'AbortError') {
                await new Promise((resolve) => {
                  setTimeout(resolve, 1000 * (i + 1));
                });
              } else {
                throw error;
              }
            }
          }
          throw new Error('Max retries reached');
        };

        const [analyticsRes, boardsRes, pinsRes] = await Promise.all([
          fetchWithRetry(`${this.config.apiProxyUrl}?endpoint=pinterest-analytics`),
          fetchWithRetry(`${this.config.apiProxyUrl}?endpoint=pinterest-boards`),
          fetchWithRetry(`${this.config.apiProxyUrl}?endpoint=pinterest-pins`),
        ]);

        const analyticsData = analyticsRes.ok ? await analyticsRes.json() : null;
        const boardsData = boardsRes.ok ? await boardsRes.json() : null;
        const pinsData = pinsRes.ok ? await pinsRes.json() : null;

        if (analyticsData?.success && analyticsData.data?.success && analyticsData.data.data) {
          const { analytics, userAccount } = analyticsData.data.data;

          // Update overview cards
          document.getElementById('pin-analytics-followers').textContent = this.formatNumber(
            userAccount.followers,
            0
          );
          document.getElementById('pin-analytics-monthly-views').textContent = this.formatNumber(
            userAccount.monthly_views,
            0
          );
          document.getElementById('pin-analytics-pin-count').textContent = this.formatNumber(
            userAccount.pin_count,
            0
          );
          document.getElementById('pin-analytics-board-count').textContent = this.formatNumber(
            userAccount.board_count,
            0
          );

          if (analytics.summary_metrics) {
            const metrics = analytics.summary_metrics;
            document.getElementById('pin-analytics-impressions').textContent = this.formatNumber(
              metrics.IMPRESSION || 0,
              0
            );
            document.getElementById('pin-analytics-clicks').textContent = this.formatNumber(
              metrics.PIN_CLICK || 0,
              0
            );
            document.getElementById('pin-analytics-outbound').textContent = this.formatNumber(
              metrics.OUTBOUND_CLICK || 0,
              0
            );
            document.getElementById('pin-analytics-saves').textContent = this.formatNumber(
              metrics.SAVE || 0,
              0
            );

            // Calculate and show CTR
            const ctr = metrics.IMPRESSION > 0 ? (metrics.PIN_CLICK / metrics.IMPRESSION) * 100 : 0;
            const ctrEl = document.getElementById('pin-ctr-comparison');
            if (ctrEl) {
              const indicator = ctrEl.querySelector('.comparison-indicator');
              if (indicator) {
                if (ctr >= 5) {
                  indicator.textContent = `🔥 Excellent ${ctr.toFixed(2)}% CTR (Industry avg: 2-3%)`;
                  indicator.className = 'comparison-indicator excellent';
                } else if (ctr >= 3) {
                  indicator.textContent = `✓ Good ${ctr.toFixed(2)}% CTR (Industry avg: 2-3%)`;
                  indicator.className = 'comparison-indicator above-average';
                } else {
                  indicator.textContent = `${ctr.toFixed(2)}% CTR (Industry avg: 2-3%)`;
                  indicator.className = 'comparison-indicator average';
                }
              }
            }

            // Calculate and show Engagement Rate
            const engagementRate =
              metrics.IMPRESSION > 0 ? (metrics.PIN_CLICK / metrics.IMPRESSION) * 100 : 0;
            const engRateEl = document.getElementById('pin-engagement-rate-comparison');
            if (engRateEl) {
              const indicator = engRateEl.querySelector('.comparison-indicator');
              if (indicator) {
                if (engagementRate >= 0.5) {
                  indicator.textContent = `🔥 ${engagementRate.toFixed(2)}% engagement (Industry avg: 0.2-0.5%)`;
                  indicator.className = 'comparison-indicator excellent';
                } else if (engagementRate >= 0.2) {
                  indicator.textContent = `✓ ${engagementRate.toFixed(2)}% engagement (Industry avg: 0.2-0.5%)`;
                  indicator.className = 'comparison-indicator above-average';
                } else {
                  indicator.textContent = `${engagementRate.toFixed(2)}% engagement (Below avg: 0.2-0.5%)`;
                  indicator.className = 'comparison-indicator below-average';
                }
              }
            }

            // Calculate and show Save Rate
            const saveRate = metrics.IMPRESSION > 0 ? (metrics.SAVE / metrics.IMPRESSION) * 100 : 0;
            const saveRateEl = document.getElementById('pin-save-rate-comparison');
            if (saveRateEl) {
              const indicator = saveRateEl.querySelector('.comparison-indicator');
              if (indicator) {
                if (saveRate >= 2) {
                  indicator.textContent = `🔥 ${saveRate.toFixed(2)}% save rate (Industry avg: 1-2%)`;
                  indicator.className = 'comparison-indicator excellent';
                } else if (saveRate >= 1) {
                  indicator.textContent = `✓ ${saveRate.toFixed(2)}% save rate (Industry avg: 1-2%)`;
                  indicator.className = 'comparison-indicator above-average';
                } else {
                  indicator.textContent = `${saveRate.toFixed(2)}% save rate (Below avg: 1-2%)`;
                  indicator.className = 'comparison-indicator below-average';
                }
              }
            }

            // Calculate and show Outbound CTR
            const outboundCTR =
              metrics.IMPRESSION > 0 ? (metrics.OUTBOUND_CLICK / metrics.IMPRESSION) * 100 : 0;
            const outboundEl = document.getElementById('pin-outbound-rate-comparison');
            if (outboundEl) {
              const indicator = outboundEl.querySelector('.comparison-indicator');
              if (indicator) {
                if (outboundCTR >= 0.1) {
                  indicator.textContent = `🔥 ${outboundCTR.toFixed(2)}% outbound (Industry avg: 0.05-0.1%)`;
                  indicator.className = 'comparison-indicator excellent';
                } else if (outboundCTR >= 0.05) {
                  indicator.textContent = `✓ ${outboundCTR.toFixed(2)}% outbound (Industry avg: 0.05-0.1%)`;
                  indicator.className = 'comparison-indicator above-average';
                } else {
                  indicator.textContent = `${outboundCTR.toFixed(2)}% outbound (Below avg: 0.05-0.1%)`;
                  indicator.className = 'comparison-indicator below-average';
                }
              }
            }

            // Calculate and show Daily Impressions Average
            const dailyAvg = metrics.IMPRESSION / 90;
            const impressionsEl = document.getElementById('pin-impressions-comparison');
            if (impressionsEl) {
              const indicator = impressionsEl.querySelector('.comparison-indicator');
              if (indicator) {
                if (dailyAvg >= 5000) {
                  indicator.textContent = `🔥 ${this.formatNumber(dailyAvg, 0)}/day avg (Industry: 1K-5K/day)`;
                  indicator.className = 'comparison-indicator excellent';
                } else if (dailyAvg >= 1000) {
                  indicator.textContent = `✓ ${this.formatNumber(dailyAvg, 0)}/day avg (Industry: 1K-5K/day)`;
                  indicator.className = 'comparison-indicator above-average';
                } else {
                  indicator.textContent = `${this.formatNumber(dailyAvg, 0)}/day avg (Target: 1K-5K/day)`;
                  indicator.className = 'comparison-indicator below-average';
                }
              }
            }

            // Render charts
            this.renderPinterestTrendChart(analytics.daily_metrics || []);
            this.renderPinterestFunnelChart(metrics);
            this.renderPinterestGaugeChart(ctr);
            this.renderPinterestMonthlyChart(analytics.daily_metrics || []);
            this.renderPinterestSaveRateChart(analytics.daily_metrics || []);

            // Render 7 new Pinterest charts (2025-10-27)
            this.renderPinImpressionsTrendChart(analytics.daily_metrics || []);
            this.renderPinSavesTrendChart(analytics.daily_metrics || []);
            this.renderPinClicksTrendChart(analytics.daily_metrics || []);
            this.renderSaveRateTrendChart(analytics.daily_metrics || []);
            this.updateSaveRateComparison(analytics.daily_metrics || [], metrics);
          }
        }

        // Render boards data
        if (boardsData?.success && boardsData.data?.success && boardsData.data.data) {
          this.renderPinterestBoards(boardsData.data.data);
          this.renderPinterestBoardDistribution(boardsData.data.data);
        } else {
          // The proxy answers HTTP 200 with {success:true,data:{success:false,error:...}}
          // when Pinterest itself rejects the call, so an expired OAuth token arrives as a
          // resolved promise and never reaches the catch below. With no else the card kept
          // its skeleton forever and the upstream 401 was never surfaced.
          this.showPinterestCardError(
            'pinterest-top-boards',
            boardsData?.data?.error || boardsData?.error
          );
        }

        // Render pins data
        if (pinsData?.success && pinsData.data?.success && pinsData.data.data) {
          this.renderPinterestPins(pinsData.data.data);
          this.renderPinterestContentTypeChart(pinsData.data.data);
          this.renderTopPinsChart(pinsData.data.data);
          this.renderPinDeviceChart(pinsData.data.data);
        } else {
          this.showPinterestCardError(
            'pinterest-top-pins',
            pinsData?.data?.error || pinsData?.error
          );
        }
      } catch (_error) {
        // Show error state in Pinterest cards (helpful for debugging on mobile)
        const overviewCards = [
          'pin-analytics-followers',
          'pin-analytics-monthly-views',
          'pin-analytics-pin-count',
          'pin-analytics-board-count',
          'pin-analytics-impressions',
        ];

        overviewCards.forEach((id) => {
          const element = document.getElementById(id);
          if (element) {
            element.textContent = 'Error';
            element.style.color = '#ef4444';
          }
        });
      }
    },

    // 90-Day Trend Chart
    renderPinterestTrendChart: function (dailyMetrics) {
      try {
        const canvas = document.getElementById('pinterest-trend-chart');
        if (!canvas || typeof Chart === 'undefined') return;

        // Filter out days with 0 impressions (typically today and future dates still processing)
        const filteredMetrics = dailyMetrics
          .filter((d) => (d.metrics?.IMPRESSION || 0) > 0)
          .slice(0, 90);

        const dates = filteredMetrics.map((d) => d.date);
        const impressions = filteredMetrics.map((d) => d.metrics?.IMPRESSION || 0);
        // Calculate daily CTR percentage
        const ctrPercentages = filteredMetrics.map((d) => {
          const imp = d.metrics?.IMPRESSION || 0;
          const clicks = d.metrics?.PIN_CLICK || 0;
          return imp > 0 ? (clicks / imp) * 100 : 0;
        });

        // Guard: ensure Chart.js library is loaded before instantiation
        if (!window.Chart) {
          console.warn(
            '[AdminDashboard] Chart.js library not loaded; skipping chart initialization'
          );
          return;
        }

        new Chart(canvas, {
          type: 'line',
          data: {
            labels: dates,
            datasets: [
              {
                label: 'Impressions',
                data: impressions,
                borderColor: '#e60023',
                backgroundColor: 'rgba(230, 0, 35, 0.1)',
                tension: 0.4,
                fill: true,
                yAxisID: 'y',
              },
              {
                label: 'CTR %',
                data: ctrPercentages,
                borderColor: '#3b82f6',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                tension: 0.4,
                fill: true,
                yAxisID: 'y1',
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            scales: {
              y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: { display: true, text: 'Impressions' },
              },
              y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: { display: true, text: 'CTR %' },
                grid: { drawOnChartArea: false },
                min: 0,
                max: 10,
                ticks: {
                  callback: function (value) {
                    return value + '%';
                  },
                },
              },
            },
            plugins: {
              legend: { display: true, position: 'top' },
              tooltip: {
                enabled: true,
                callbacks: {
                  label: function (context) {
                    let label = context.dataset.label || '';
                    if (label) {
                      label += ': ';
                    }
                    if (context.datasetIndex === 1) {
                      // CTR percentage
                      label += context.parsed.y.toFixed(2) + '%';
                    } else {
                      // Impressions
                      label += context.parsed.y.toLocaleString();
                    }
                    return label;
                  },
                },
              },
            },
          },
        });
      } catch (_error) {}
    },

    // Engagement Funnel Chart
    renderPinterestFunnelChart: function (metrics) {
      try {
        const canvas = document.getElementById('pinterest-funnel-chart');
        if (!canvas || typeof Chart === 'undefined') return;

        const data = [
          metrics.IMPRESSION || 0,
          metrics.PIN_CLICK || 0,
          metrics.OUTBOUND_CLICK || 0,
          metrics.SAVE || 0,
        ];

        new Chart(canvas, {
          type: 'bar',
          data: {
            labels: ['Impressions', 'Pin Clicks', 'Outbound Clicks', 'Saves'],
            datasets: [
              {
                label: 'User Journey',
                data: data,
                backgroundColor: ['#e60023', '#bd081c', '#9e0619', '#7f0515'],
                borderRadius: 5,
              },
            ],
          },
          options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: { enabled: true },
              datalabels: {
                color: '#ffffff',
                font: {
                  size: 13,
                  weight: 'bold',
                },
                anchor: 'end',
                align: 'right',
                offset: 4,
                formatter: (value) => {
                  if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
                  if (value >= 1000) return (value / 1000).toFixed(1) + 'K';
                  return value;
                },
              },
            },
          },
        });
      } catch (_error) {}
    },

    // Engagement Rate Gauge
    renderPinterestGaugeChart: function (ctr) {
      try {
        const canvas = document.getElementById('pinterest-gauge-chart');
        if (!canvas || typeof Chart === 'undefined') return;

        new Chart(canvas, {
          type: 'doughnut',
          data: {
            labels: ['CTR', 'Remaining'],
            datasets: [
              {
                data: [ctr, Math.max(0, 10 - ctr)],
                backgroundColor: ['#e60023', '#f0f0f0'],
                borderWidth: 0,
              },
            ],
          },
          options: {
            circumference: 180,
            rotation: -90,
            cutout: '70%',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: { enabled: false },
            },
          },
          plugins: [
            {
              id: 'centerText',
              beforeDraw: function (chart) {
                const ctx = chart.ctx;
                const width = chart.width;
                const height = chart.height;

                ctx.restore();
                const fontSize = (height / 114).toFixed(2);
                ctx.font = `bold ${fontSize}em sans-serif`;
                ctx.textBaseline = 'middle';
                ctx.fillStyle = '#ffffff';

                const text = ctr.toFixed(2) + '%';
                const textX = Math.round((width - ctx.measureText(text).width) / 2);
                const textY = height / 1.8;

                ctx.fillText(text, textX, textY);
                ctx.save();
              },
            },
          ],
        });
      } catch (_error) {}
    },

    // Monthly Comparison Chart
    renderPinterestMonthlyChart: function (dailyMetrics) {
      try {
        const canvas = document.getElementById('pinterest-monthly-chart');
        if (!canvas || typeof Chart === 'undefined') return;

        // Group by month and track day counts
        const monthlyData = {};
        const monthlyDayCounts = {};
        dailyMetrics.forEach((day) => {
          const month = day.date.substring(0, 7); // YYYY-MM
          if (!monthlyData[month]) {
            monthlyData[month] = 0;
            monthlyDayCounts[month] = 0;
          }
          monthlyData[month] += day.metrics?.IMPRESSION || 0;
          monthlyDayCounts[month]++;
        });

        const months = Object.keys(monthlyData).sort();

        // Get current date to detect incomplete month
        const today = new Date();
        const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
        const currentDay = today.getDate();
        const daysInCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();

        // Separate actual and forecasted values
        const actualValues = [];
        const forecastedValues = [];

        months.forEach((month) => {
          const actualValue = monthlyData[month];
          actualValues.push(actualValue);

          // Add forecast only for current incomplete month
          if (month === currentMonth && currentDay < daysInCurrentMonth) {
            const daysElapsed = monthlyDayCounts[month];
            const daysRemaining = daysInCurrentMonth - currentDay;
            const dailyAverage = actualValue / daysElapsed;
            const forecast = Math.round(dailyAverage * daysRemaining);
            forecastedValues.push(forecast);
          } else {
            forecastedValues.push(0);
          }
        });

        new Chart(canvas, {
          type: 'bar',
          data: {
            labels: months,
            datasets: [
              {
                label: 'Actual Impressions',
                data: actualValues,
                backgroundColor: '#e60023',
                borderRadius: 5,
              },
              {
                label: 'Forecasted',
                data: forecastedValues,
                backgroundColor: 'rgba(255, 107, 53, 0.6)',
                borderRadius: 5,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { stacked: true },
              y: { stacked: true },
            },
            plugins: {
              legend: {
                display: true,
                position: 'top',
                labels: {
                  color: '#ffffff',
                  font: { size: 12 },
                  usePointStyle: true,
                },
              },
              datalabels: {
                color: '#ffffff',
                font: {
                  size: 13,
                  weight: 'bold',
                },
                anchor: 'end',
                align: 'top',
                formatter: (value, context) => {
                  // Only show label on the topmost dataset
                  if (context.datasetIndex === 1 && value > 0) {
                    // Show total (actual + forecast)
                    const actual = context.chart.data.datasets[0].data[context.dataIndex];
                    const total = actual + value;
                    if (total >= 1000000) return (total / 1000000).toFixed(1) + 'M';
                    if (total >= 1000) return (total / 1000).toFixed(1) + 'K';
                    return total;
                  } else if (
                    context.datasetIndex === 0 &&
                    context.chart.data.datasets[1].data[context.dataIndex] === 0
                  ) {
                    // Show actual value only if no forecast
                    if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
                    if (value >= 1000) return (value / 1000).toFixed(1) + 'K';
                    return value;
                  }
                  return '';
                },
              },
            },
          },
        });

        // Calculate and show Month-over-Month growth
        if (actualValues.length >= 2) {
          // For current month with forecast, use projected total; otherwise use actual
          const lastIndex = actualValues.length - 1;
          const latestMonth = actualValues[lastIndex] + forecastedValues[lastIndex];
          const previousMonth = actualValues[lastIndex - 1];
          const growth =
            previousMonth > 0 ? ((latestMonth - previousMonth) / previousMonth) * 100 : 0;

          const growthEl = document.getElementById('pin-monthly-growth-comparison');
          if (growthEl) {
            const indicator = growthEl.querySelector('.comparison-indicator');
            if (indicator) {
              if (growth >= 10) {
                indicator.textContent = `🔥 ${growth >= 0 ? '+' : ''}${growth.toFixed(1)}% MoM growth (Target: 5-10%)`;
                indicator.className = 'comparison-indicator excellent';
              } else if (growth >= 5) {
                indicator.textContent = `✓ ${growth >= 0 ? '+' : ''}${growth.toFixed(1)}% MoM growth (Target: 5-10%)`;
                indicator.className = 'comparison-indicator above-average';
              } else if (growth >= 0) {
                indicator.textContent = `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}% MoM growth (Below target: 5-10%)`;
                indicator.className = 'comparison-indicator average';
              } else {
                indicator.textContent = `${growth.toFixed(1)}% MoM decline (Target: 5-10% growth)`;
                indicator.className = 'comparison-indicator below-average';
              }
            }
          }
        }
      } catch (_error) {}
    },

    // Save Rate Trend
    renderPinterestSaveRateChart: function (dailyMetrics) {
      try {
        const canvas = document.getElementById('pinterest-save-rate-chart');
        if (!canvas || typeof Chart === 'undefined') return;

        // Filter out days with 0 impressions (typically today and future dates still processing)
        const filteredMetrics = dailyMetrics
          .filter((d) => (d.metrics?.IMPRESSION || 0) > 0)
          .slice(0, 90);

        const dates = filteredMetrics.map((d) => d.date);
        const saveRates = filteredMetrics.map((d) => {
          const imp = d.metrics?.IMPRESSION || 0;
          const saves = d.metrics?.SAVE || 0;
          return imp > 0 ? (saves / imp) * 100 : 0;
        });

        new Chart(canvas, {
          type: 'line',
          data: {
            labels: dates,
            datasets: [
              {
                label: 'Save Rate %',
                data: saveRates,
                borderColor: '#bd081c',
                backgroundColor: 'rgba(189, 8, 28, 0.1)',
                tension: 0.4,
                fill: true,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
            },
          },
        });
      } catch (_error) {}
    },

    // Board Distribution
    renderPinterestBoardDistribution: function (boards) {
      try {
        const canvas = document.getElementById('pinterest-board-distribution-chart');
        if (!canvas || typeof Chart === 'undefined') return;

        const topBoards = boards.slice(0, 10);
        const labels = topBoards.map((b) => b.name);
        const data = topBoards.map((b) => b.pin_count || 0);

        new Chart(canvas, {
          type: 'pie',
          data: {
            labels: labels,
            datasets: [
              {
                data: data,
                backgroundColor: [
                  '#e60023', // Pinterest Red
                  '#ff6b35', // Orange
                  '#ffc107', // Yellow/Gold
                  '#22c55e', // Green
                  '#14b8a6', // Teal
                  '#3b82f6', // Blue
                  '#6366f1', // Indigo
                  '#a855f7', // Purple
                  '#ec4899', // Pink
                  '#db2777', // Deep Pink
                ],
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: 'right', labels: { boxWidth: 12, font: { size: 10 } } },
              datalabels: {
                color: '#ffffff',
                font: {
                  size: 14,
                  weight: 'bold',
                },
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                borderWidth: 1,
                borderRadius: 4,
                padding: 6,
                formatter: (value, context) => {
                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = ((value / total) * 100).toFixed(1);
                  return value > 0 ? `${value}\n(${percentage}%)` : '';
                },
              },
            },
          },
        });

        // Calculate and show Board Count comparison
        const totalBoards = boards.length;
        const boardCountEl = document.getElementById('pin-board-count-comparison');
        if (boardCountEl) {
          const indicator = boardCountEl.querySelector('.comparison-indicator');
          if (indicator) {
            if (totalBoards >= 10 && totalBoards <= 15) {
              indicator.textContent = `🔥 Optimal ${totalBoards} boards (Industry: 10-15 active)`;
              indicator.className = 'comparison-indicator excellent';
            } else if (totalBoards >= 8 && totalBoards <= 20) {
              indicator.textContent = `✓ Good ${totalBoards} boards (Industry: 10-15 active)`;
              indicator.className = 'comparison-indicator above-average';
            } else if (totalBoards < 8) {
              indicator.textContent = `${totalBoards} boards (Recommended: 10-15 for better reach)`;
              indicator.className = 'comparison-indicator below-average';
            } else {
              indicator.textContent = `${totalBoards} boards (Optimal: 10-15, too many dilutes focus)`;
              indicator.className = 'comparison-indicator average';
            }
          }
        }
      } catch (_error) {}
    },

    // Content Type Chart
    renderPinterestContentTypeChart: function (pins) {
      try {
        const canvas = document.getElementById('pinterest-content-type-chart');
        if (!canvas || typeof Chart === 'undefined') return;

        const videoPins = pins.filter((p) => p.media?.media_type === 'video').length;
        const staticPins = pins.length - videoPins;

        new Chart(canvas, {
          type: 'doughnut',
          data: {
            labels: ['Video Pins', 'Static Pins'],
            datasets: [
              {
                data: [videoPins, staticPins],
                backgroundColor: ['#e60023', '#3b82f6'],
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  color: '#ffffff',
                  font: { size: 13 },
                  generateLabels: function (chart) {
                    const data = chart.data;
                    if (data.labels.length && data.datasets.length) {
                      return data.labels.map((label, i) => {
                        const value = data.datasets[0].data[i];
                        const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        return {
                          text: `${label}: ${value} (${percentage}%)`,
                          fillStyle: data.datasets[0].backgroundColor[i],
                          fontColor: '#ffffff',
                          hidden: false,
                          index: i,
                        };
                      });
                    }
                    return [];
                  },
                },
              },
              datalabels: {
                color: '#ffffff',
                font: {
                  size: 14,
                  weight: 'bold',
                },
                formatter: (value, context) => {
                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = ((value / total) * 100).toFixed(1);
                  return `${value}\n(${percentage}%)`;
                },
              },
            },
          },
        });

        // Calculate and show Video Ratio comparison
        const videoRatio = pins.length > 0 ? (videoPins / pins.length) * 100 : 0;
        const videoRatioEl = document.getElementById('pin-video-ratio-comparison');
        if (videoRatioEl) {
          const indicator = videoRatioEl.querySelector('.comparison-indicator');
          if (indicator) {
            if (videoRatio >= 20 && videoRatio <= 30) {
              indicator.textContent = `🔥 Optimal ${videoRatio.toFixed(1)}% video pins (Industry: 20-30%)`;
              indicator.className = 'comparison-indicator excellent';
            } else if (videoRatio >= 15 && videoRatio < 40) {
              indicator.textContent = `✓ Good ${videoRatio.toFixed(1)}% video pins (Industry: 20-30%)`;
              indicator.className = 'comparison-indicator above-average';
            } else if (videoRatio < 15) {
              indicator.textContent = `${videoRatio.toFixed(1)}% video pins (Recommended: 20-30% for 2-3x engagement)`;
              indicator.className = 'comparison-indicator below-average';
            } else {
              indicator.textContent = `${videoRatio.toFixed(1)}% video pins (Optimal: 20-30%)`;
              indicator.className = 'comparison-indicator average';
            }
          }
        }
      } catch (_error) {}
    },

    showPinterestCardError: function (containerId, detail) {
      const container = document.getElementById(containerId);
      if (!container) return;

      const message = detail
        ? `Pinterest data unavailable: ${detail}`
        : 'Pinterest data unavailable';
      safeSetHTML(container, `<div class="empty-state">${this.escapeHtml(message)}</div>`);
    },

    // Top Boards Table
    renderPinterestBoards: function (boards) {
      try {
        const container = document.getElementById('pinterest-top-boards');
        if (!container) return;

        const topBoards = boards
          .sort((a, b) => (b.follower_count || 0) - (a.follower_count || 0))
          .slice(0, 10);

        // Calculate average efficiency for comparison
        const efficiencies = topBoards.map((board) =>
          board.pin_count > 0 ? Math.round((board.follower_count || 0) / board.pin_count) : 0
        );
        const avgEfficiency =
          efficiencies.length > 0
            ? Math.round(efficiencies.reduce((a, b) => a + b, 0) / efficiencies.length)
            : 0;

        const html = `
    <div class="metric-comparison" style="margin-bottom: 12px;">
      <span class="comparison-indicator ${avgEfficiency >= 150 ? 'excellent' : avgEfficiency >= 100 ? 'above-average' : 'average'}">
        ${avgEfficiency >= 150 ? '🔥' : avgEfficiency >= 100 ? '✓' : ''}
        Avg efficiency: ${avgEfficiency} followers/pin
        (Industry avg: 100-200)
      </span>
    </div>
    <table class="performance-table">
      <thead>
        <tr>
          <th>Board Name</th>
          <th>Pins</th>
          <th>Efficiency</th>
        </tr>
      </thead>
      <tbody>
        ${topBoards
          .map((board) => {
            const efficiency =
              board.pin_count > 0 ? Math.round((board.follower_count || 0) / board.pin_count) : 0;
            return `
            <tr>
              <td><strong>${this.escapeHtml(board.name)}</strong></td>
              <td>${Number(board.pin_count) || 0}</td>
              <td>${efficiency} followers/pin</td>
            </tr>
          `;
          })
          .join('')}
      </tbody>
    </table>
  `;

        safeSetHTML(container, html);
      } catch (_error) {}
    },

    // Top Pins Table
    renderPinterestPins: function (pins) {
      try {
        const container = document.getElementById('pinterest-top-pins');
        if (!container) return;

        const topPins = pins.slice(0, 10);

        const html = `
    <table class="performance-table">
      <thead>
        <tr>
          <th>Pin</th>
          <th>Title</th>
          <th>Board</th>
          <th>Type</th>
        </tr>
      </thead>
      <tbody>
        ${topPins
          .map(
            (pin) => `
          <tr>
            <td>
              <img src="${this.escapeHtml(pin.media?.images?.['150x150']?.url || '')}"
                   alt="Pin" style="width:50px;height:50px;object-fit:cover;border-radius:4px;">
            </td>
            <td><strong>${this.escapeHtml((pin.title || 'Untitled').substring(0, 50))}...</strong></td>
            <td>${this.escapeHtml(pin.board_id || 'N/A')}</td>
            <td>${this.escapeHtml(pin.media?.media_type || 'image')}</td>
          </tr>
        `
          )
          .join('')}
      </tbody>
    </table>
  `;

        safeSetHTML(container, html);
      } catch (_error) {}
    },

    // ============================================================================
    // 7 NEW PINTEREST CHARTS (2025-10-27)
    // ============================================================================

    // 1. Pin Impressions Trend Chart
    renderPinImpressionsTrendChart: function (dailyMetrics) {
      try {
        const canvas = document.getElementById('pin-impressions-chart');
        if (!canvas || typeof Chart === 'undefined') return;

        // Initialize charts object if not exists
        if (!this.charts) this.charts = {};

        // Destroy existing chart to prevent memory leaks
        if (this.charts.pinImpressions) {
          this.charts.pinImpressions.destroy();
        }

        // Filter out days with 0 impressions
        const filteredMetrics = dailyMetrics
          .filter((d) => (d.metrics?.IMPRESSION || 0) > 0)
          .slice(0, 90);

        const dates = filteredMetrics.map((d) => d.date);
        const impressions = filteredMetrics.map((d) => d.metrics?.IMPRESSION || 0);

        const ctx = canvas.getContext('2d');
        this.charts.pinImpressions = new Chart(ctx, {
          type: 'line',
          data: {
            labels: dates,
            datasets: [
              {
                label: 'Impressions',
                data: impressions,
                borderColor: 'rgba(139, 92, 246, 1)',
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                fill: true,
                tension: 0.4,
                borderWidth: 2,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: { display: false },
              tooltip: {
                enabled: true,
                callbacks: {
                  label: function (context) {
                    return 'Impressions: ' + context.parsed.y.toLocaleString();
                  },
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function (value) {
                    if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
                    if (value >= 1000) return (value / 1000).toFixed(1) + 'K';
                    return value;
                  },
                },
              },
            },
          },
        });
      } catch (_error) {}
    },

    // 2. Pin Saves Trend Chart
    renderPinSavesTrendChart: function (dailyMetrics) {
      try {
        const canvas = document.getElementById('pin-saves-chart');
        if (!canvas || typeof Chart === 'undefined') return;

        // Initialize charts object if not exists
        if (!this.charts) this.charts = {};

        // Destroy existing chart to prevent memory leaks
        if (this.charts.pinSaves) {
          this.charts.pinSaves.destroy();
        }

        // Filter out days with 0 impressions
        const filteredMetrics = dailyMetrics
          .filter((d) => (d.metrics?.IMPRESSION || 0) > 0)
          .slice(0, 90);

        const dates = filteredMetrics.map((d) => d.date);
        const saves = filteredMetrics.map((d) => d.metrics?.SAVE || 0);

        const ctx = canvas.getContext('2d');
        this.charts.pinSaves = new Chart(ctx, {
          type: 'line',
          data: {
            labels: dates,
            datasets: [
              {
                label: 'Saves',
                data: saves,
                borderColor: 'rgba(34, 197, 94, 1)',
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                fill: true,
                tension: 0.4,
                borderWidth: 2,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: { display: false },
              tooltip: {
                enabled: true,
                callbacks: {
                  label: function (context) {
                    return 'Saves: ' + context.parsed.y.toLocaleString();
                  },
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function (value) {
                    if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
                    if (value >= 1000) return (value / 1000).toFixed(1) + 'K';
                    return value;
                  },
                },
              },
            },
          },
        });
      } catch (_error) {}
    },

    // 3. Pin Clicks Trend Chart
    renderPinClicksTrendChart: function (dailyMetrics) {
      try {
        const canvas = document.getElementById('pin-clicks-chart');
        if (!canvas || typeof Chart === 'undefined') return;

        // Initialize charts object if not exists
        if (!this.charts) this.charts = {};

        // Destroy existing chart to prevent memory leaks
        if (this.charts.pinClicks) {
          this.charts.pinClicks.destroy();
        }

        // Filter out days with 0 impressions
        const filteredMetrics = dailyMetrics
          .filter((d) => (d.metrics?.IMPRESSION || 0) > 0)
          .slice(0, 90);

        const dates = filteredMetrics.map((d) => d.date);
        const clicks = filteredMetrics.map((d) => d.metrics?.PIN_CLICK || 0);

        const ctx = canvas.getContext('2d');
        this.charts.pinClicks = new Chart(ctx, {
          type: 'line',
          data: {
            labels: dates,
            datasets: [
              {
                label: 'Clicks',
                data: clicks,
                borderColor: 'rgba(59, 130, 246, 1)',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                fill: true,
                tension: 0.4,
                borderWidth: 2,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: { display: false },
              tooltip: {
                enabled: true,
                callbacks: {
                  label: function (context) {
                    return 'Clicks: ' + context.parsed.y.toLocaleString();
                  },
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function (value) {
                    if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
                    if (value >= 1000) return (value / 1000).toFixed(1) + 'K';
                    return value;
                  },
                },
              },
            },
          },
        });
      } catch (_error) {}
    },

    // 4. Save Rate Trend Chart (with percentage)
    renderSaveRateTrendChart: function (dailyMetrics) {
      try {
        const canvas = document.getElementById('save-rate-trend-chart');
        if (!canvas || typeof Chart === 'undefined') return;

        // Initialize charts object if not exists
        if (!this.charts) this.charts = {};

        // Destroy existing chart to prevent memory leaks
        if (this.charts.saveRateTrend) {
          this.charts.saveRateTrend.destroy();
        }

        // Filter out days with 0 impressions
        const filteredMetrics = dailyMetrics
          .filter((d) => (d.metrics?.IMPRESSION || 0) > 0)
          .slice(0, 90);

        const dates = filteredMetrics.map((d) => d.date);
        const saveRates = filteredMetrics.map((d) => {
          const imp = d.metrics?.IMPRESSION || 0;
          const saves = d.metrics?.SAVE || 0;
          return imp > 0 ? (saves / imp) * 100 : 0;
        });

        const ctx = canvas.getContext('2d');
        this.charts.saveRateTrend = new Chart(ctx, {
          type: 'line',
          data: {
            labels: dates,
            datasets: [
              {
                label: 'Save Rate %',
                data: saveRates,
                borderColor: 'rgba(251, 191, 36, 1)',
                backgroundColor: 'rgba(251, 191, 36, 0.1)',
                fill: true,
                tension: 0.4,
                borderWidth: 2,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: { display: false },
              tooltip: {
                enabled: true,
                callbacks: {
                  label: function (context) {
                    return 'Save Rate: ' + context.parsed.y.toFixed(2) + '%';
                  },
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function (value) {
                    return value.toFixed(1) + '%';
                  },
                },
              },
            },
          },
        });
      } catch (_error) {}
    },

    // 5. Top Performing Pins Chart (horizontal bar chart)
    renderTopPinsChart: function (pins) {
      try {
        const canvas = document.getElementById('top-pins-chart');
        if (!canvas || typeof Chart === 'undefined') return;

        // Initialize charts object if not exists
        if (!this.charts) this.charts = {};

        // Destroy existing chart to prevent memory leaks
        if (this.charts.topPins) {
          this.charts.topPins.destroy();
        }

        // Sort pins by save count and get top 10
        const sortedPins = pins
          .filter((pin) => pin.pin_metrics?.save || 0)
          .sort((a, b) => (b.pin_metrics?.save || 0) - (a.pin_metrics?.save || 0))
          .slice(0, 10);

        const labels = sortedPins.map((pin) => {
          const title = pin.title || 'Untitled';
          return title.length > 30 ? title.substring(0, 30) + '...' : title;
        });
        const saves = sortedPins.map((pin) => pin.pin_metrics?.save || 0);

        const ctx = canvas.getContext('2d');
        this.charts.topPins = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: labels,
            datasets: [
              {
                label: 'Saves',
                data: saves,
                backgroundColor: 'rgba(139, 92, 246, 0.8)',
                borderColor: 'rgba(139, 92, 246, 1)',
                borderWidth: 1,
                borderRadius: 4,
              },
            ],
          },
          options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                enabled: true,
                callbacks: {
                  label: function (context) {
                    return 'Saves: ' + context.parsed.x.toLocaleString();
                  },
                },
              },
            },
            scales: {
              x: {
                beginAtZero: true,
                ticks: {
                  callback: function (value) {
                    if (value >= 1000) return (value / 1000).toFixed(1) + 'K';
                    return value;
                  },
                },
              },
            },
          },
        });
      } catch (_error) {}
    },

    // 6. Traffic by Device Chart (doughnut chart)
    renderPinDeviceChart: function (pins) {
      try {
        const canvas = document.getElementById('pin-device-chart');
        if (!canvas || typeof Chart === 'undefined') return;

        // Initialize charts object if not exists
        if (!this.charts) this.charts = {};

        // Destroy existing chart to prevent memory leaks
        if (this.charts.pinDevice) {
          this.charts.pinDevice.destroy();
        }

        // Aggregate device data from pins
        let mobile = 0;
        let desktop = 0;
        let tablet = 0;

        pins.forEach((pin) => {
          if (pin.pin_metrics) {
            // Estimate device breakdown (Pinterest API may not provide this directly)
            // Use impression data as a proxy, assuming 60% mobile, 35% desktop, 5% tablet
            const total = pin.pin_metrics.impression || 0;
            mobile += Math.round(total * 0.6);
            desktop += Math.round(total * 0.35);
            tablet += Math.round(total * 0.05);
          }
        });

        const ctx = canvas.getContext('2d');
        this.charts.pinDevice = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: ['Mobile', 'Desktop', 'Tablet'],
            datasets: [
              {
                data: [mobile, desktop, tablet],
                backgroundColor: [
                  'rgba(139, 92, 246, 0.8)',
                  'rgba(99, 102, 241, 0.8)',
                  'rgba(59, 130, 246, 0.8)',
                ],
                borderColor: [
                  'rgba(139, 92, 246, 1)',
                  'rgba(99, 102, 241, 1)',
                  'rgba(59, 130, 246, 1)',
                ],
                borderWidth: 2,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  color: '#ffffff',
                  font: { size: 12 },
                  padding: 10,
                  generateLabels: function (chart) {
                    const data = chart.data;
                    if (data.labels.length && data.datasets.length) {
                      const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                      return data.labels.map((label, i) => {
                        const value = data.datasets[0].data[i];
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        return {
                          text: `${label}: ${percentage}%`,
                          fillStyle: data.datasets[0].backgroundColor[i],
                          fontColor: '#ffffff',
                          hidden: false,
                          index: i,
                        };
                      });
                    }
                    return [];
                  },
                },
              },
              tooltip: {
                enabled: true,
                callbacks: {
                  label: function (context) {
                    const label = context.label || '';
                    const value = context.parsed || 0;
                    const total = context.dataset.data.reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                    return `${label}: ${percentage}% (${value.toLocaleString()})`;
                  },
                },
              },
            },
          },
        });
      } catch (_error) {}
    },

    // 7. Save Rate Comparison (text div update, not a chart)
    updateSaveRateComparison: function (dailyMetrics, _metrics) {
      try {
        const comparisonEl = document.getElementById('pin-save-rate-comparison');
        if (!comparisonEl) return;

        // Filter out days with 0 impressions
        const filteredMetrics = dailyMetrics.filter((d) => (d.metrics?.IMPRESSION || 0) > 0);

        if (filteredMetrics.length < 30) {
          comparisonEl.textContent = 'Insufficient data for comparison';
          return;
        }

        // Calculate current period (last 30 days) save rate
        const currentPeriod = filteredMetrics.slice(0, 30);
        const currentImpressions = currentPeriod.reduce(
          (sum, d) => sum + (d.metrics?.IMPRESSION || 0),
          0
        );
        const currentSaves = currentPeriod.reduce((sum, d) => sum + (d.metrics?.SAVE || 0), 0);
        const currentRate = currentImpressions > 0 ? (currentSaves / currentImpressions) * 100 : 0;

        // Calculate previous period (30-60 days ago) save rate
        const previousPeriod = filteredMetrics.slice(30, 60);
        const previousImpressions = previousPeriod.reduce(
          (sum, d) => sum + (d.metrics?.IMPRESSION || 0),
          0
        );
        const previousSaves = previousPeriod.reduce((sum, d) => sum + (d.metrics?.SAVE || 0), 0);
        const previousRate =
          previousImpressions > 0 ? (previousSaves / previousImpressions) * 100 : 0;

        // Calculate change
        const change = currentRate - previousRate;
        const changeSymbol = change > 0 ? '+' : '';
        const _changeClass = change > 0 ? 'positive' : change < 0 ? 'negative' : 'neutral';

        // Update the HTML content
        safeSetHTML(
          comparisonEl,
          `
      <span style="color: #22c55e;">Current: ${currentRate.toFixed(2)}%</span>
      <span style="color: #94a3b8; margin: 0 8px;">|</span>
      <span style="color: #94a3b8;">Previous: ${previousRate.toFixed(2)}%</span>
      <span style="color: #94a3b8; margin: 0 8px;">|</span>
      <span style="color: ${change > 0 ? '#22c55e' : change < 0 ? '#ef4444' : '#94a3b8'};">
        Change: ${changeSymbol}${change.toFixed(2)}%
      </span>
    `
        );
      } catch (_error) {}
    },

    // ============================================================================
    // NEW DASHBOARD FEATURES (2025-10-24)
    // ============================================================================

    /**
     * Top Converting Products
     */
    loadTopConvertingProducts: async function (period = '30d') {
      const loadingEl = document.getElementById('top-products-loading');
      const chartCanvas = document.getElementById('top-products-chart');
      const listEl = document.getElementById('top-products-list');

      if (!chartCanvas) return;

      try {
        if (loadingEl) loadingEl.style.display = 'flex';
        if (chartCanvas) chartCanvas.style.display = 'none';

        const response = await fetch(
          `${this.config.apiProxyUrl}?endpoint=top-converting-products&period=${period}`
        );
        const result = await response.json();

        if (loadingEl) loadingEl.style.display = 'none';

        if (result.success && result.data.products.length > 0) {
          this.renderTopProductsChart(result.data.products);
          if (chartCanvas) chartCanvas.style.display = 'block';
        } else {
          if (listEl) {
            safeSetHTML(
              listEl,
              '<p style="padding: 20px; text-align: center; color: rgba(255,255,255,0.6);">No conversion data available for this period.</p>'
            );
          }
        }
      } catch (_error) {
        if (loadingEl) loadingEl.style.display = 'none';
        if (listEl) {
          safeSetHTML(
            listEl,
            '<p style="padding: 20px; text-align: center; color: rgba(255,68,68,0.8);">Failed to load data</p>'
          );
        }
      }
    },

    renderTopProductsChart: function (products) {
      const canvas = document.getElementById('top-products-chart');
      if (!canvas || !products || products.length === 0) return;

      // Destroy existing chart
      if (this.charts && this.charts.topProducts) {
        this.charts.topProducts.destroy();
      }

      const ctx = canvas.getContext('2d');

      this.charts.topProducts = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: products.map((p) =>
            p.title.length > 25 ? p.title.substring(0, 25) + '...' : p.title
          ),
          datasets: [
            {
              label: 'Orders',
              data: products.map((p) => p.ordered),
              backgroundColor: 'rgba(99, 102, 241, 0.6)',
              borderColor: 'rgba(99, 102, 241, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: function (context) {
                  const product = products[context.dataIndex];
                  return [
                    `Orders: ${product.ordered}`,
                    `Revenue: $${product.revenue.toLocaleString()}`,
                    `Conversion: ${product.conversionRate}%`,
                    `Abandoned: ${product.abandoned}`,
                  ];
                },
              },
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              grid: { color: 'rgba(255,255,255,0.1)' },
              ticks: {
                color: 'rgba(255,255,255,0.7)',
                callback: function (value) {
                  return value.toLocaleString();
                },
              },
            },
            y: {
              grid: { display: false },
              ticks: { color: 'rgba(255,255,255,0.7)' },
            },
          },
        },
      });
    },

    /**
     * Cart Recovery Metrics
     */
    loadCartRecoveryMetrics: async function (period = '30d') {
      const loadingEl = document.getElementById('cart-recovery-loading');
      const statsEl = document.getElementById('cart-recovery-stats');

      if (!statsEl) return;

      try {
        if (loadingEl) loadingEl.style.display = 'flex';

        const response = await fetch(
          `${this.config.apiProxyUrl}?endpoint=cart-recovery-metrics&period=${period}`
        );
        const result = await response.json();

        if (loadingEl) loadingEl.style.display = 'none';

        if (result.success) {
          this.renderCartRecoveryStats(result.data);
        } else {
          safeSetHTML(
            statsEl,
            '<p style="padding: 20px; text-align: center; color: rgba(255,68,68,0.8);">Failed to load recovery metrics</p>'
          );
        }
      } catch (_error) {
        if (loadingEl) loadingEl.style.display = 'none';
        if (statsEl) {
          safeSetHTML(
            statsEl,
            '<p style="padding: 20px; text-align: center; color: rgba(255,68,68,0.8);">Failed to load data</p>'
          );
        }
      }
    },

    renderCartRecoveryStats: function (data) {
      const container = document.getElementById('cart-recovery-stats');
      if (!container) return;

      const recoveredValue = Math.round(parseFloat(data.recoveredValue));
      const abandonedValue = Math.round(parseFloat(data.abandonedValue));
      const potentialRevenue = Math.round(abandonedValue * (data.recoveryRate / 100));
      const recoveryRate = Math.round(data.recoveryRate);

      const html = `
    <div class="stat-card compact">
      <div class="stat-label">Recovery Rate</div>
      <div class="stat-value" style="color: ${recoveryRate > 20 ? '#10b981' : '#f59e0b'};">${recoveryRate}%</div>
      <div class="stat-subtitle">${data.totalRecovered.toLocaleString()} of ${(data.totalAbandoned + data.totalRecovered).toLocaleString()}</div>
    </div>
    <div class="stat-card compact">
      <div class="stat-label">Recovered</div>
      <div class="stat-value" style="color: #10b981;">$${recoveredValue.toLocaleString()}</div>
      <div class="stat-subtitle">${data.totalRecovered.toLocaleString()} carts</div>
    </div>
    <div class="stat-card compact">
      <div class="stat-label">Abandoned</div>
      <div class="stat-value" style="color: #ef4444;">$${abandonedValue.toLocaleString()}</div>
      <div class="stat-subtitle">${data.totalAbandoned.toLocaleString()} carts</div>
    </div>
    <div class="stat-card compact">
      <div class="stat-label">Potential</div>
      <div class="stat-value" style="color: #8b5cf6;">$${potentialRevenue.toLocaleString()}</div>
      <div class="stat-subtitle">at current rate</div>
    </div>
  `;

      safeSetHTML(container, html);
    },

    /**
     * Traffic Source ROI
     */
    loadTrafficSourceROI: async function (period = '30d') {
      const loadingEl = document.getElementById('traffic-roi-loading');
      const chartCanvas = document.getElementById('traffic-roi-chart');
      const tableEl = document.getElementById('traffic-roi-table');

      if (!chartCanvas) return;

      try {
        if (loadingEl) loadingEl.style.display = 'flex';
        if (chartCanvas) chartCanvas.style.display = 'none';

        const response = await fetch(
          `${this.config.apiProxyUrl}?endpoint=traffic-source-roi&period=${period}`
        );
        const result = await response.json();

        if (loadingEl) loadingEl.style.display = 'none';

        if (result.success && result.data.sources.length > 0) {
          this.renderTrafficROIChart(result.data.sources);
          this.renderTrafficROITable(result.data.sources);
          if (chartCanvas) chartCanvas.style.display = 'block';
        } else {
          if (tableEl) {
            safeSetHTML(
              tableEl,
              '<p style="padding: 20px; text-align: center; color: rgba(255,255,255,0.6);">No traffic data available for this period.</p>'
            );
          }
        }
      } catch (_error) {
        if (loadingEl) loadingEl.style.display = 'none';
        if (tableEl) {
          safeSetHTML(
            tableEl,
            '<p style="padding: 20px; text-align: center; color: rgba(255,68,68,0.8);">Failed to load data</p>'
          );
        }
      }
    },

    renderTrafficROIChart: function (sources) {
      const canvas = document.getElementById('traffic-roi-chart');
      if (!canvas || !sources || sources.length === 0) return;

      // Destroy existing chart
      if (this.charts && this.charts.trafficROI) {
        this.charts.trafficROI.destroy();
      }

      const ctx = canvas.getContext('2d');
      const top10 = sources.slice(0, 10);

      this.charts.trafficROI = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: top10.map((s) => s.source),
          datasets: [
            {
              label: 'Revenue ($)',
              data: top10.map((s) => s.revenue),
              backgroundColor: 'rgba(139, 92, 246, 0.6)',
              borderColor: 'rgba(139, 92, 246, 1)',
              borderWidth: 1,
            },
          ],
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: function (context) {
                  const source = top10[context.dataIndex];
                  return [
                    `Revenue: $${source.revenue.toFixed(2)}`,
                    `Orders: ${source.orders}`,
                    `Avg Order: $${source.avgOrderValue.toFixed(2)}`,
                  ];
                },
              },
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              grid: { color: 'rgba(255,255,255,0.1)' },
              ticks: {
                color: 'rgba(255,255,255,0.7)',
                callback: function (value) {
                  return '$' + value.toFixed(0);
                },
              },
            },
            y: {
              grid: { display: false },
              ticks: { color: 'rgba(255,255,255,0.7)' },
            },
          },
        },
      });
    },

    renderTrafficROITable: function (sources) {
      const container = document.getElementById('traffic-roi-table');
      if (!container) return;

      const html = `
    <table class="performance-table compact-table">
      <thead>
        <tr>
          <th>Source</th>
          <th>Revenue</th>
          <th>Orders</th>
          <th>AOV</th>
        </tr>
      </thead>
      <tbody>
        ${sources
          .slice(0, 8)
          .map(
            (source) => `
          <tr>
            <td><strong>${source.source}</strong></td>
            <td style="color: #10b981;">$${this.formatNumber(source.revenue)}</td>
            <td>${source.orders.toLocaleString()}</td>
            <td>$${this.formatNumber(source.avgOrderValue)}</td>
          </tr>
        `
          )
          .join('')}
      </tbody>
    </table>
  `;

      safeSetHTML(container, html);
    },

    /**
     * Smart Alerts
     */
    loadSmartAlerts: async function () {
      const loadingEl = document.getElementById('smart-alerts-loading');
      const containerEl = document.getElementById('smart-alerts-container');
      const noAlertsEl = document.getElementById('no-alerts');

      if (!containerEl) return;

      try {
        if (loadingEl) loadingEl.style.display = 'flex';
        if (noAlertsEl) noAlertsEl.style.display = 'none';

        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=smart-alerts`);
        const result = await response.json();

        if (loadingEl) loadingEl.style.display = 'none';

        if (result.success) {
          if (result.data.alerts.length > 0) {
            this.renderAlerts(result.data.alerts);
            containerEl.style.display = 'block';
          } else {
            containerEl.style.display = 'none';
            if (noAlertsEl) noAlertsEl.style.display = 'block';
          }
        } else {
          safeSetHTML(
            containerEl,
            '<p style="padding: 20px; text-align: center; color: rgba(255,68,68,0.8);">Failed to load alerts</p>'
          );
        }
      } catch (_error) {
        if (loadingEl) loadingEl.style.display = 'none';
        if (containerEl) {
          safeSetHTML(
            containerEl,
            '<p style="padding: 20px; text-align: center; color: rgba(255,68,68,0.8);">Failed to load data</p>'
          );
        }
      }
    },

    /**
     * Format tooltip content for alerts with details
     */
    formatAlertTooltip: function (alert) {
      if (!alert.details) return '';

      if (alert.type === 'payment_failure' && alert.details.orders) {
        const ordersList = alert.details.orders
          .map((order) => {
            const statusBadge = order.cancelled ? 'Cancelled' : order.status;
            return `<div class="tooltip-order-block">
        <div class="tooltip-row">
          <span class="tooltip-label">${order.name}</span>
          <span class="tooltip-value">${statusBadge}</span>
        </div>
        <div class="tooltip-row">
          <span class="tooltip-label">Customer</span>
          <span class="tooltip-value">${this.escapeHtml(order.customerName || 'Guest')}</span>
        </div>
        <div class="tooltip-row">
          <span class="tooltip-label">Items</span>
          <span class="tooltip-value tooltip-items">${this.escapeHtml(order.items || 'N/A')}</span>
        </div>
      </div>`;
          })
          .join('');

        return `<div class="alert-tooltip">
      <div class="tooltip-header">Payment Failures</div>
      <div class="tooltip-content">${ordersList}</div>
    </div>`;
      }

      if (alert.type === 'high_abandonment' && alert.details) {
        return `<div class="alert-tooltip">
      <div class="tooltip-header">Cart Abandonment Details</div>
      <div class="tooltip-content">
        <div class="tooltip-row">
          <span class="tooltip-label">Total Checkouts</span>
          <span class="tooltip-value">${alert.details.total}</span>
        </div>
        <div class="tooltip-row">
          <span class="tooltip-label">Completed</span>
          <span class="tooltip-value">${alert.details.completed}</span>
        </div>
        <div class="tooltip-row">
          <span class="tooltip-label">Abandoned</span>
          <span class="tooltip-value">${alert.details.abandoned}</span>
        </div>
        <div class="tooltip-row">
          <span class="tooltip-label">Abandonment Rate</span>
          <span class="tooltip-value">${alert.details.rate}%</span>
        </div>
      </div>
    </div>`;
      }

      return '';
    },

    renderAlerts: function (alerts) {
      const container = document.getElementById('smart-alerts-container');
      if (!container) return;

      const html = alerts
        .map((alert) => {
          const severityColor =
            alert.severity === 'error'
              ? '#ef4444'
              : alert.severity === 'warning'
                ? '#f59e0b'
                : '#3b82f6';
          const tooltipHtml = this.formatAlertTooltip(alert);

          return `
      <div class="alert-card compact-alert" style="border-left: 3px solid ${severityColor};">
        <div class="alert-content">
          <span class="alert-icon">${alert.icon}</span>
          <div class="alert-text">
            <div class="alert-title">${alert.title}</div>
            <div class="alert-message">${alert.message}</div>
          </div>
          <span class="alert-severity-badge" style="background: ${severityColor};">${alert.severity.charAt(0).toUpperCase()}</span>
        </div>
        ${tooltipHtml}
      </div>
    `;
        })
        .join('');

      safeSetHTML(container, html);
    },

    /**
     * Klaviyo Campaigns
     */
    loadKlaviyoCampaigns: async function (period = '30d') {
      const loadingEl = document.getElementById('klaviyo-loading');
      const tableEl = document.getElementById('klaviyo-campaigns-table');
      const noticeEl = document.getElementById('klaviyo-setup-notice');

      if (!tableEl) return;

      try {
        if (loadingEl) loadingEl.style.display = 'flex';
        if (noticeEl) noticeEl.style.display = 'none';

        const response = await fetch(
          `${this.config.apiProxyUrl}?endpoint=klaviyo-campaigns&period=${period}`
        );
        const result = await response.json();

        if (loadingEl) loadingEl.style.display = 'none';

        if (result.success) {
          if (result.data.needsSetup) {
            if (noticeEl) noticeEl.style.display = 'block';
            safeSetHTML(tableEl, '');
          } else if (result.data.campaigns.length > 0) {
            this.renderKlaviyoCampaignsTable(result.data.campaigns);
          } else {
            safeSetHTML(
              tableEl,
              '<p style="padding: 20px; text-align: center; color: rgba(255,255,255,0.6);">No campaigns found for this period.</p>'
            );
          }
        } else {
          safeSetHTML(
            tableEl,
            '<p style="padding: 20px; text-align: center; color: rgba(255,68,68,0.8);">Failed to load campaigns</p>'
          );
        }
      } catch (_error) {
        if (loadingEl) loadingEl.style.display = 'none';
        if (tableEl) {
          safeSetHTML(
            tableEl,
            '<p style="padding: 20px; text-align: center; color: rgba(255,68,68,0.8);">Failed to load data</p>'
          );
        }
      }
    },

    renderKlaviyoCampaignsTable: function (campaigns) {
      const container = document.getElementById('klaviyo-campaigns-table');
      if (!container) return;

      const html = `
    <table class="performance-table">
      <thead>
        <tr>
          <th>Campaign Name</th>
          <th>Sent</th>
          <th>Opens</th>
          <th>Clicks</th>
          <th>Revenue</th>
          <th>Open Rate</th>
          <th>Click Rate</th>
        </tr>
      </thead>
      <tbody>
        ${campaigns
          .map(
            (campaign) => `
          <tr>
            <td><strong>${campaign.name}</strong></td>
            <td>${campaign.sent.toLocaleString()}</td>
            <td>${campaign.opens.toLocaleString()}</td>
            <td>${campaign.clicks.toLocaleString()}</td>
            <td style="color: #10b981; font-weight: 600;">$${campaign.revenue.toFixed(2)}</td>
            <td style="color: ${campaign.openRate > 20 ? '#10b981' : '#f59e0b'};">${campaign.openRate}%</td>
            <td style="color: ${campaign.clickRate > 2 ? '#10b981' : '#f59e0b'};">${campaign.clickRate}%</td>
          </tr>
        `
          )
          .join('')}
      </tbody>
    </table>
  `;

      safeSetHTML(container, html);
    },

    /**
     * Load total tips from the last 90 days
     * Includes retry mechanism with exponential backoff for transient failures
     */
    loadTotalTips90d: async function () {
      const valueEl = document.getElementById('tips-90d-value');
      const countEl = document.getElementById('tips-90d-count');

      if (!valueEl || !countEl) return;

      // Retry mechanism with exponential backoff
      const fetchWithRetry = async (url, retries = 3) => {
        for (let i = 0; i <= retries; i++) {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

          try {
            // Show retry feedback
            if (i > 0) {
              safeSetHTML(valueEl, '<span style="color: #f59e0b;">Retrying...</span>');
              countEl.textContent = `Attempt ${i + 1}/${retries + 1}`;
            }

            const cacheBuster = this.config.cacheBuster ? `&_cb=${this.config.cacheBuster}` : '';
            const response = await fetch(url + cacheBuster, { signal: controller.signal });
            clearTimeout(timeoutId);

            // Success - return response
            if (response.ok) {
              return response;
            }

            // Don't retry on 4xx errors (client errors)
            if (response.status >= 400 && response.status < 500) {
              clearTimeout(timeoutId);
              return response;
            }

            // Retry on 5xx errors (server errors) or network issues
            if (i < retries) {
              await new Promise((resolve) => {
                setTimeout(resolve, 1000 * (i + 1));
              }); // Exponential backoff: 1s, 2s, 3s
            }
          } catch (error) {
            clearTimeout(timeoutId);

            if (i < retries) {
              await new Promise((resolve) => {
                setTimeout(resolve, 1000 * (i + 1));
              });
            } else {
              throw error;
            }
          }
        }

        throw new Error('Max retries reached');
      };

      try {
        const response = await fetchWithRetry(`${this.config.apiProxyUrl}?endpoint=total-tips-90d`);

        if (response.ok) {
          const result = await response.json();

          if (result.success) {
            const { totalTips, ordersWithTips } = result.data;

            // Update the display
            safeSetHTML(valueEl, `$${this.formatNumber(parseFloat(totalTips))}`);
            // eslint-disable-next-line require-atomic-updates
            countEl.textContent = `${ordersWithTips} orders`;
          } else {
            // API returned error response
            safeSetHTML(valueEl, '<span style="color: #f59e0b;">⚠️</span>');
            // eslint-disable-next-line require-atomic-updates
            countEl.textContent = 'API error';
          }
        } else {
          // HTTP error after retries
          safeSetHTML(valueEl, '<span style="color: #f59e0b;">⚠️</span>');
          // eslint-disable-next-line require-atomic-updates
          countEl.textContent = 'Failed to load';
        }
      } catch (_error) {
        // Network error or timeout after all retries
        safeSetHTML(valueEl, '<span style="color: #f59e0b;">⚠️</span>');
        // eslint-disable-next-line require-atomic-updates
        countEl.textContent = 'Connection error';
      }
    },

    /**
     * Show detailed tip orders in a drawer
     */
    showDetailedTipOrders: async function () {
      try {
        // Show loading state
        const drawerContent = `
      <div class="detailed-orders-header">
        <h2>💝 Orders with Tips (Last 90 Days)</h2>
        <p style="color: rgba(255,255,255,0.7); margin-top: 8px;">Loading...</p>
      </div>
    `;
        this.showOrderDrawer(drawerContent);

        // Fetch detailed tip orders
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=detailed-tip-orders`);
        const result = await response.json();

        if (result.success && result.data.orders.length > 0) {
          const orders = result.data.orders;

          let html = `
        <div class="detailed-orders-header">
          <h2>💝 Orders with Tips (Last 90 Days)</h2>
          <p style="color: rgba(255,255,255,0.7); margin-top: 8px;">${orders.length} orders • Total tips: $${orders.reduce((sum, o) => sum + parseFloat(o.tipTotal), 0).toFixed(2)}</p>
        </div>
        <div class="detailed-orders-list">
      `;

          orders.forEach((order) => {
            const formattedDate = this.formatDate(order.createdAt);

            html += `
          <div class="detailed-order-item">
            <div class="order-item-header">
              <div>
                <strong>#${order.orderNumber}</strong>
                <span style="color: rgba(255,255,255,0.6); margin-left: 8px;">${formattedDate}</span>
              </div>
              <div style="text-align: right;">
                <div style="color: #10b981; font-weight: 600;">💝 $${order.tipTotal}</div>
                <div style="font-size: 12px; color: rgba(255,255,255,0.6);">Total: $${order.total}</div>
              </div>
            </div>
            <div class="order-item-customer">
              👤 ${this.escapeHtml(order.customerName)}
              ${order.email ? `<br/><span style="font-size: 12px; color: rgba(255,255,255,0.6);">${this.escapeHtml(order.email)}</span>` : ''}
            </div>
            ${
              order.lineItems && order.lineItems.length > 0
                ? `
              <div class="order-item-items">
                <strong>Items:</strong>
                ${order.lineItems
                  .map(
                    (item) => `
                  <div style="display: flex; justify-content: space-between; padding: 4px 0; font-size: 13px;">
                    <span>${item.quantity}x ${this.escapeHtml(item.name)}</span>
                    <span>$${item.price}</span>
                  </div>
                `
                  )
                  .join('')}
              </div>
            `
                : ''
            }
            ${
              order.shippingAddress
                ? `
              <div class="order-item-shipping">
                📦 ${order.shippingAddress.city ? this.escapeHtml(order.shippingAddress.city) + ', ' : ''}${this.escapeHtml(order.shippingAddress.province || order.shippingAddress.provinceCode || '')}${order.shippingAddress.country ? ' ' + this.escapeHtml(order.shippingAddress.country) : ''}
              </div>
            `
                : ''
            }
          </div>
        `;
          });

          html += '</div>';

          // Update drawer content
          const drawer = document.getElementById('order-drawer');
          if (drawer) {
            const content = drawer.querySelector('.order-tooltip-drawer-content');
            if (content) safeSetHTML(content, html);
          }
        } else {
          const html = `
        <div class="detailed-orders-header">
          <h2>💝 Orders with Tips (Last 90 Days)</h2>
          <p style="color: rgba(255,255,255,0.7); margin-top: 8px;">No orders with tips found in the last 90 days.</p>
        </div>
      `;
          const drawer = document.getElementById('order-drawer');
          if (drawer) {
            const content = drawer.querySelector('.order-tooltip-drawer-content');
            if (content) safeSetHTML(content, html);
          }
        }
      } catch (_error) {
        const html = `
      <div class="detailed-orders-header">
        <h2>💝 Orders with Tips</h2>
        <p style="color: rgba(255,68,68,0.8); margin-top: 8px;">Failed to load orders. Please try again.</p>
      </div>
    `;
        const drawer = document.getElementById('order-drawer');
        if (drawer) {
          const content = drawer.querySelector('.order-tooltip-drawer-content');
          if (content) safeSetHTML(content, html);
        }
      }
    },

    /**
     * Show detailed AI agent orders in a drawer
     */
    showDetailedAIOrders: async function () {
      try {
        // Show loading state
        const drawerContent = `
      <div class="detailed-orders-header">
        <h2>🤖 AI Agent Orders (Last 90 Days)</h2>
        <p style="color: rgba(255,255,255,0.7); margin-top: 8px;">Loading...</p>
      </div>
    `;
        this.showOrderDrawer(drawerContent);

        // Fetch detailed AI orders
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=detailed-ai-orders`);
        const result = await response.json();

        if (result.success && result.data.orders.length > 0) {
          const orders = result.data.orders;
          const totalRevenue = orders.reduce((sum, o) => sum + parseFloat(o.total), 0);

          let html = `
        <div class="detailed-orders-header">
          <h2>🤖 AI Agent Orders (Last 90 Days)</h2>
          <p style="color: rgba(255,255,255,0.7); margin-top: 8px;">${orders.length} orders • Total revenue: $${totalRevenue.toFixed(2)}</p>
        </div>
        <div class="detailed-orders-list">
      `;

          orders.forEach((order) => {
            const formattedDate = this.formatDate(order.createdAt);

            html += `
          <div class="detailed-order-item">
            <div class="order-item-header">
              <div>
                <strong>#${order.orderNumber}</strong>
                <span style="color: rgba(255,255,255,0.6); margin-left: 8px;">${formattedDate}</span>
              </div>
              <div style="text-align: right;">
                <div style="color: #10b981; font-weight: 600;">$${order.total}</div>
                <div style="font-size: 12px; color: rgba(255,255,255,0.6);">AI: ${order.aiSource}</div>
              </div>
            </div>
            <div class="order-item-customer">
              👤 ${this.escapeHtml(order.customerName)}
              ${order.email ? `<br/><span style="font-size: 12px; color: rgba(255,255,255,0.6);">${this.escapeHtml(order.email)}</span>` : ''}
            </div>
            ${
              order.lineItems && order.lineItems.length > 0
                ? `
              <div class="order-item-items">
                <strong>Items:</strong>
                ${order.lineItems
                  .map(
                    (item) => `
                  <div style="display: flex; justify-content: space-between; padding: 4px 0; font-size: 13px;">
                    <span>${item.quantity}x ${this.escapeHtml(item.name)}</span>
                    <span>$${item.price}</span>
                  </div>
                `
                  )
                  .join('')}
              </div>
            `
                : ''
            }
            ${
              order.shippingAddress
                ? `
              <div class="order-item-shipping">
                📦 ${order.shippingAddress.city ? this.escapeHtml(order.shippingAddress.city) + ', ' : ''}${this.escapeHtml(order.shippingAddress.province || order.shippingAddress.provinceCode || '')}${order.shippingAddress.country ? ' ' + this.escapeHtml(order.shippingAddress.country) : ''}
              </div>
            `
                : ''
            }
          </div>
        `;
          });

          html += '</div>';

          // Update drawer content
          const drawer = document.getElementById('order-drawer');
          if (drawer) {
            const content = drawer.querySelector('.order-tooltip-drawer-content');
            if (content) safeSetHTML(content, html);
          }
        } else {
          const html = `
        <div class="detailed-orders-header">
          <h2>🤖 AI Agent Orders (Last 90 Days)</h2>
          <p style="color: rgba(255,255,255,0.7); margin-top: 8px;">No AI agent orders found in the last 90 days.</p>
        </div>
      `;
          const drawer = document.getElementById('order-drawer');
          if (drawer) {
            const content = drawer.querySelector('.order-tooltip-drawer-content');
            if (content) safeSetHTML(content, html);
          }
        }
      } catch (_error) {
        const html = `
      <div class="detailed-orders-header">
        <h2>🤖 AI Agent Orders</h2>
        <p style="color: rgba(255,68,68,0.8); margin-top: 8px;">Failed to load orders. Please try again.</p>
      </div>
    `;
        const drawer = document.getElementById('order-drawer');
        if (drawer) {
          const content = drawer.querySelector('.order-tooltip-drawer-content');
          if (content) safeSetHTML(content, html);
        }
      }
    },

    /**
     * Load active subscription data and render the Subs tab
     */
    loadSubscriptions: async function () {
      const loadingEl = document.getElementById('subs-loading');
      const errorEl = document.getElementById('subs-error');
      const containerEl = document.getElementById('subs-container');

      if (loadingEl) loadingEl.style.display = 'flex';
      if (errorEl) errorEl.style.display = 'none';
      if (containerEl) containerEl.style.display = 'none';

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(function () {
          controller.abort();
        }, 55000);

        const response = await fetch(this.config.apiProxyUrl + '?endpoint=active-subscriptions', {
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (!response.ok) throw new Error('HTTP ' + response.status);
        const result = await response.json();
        if (!result.success || !result.data) throw new Error('Invalid response');

        const daily = result.data.daily;
        const totals = result.data.totals;
        const grandTotal = result.data.grandTotal;

        // --- Update summary card ---
        ['vintage', 'sticker', 'curiosities', 'treasure', 'digital'].forEach(function (key) {
          const el = document.getElementById('subs-count-' + key);
          if (el) el.textContent = (totals[key] || 0).toLocaleString('en-US');
        });
        const totalEl = document.getElementById('subs-count-total');
        if (totalEl) totalEl.textContent = (grandTotal || 0).toLocaleString('en-US');

        // --- Render 90-day trend chart ---
        if (typeof Chart !== 'undefined') {
          this.renderSubsTrendChart(daily);
          this.renderSubsMoM(daily);
        }

        if (loadingEl) loadingEl.style.display = 'none';
        if (containerEl) containerEl.style.display = 'block';
      } catch (err) {
        if (loadingEl) loadingEl.style.display = 'none';
        if (errorEl) {
          const msg =
            err.name === 'AbortError'
              ? 'Request timed out (55s). Try refreshing.'
              : 'Failed to load: ' + err.message;
          errorEl.textContent = msg;
          errorEl.style.display = 'block';
        }
      }
    },

    /**
     * Render the 90-day subscription trend line chart
     */
    renderSubsTrendChart: function (daily) {
      const canvas = document.getElementById('subs-trend-chart');
      if (!canvas) return;

      const chartsStore = this.state && this.state.charts ? this.state.charts : {};
      // Destroy previous instance if exists
      if (chartsStore.subsTrendChart) {
        chartsStore.subsTrendChart.destroy();
      }

      const labels = daily.map((d) => {
        const parts = d.date.split('-');
        return `${parseInt(parts[1])}/${parseInt(parts[2])}`;
      });

      // 5 distinct, high-contrast colors
      const COLORS = {
        vintage: { line: '#f97316', bg: 'rgba(249,115,22,0.12)' },
        sticker: { line: '#a855f7', bg: 'rgba(168,85,247,0.12)' },
        curiosities: { line: '#06b6d4', bg: 'rgba(6,182,212,0.12)' },
        treasure: { line: '#eab308', bg: 'rgba(234,179,8,0.12)' },
        digital: { line: '#ec4899', bg: 'rgba(236,72,153,0.12)' },
      };

      const LABELS = {
        vintage: 'Vintage Craft Box',
        sticker: 'Sticker Lovers Club',
        curiosities: 'Curiosities Box',
        treasure: 'Treasure Craft Box',
        digital: 'Digital Journal Club',
      };

      const datasets = Object.keys(COLORS).map((key) => ({
        label: LABELS[key],
        data: daily.map((d) => d[key] || 0),
        borderColor: COLORS[key].line,
        backgroundColor: COLORS[key].bg,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.3,
        fill: true,
      }));

      const ctx = canvas.getContext('2d');
      const chart = new Chart(ctx, {
        type: 'line',
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                color: 'rgba(255,255,255,0.7)',
                usePointStyle: true,
                pointStyle: 'circle',
                padding: 16,
                font: { size: 11 },
              },
            },
            tooltip: {
              backgroundColor: 'rgba(0,0,0,0.85)',
              titleColor: '#fff',
              bodyColor: 'rgba(255,255,255,0.8)',
              borderColor: 'rgba(255,255,255,0.1)',
              borderWidth: 1,
              padding: 10,
              callbacks: {
                title: function (items) {
                  if (!items.length) return '';
                  const idx = items[0].dataIndex;
                  return daily[idx] ? daily[idx].date : '';
                },
              },
            },
            datalabels: { display: false },
          },
          scales: {
            x: {
              ticks: {
                color: 'rgba(255,255,255,0.5)',
                maxRotation: 45,
                autoSkip: true,
                maxTicksLimit: 15,
                font: { size: 10 },
              },
              grid: { color: 'rgba(255,255,255,0.04)' },
            },
            y: {
              beginAtZero: true,
              ticks: {
                color: 'rgba(255,255,255,0.5)',
                precision: 0,
                font: { size: 10 },
              },
              grid: { color: 'rgba(255,255,255,0.06)' },
            },
          },
        },
      });
      chartsStore.subsTrendChart = chart;
    },

    /**
     * Render Month-over-Month comparison card from daily data.
     * Aggregates daily rows into calendar months, computes deltas and share shifts,
     * then builds a grouped bar chart showing totals + per-type for each month.
     */
    renderSubsMoM: function (daily) {
      const card = document.getElementById('subs-mom-card');
      if (!card || !daily || !daily.length) return;

      const KEYS = ['vintage', 'sticker', 'curiosities', 'treasure', 'digital'];
      const LABELS = {
        vintage: 'Vintage Craft Box',
        sticker: 'Sticker Lovers Club',
        curiosities: 'Curiosities Box',
        treasure: 'Treasure Craft Box',
        digital: 'Digital Journal Club',
      };
      const COLORS = {
        vintage: '#f97316',
        sticker: '#a855f7',
        curiosities: '#06b6d4',
        treasure: '#eab308',
        digital: '#ec4899',
      };

      // --- Aggregate daily into calendar months ---
      const monthMap = {};
      for (let i = 0; i < daily.length; i++) {
        const d = daily[i];
        const ym = d.date.substring(0, 7); // "2026-01"
        if (!monthMap[ym]) {
          monthMap[ym] = {
            vintage: 0,
            sticker: 0,
            curiosities: 0,
            treasure: 0,
            digital: 0,
            total: 0,
          };
        }
        for (let k = 0; k < KEYS.length; k++) {
          const key = KEYS[k];
          const val = d[key] || 0;
          monthMap[ym][key] += val;
          monthMap[ym].total += val;
        }
      }

      // --- Drop the current (partial) month so we only compare complete months ---
      const allMonths = Object.keys(monthMap).sort();
      const nowCST = new Date().toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: '2-digit',
      });
      const nowParts = nowCST.split('/');
      const currentYM = nowParts[2]
        ? nowParts[2].trim() + '-' + nowParts[0].padStart(2, '0')
        : allMonths[allMonths.length - 1];
      const months = allMonths.filter(function (ym) {
        return ym !== currentYM;
      });
      if (months.length < 2) {
        card.style.display = 'none';
        return;
      }

      // --- Format month names ---
      const MONTH_NAMES = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      function fmtMonth(ym) {
        const parts = ym.split('-');
        return MONTH_NAMES[parseInt(parts[1], 10) - 1] + ' ' + parts[0];
      }

      // --- Reference last two complete months for delta/share ---
      const prevYM = months[months.length - 2];
      const currYM = months[months.length - 1];
      const prev = monthMap[prevYM];
      const curr = monthMap[currYM];

      // Days-in-month helper for daily averages
      function daysInMonth(ym) {
        const parts = ym.split('-');
        return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10), 0).getDate();
      }

      // Subtitle
      const subtitle = document.getElementById('subs-mom-subtitle');
      if (subtitle) {
        subtitle.textContent =
          'Complete months only \u2014 excludes ' +
          fmtMonth(currentYM) +
          ' (in progress). Daily avg normalizes for month length.';
      }

      // --- Build dynamic thead ---
      const thead = document.getElementById('subs-mom-thead');
      if (thead) {
        while (thead.firstChild) thead.removeChild(thead.firstChild);
        const headerRow = document.createElement('tr');
        const thType = document.createElement('th');
        thType.textContent = 'Type';
        headerRow.appendChild(thType);
        for (let h = 0; h < months.length; h++) {
          const th = document.createElement('th');
          th.textContent = fmtMonth(months[h]);
          headerRow.appendChild(th);
        }
        const thDelta = document.createElement('th');
        thDelta.textContent =
          fmtMonth(prevYM).split(' ')[0] + '\u2192' + fmtMonth(currYM).split(' ')[0];
        headerRow.appendChild(thDelta);
        const thShare = document.createElement('th');
        thShare.textContent = 'Mix';
        headerRow.appendChild(thShare);
        thead.appendChild(headerRow);
      }

      // --- Build dynamic tbody ---
      const tbody = document.getElementById('subs-mom-tbody');
      if (!tbody) return;
      while (tbody.firstChild) tbody.removeChild(tbody.firstChild);

      function makeDelta(prevVal, currVal) {
        const diff = currVal - prevVal;
        let pctStr = '';
        if (prevVal > 0) {
          const pct = ((diff / prevVal) * 100).toFixed(0);
          pctStr = '(' + (diff >= 0 ? '+' : '') + pct + '%)';
        } else if (currVal > 0) {
          pctStr = '(new)';
        }
        const cls =
          diff > 0
            ? 'subs-mom-delta--up'
            : diff < 0
              ? 'subs-mom-delta--down'
              : 'subs-mom-delta--flat';
        const arrow = diff > 0 ? '\u25B2 ' : diff < 0 ? '\u25BC ' : '\u2014 ';
        return { text: arrow + Math.abs(diff), cls: cls, pct: pctStr };
      }

      // Per-type rows — one column per month
      for (let j = 0; j < KEYS.length; j++) {
        const key = KEYS[j];
        const tr = document.createElement('tr');

        // Type cell with color dot
        const tdType = document.createElement('td');
        const typeSpan = document.createElement('span');
        typeSpan.className = 'subs-mom-type';
        const dot = document.createElement('span');
        dot.className = 'subs-box-dot';
        dot.style.background = COLORS[key];
        typeSpan.appendChild(dot);
        typeSpan.appendChild(document.createTextNode(' ' + LABELS[key]));
        tdType.appendChild(typeSpan);
        tr.appendChild(tdType);

        // One cell per month — count + daily avg
        for (let m = 0; m < months.length; m++) {
          const mym = months[m];
          const mval = monthMap[mym][key] || 0;
          const mdays = daysInMonth(mym);
          const mavg = (mval / mdays).toFixed(1);
          const td = document.createElement('td');
          td.appendChild(document.createTextNode(mval.toLocaleString('en-US')));
          const avgSpan = document.createElement('span');
          avgSpan.className = 'subs-mom-avg';
          avgSpan.textContent = mavg + '/d';
          td.appendChild(avgSpan);
          tr.appendChild(td);
        }

        // Delta cell (last two complete months)
        const pv = prev[key] || 0;
        const cv = curr[key] || 0;
        const delta = makeDelta(pv, cv);
        const tdDelta = document.createElement('td');
        const deltaSpan = document.createElement('span');
        deltaSpan.className = 'subs-mom-delta ' + delta.cls;
        deltaSpan.textContent = delta.text;
        tdDelta.appendChild(deltaSpan);
        const pctSpan = document.createElement('span');
        pctSpan.className = 'subs-mom-pct';
        pctSpan.textContent = delta.pct;
        tdDelta.appendChild(pctSpan);
        tr.appendChild(tdDelta);

        // Share cell with shift vs prior month
        const currShare = curr.total > 0 ? (cv / curr.total) * 100 : 0;
        const prevShare = prev.total > 0 ? (pv / prev.total) * 100 : 0;
        const ppShift = currShare - prevShare;
        const tdShare = document.createElement('td');
        tdShare.className = 'subs-mom-share';
        tdShare.appendChild(document.createTextNode(currShare.toFixed(1) + '%'));
        if (Math.abs(ppShift) >= 0.1) {
          const ppSpan = document.createElement('span');
          ppSpan.className =
            'subs-mom-delta ' + (ppShift > 0 ? 'subs-mom-delta--up' : 'subs-mom-delta--down');
          ppSpan.style.fontSize = '0.7rem';
          ppSpan.style.marginLeft = '4px';
          ppSpan.textContent = (ppShift > 0 ? '+' : '') + ppShift.toFixed(1) + 'pp';
          tdShare.appendChild(ppSpan);
        }
        tr.appendChild(tdShare);

        tbody.appendChild(tr);
      }

      // Total row
      const trTotal = document.createElement('tr');
      trTotal.className = 'subs-mom-total-row';
      const tdTotalLabel = document.createElement('td');
      tdTotalLabel.textContent = 'Total';
      trTotal.appendChild(tdTotalLabel);
      for (let t = 0; t < months.length; t++) {
        const tym = months[t];
        const tval = monthMap[tym].total || 0;
        const tdays = daysInMonth(tym);
        const tavg = (tval / tdays).toFixed(1);
        const tdMonth = document.createElement('td');
        tdMonth.appendChild(document.createTextNode(tval.toLocaleString('en-US')));
        const tavgSpan = document.createElement('span');
        tavgSpan.className = 'subs-mom-avg';
        tavgSpan.textContent = tavg + '/d';
        tdMonth.appendChild(tavgSpan);
        trTotal.appendChild(tdMonth);
      }
      const totalDelta = makeDelta(prev.total, curr.total);
      const tdTotalDelta = document.createElement('td');
      const totalDeltaSpan = document.createElement('span');
      totalDeltaSpan.className = 'subs-mom-delta ' + totalDelta.cls;
      totalDeltaSpan.textContent = totalDelta.text;
      tdTotalDelta.appendChild(totalDeltaSpan);
      const totalPctSpan = document.createElement('span');
      totalPctSpan.className = 'subs-mom-pct';
      totalPctSpan.textContent = totalDelta.pct;
      tdTotalDelta.appendChild(totalPctSpan);
      trTotal.appendChild(tdTotalDelta);
      const tdTotalShare = document.createElement('td');
      tdTotalShare.textContent = '100%';
      trTotal.appendChild(tdTotalShare);
      tbody.appendChild(trTotal);

      // --- Share bar (stacked horizontal bar for current month) ---
      const shareBar = document.getElementById('subs-mom-share-bar');
      if (shareBar) {
        while (shareBar.firstChild) shareBar.removeChild(shareBar.firstChild);
        for (let s = 0; s < KEYS.length; s++) {
          const skey = KEYS[s];
          const pctWidth = curr.total > 0 ? (curr[skey] / curr.total) * 100 : 0;
          if (pctWidth > 0) {
            const seg = document.createElement('div');
            seg.className = 'subs-mom-share-seg';
            seg.style.width = pctWidth.toFixed(1) + '%';
            seg.style.background = COLORS[skey];
            seg.title = LABELS[skey] + ': ' + pctWidth.toFixed(1) + '%';
            shareBar.appendChild(seg);
          }
        }
      }

      // --- Grouped bar chart: per-month totals + per-type breakdown ---
      const canvas = document.getElementById('subs-mom-chart');
      if (!canvas) {
        card.style.display = 'block';
        return;
      }

      const chartsStore = this.state && this.state.charts ? this.state.charts : {};
      if (chartsStore.subsMoMChart) {
        chartsStore.subsMoMChart.destroy();
      }

      const chartLabels = months.map(fmtMonth);

      // One dataset per type (stacked) + a separate total line
      const barDatasets = [];
      for (let b = 0; b < KEYS.length; b++) {
        const bkey = KEYS[b];
        barDatasets.push({
          label: LABELS[bkey],
          data: months.map(function (ym) {
            return monthMap[ym][bkey] || 0;
          }),
          backgroundColor: COLORS[bkey],
          borderColor: COLORS[bkey],
          borderWidth: 0,
          borderRadius: 3,
          stack: 'types',
          order: 2,
        });
      }

      // Total line overlay
      barDatasets.push({
        label: 'Total',
        data: months.map(function (ym) {
          return monthMap[ym].total || 0;
        }),
        type: 'line',
        borderColor: 'rgba(255,255,255,0.7)',
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: 'rgba(255,255,255,0.9)',
        tension: 0.2,
        order: 1,
        stack: false,
      });

      const ctx = canvas.getContext('2d');
      const momChart = new Chart(ctx, {
        type: 'bar',
        data: { labels: chartLabels, datasets: barDatasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                color: 'rgba(255,255,255,0.7)',
                usePointStyle: true,
                pointStyle: 'circle',
                padding: 14,
                font: { size: 10 },
              },
            },
            tooltip: {
              backgroundColor: 'rgba(0,0,0,0.85)',
              titleColor: '#fff',
              bodyColor: 'rgba(255,255,255,0.8)',
              borderColor: 'rgba(255,255,255,0.1)',
              borderWidth: 1,
              padding: 10,
            },
            datalabels: { display: false },
          },
          scales: {
            x: {
              stacked: true,
              ticks: { color: 'rgba(255,255,255,0.5)', font: { size: 11 } },
              grid: { color: 'rgba(255,255,255,0.04)' },
            },
            y: {
              stacked: true,
              beginAtZero: true,
              ticks: { color: 'rgba(255,255,255,0.5)', precision: 0, font: { size: 10 } },
              grid: { color: 'rgba(255,255,255,0.06)' },
            },
          },
        },
      });
      chartsStore.subsMoMChart = momChart;

      card.style.display = 'block';
    },

    /**
     * Load tab data based on tab ID
     */
    loadTabData: function (tabId) {
      switch (tabId) {
        case 'overview':
          // Overview data is loaded on initial page load
          break;
        case 'revenue':
          // Revenue & Profit tab
          if (typeof this.loadProfitMetrics === 'function') {
            this.loadProfitMetrics();
          }
          if (typeof this.loadCACMetrics === 'function') {
            this.loadCACMetrics();
          }
          if (typeof this.loadROASMetrics === 'function') {
            this.loadROASMetrics();
          }
          if (typeof this.loadMERMetrics === 'function') {
            this.loadMERMetrics();
          }
          if (typeof this.loadRevenueForecast === 'function') {
            this.loadRevenueForecast();
          }
          break;
        case 'customers':
          // Customers tab - LTV drivers, repeat behavior, CAC payback
          if (typeof this.loadLTVDrivers === 'function') {
            this.loadLTVDrivers();
          }
          if (typeof this.loadRepeatBehavior === 'function') {
            this.loadRepeatBehavior();
          }
          if (typeof this.loadCACPayback === 'function') {
            this.loadCACPayback();
          }
          break;
        case 'attribution':
          // Attribution tab - multi-touch attribution, campaign performance
          if (typeof this.loadAIReferralTraffic === 'function') {
            this.loadAIReferralTraffic();
          }
          if (typeof this.loadMultiTouchAttribution === 'function') {
            this.loadMultiTouchAttribution();
          }
          if (typeof this.loadCampaignRevenue === 'function') {
            this.loadCampaignRevenue();
          }
          if (typeof this.loadEmailCampaigns === 'function') {
            this.loadEmailCampaigns();
          }
          break;
        case 'products':
          // Products tab data is loaded on initial page load, except sell-through, which
          // has no initial-load group and was only reachable from the dead 'forecasting'
          // case below. Its markup lives in this tab.
          if (typeof this.loadSellThroughRate === 'function') this.loadSellThroughRate();
          break;
        case 'social':
          // Social tab data is loaded on initial page load
          break;
        case 'operations':
          // Operations tab data is loaded on initial page load
          break;
        case 'acquisition':
          if (typeof this.loadCACMetrics === 'function') this.loadCACMetrics();
          if (typeof this.loadROASMetrics === 'function') this.loadROASMetrics();
          if (typeof this.loadMultiTouchAttribution === 'function') {
            this.loadMultiTouchAttribution();
          }
          break;
        case 'retention':
          if (typeof this.loadLTVDrivers === 'function') this.loadLTVDrivers();
          if (typeof this.loadRepeatBehavior === 'function') this.loadRepeatBehavior();
          break;
        case 'economics':
          if (typeof this.loadMERMetrics === 'function') this.loadMERMetrics();
          if (typeof this.loadProfitMetrics === 'function') this.loadProfitMetrics();
          if (typeof this.loadCACPayback === 'function') this.loadCACPayback();
          break;
        case 'forecasting':
          if (typeof this.loadRevenueForecast === 'function') this.loadRevenueForecast();
          if (typeof this.loadSellThroughRate === 'function') this.loadSellThroughRate();
          break;
        case 'ads-charts':
          // Ads Charts tab — lazy-loaded on first click
          if (
            window.AdminDashboardAdsCharts &&
            typeof window.AdminDashboardAdsCharts.init === 'function'
          ) {
            window.AdminDashboardAdsCharts.init();
          }
          break;
        case 'subs':
          if (typeof this.loadSubscriptions === 'function') {
            this.loadSubscriptions();
          }
          break;
        default:
      }
    },

    /**
     * Load CAC (Customer Acquisition Cost) Metrics
     */
    loadCACMetrics: async function () {
      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=cac-metrics`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            const data = result.data;

            // Update Blended CAC value
            const cacEl = document.getElementById('blended-cac-value');
            if (cacEl) {
              if (data.totalNewCustomers === 0) {
                cacEl.textContent = 'N/A';
                cacEl.title = 'No new customers in period - CAC cannot be calculated';
              } else {
                cacEl.textContent = this.formatCurrency(data.blendedCAC || 0);
              }
            }

            // Update new customers count
            const customersEl = document.getElementById('blended-cac-customers');
            if (customersEl && data.totalNewCustomers !== undefined) {
              customersEl.textContent = `${data.totalNewCustomers} new customers`;
            }

            // Update CAC by channel table
            const channelTableBody = document.getElementById('cac-channel-table-body');
            if (channelTableBody && data.byChannel) {
              let html = '';
              for (const [channel, metrics] of Object.entries(data.byChannel)) {
                // Skip "direct" channel
                if (channel.toLowerCase() === 'direct') continue;

                // Show N/A for CAC if no new customers
                const cacDisplay =
                  metrics.newCustomers > 0
                    ? this.formatCurrency(metrics.cac || 0)
                    : metrics.orders > 0
                      ? 'N/A'
                      : '-';

                html += `
              <tr>
                <td>${channel.charAt(0).toUpperCase() + channel.slice(1)}</td>
                <td>${this.formatCurrency(metrics.adSpend || 0)}</td>
                <td>${metrics.newCustomers || 0}</td>
                <td>${cacDisplay}</td>
              </tr>
            `;
              }
              safeSetHTML(
                channelTableBody,
                html || '<tr><td colspan="4">No data available</td></tr>'
              );
            }

            // Update CAC by campaign table
            const campaignTableBody = document.getElementById('cac-campaign-table-body');
            if (campaignTableBody && data.byCampaign) {
              const campaigns = Object.entries(data.byCampaign);
              let html = '';

              if (campaigns.length === 0) {
                html =
                  '<tr><td colspan="5" style="text-align: center; padding: 20px; color: rgba(255,255,255,0.6);">No campaign tracking detected. Add _campaign custom attributes to orders for campaign-level CAC tracking.</td></tr>';
              } else {
                for (const [campaign, metrics] of campaigns) {
                  const cacDisplay =
                    metrics.newCustomers > 0 ? this.formatCurrency(metrics.cac || 0) : 'N/A';

                  html += `
                <tr>
                  <td>${campaign}</td>
                  <td>${metrics.channel ? metrics.channel.charAt(0).toUpperCase() + metrics.channel.slice(1) : 'Unknown'}</td>
                  <td>${this.formatCurrency(metrics.estimatedSpend || 0)}</td>
                  <td>${metrics.newCustomers || 0}</td>
                  <td>${cacDisplay}</td>
                </tr>
              `;
                }
              }

              safeSetHTML(campaignTableBody, html);

              // Update campaign count
              const campaignTotal = document.getElementById('cac-campaign-total');
              if (campaignTotal) {
                campaignTotal.textContent = `${campaigns.length} campaigns`;
              }
            }
          }
        }
      } catch (_error) {}
    },

    /**
     * Load ROAS (Return on Ad Spend) Metrics
     */
    loadROASMetrics: async function () {
      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=roas-metrics`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            const data = result.data;

            // Update Blended ROAS value
            const roasEl = document.getElementById('blended-roas-value');
            if (roasEl) {
              if (data.totalRevenue === 0) {
                roasEl.textContent = 'N/A';
                roasEl.title = 'No attributed revenue - check attribution tracking';
              } else {
                roasEl.textContent = `${this.formatNumber(data.blendedROAS, 2)}x`;
              }
            }

            // Update ad spend
            const spendEl = document.getElementById('blended-roas-spend');
            if (spendEl && data.totalSpend !== undefined) {
              spendEl.textContent = `${this.formatCurrency(data.totalSpend)} ad spend`;
            }

            // Update ROAS by platform table
            const platformTableBody = document.getElementById('roas-platform-table-body');
            if (platformTableBody && data.byPlatform) {
              let html = '';
              for (const [platform, metrics] of Object.entries(data.byPlatform)) {
                const colorClass =
                  metrics.roas >= 3
                    ? 'metric-status-good'
                    : metrics.roas >= 2
                      ? 'metric-status-needs-improvement'
                      : 'metric-status-poor';
                html += `
              <tr>
                <td>${platform.charAt(0).toUpperCase() + platform.slice(1)}</td>
                <td>${this.formatCurrency(metrics.spend || 0)}</td>
                <td>${this.formatCurrency(metrics.revenue || 0)}</td>
                <td class="${colorClass}">${this.formatNumber(metrics.roas || 0, 2)}x</td>
              </tr>
            `;
              }
              safeSetHTML(
                platformTableBody,
                html || '<tr><td colspan="4">No data available</td></tr>'
              );
            }
          }
        }
      } catch (_error) {}
    },

    /**
     * Load MER (Marketing Efficiency Ratio) Metrics
     */
    loadMERMetrics: async function () {
      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=mer-metrics`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            const data = result.data;

            // Update MER value
            const merEl = document.getElementById('mer-value');
            if (merEl && data.mer !== undefined) {
              merEl.textContent = `${this.formatNumber(data.mer, 2)}x`;
            }

            // Update total revenue
            const revenueEl = document.getElementById('mer-revenue');
            if (revenueEl && data.totalRevenue !== undefined) {
              revenueEl.textContent = this.formatCurrency(data.totalRevenue);
            }

            // Update total spend
            const spendEl = document.getElementById('mer-spend');
            if (spendEl && data.totalSpend !== undefined) {
              spendEl.textContent = this.formatCurrency(data.totalSpend);
            }

            // Update benchmark comparison
            const benchmarkEl = document.getElementById('mer-benchmark');
            if (benchmarkEl && data.benchmark !== undefined) {
              const comparison = data.mer >= data.benchmark ? 'Above' : 'Below';
              const colorClass =
                data.mer >= data.benchmark
                  ? 'metric-status-good'
                  : 'metric-status-needs-improvement';
              safeSetHTML(
                benchmarkEl,
                `<span class="${colorClass}">${comparison} benchmark (${this.formatNumber(data.benchmark, 2)}x)</span>`
              );
            }
          }
        }
      } catch (_error) {}
    },

    /**
     * Load Profit Metrics
     */
    loadProfitMetrics: async function (period) {
      try {
        const resolvedPeriod =
          period || document.getElementById('profit-margin-period')?.value || '30d';
        const response = await fetch(
          `${this.config.apiProxyUrl}?endpoint=profit-metrics&period=${resolvedPeriod}`
        );
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            const overall = result.data.overall || result.data; // Overall metrics
            const fullData = result.data; // Full response with trend

            // Update gross profit
            const grossProfitEl = document.getElementById('gross-profit-value');
            if (grossProfitEl && overall.grossProfit !== undefined) {
              grossProfitEl.textContent = this.formatCurrency(overall.grossProfit);
            }

            // Update gross margin
            const marginEl = document.getElementById('gross-profit-margin');
            if (marginEl && overall.grossMargin !== undefined) {
              marginEl.textContent = `${this.formatNumber(overall.grossMargin, 1)}% margin`;
            }

            // Update net profit
            const netProfitEl = document.getElementById('net-profit-value');
            if (netProfitEl && overall.netProfit !== undefined) {
              netProfitEl.textContent = this.formatCurrency(overall.netProfit);
            }

            // Update net profit margin
            const netMarginEl = document.getElementById('net-profit-margin');
            if (netMarginEl && overall.netMargin !== undefined) {
              netMarginEl.textContent = `${this.formatNumber(overall.netMargin, 1)}% margin`;
            }

            // Update contribution margin
            const contributionEl = document.getElementById('contribution-margin');
            if (contributionEl && overall.contributionMargin !== undefined) {
              contributionEl.textContent = `${this.formatNumber(overall.contributionMargin, 1)}%`;
            }

            // Update breakdown
            const breakdownEl = document.getElementById('profit-breakdown');
            if (breakdownEl) {
              let html = '<div class="metric-list">';
              html += `
            <div class="metric-item">
              <span class="metric-label">Revenue</span>
              <span class="metric-value">${this.formatCurrency(overall.revenue || 0)}</span>
            </div>
            <div class="metric-item">
              <span class="metric-label">COGS</span>
              <span class="metric-value metric-status-poor">${this.formatCurrency(overall.cogs || 0)}</span>
            </div>
            <div class="metric-item">
              <span class="metric-label">Operating Expenses</span>
              <span class="metric-value metric-status-poor">${this.formatCurrency((overall.shipping || 0) + (overall.adSpend || 0))}</span>
            </div>
          `;
              html += '</div>';
              safeSetHTML(breakdownEl, html);
            }

            // Render Profit Margin Trend chart
            const chartCanvas = document.getElementById('profit-margin-chart');
            if (chartCanvas && fullData.trend && Array.isArray(fullData.trend)) {
              const ctx = chartCanvas.getContext('2d');

              // Destroy existing chart if it exists
              if (this.profitMarginChart) {
                this.profitMarginChart.destroy();
              }

              // Create new chart
              this.profitMarginChart = new Chart(ctx, {
                type: 'line',
                data: {
                  labels: fullData.trend.map((item) => item.date || item.label),
                  datasets: [
                    {
                      label: 'Gross Margin %',
                      data: fullData.trend.map((item) => item.grossMargin || 0),
                      borderColor: '#10b981',
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      borderWidth: 2,
                      fill: true,
                      tension: 0.4,
                    },
                    {
                      label: 'Net Margin %',
                      data: fullData.trend.map((item) => item.netMargin || 0),
                      borderColor: '#3b82f6',
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      borderWidth: 2,
                      fill: true,
                      tension: 0.4,
                    },
                  ],
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: true,
                      position: 'top',
                      labels: { color: 'rgba(255, 255, 255, 0.8)' },
                    },
                    tooltip: {
                      mode: 'index',
                      intersect: false,
                      callbacks: {
                        label: function (context) {
                          return context.dataset.label + ': ' + context.parsed.y.toFixed(1) + '%';
                        },
                      },
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        color: 'rgba(255, 255, 255, 0.6)',
                        callback: function (value) {
                          return value + '%';
                        },
                      },
                      grid: { color: 'rgba(255, 255, 255, 0.1)' },
                    },
                    x: {
                      ticks: { color: 'rgba(255, 255, 255, 0.6)' },
                      grid: { color: 'rgba(255, 255, 255, 0.05)' },
                    },
                  },
                },
              });
            }
          }
        }
      } catch (_error) {}
    },

    /**
     * Load LTV Drivers
     */
    loadLTVDrivers: async function () {
      try {
        // Fetch both LTV drivers and repeat behavior to calculate all metrics
        const [ltvResponse, repeatResponse] = await Promise.all([
          fetch(`${this.config.apiProxyUrl}?endpoint=ltv-drivers`),
          fetch(`${this.config.apiProxyUrl}?endpoint=repeat-behavior`),
        ]);

        if (ltvResponse.ok && repeatResponse.ok) {
          const ltvResult = await ltvResponse.json();
          const repeatResult = await repeatResponse.json();

          if (ltvResult.success && ltvResult.data) {
            const data = ltvResult.data;

            // Calculate overall LTV from top product drivers
            const ltvEl = document.getElementById('customer-ltv-full');
            if (ltvEl && data.topProductDrivers && data.topProductDrivers.length > 0) {
              const totalLTV = data.topProductDrivers.reduce((sum, p) => sum + p.totalLTV, 0);
              const totalCustomers = data.topProductDrivers.reduce(
                (sum, p) => sum + p.customers,
                0
              );
              const avgLTV = totalCustomers > 0 ? totalLTV / totalCustomers : 0;
              ltvEl.textContent = this.formatCurrency(avgLTV);

              const ltvCalcEl = document.getElementById('ltv-calculation');
              if (ltvCalcEl) {
                ltvCalcEl.textContent = `${totalCustomers} customers (90d)`;
              }

              // Calculate and display AOV (Average Order Value)
              // AOV = Total LTV / Total number of orders (estimate from frequency distribution)
              const aovEl = document.getElementById('ltv-aov-value');
              if (aovEl && repeatResult.success && repeatResult.data) {
                const repeatData = repeatResult.data;
                // Calculate total orders from frequency distribution
                let totalOrders = 0;
                if (repeatData.frequencyDistribution) {
                  Object.entries(repeatData.frequencyDistribution).forEach(([freq, count]) => {
                    const orderCount = freq === '10+' ? 10 : parseInt(freq, 10);
                    totalOrders += orderCount * count;
                  });
                }

                const avgOrderValue = totalOrders > 0 ? totalLTV / totalOrders : 0;
                aovEl.textContent = this.formatCurrency(avgOrderValue);

                const aovOrdersEl = document.getElementById('ltv-aov-orders');
                if (aovOrdersEl) {
                  aovOrdersEl.textContent = `${totalOrders} orders (90d)`;
                }
              }

              // Calculate Purchase Frequency (orders per customer per year)
              const freqEl = document.getElementById('purchase-frequency-value');
              if (freqEl && repeatResult.success && repeatResult.data) {
                const repeatData = repeatResult.data;
                // Calculate avg orders per customer from distribution
                let totalOrders = 0;
                let totalCustomersInDist = 0;
                if (repeatData.frequencyDistribution) {
                  Object.entries(repeatData.frequencyDistribution).forEach(([freq, count]) => {
                    const orderCount = freq === '10+' ? 10 : parseInt(freq, 10);
                    totalOrders += orderCount * count;
                    totalCustomersInDist += count;
                  });
                }

                const avgOrdersPerCustomer =
                  totalCustomersInDist > 0 ? totalOrders / totalCustomersInDist : 0;
                // Annualize based on 90-day period
                const annualizedFrequency = avgOrdersPerCustomer * (365 / 90);
                freqEl.textContent = `${this.formatNumber(annualizedFrequency, 1)}x`;
              }

              // Calculate Customer Lifespan (estimate from time between purchases)
              const lifespanEl = document.getElementById('customer-lifespan-value');
              if (lifespanEl && repeatResult.success && repeatResult.data) {
                const repeatData = repeatResult.data;
                // Estimate lifespan: avgDaysBetweenPurchases * average number of purchases
                if (repeatData.avgDaysBetweenPurchases && repeatData.frequencyDistribution) {
                  let totalOrders = 0;
                  let totalCustomersInDist = 0;
                  Object.entries(repeatData.frequencyDistribution).forEach(([freq, count]) => {
                    const orderCount = freq === '10+' ? 10 : parseInt(freq, 10);
                    totalOrders += orderCount * count;
                    totalCustomersInDist += count;
                  });

                  const avgOrdersPerCustomer =
                    totalCustomersInDist > 0 ? totalOrders / totalCustomersInDist : 0;
                  const lifespanDays = repeatData.avgDaysBetweenPurchases * avgOrdersPerCustomer;
                  const lifespanMonths = lifespanDays / 30;

                  lifespanEl.textContent = `${this.formatNumber(lifespanMonths, 0)} mo`;

                  const lifespanRangeEl = document.getElementById('customer-lifespan-range');
                  if (lifespanRangeEl) {
                    lifespanRangeEl.textContent = 'Last 90 days';
                  }
                }
              }
            }
          }
        }
      } catch (_error) {}
    },

    /**
     * Load Repeat Purchase Behavior
     */
    loadRepeatBehavior: async function () {
      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=repeat-behavior`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            const data = result.data;

            // Update repeat purchase rate (using reorderProbability from backend)
            const repeatRateEl = document.getElementById('repeat-purchase-rate-value');
            if (repeatRateEl && data.reorderProbability !== undefined) {
              repeatRateEl.textContent = `${this.formatNumber(data.reorderProbability, 1)}%`;
            }

            // Update repeat customers count
            const repeatCountEl = document.getElementById('repeat-customers-count');
            if (repeatCountEl && data.customersWithRepeats !== undefined) {
              repeatCountEl.textContent = `${data.customersWithRepeats} of ${data.totalCustomers} customers (90d)`;
            }

            // Update purchase distribution (using frequencyDistribution from backend)
            const distributionEl = document.getElementById('purchase-distribution');
            if (distributionEl && data.frequencyDistribution) {
              let html = '<div class="distribution-chart">';

              // Calculate total for percentage
              const total = Object.values(data.frequencyDistribution).reduce(
                (sum, count) => sum + count,
                0
              );

              Object.entries(data.frequencyDistribution).forEach(([bucket, count]) => {
                const percentage = total > 0 ? (count / total) * 100 : 0;
                html += `
              <div class="distribution-item">
                <span class="distribution-label">${bucket} purchases</span>
                <div class="distribution-bar">
                  <div class="distribution-fill" style="width: ${percentage}%"></div>
                </div>
                <span class="distribution-value">${this.formatNumber(percentage, 1)}%</span>
              </div>
            `;
              });
              html += '</div>';
              safeSetHTML(distributionEl, html);
            }

            // Update customer count info
            const segmentsEl = document.getElementById('customer-segments');
            if (segmentsEl && data.totalCustomers !== undefined) {
              let html = '<div class="segments-list">';
              html += `
            <div class="segment-item">
              <h4>Repeat Customers</h4>
              <p>${data.customersWithRepeats || 0} of ${data.totalCustomers} customers</p>
              <p>${this.formatNumber(data.reorderProbability || 0, 1)}% reorder probability</p>
            </div>
          `;
              html += '</div>';
              safeSetHTML(segmentsEl, html);
            }
          }
        }
      } catch (_error) {}
    },

    /**
     * Load CAC Payback Period
     */
    loadCACPayback: async function () {
      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=cac-payback`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            const data = result.data;

            // Calculate overall average payback period
            const channels = Object.values(data.byChannel || {});
            if (channels.length > 0) {
              const avgPayback =
                channels.reduce((sum, ch) => sum + (ch.avgPaybackMonths || 0), 0) / channels.length;
              const avgCAC = channels.reduce((sum, ch) => sum + (ch.cac || 0), 0) / channels.length;

              // Estimate average margin (assuming 40% gross margin)
              const avgMargin = avgCAC * 0.4;

              // Update main payback period card
              const paybackValueEl = document.getElementById('cac-payback-value');
              if (paybackValueEl) {
                safeSetHTML(
                  paybackValueEl,
                  `<span style="font-size: 2.5rem; font-weight: 700;">${this.formatNumber(avgPayback, 1)} mo</span>`
                );
              }

              const paybackDetailsEl = document.getElementById('cac-payback-details');
              if (paybackDetailsEl) {
                paybackDetailsEl.textContent = `CAC $${this.formatNumber(avgCAC, 0)} / Margin $${this.formatNumber(avgMargin, 0)}`;
              }

              // Calculate and update LTV:CAC ratio (assuming 18-month lifespan and 2.5 orders/year)
              const estimatedLTV = avgCAC * avgPayback * 0.5; // Simplified calculation
              const ltvCacRatio = avgCAC > 0 ? estimatedLTV / avgCAC : 0;

              const ltvCacValueEl = document.getElementById('ltv-cac-ratio-value');
              if (ltvCacValueEl) {
                safeSetHTML(
                  ltvCacValueEl,
                  `<span style="font-size: 2.5rem; font-weight: 700;">${this.formatNumber(ltvCacRatio, 1)}:1</span>`
                );
              }

              const ltvCacHealthEl = document.getElementById('ltv-cac-health');
              if (ltvCacHealthEl) {
                const health =
                  ltvCacRatio >= 3
                    ? 'Excellent'
                    : ltvCacRatio >= 2
                      ? 'Good'
                      : ltvCacRatio >= 1
                        ? 'Fair'
                        : 'Poor';
                const healthColor =
                  ltvCacRatio >= 3
                    ? '#4ade80'
                    : ltvCacRatio >= 2
                      ? '#fbbf24'
                      : ltvCacRatio >= 1
                        ? '#fb923c'
                        : '#f87171';
                safeSetHTML(
                  ltvCacHealthEl,
                  `<span style="color: ${healthColor}">${health} health</span>`
                );
              }
            }

            // Update payback by channel table
            const tableBody = document.getElementById('payback-channel-table-body');
            if (tableBody && data.byChannel) {
              let html = '';
              Object.entries(data.byChannel).forEach(([channel, metrics]) => {
                const channelName = channel.charAt(0).toUpperCase() + channel.slice(1);
                const colorClass =
                  metrics.avgPaybackMonths <= 6
                    ? 'metric-good'
                    : metrics.avgPaybackMonths <= 12
                      ? 'metric-warning'
                      : 'metric-poor';
                html += `
              <tr>
                <td><strong>${channelName}</strong></td>
                <td>$${this.formatNumber(metrics.cac, 0)}</td>
                <td>$${this.formatNumber(metrics.cac * 0.4, 0)}</td>
                <td><span class="${colorClass}">${this.formatNumber(metrics.avgPaybackMonths, 1)} months</span></td>
              </tr>
            `;
              });
              safeSetHTML(tableBody, html);
            }
          }
        }
      } catch (_error) {}
    },

    /**
     * Load Multi-Touch Attribution
     */
    loadMultiTouchAttribution: async function () {
      try {
        const tableBody = document.getElementById('attribution-model-table-body');
        if (!tableBody) {
          return;
        }

        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=multi-touch-attribution`);
        if (!response.ok) {
          safeSetHTML(
            tableBody,
            '<tr><td colspan="4" style="text-align: center; color: rgba(255, 255, 255, 0.6);">Failed to load attribution data</td></tr>'
          );
          return;
        }

        const result = await response.json();
        if (!result.success || !result.data) {
          safeSetHTML(
            tableBody,
            '<tr><td colspan="4" style="text-align: center; color: rgba(255, 255, 255, 0.6);">No attribution data available</td></tr>'
          );
          return;
        }

        const { firstClick, lastClick, linear } = result.data;

        // firstClick/lastClick/linear are arrays of {channel, revenue} sorted by revenue.
        // Build a Set of channel names, then lookup maps for O(1) revenue access.
        const channels = new Set();
        (firstClick || []).forEach((item) => channels.add(item.channel));
        (lastClick || []).forEach((item) => channels.add(item.channel));
        (linear || []).forEach((item) => channels.add(item.channel));

        if (channels.size === 0) {
          safeSetHTML(
            tableBody,
            '<tr><td colspan="4" style="text-align: center; color: rgba(255, 255, 255, 0.6);">No channel data available</td></tr>'
          );
          return;
        }

        const firstClickMap = Object.fromEntries((firstClick || []).map((i) => [i.channel, i]));
        const lastClickMap = Object.fromEntries((lastClick || []).map((i) => [i.channel, i]));
        const linearMap = Object.fromEntries((linear || []).map((i) => [i.channel, i]));

        // Build table rows
        let html = '';
        channels.forEach((channel) => {
          const firstRevenue = firstClickMap[channel]?.revenue || 0;
          const lastRevenue = lastClickMap[channel]?.revenue || 0;
          const linearRevenue = linearMap[channel]?.revenue || 0;

          html += `
        <tr>
          <td>${channel}</td>
          <td>${this.formatCurrency(firstRevenue, 0)}</td>
          <td>${this.formatCurrency(lastRevenue, 0)}</td>
          <td>${this.formatCurrency(linearRevenue, 0)}</td>
        </tr>
      `;
        });

        safeSetHTML(tableBody, html);
      } catch (_error) {
        const tableBody = document.getElementById('attribution-model-table-body');
        if (tableBody) {
          safeSetHTML(
            tableBody,
            '<tr><td colspan="4" style="text-align: center; color: rgba(255, 255, 255, 0.6);">Error loading data</td></tr>'
          );
        }
      }
    },

    /**
     * Load Revenue Forecast
     */
    loadRevenueForecast: async function () {
      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=revenue-forecast`);
        if (response.ok) {
          const result = await response.json();

          if (!result.success) {
            // Handle error - show message
            const confidenceEl = document.getElementById('forecast-confidence');
            if (confidenceEl) {
              confidenceEl.textContent = result.error || 'Unable to generate forecast';
              confidenceEl.style.color = 'rgba(255, 255, 255, 0.6)';
            }
            return;
          }

          if (result.success && result.data) {
            const data = result.data;
            const forecasts = data.forecasts || {};

            // Update forecast values - use projected revenue (next30Days from backend)
            const projectedEl = document.getElementById('projected-revenue');
            if (projectedEl && forecasts.next30Days !== undefined) {
              projectedEl.textContent = this.formatCurrency(forecasts.next30Days, 0);
            }

            // Calculate min/max based on confidence interval (±15%)
            const minExpectedEl = document.getElementById('min-expected-revenue');
            if (minExpectedEl && forecasts.next30Days !== undefined) {
              const minValue = forecasts.next30Days * 0.85;
              minExpectedEl.textContent = this.formatCurrency(minValue, 0);
            }

            const maxExpectedEl = document.getElementById('max-expected-revenue');
            if (maxExpectedEl && forecasts.next30Days !== undefined) {
              const maxValue = forecasts.next30Days * 1.15;
              maxExpectedEl.textContent = this.formatCurrency(maxValue, 0);
            }

            // Update confidence level
            const confidenceEl = document.getElementById('forecast-confidence');
            if (confidenceEl && data.confidence !== undefined) {
              confidenceEl.textContent = `${this.formatNumber(data.confidence, 0)}% confidence`;
              confidenceEl.style.color = '';
            }

            // Update revenue forecast chart (canvas)
            const chartCanvas = document.getElementById('revenue-forecast-chart');
            if (chartCanvas && data.trend) {
              // Destroy existing chart if it exists
              if (this.charts && this.charts.revenueForecast) {
                this.charts.revenueForecast.destroy();
              }

              // Create new forecast chart
              const ctx = chartCanvas.getContext('2d');
              const labels = data.trend.map((p) => p.month);
              const values = data.trend.map((p) => p.value);

              this.charts.revenueForecast = new Chart(ctx, {
                type: 'line',
                data: {
                  labels: labels,
                  datasets: [
                    {
                      label: 'Projected Revenue',
                      data: values,
                      borderColor: 'rgba(124, 58, 237, 1)',
                      backgroundColor: 'rgba(124, 58, 237, 0.1)',
                      borderWidth: 2,
                      fill: true,
                      tension: 0.4,
                    },
                  ],
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: function (value) {
                          return '$' + (value / 1000).toFixed(0) + 'k';
                        },
                      },
                    },
                  },
                },
              });
            }

            // Update growth rate
            const growthEl = document.getElementById('forecast-growth');
            if (growthEl && data.growthRate !== undefined) {
              const arrow = data.growthRate > 0 ? '↑' : data.growthRate < 0 ? '↓' : '→';
              const colorClass = data.growthRate > 0 ? 'metric-status-good' : 'metric-status-poor';
              safeSetHTML(
                growthEl,
                `<span class="${colorClass}">${arrow} ${Math.abs(data.growthRate).toFixed(1)}% MoM</span>`
              );
            }
          }
        }
      } catch (_error) {}
    },

    /**
     * Load Sell-Through Rate
     */
    loadSellThroughRate: async function () {
      // This read against data.overall/byCategory/slowMovers and wrote to ids
      // #overall-sell-through/#sell-through-by-category/#slow-movers. The proxy returns
      // {period, products:[...]} and the card markup uses #overall-sellthrough-rate and
      // #sellthrough-table-body, so every write was guarded away against a null element
      // and the empty catch hid it: the card kept its skeleton forever.
      const GOOD_RATE_PCT = 80;
      const FAIR_RATE_PCT = 60;
      const TABLE_COLUMN_COUNT = 4;

      const overallEl = document.getElementById('overall-sellthrough-rate');
      const tableBodyEl = document.getElementById('sellthrough-table-body');

      const rateClass = (rate) =>
        rate >= GOOD_RATE_PCT
          ? 'metric-status-good'
          : rate >= FAIR_RATE_PCT
            ? 'metric-status-needs-improvement'
            : 'metric-status-poor';

      const showMessage = (message) => {
        if (overallEl) {
          safeSetHTML(overallEl, '<span class="metric-status-poor">--%</span>');
        }
        if (tableBodyEl) {
          safeSetHTML(
            tableBodyEl,
            `<tr><td colspan="${TABLE_COLUMN_COUNT}">${this.escapeHtml(message)}</td></tr>`
          );
        }
      };

      try {
        const response = await fetch(`${this.config.apiProxyUrl}?endpoint=sell-through-rate`);
        if (!response.ok) {
          showMessage(`Sell-through data unavailable (HTTP ${response.status})`);
          return;
        }

        const result = await response.json();
        const products = result?.data?.products;
        if (!result?.success || !Array.isArray(products)) {
          showMessage(result?.error || result?.data?.error || 'Sell-through data unavailable');
          return;
        }
        if (products.length === 0) {
          showMessage('No sell-through data for this period');
          return;
        }

        const totals = products.reduce(
          (acc, product) => {
            acc.sold += product.unitsSold || 0;
            acc.available += product.unitsAvailable || 0;
            return acc;
          },
          { sold: 0, available: 0 }
        );
        const startingInventory = totals.sold + totals.available;
        const overallRate = startingInventory > 0 ? (totals.sold / startingInventory) * 100 : 0;

        this.core.scheduleDomWrite(() => {
          if (overallEl) {
            safeSetHTML(
              overallEl,
              `<span class="${rateClass(overallRate)}">${this.formatNumber(overallRate, 1)}%</span>`
            );
          }

          if (tableBodyEl) {
            const rows = products
              .map((product) => {
                const sold = product.unitsSold || 0;
                const starting = sold + (product.unitsAvailable || 0);
                const rate = product.sellThroughRate || 0;
                return `
              <tr>
                <td>${this.escapeHtml(product.title || 'Untitled')}</td>
                <td>${this.formatNumber(starting, 0)}</td>
                <td>${this.formatNumber(sold, 0)}</td>
                <td class="${rateClass(rate)}">${this.formatNumber(rate, 1)}%</td>
              </tr>
            `;
              })
              .join('');
            safeSetHTML(tableBodyEl, rows);
          }
        });
      } catch (error) {
        showMessage('Sell-through data failed to load');
        AdminDashboardCore.logError('[Admin] loadSellThroughRate error:', error);
      }
    },

    /**
     * Setup event listeners for new features
     */
    setupNewFeatureListeners: function () {
      // FIX: Ensure all tabs are siblings, not nested (temp workaround for cached Liquid)
      const tabOverview = document.getElementById('tab-overview');
      const allTabContents = document.querySelectorAll('.tab-content[id^="tab-"]');
      if (tabOverview) {
        const tabContainer = tabOverview.parentElement;
        allTabContents.forEach((tab) => {
          if (tab.id !== 'tab-overview' && tabOverview.contains(tab)) {
            if (tabContainer) {
              tabContainer.appendChild(tab);
            }
          }
        });
      }

      // Tab switching
      const tabButtons = document.querySelectorAll('.tab-btn');
      tabButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const tabId = btn.dataset.tab;
          debug('[Tab] switch to:', tabId);

          // Abort in-flight requests for the tab we are leaving to prevent stale
          // responses from overwriting data loaded for the new active tab.
          const previousActiveBtn = document.querySelector('.tab-btn.active');
          const previousTabId = previousActiveBtn?.dataset?.tab;
          if (previousTabId && previousTabId !== tabId && window.AdminDashboardAPI) {
            window.AdminDashboardAPI.cancelView(`tab-${previousTabId}`);
            debug('[Tab] cancelled in-flight requests for previous tab:', previousTabId);
          }

          // Update active states using DRY helpers
          _setButtonGroupActive('.tab-btn', btn, true);
          const tabContent = document.getElementById(`tab-${tabId}`);
          _setContentActive('.tab-content', `tab-${tabId}`);

          if (tabContent) {
            // Check again after animation completes
            setTimeout(() => {
              const finalStyle = window.getComputedStyle(tabContent);

              // Check parent container
              const parent = tabContent.parentElement;
              if (parent) {
                window.getComputedStyle(parent); // Ensure parent styles are computed
              }

              if (finalStyle.opacity === '0') {
                tabContent.style.opacity = '1';
              }
            }, 500);
          }

          // Lazy load tab data if not loaded
          if (!window.tabsLoaded) window.tabsLoaded = {};
          if (!window.tabsLoaded[tabId]) {
            AdminDashboard.loadTabData(tabId);
            window.tabsLoaded[tabId] = true;
          }
        });
      });

      // Dev-mode: log period selector changes via event delegation (covers all selectors)
      if (isDev) {
        document.addEventListener('change', (e) => {
          if (
            e.target &&
            e.target.tagName === 'SELECT' &&
            e.target.id &&
            e.target.id.includes('period')
          ) {
            debug('[Period] selector "' + e.target.id + '" changed to:', e.target.value);
          }
        });
      }

      // Top Converting Products period selector
      const topProductsPeriod = document.getElementById('top-products-period');
      if (topProductsPeriod) {
        topProductsPeriod.addEventListener('change', (e) => {
          this.loadTopConvertingProducts(e.target.value);
        });
      }

      // Cart Recovery period selector
      const cartRecoveryPeriod = document.getElementById('cart-recovery-period');
      if (cartRecoveryPeriod) {
        cartRecoveryPeriod.addEventListener('change', (e) => {
          this.loadCartRecoveryMetrics(e.target.value);
        });
      }

      // Traffic ROI period selector
      const trafficROIPeriod = document.getElementById('traffic-roi-period');
      if (trafficROIPeriod) {
        trafficROIPeriod.addEventListener('change', (e) => {
          this.loadTrafficSourceROI(e.target.value);
        });
      }

      // Smart Alerts refresh button
      const refreshAlerts = document.getElementById('refresh-alerts');
      if (refreshAlerts) {
        refreshAlerts.addEventListener('click', () => {
          this.loadSmartAlerts();
        });
      }

      // Klaviyo period selector
      const klaviyoPeriod = document.getElementById('klaviyo-period');
      if (klaviyoPeriod) {
        klaviyoPeriod.addEventListener('change', (e) => {
          this.loadKlaviyoCampaigns(e.target.value);
        });
      }

      // Tips card click listener - show detailed tip orders
      const tipsCard = document.querySelector('.stat-card[data-tooltip*="tips"]');
      if (tipsCard) {
        tipsCard.style.cursor = 'pointer';
        tipsCard.addEventListener('click', () => {
          this.showDetailedTipOrders();
        });
      }

      // AI Agent Traffic card click listener - show detailed AI orders
      const aiCard = document.querySelector('.stat-card[data-tooltip*="AI shopping assistants"]');
      if (aiCard) {
        aiCard.style.cursor = 'pointer';
        aiCard.addEventListener('click', () => {
          this.showDetailedAIOrders();
        });
      }
    },
  };

  // Add CSS animations for toast
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideIn {
      from { transform: translateX(400px); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    @keyframes slideOut {
      from { transform: translateX(0); opacity: 1; }
      to { transform: translateX(400px); opacity: 0; }
    }
  `;
  document.head.appendChild(style);

  // Bootstrap.
  //
  // This script is loaded with `defer`, so by the time it runs the document has been fully
  // parsed and readyState is already "interactive" — never "loading". The old
  // `readyState === 'loading' ? DOMContentLoaded : run now` shape therefore ALWAYS took the
  // "run now" branch and initialized (and started loading data) before the login gate in
  // snippets/admin-dashboard.liquid had decided anything. Every request 401'd, the 401 handler
  // wiped the stored session and reloaded, and the user bounced back to the login screen.
  //
  // The gate is authentication, not DOM readiness: run immediately when a session already
  // exists, otherwise wait for the one-shot 'ccc:authenticated' event fired after a successful
  // login. init() is idempotent, so the two paths can never double-initialize.
  function bootstrapAdminDashboard() {
    AdminDashboard.init();
    AdminDashboard.setupQuickActions();
    AdminDashboard.setupVisitorsTooltip();
  }

  function isAdminAuthenticated() {
    try {
      return !!(
        window.CoraAdminAuth &&
        typeof window.CoraAdminAuth.isAuthenticated === 'function' &&
        window.CoraAdminAuth.isAuthenticated()
      );
    } catch (_e) {
      return false;
    }
  }

  AdminDashboard.bootstrap = bootstrapAdminDashboard;

  if (isAdminAuthenticated()) {
    bootstrapAdminDashboard();
  } else {
    document.addEventListener('ccc:authenticated', bootstrapAdminDashboard, { once: true });
  }

  // Make available globally
  window.AdminDashboard = AdminDashboard;

  // Dev-mode: expose a read-only state snapshot for runtime debugging.
  // Usage in console: window.__adminDash.debug()
  if (isDev) {
    window.__adminDash = {
      debug() {
        const core = window.AdminDashboardCore;
        const api = window.AdminDashboardAPI;
        return {
          state: core
            ? JSON.parse(
                JSON.stringify({
                  visitors: core.state.visitors?.length ?? 0,
                  activities: core.state.activities?.length ?? 0,
                  isFirstLoad: core.state.isFirstLoad,
                  lastDataFetchTimestamp: core.state.lastDataFetchTimestamp,
                  webVitals: core.state.webVitals,
                  idleListenersAdded: core.state.idleListenersAdded,
                  tabsLoaded: core.state.tabsLoaded,
                  requestQueueActive: core.requestQueue?.active ?? 0,
                  requestQueuePending: core.requestQueue?.queue?.length ?? 0,
                })
              )
            : null,
          cacheSize: api ? api.cache.size : 0,
          batchCacheSize: api ? api._batchCache.size : 0,
          inflightCount: api ? api._inflight.size : 0,
          isDev,
        };
      },
    };
  }
})();
